/**
 * Тип-сейфные функции сообщений. Имена сгенерированы из ключей messages/ru.json.
 * Вместо Paraglide compile step — прямой lookup + interpolate через runtime.
 *
 * Использование:
 *   import * as m from '@staiter/i18n';
 *   m.nav_dashboard();
 *   m.wo_warning_body_insufficient_stock({ item, sku, needed, available });
 */
import { getLanguageTag, t } from './runtime.js';
/**
 * F4.8 — плюрализация. Русский: 3 формы (one/few/many); прочие локали: one/other.
 * Заменяет наивную конкатенацию `{count} задач` (давало «2 задач» вместо «2 задачи»).
 */
function pluralForm(n, forms) {
    if (getLanguageTag() === 'ru') {
        const m10 = Math.abs(n) % 10;
        const m100 = Math.abs(n) % 100;
        if (m10 === 1 && m100 !== 11)
            return forms.one;
        if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20))
            return forms.few;
        return forms.many;
    }
    return n === 1 ? forms.one : forms.many;
}
/** F4.8 — «N задача/задачи/задач» с корректной плюрализацией (шаблоны проектов). */
export const tmpl_tasks_count = (p) => {
    const word = pluralForm(p.count, {
        one: t('tmpl_tasks_one'),
        few: t('tmpl_tasks_few'),
        many: t('tmpl_tasks_many'),
    });
    return `${p.count} ${word}`;
};
// ─── common + nav ────────────────────────────────────────────────────
/** D4.2 (TE-F1) — подписи горячих клавиш: оверлей виден с каждой страницы. */
export const shortcut_goto_dashboard = () => t('shortcut_goto_dashboard');
export const shortcut_goto_runs = () => t('shortcut_goto_runs');
export const shortcut_goto_conflicts = () => t('shortcut_goto_conflicts');
export const shortcut_goto_anomalies = () => t('shortcut_goto_anomalies');
export const shortcut_focus_search = () => t('shortcut_focus_search');
export const shortcut_new_run = () => t('shortcut_new_run');
export const shortcut_help = () => t('shortcut_help');
export const shortcut_list_nav = () => t('shortcut_list_nav');
export const shortcut_resolve_selected = () => t('shortcut_resolve_selected');
export const shortcut_open = () => t('shortcut_open');
export const shortcut_close = () => t('shortcut_close');
export const shortcut_command_palette = () => t('shortcut_command_palette');
export const shortcuts_overlay_title = () => t('shortcuts_overlay_title');
export const app_title_default = () => t('app_title_default');
export const app_title_loading = () => t('app_title_loading');
export const nav_section_agent_review = () => t('nav_section_agent_review');
export const nav_section_workspaces = () => t('nav_section_workspaces');
export const nav_section_inventory_ops = () => t('nav_section_inventory_ops');
export const nav_section_projects = () => t('nav_section_projects');
export const nav_section_finance = () => t('nav_section_finance');
export const nav_finance_tax_summary = () => t('nav_finance_tax_summary');
export const nav_section_reporting = () => t('nav_section_reporting');
export const nav_section_admin = () => t('nav_section_admin');
export const nav_dashboard = () => t('nav_dashboard');
export const nav_runs = () => t('nav_runs');
export const nav_conflicts = () => t('nav_conflicts');
export const nav_anomalies = () => t('nav_anomalies');
export const nav_evidence = () => t('nav_evidence');
export const nav_chat = () => t('nav_chat');
export const nav_approvals = () => t('nav_approvals');
export const nav_reconciliation = () => t('nav_reconciliation');
export const nav_writeoff = () => t('nav_writeoff');
export const nav_cost = () => t('nav_cost');
export const nav_period_close = () => t('nav_period_close');
export const nav_items = () => t('nav_items');
export const nav_warehouses = () => t('nav_warehouses');
export const nav_batches = () => t('nav_batches');
export const nav_boms = () => t('nav_boms');
export const nav_stock_balance = () => t('nav_stock_balance');
export const nav_stock_ledger = () => t('nav_stock_ledger');
export const nav_stock_entries = () => t('nav_stock_entries');
export const nav_stock_reconciliation = () => t('nav_stock_reconciliation');
export const nav_work_orders = () => t('nav_work_orders');
export const nav_landed_cost = () => t('nav_landed_cost');
export const nav_excel = () => t('nav_excel');
export const nav_docs = () => t('nav_docs');
export const nav_admin = () => t('nav_admin');
export const nav_profile = () => t('nav_profile');
export const nav_logout = () => t('nav_logout');
// ─── Phase 2 nav + coming-soon placeholders ──────────────────────────
export const nav_counterparties = () => t('nav_counterparties');
export const nav_invoices_ar = () => t('nav_invoices_ar');
export const nav_invoices_ap = () => t('nav_invoices_ap');
export const nav_payments = () => t('nav_payments');
export const nav_settlements = () => t('nav_settlements');
export const nav_finance_cogs = () => t('nav_finance_cogs');
export const nav_finance_cash_flow = () => t('nav_finance_cash_flow');
export const nav_finance_pnl = () => t('nav_finance_pnl');
export const nav_finance_esf = () => t('nav_finance_esf');
export const nav_finance_kpn = () => t('nav_finance_kpn');
export const nav_budgets = () => t('nav_budgets');
export const coming_soon_title = () => t('coming_soon_title');
export const coming_soon_description = () => t('coming_soon_description');
export const phase2_counterparties_title = () => t('phase2_counterparties_title');
export const phase2_invoices_ar_title = () => t('phase2_invoices_ar_title');
export const phase2_invoices_ap_title = () => t('phase2_invoices_ap_title');
export const phase2_payments_title = () => t('phase2_payments_title');
export const phase2_settlements_title = () => t('phase2_settlements_title');
export const phase2_finance_cogs_title = () => t('phase2_finance_cogs_title');
export const phase2_finance_cash_flow_title = () => t('phase2_finance_cash_flow_title');
export const phase2_finance_pnl_title = () => t('phase2_finance_pnl_title');
export const phase2_finance_esf_title = () => t('phase2_finance_esf_title');
export const phase2_finance_kpn_title = () => t('phase2_finance_kpn_title');
export const phase2_budgets_title = () => t('phase2_budgets_title');
// ─── ESF chart legend (FE-265 Pass 28) ────────────────────────────────
export const esf_chart_legend_pushed = () => t('esf_chart_legend_pushed');
export const esf_chart_legend_approved = () => t('esf_chart_legend_approved');
export const esf_chart_legend_draft = () => t('esf_chart_legend_draft');
// ─── Counterparty page ────────────────────────────────────────────────
export const cp_column_name = () => t('cp_column_name');
export const cp_column_kind = () => t('cp_column_kind');
export const cp_column_bin_iin = () => t('cp_column_bin_iin');
export const cp_column_bin = () => t('cp_column_bin');
export const cp_column_iin = () => t('cp_column_iin');
export const cp_bin_iin_placeholder = () => t('cp_bin_iin_placeholder');
export const cp_bin_iin_hint = () => t('cp_bin_iin_hint');
export const item_dialog_section_identity = () => t('item_dialog_section_identity');
export const item_dialog_section_classification = () => t('item_dialog_section_classification');
export const item_dialog_section_tracking = () => t('item_dialog_section_tracking');
export const item_field_type_hint = () => t('item_field_type_hint');
export const item_field_default_uom_hint = () => t('item_field_default_uom_hint');
export const item_field_stock_uom_hint = () => t('item_field_stock_uom_hint');
export const item_field_has_batch_hint = () => t('item_field_has_batch_hint');
export const item_field_has_serial_hint = () => t('item_field_has_serial_hint');
export const stock_entry_wizard_section_external = () => t('stock_entry_wizard_section_external');
export const stock_entry_wizard_section_internal = () => t('stock_entry_wizard_section_internal');
export const stock_entry_wizard_section_production = () => t('stock_entry_wizard_section_production');
export const stock_entry_wizard_help_link = () => t('stock_entry_wizard_help_link');
/** FE-71 Sprint N+ batch 3: source_system enum → label resolver.
 * Pass 70 FE-N: defensive — t() не бросает, возвращает `[missing: key]` строку.
 * Fallback к raw code если key missing (как payment_state_label выше). */
export const source_system_label = (p) => {
    const r = t(`source_system_label_${p.code}`);
    return typeof r === 'string' && r.startsWith('[missing:') ? p.code : r;
};
export const warehouses_btn_delete_aria = () => t('warehouses_btn_delete_aria');
export const warehouses_search_placeholder = () => t('warehouses_search_placeholder');
// FE-246 Pass 38: stock balance 3 Hero metrics
/** FE-91 Sprint O+: SR kind enum → localized label. */
export const sr_kind_label = (p) => {
    const key = `sr_kind_label_${p.kind}`;
    try {
        return t(key);
    }
    catch {
        return p.kind;
    }
};
/** FE-94 Sprint O+: SR status enum → localized label. */
export const sr_status_label = (p) => {
    const key = `sr_status_label_${p.status}`;
    try {
        return t(key);
    }
    catch {
        return p.status;
    }
};
export const invoice_wizard_step1_label = () => t('invoice_wizard_step1_label');
export const invoice_wizard_step2_label = () => t('invoice_wizard_step2_label');
export const invoice_wizard_step3_label = () => t('invoice_wizard_step3_label');
export const invoice_wizard_step4_label = () => t('invoice_wizard_step4_label');
export const settlement_wizard_step1_label = () => t('settlement_wizard_step1_label');
export const settlement_wizard_step2_label = () => t('settlement_wizard_step2_label');
export const settlement_wizard_step3_label = () => t('settlement_wizard_step3_label');
export const bom_wizard_step1_label = () => t('bom_wizard_step1_label');
export const bom_wizard_step2_label = () => t('bom_wizard_step2_label');
export const bom_wizard_step3_label = () => t('bom_wizard_step3_label');
export const budget_wizard_step1_label = () => t('budget_wizard_step1_label');
export const budget_wizard_step2_label = () => t('budget_wizard_step2_label');
export const budget_wizard_step3_label = () => t('budget_wizard_step3_label');
export const item_dialog_btn_create_and_add = () => t('item_dialog_btn_create_and_add');
export const item_dialog_btn_create_and_open = () => t('item_dialog_btn_create_and_open');
export const se_type_hint_requires_wo = () => t('se_type_hint_requires_wo');
export const se_type_hint_no_cost = () => t('se_type_hint_no_cost');
export const se_wizard_step1_label = () => t('se_wizard_step1_label');
export const se_wizard_step2_label = () => t('se_wizard_step2_label');
export const se_wizard_step3_label = () => t('se_wizard_step3_label');
export const se_wizard_stepper_aria = () => t('se_wizard_stepper_aria');
export const wo_new_section_product = () => t('wo_new_section_product');
export const wo_new_section_schedule = () => t('wo_new_section_schedule');
export const wo_new_section_warehouses = () => t('wo_new_section_warehouses');
export const wo_new_section_notes = () => t('wo_new_section_notes');
export const login_remember_me_label = () => t('login_remember_me_label');
export const login_remember_me_hint = () => t('login_remember_me_hint');
export const se_bulk_paste_label = () => t('se_bulk_paste_label');
export const se_bulk_paste_prompt = () => t('se_bulk_paste_prompt');
export const se_bulk_paste_no_rows = () => t('se_bulk_paste_no_rows');
export const lc_new_guidance = () => t('lc_new_guidance');
export const wo_stay_on_page_hint = () => t('wo_stay_on_page_hint');
export const ai_content_disclaimer_short = () => t('ai_content_disclaimer_short');
export const ai_content_disclaimer_full = () => t('ai_content_disclaimer_full');
export const evidence_dedup_hint = () => t('evidence_dedup_hint');
export const approvals_empty_filter_title = () => t('approvals_empty_filter_title');
export const approvals_empty_filter_action = () => t('approvals_empty_filter_action');
export const lc_col_charge_title = () => t('lc_col_charge_title');
export const lc_col_charge_amount = () => t('lc_col_charge_amount');
export const lc_col_charge_currency = () => t('lc_col_charge_currency');
export const lc_col_charge_basis = () => t('lc_col_charge_basis');
export const cp_column_tax_status = () => t('cp_column_tax_status');
export const cp_column_currency = () => t('cp_column_currency');
export const cp_column_active = () => t('cp_column_active');
export const cp_column_actions = () => t('cp_column_actions');
export const cp_kind_customer = () => t('cp_kind_customer');
export const cp_kind_supplier = () => t('cp_kind_supplier');
export const cp_kind_employee = () => t('cp_kind_employee');
export const cp_kind_other = () => t('cp_kind_other');
export const cp_tax_vat_payer = () => t('cp_tax_vat_payer');
export const cp_tax_simplified = () => t('cp_tax_simplified');
export const cp_tax_patent = () => t('cp_tax_patent');
export const cp_tax_individual = () => t('cp_tax_individual');
export const cp_filter_q_placeholder = () => t('cp_filter_q_placeholder');
export const cp_filter_all_kinds = () => t('cp_filter_all_kinds');
export const cp_empty_title = () => t('cp_empty_title');
export const cp_button_new = () => t('cp_button_new');
export const cp_button_push_1c = () => t('cp_button_push_1c');
export const cp_toast_created = () => t('cp_toast_created');
export const cp_toast_create_failed = () => t('cp_toast_create_failed');
export const cp_toast_updated = () => t('cp_toast_updated');
export const cp_toast_update_failed = () => t('cp_toast_update_failed');
export const wo_toast_updated = () => t('wo_toast_updated');
export const wo_toast_update_failed = () => t('wo_toast_update_failed');
export const wo_field_qty_to_produce = () => t('wo_field_qty_to_produce');
export const wo_field_planned_start = () => t('wo_field_planned_start');
export const wo_field_planned_end = () => t('wo_field_planned_end');
export const wo_field_remarks = () => t('wo_field_remarks');
export const wo_edit_locked_title = () => t('wo_edit_locked_title');
export const wo_edit_locked_hint = (p) => t('wo_edit_locked_hint', p);
export const so_toast_updated = () => t('so_toast_updated');
export const so_toast_update_failed = () => t('so_toast_update_failed');
export const so_edit_locked_title = () => t('so_edit_locked_title');
export const so_edit_locked_hint = (p) => t('so_edit_locked_hint', p);
export const cp_field_payment_terms = () => t('cp_field_payment_terms');
export const cp_field_contract_number = () => t('cp_field_contract_number');
export const cp_field_contract_date = () => t('cp_field_contract_date');
export const cp_field_onec_ref = () => t('cp_field_onec_ref');
export const cp_kind_label = (p) => t(`cp_kind_${p.kind}`);
export const cp_tax_status_label = (p) => t(`cp_tax_${p.tax_status}`);
// ─── Invoice page ────────────────────────────────────────────────────
export const invoice_column_doc_number = () => t('invoice_column_doc_number');
export const invoice_column_issue_date = () => t('invoice_column_issue_date');
export const invoice_column_due_date = () => t('invoice_column_due_date');
export const invoice_column_counterparty = () => t('invoice_column_counterparty');
export const invoice_column_total = () => t('invoice_column_total');
export const invoice_column_outstanding = () => t('invoice_column_outstanding');
export const invoice_column_state = () => t('invoice_column_state');
export const invoice_state_draft = () => t('invoice_state_draft');
export const invoice_state_posted = () => t('invoice_state_posted');
export const invoice_state_partially_paid = () => t('invoice_state_partially_paid');
export const invoice_state_paid = () => t('invoice_state_paid');
export const invoice_state_cancelled = () => t('invoice_state_cancelled');
export const invoice_state_void = () => t('invoice_state_void');
export const invoice_state_pending_approval = () => t('invoice_state_pending_approval');
/** FE-N Pass 73: defensive enum→label resolver (как payment_state_label). */
export const invoice_state_label = (p) => {
    const r = t(`invoice_state_${p.state}`);
    return typeof r === 'string' && r.startsWith('[missing:') ? p.state : r;
};
export const invoice_detail_notes = () => t('invoice_detail_notes');
export const invoice_detail_onec_ref = () => t('invoice_detail_onec_ref');
export const invoice_column_subtotal = () => t('invoice_column_subtotal');
export const invoice_column_vat_total = () => t('invoice_column_vat_total');
export const invoice_empty_title = () => t('invoice_empty_title');
export const invoice_button_new = () => t('invoice_button_new');
export const invoice_button_post = () => t('invoice_button_post');
export const invoice_button_cancel = () => t('invoice_button_cancel');
export const invoice_button_push_1c = () => t('invoice_button_push_1c');
export const invoice_toast_updated = () => t('invoice_toast_updated');
export const invoice_toast_update_failed = () => t('invoice_toast_update_failed');
export const invoice_field_issue_date = () => t('invoice_field_issue_date');
export const invoice_field_due_date = () => t('invoice_field_due_date');
export const invoice_field_notes = () => t('invoice_field_notes');
export const payment_toast_updated = () => t('payment_toast_updated');
export const payment_toast_update_failed = () => t('payment_toast_update_failed');
export const payment_field_payment_date = () => t('payment_field_payment_date');
export const payment_field_amount = () => t('payment_field_amount');
export const payment_field_notes = () => t('payment_field_notes');
export const error_entity_not_editable = (p) => t('error_entity_not_editable', p);
export const invoice_edit_title = () => t('invoice_edit_title');
export const payment_edit_title = () => t('payment_edit_title');
export const payment_field_currency = () => t('payment_field_currency');
export const payment_field_method = () => t('payment_field_method');
export const stock_entry_edit_title = () => t('stock_entry_edit_title');
export const sr_edit_title = () => t('sr_edit_title');
export const sr_toast_updated = () => t('sr_toast_updated');
export const sr_toast_update_failed = () => t('sr_toast_update_failed');
export const detail_last_updated = () => t('detail_last_updated');
export const detail_view_audit_log = () => t('detail_view_audit_log');
export const lc_edit_title = () => t('lc_edit_title');
export const lc_toast_updated = () => t('lc_toast_updated');
export const lc_toast_update_failed = () => t('lc_toast_update_failed');
export const route_sr_col_counted_qty = () => t('route_sr_col_counted_qty');
export const route_sr_col_warehouse = () => t('route_sr_col_warehouse');
export const route_sr_col_kind = () => t('route_sr_col_kind');
export const route_sr_col_status = () => t('route_sr_col_status');
export const route_sr_col_posting_date = () => t('route_sr_col_posting_date');
export const stock_entry_field_posting_date = () => t('stock_entry_field_posting_date');
export const stock_entry_field_notes = () => t('stock_entry_field_notes');
export const budget_edit_title = () => t('budget_edit_title');
export const budget_field_name = () => t('budget_field_name');
export const budget_field_project_tag = () => t('budget_field_project_tag');
export const budget_field_period_from = () => t('budget_field_period_from');
export const budget_field_period_to = () => t('budget_field_period_to');
// FE Pass 30: invoice detail line columns
export const invoice_column_line_no = () => t('invoice_column_line_no');
export const invoice_column_description = () => t('invoice_column_description');
export const invoice_column_qty = () => t('invoice_column_qty');
export const invoice_column_unit_price = () => t('invoice_column_unit_price');
export const invoice_column_vat = () => t('invoice_column_vat');
export const invoice_column_line_total = () => t('invoice_column_line_total');
// ─── Payment page ────────────────────────────────────────────────────
export const payment_column_date = () => t('payment_column_date');
export const payment_column_counterparty = () => t('payment_column_counterparty');
export const payment_column_amount = () => t('payment_column_amount');
export const payment_column_method = () => t('payment_column_method');
export const payment_column_direction = () => t('payment_column_direction');
export const payment_column_state = () => t('payment_column_state');
export const payment_detail_external_ref = () => t('payment_detail_external_ref');
export const payment_detail_bank_account = () => t('payment_detail_bank_account');
export const payment_detail_notes = () => t('payment_detail_notes');
export const payment_detail_onec_ref = () => t('payment_detail_onec_ref');
export const payment_detail_cf_article = () => t('payment_detail_cf_article');
export const payment_detail_cf_group = () => t('payment_detail_cf_group');
export const payment_direction_inflow = () => t('payment_direction_inflow');
export const payment_direction_outflow = () => t('payment_direction_outflow');
export const payment_method_bank_transfer = () => t('payment_method_bank_transfer');
export const payment_method_cash = () => t('payment_method_cash');
export const payment_method_card = () => t('payment_method_card');
export const payment_method_offset = () => t('payment_method_offset');
export const payment_method_other = () => t('payment_method_other');
export const payment_method_label = (p) => t(`payment_method_${p.method}`);
// FE-229: defensive — fallback к raw state code если key missing.
export const payment_state_label = (p) => {
    const r = t(`payment_state_${p.state}`);
    return typeof r === 'string' && r.startsWith('[missing:') ? p.state : r;
};
export const payment_empty_title = () => t('payment_empty_title');
export const payment_button_new = () => t('payment_button_new');
export const payment_button_allocate = () => t('payment_button_allocate');
export const payment_allocate_dialog_title = () => t('payment_allocate_dialog_title');
export const payment_allocate_method_manual = () => t('payment_allocate_method_manual');
export const payment_allocate_method_fifo = () => t('payment_allocate_method_fifo');
export const payment_allocations_empty_hint = () => t('payment_allocations_empty_hint');
export const payment_allocations_disabled_hint = () => t('payment_allocations_disabled_hint');
export const payment_external_ref_label = () => t('payment_external_ref_label');
export const payment_external_ref_hint = () => t('payment_external_ref_hint');
export const payment_notes_label = () => t('payment_notes_label');
export const payment_notes_placeholder = () => t('payment_notes_placeholder');
export const settlement_as_of_label = () => t('settlement_as_of_label');
export const settlement_as_of_hint = () => t('settlement_as_of_hint');
export const approval_task_kind_counterparty_push_1c = () => t('approval_task_kind_counterparty_push_1c');
export const approval_task_kind_kpn_push = () => t('approval_task_kind_kpn_push');
export const approval_task_kind_esf_push = () => t('approval_task_kind_esf_push');
export const approval_task_kind_landed_cost_submit = () => t('approval_task_kind_landed_cost_submit');
export const approval_task_kind_invoice_post = () => t('approval_task_kind_invoice_post');
export const approval_task_kind_stock_reconciliation_submit = () => t('approval_task_kind_stock_reconciliation_submit');
export const approval_task_kind_work_order_release = () => t('approval_task_kind_work_order_release');
export const approval_task_kind_work_order_transition = () => t('approval_task_kind_work_order_transition');
export const approval_task_kind_stock_entry_submit = () => t('approval_task_kind_stock_entry_submit');
export const approval_task_kind_writeoff = () => t('approval_task_kind_writeoff');
export const approval_task_kind_period_close = () => t('approval_task_kind_period_close');
export const approval_task_kind_regulatory_push = () => t('approval_task_kind_regulatory_push');
export const approval_task_kind_run_result = () => t('approval_task_kind_run_result');
/**
 * Approval task kind label resolver. Maps backend enum codes to localized strings.
 * Falls back to raw code if unknown — visible regression signal for `/tester` skill.
 */
export const approval_task_kind = (params) => {
    const map = {
        counterparty_push_1c: approval_task_kind_counterparty_push_1c,
        kpn_push: approval_task_kind_kpn_push,
        esf_push: approval_task_kind_esf_push,
        landed_cost_submit: approval_task_kind_landed_cost_submit,
        invoice_post: approval_task_kind_invoice_post,
        stock_reconciliation_submit: approval_task_kind_stock_reconciliation_submit,
        work_order_release: approval_task_kind_work_order_release,
        work_order_transition: approval_task_kind_work_order_transition,
        stock_entry_submit: approval_task_kind_stock_entry_submit,
        writeoff: approval_task_kind_writeoff,
        period_close: approval_task_kind_period_close,
        regulatory_push: approval_task_kind_regulatory_push,
        run_result: approval_task_kind_run_result,
    };
    const fn = map[params.kind];
    return fn ? fn() : params.kind;
};
export const approval_request_changes_btn = () => t('approval_request_changes_btn');
export const approval_request_changes_dialog_title = () => t('approval_request_changes_dialog_title');
export const approval_request_changes_placeholder = () => t('approval_request_changes_placeholder');
export const approval_self_signature_blocked = () => t('approval_self_signature_blocked');
export const approval_already_signed_blocked = () => t('approval_already_signed_blocked');
// ─── Settlement page ─────────────────────────────────────────────────
export const settlement_column_doc_number = () => t('settlement_column_doc_number');
export const settlement_column_date = () => t('settlement_column_date');
export const settlement_column_counterparty = () => t('settlement_column_counterparty');
export const settlement_column_net_amount = () => t('settlement_column_net_amount');
export const settlement_column_state = () => t('settlement_column_state');
export const settlement_state_draft = () => t('settlement_state_draft');
export const settlement_state_posted = () => t('settlement_state_posted');
export const settlement_state_cancelled = () => t('settlement_state_cancelled');
export const settlement_empty_title = () => t('settlement_empty_title');
export const settlement_button_new = () => t('settlement_button_new');
export const settlement_button_propose = () => t('settlement_button_propose');
// FE Pass 28: settlement detail line table
export const settlement_column_line_no = () => t('settlement_column_line_no');
export const settlement_column_direction = () => t('settlement_column_direction');
export const settlement_direction_ar_to_ap = () => t('settlement_direction_ar_to_ap');
export const settlement_direction_ap_to_ar = () => t('settlement_direction_ap_to_ar');
// ─── Budget page ─────────────────────────────────────────────────────
export const budget_column_name = () => t('budget_column_name');
export const budget_column_period = () => t('budget_column_period');
export const budget_column_revenue_plan = () => t('budget_column_revenue_plan');
export const budget_column_cost_plan = () => t('budget_column_cost_plan');
export const budget_column_state = () => t('budget_column_state');
export const budget_state_draft = () => t('budget_state_draft');
export const budget_state_pending_approval = () => t('budget_state_pending_approval');
export const budget_state_active = () => t('budget_state_active');
export const budget_state_locked = () => t('budget_state_locked');
export const budget_state_archived = () => t('budget_state_archived');
export const budget_empty_title = () => t('budget_empty_title');
export const budget_button_new = () => t('budget_button_new');
export const budget_button_activate = () => t('budget_button_activate');
export const budget_button_lock = () => t('budget_button_lock');
export const budget_button_archive = () => t('budget_button_archive');
export const budget_tab_overview = () => t('budget_tab_overview');
export const budget_tab_variance = () => t('budget_tab_variance');
export const variance_column_account = () => t('variance_column_account');
export const variance_column_plan = () => t('variance_column_plan');
export const variance_column_actual = () => t('variance_column_actual');
export const variance_column_diff = () => t('variance_column_diff');
export const variance_column_pct = () => t('variance_column_pct');
export const variance_column_flag = () => t('variance_column_flag');
export const variance_flag_ok = () => t('variance_flag_ok');
export const variance_flag_warning = () => t('variance_flag_warning');
export const variance_flag_critical = () => t('variance_flag_critical');
// ─── Finance Dashboard ───────────────────────────────────────────────
export const finance_dashboard_filter_period = () => t('finance_dashboard_filter_period');
// ─── COGS page ───────────────────────────────────────────────────────
export const cogs_filter_date_from = () => t('cogs_filter_date_from');
export const cogs_filter_date_to = () => t('cogs_filter_date_to');
export const cogs_column_item = () => t('cogs_column_item');
export const cogs_column_qty_sold = () => t('cogs_column_qty_sold');
export const cogs_column_cogs_amount = () => t('cogs_column_cogs_amount');
export const cogs_column_avg_cost = () => t('cogs_column_avg_cost');
export const cogs_column_share = () => t('cogs_column_share');
export const cogs_column_valuation = () => t('cogs_column_valuation');
export const cogs_empty_title = () => t('cogs_empty_title');
export const cogs_button_snapshot = () => t('cogs_button_snapshot');
export const cogs_totals = () => t('cogs_totals');
// ─── Cash flow + P&L + ESF + Tax summary ─────────────────────────────
export const cash_flow_actual_title = () => t('cash_flow_actual_title');
export const cash_flow_forecast_title = () => t('cash_flow_forecast_title');
export const cash_flow_forecast_badge_ai = () => t('cash_flow_forecast_badge_ai');
export const cash_flow_bucket_overdue = () => t('cash_flow_bucket_overdue');
export const cash_flow_bucket_0_30 = () => t('cash_flow_bucket_0_30');
export const cash_flow_bucket_31_60 = () => t('cash_flow_bucket_31_60');
export const cash_flow_bucket_61_90 = () => t('cash_flow_bucket_61_90');
export const cash_flow_net_expected = () => t('cash_flow_net_expected');
export const cf_drill_title = (p) => t('cf_drill_title', p);
export const role_abilities_preview = (p) => t('role_abilities_preview', p);
export const cf_drill_col_net = (p) => t('cf_drill_col_net', p);
export const cf_drill_total = (p) => t('cf_drill_total', p);
export const cf_drill_col_article = (p) => t('cf_drill_col_article', p);
export const cf_drill_col_group = (p) => t('cf_drill_col_group', p);
export const cf_drill_col_project = (p) => t('cf_drill_col_project', p);
export const cf_drill_no_article = (p) => t('cf_drill_no_article', p);
export const cf_drill_empty = (p) => t('cf_drill_empty', p);
export const cash_flow_dashboard_widget_title = () => t('cash_flow_dashboard_widget_title');
export const pnl_revenue = () => t('pnl_revenue');
export const pnl_cogs = () => t('pnl_cogs');
export const pnl_gross_profit = () => t('pnl_gross_profit');
export const pnl_gross_margin = () => t('pnl_gross_margin');
export const pnl_opex = () => t('pnl_opex');
export const pnl_operating_profit = () => t('pnl_operating_profit');
export const pnl_tax = () => t('pnl_tax');
export const pnl_net_profit = () => t('pnl_net_profit');
export const pnl_net_margin = () => t('pnl_net_margin');
export const esf_column_doc_number = () => t('esf_column_doc_number');
export const esf_column_period = () => t('esf_column_period');
export const esf_column_state = () => t('esf_column_state');
export const esf_column_onec_ref = () => t('esf_column_onec_ref');
export const esf_state_draft = () => t('esf_state_draft');
export const esf_state_pending_approval = () => t('esf_state_pending_approval');
export const esf_state_approved = () => t('esf_state_approved');
export const esf_state_pushed_to_1c = () => t('esf_state_pushed_to_1c');
export const esf_state_rejected = () => t('esf_state_rejected');
export const esf_state_cancelled = () => t('esf_state_cancelled');
export const esf_column_locale = () => t('esf_column_locale');
export const esf_column_approved_by = () => t('esf_column_approved_by');
export const esf_empty_title = () => t('esf_empty_title');
export const esf_button_push = () => t('esf_button_push');
export const tax_summary_vat_payable = () => t('tax_summary_vat_payable');
export const tax_summary_vat_credit = () => t('tax_summary_vat_credit');
export const tax_summary_vat_net = () => t('tax_summary_vat_net');
export const tax_summary_income_tax_advance = () => t('tax_summary_income_tax_advance');
// ─── common UI ────────────────────────────────────────────────────────
export const common_column_id = () => t('common_column_id');
export const common_button_save = () => t('common_button_save');
export const common_yes = () => t('common_yes');
export const common_no = () => t('common_no');
// FE-60: shared document status labels (draft/submitted/cancelled/posted/etc).
// Используется SR/SE/LC/etc detail pages — заменяет raw enum text в UI.
export const doc_status_label = (p) => t(`doc_status_${p.status}`);
export const common_button_cancel = () => t('common_button_cancel');
export const common_button_delete = () => t('common_button_delete');
export const common_button_edit = () => t('common_button_edit');
export const common_button_create = () => t('common_button_create');
export const common_button_confirm = () => t('common_button_confirm');
export const common_button_back = () => t('common_button_back');
export const common_button_next = () => t('common_button_next');
export const common_button_retry = () => t('common_button_retry');
export const common_button_submit = () => t('common_button_submit');
export const common_button_close = () => t('common_button_close');
export const common_button_search = () => t('common_button_search');
export const common_button_filter = () => t('common_button_filter');
export const common_form_missing_fields = (p) => t('common_form_missing_fields', p);
export const common_select_customer_placeholder = () => t('common_select_customer_placeholder');
export const common_button_export = () => t('common_button_export');
export const common_button_refresh = () => t('common_button_refresh');
export const common_toast_saved = () => t('common_toast_saved');
export const common_toast_deleted = () => t('common_toast_deleted');
export const common_toast_submitted = () => t('common_toast_submitted');
export const common_toast_cancelled = () => t('common_toast_cancelled');
export const common_toast_approved = () => t('common_toast_approved');
export const common_toast_rejected = () => t('common_toast_rejected');
export const common_toast_error = () => t('common_toast_error');
export const common_toast_copy_success = () => t('common_toast_copy_success');
export const common_toast_copy_failed = () => t('common_toast_copy_failed');
export const common_empty_state_no_data = () => t('common_empty_state_no_data');
export const common_empty_state_try_filter = () => t('common_empty_state_try_filter');
export const common_empty_state_create_first = () => t('common_empty_state_create_first');
export const common_loader_loading = () => t('common_loader_loading');
export const common_loader_saving = () => t('common_loader_saving');
export const common_loader_submitting = () => t('common_loader_submitting');
export const common_error_generic = () => t('common_error_generic');
export const common_error_network = () => t('common_error_network');
export const common_error_permission = () => t('common_error_permission');
export const common_error_not_found = () => t('common_error_not_found');
export const common_error_validation = () => t('common_error_validation');
export const common_error_server = () => t('common_error_server');
// ─── wo state + warnings ───────────────────────────────────────────────
export const wo_state_label = (p) => t(`wo_state_${p.state}`);
export const wo_warning_type_label = (p) => t(`wo_warning_type_${p.type}`);
export const wo_warning_body_insufficient_stock = (p) => t('wo_warning_body_insufficient_stock', p);
export const wo_warning_body_batch_expired = (p) => t('wo_warning_body_batch_expired', p);
export const wo_warning_body_batch_expiry_imminent = (p) => t('wo_warning_body_batch_expiry_imminent', p);
export const wo_warning_body_bom_alternate_used = (p) => t('wo_warning_body_bom_alternate_used', p);
export const wo_warning_body_bom_scrap_exceeded = (p) => t('wo_warning_body_bom_scrap_exceeded', p);
export const wo_warning_body_job_card_missing = (p) => t('wo_warning_body_job_card_missing', p);
export const wo_warning_body_cost_variance_high = (p) => t('wo_warning_body_cost_variance_high', p);
export const wo_warning_body_no_active_bom_version = (p) => t('wo_warning_body_no_active_bom_version', p);
// ─── severity / approval / runtime / business ────────────────────────
export const severity_label = (p) => t(`severity_${p.severity}`);
export const approval_tier_label = (p) => t(`approval_tier_${p.tier}`);
export const approval_tier_tooltip = (p) => t(`approval_tier_tooltip_${p.tier}`);
export const runtime_status_label = (p) => t(`runtime_status_${p.status}`);
// FE-173 Sprint Q+ post-browser-verify: BE возвращает non-canonical values
// ('completed' вместо 'completed_draft', 'failed', 'pending_review', 'pending_external',
// 'in_progress') — BE-1 schema drift был неполный. Defensive: fallback к raw status code
// если key missing, чтобы UI не показывал "[missing: business_status_completed]" пользователю.
export const business_status_label = (p) => {
    const r = t(`business_status_${p.status}`);
    return typeof r === 'string' && r.startsWith('[missing:') ? p.status : r;
};
// ─── units + currency ─────────────────────────────────────────────────
// FE-163 Sprint Q+ browser-verify: BE может возвращать русские UoM strings
// ("шт", "м2", "кг") как legacy data — fallback к raw code если key missing,
// чтобы UI не показывал "[missing: units_шт_short]" пользователю.
export const units_short = (p) => {
    const r = t(`units_${p.code}_short`);
    return typeof r === 'string' && r.startsWith('[missing:') ? p.code : r;
};
export const units_long = (p) => {
    const r = t(`units_${p.code}_long`);
    return typeof r === 'string' && r.startsWith('[missing:') ? p.code : r;
};
export const currency_symbol = (p) => {
    const r = t(`currency_${p.code}_symbol`);
    return typeof r === 'string' && r.startsWith('[missing:') ? p.code : r;
};
export const currency_name = (p) => t(`currency_${p.code}_name`);
// ─── topbar / user menu ──────────────────────────────────────────────
export const topbar_user_settings_link = () => t('topbar_user_settings_link');
// ─── errors ───────────────────────────────────────────────────────────
export const error_generic = (p) => t('error_generic', p);
/** DR-F11 — запись baseline с методом расчёта, неработающим в текущем составе задач. */
export const error_task_module_method_unavailable = () => t('error_task_module_method_unavailable');
export const error_task_module_validation = () => t('error_task_module_validation');
export const error_task_module_due_before_start = () => t('error_task_module_due_before_start');
export const error_toast_title = () => t('error_toast_title');
// ─── F1/F3 (пак stock-entry-wizard) — поля мастера складского документа ───
export const route_se_new_voucher_label = () => t('route_se_new_voucher_label');
export const route_se_new_voucher_placeholder = () => t('route_se_new_voucher_placeholder');
export const route_se_new_voucher_hint = () => t('route_se_new_voucher_hint');
export const route_se_new_line_rate_label = () => t('route_se_new_line_rate_label');
export const route_se_new_line_rate_placeholder = () => t('route_se_new_line_rate_placeholder');
export const route_se_new_line_rate_hint_required = () => t('route_se_new_line_rate_hint_required');
export const route_se_new_line_rate_hint_optional = () => t('route_se_new_line_rate_hint_optional');
export const route_se_new_err_rate_required = () => t('route_se_new_err_rate_required');
export const route_se_new_err_supplier_required = () => t('route_se_new_err_supplier_required');
export const route_se_new_err_cost_center_required = () => t('route_se_new_err_cost_center_required');
export const route_se_new_err_wo_required = () => t('route_se_new_err_wo_required');
export const route_se_new_err_source_required = () => t('route_se_new_err_source_required');
export const route_se_new_err_target_required = () => t('route_se_new_err_target_required');
export const route_se_new_lines_total_label = () => t('route_se_new_lines_total_label');
export const route_se_new_line_amount_label = () => t('route_se_new_line_amount_label');
export const route_se_list_empty_scope_title = () => t('route_se_list_empty_scope_title');
export const route_se_list_empty_scope_description = () => t('route_se_list_empty_scope_description');
export const route_sr_warehouse_multi = (p) => t('route_sr_warehouse_multi', p);
export const route_se_new_strict_expiry_hint = () => t('route_se_new_strict_expiry_hint');
export const cp_picker_empty_hint = () => t('cp_picker_empty_hint');
export const route_sr_preview_disabled_reason = () => t('route_sr_preview_disabled_reason');
export const route_sr_cancel_disabled_reason = () => t('route_sr_cancel_disabled_reason');
export const route_sr_cancel_disabled_opening = () => t('route_sr_cancel_disabled_opening');
export const route_stock_col_rate_with_currency = (p) => t('route_stock_col_rate_with_currency', p);
export const route_stock_col_value_with_currency = (p) => t('route_stock_col_value_with_currency', p);
export const route_sr_preview_no_variance_title = () => t('route_sr_preview_no_variance_title');
export const route_sr_preview_no_variance_desc = () => t('route_sr_preview_no_variance_desc');
export const route_sr_preview_failed_title = () => t('route_sr_preview_failed_title');
export const route_sr_preview_failed_desc = () => t('route_sr_preview_failed_desc');
export const route_sr_submit_blocked_no_preview = () => t('route_sr_submit_blocked_no_preview');
export const route_sr_summary_max_variance_qty = () => t('route_sr_summary_max_variance_qty');
export const route_sr_reason_required_hint = () => t('route_sr_reason_required_hint');
export const route_sr_reason_footnote = () => t('route_sr_reason_footnote');
export const cp_picker_label_default = () => t('cp_picker_label_default');
/**
 * F6 — пустой результат НАЗЫВАЕТ, кого искали.
 *
 * Пикер фильтрует по виду контрагента, а сообщал «Контрагент не найден»: у
 * компании, где есть покупатели и нет поставщиков, поле выглядело сломанным —
 * человек вводил знакомое имя и не получал ничего. Причина не в данных, а в
 * фильтре, и её надо назвать вслух.
 */
export const cp_picker_no_results = (kind) => {
    const key = `cp_picker_no_results_${kind ?? 'any'}`;
    const result = t(key);
    if (typeof result === 'string' && result.startsWith('[missing:')) {
        return t('cp_picker_no_results_any');
    }
    return result;
};
// Коды серверных отказов по полю — читает `applyValidationErrors` через
// `error_by_code`; без ключей человек увидел бы «Неизвестная ошибка».
export const error_validation_rate_required = () => t('error_validation_rate_required');
export const error_validation_rate_unavailable = () => t('error_validation_rate_unavailable');
/**
 * F1 — подпись предупреждения предпросмотра по КОДУ типа.
 *
 * Бэкенд шлёт только код (`near_expiry_batch`, `low_stock_margin`) — свободного
 * текста в UI-схемах нет с тех пор, как правило №19 его запретило. Экран
 * показывал `w.message`, которого в ответе нет: строка выходила пустой при
 * живом предупреждении.
 */
export const se_submit_warning_by_type = (type) => {
    const key = `se_submit_warning_${type}`;
    const result = t(key);
    if (typeof result === 'string' && result.startsWith('[missing:')) {
        return t('se_submit_warning_unknown', { type });
    }
    return result;
};
/**
 * Локализация структурного ErrorCode из `@staiter/shared`.
 * `'resource.not_found'` → `m.error_by_code('resource.not_found')` → lookup 'error_resource_not_found'.
 *
 * S-FIX-2-FE.1.3 — defensive fallback: если ключ отсутствует в локали,
 * runtime.t() возвращает строку `[missing: …]`. Эту маркер-строку нельзя показывать
 * пользователю напрямую — fallback на `error_unknown` с trace-id.
 * Маркер всё равно печатается в console.warn для observability в dev/CI.
 */
export const error_trace_label = () => t('error_trace_label');
export const ctx_group_show_hidden = (p) => t('ctx_group_show_hidden', p);
export const ctx_group_hide_hidden = () => t('ctx_group_hide_hidden');
export const tm_notif_settings_save_failed = () => t('tm_notif_settings_save_failed');
export const tm_notif_settings_save_failed_subject = (p) => t('tm_notif_settings_save_failed_subject', p);
export const error_by_code = (code, params) => {
    const key = `error_${code.replace(/\./g, '_')}`;
    const result = t(key, params);
    if (typeof result === 'string' && result.startsWith('[missing:')) {
        // S-FIX-2-FE.1.3: dev/CI observability without depending on @types/node.
        // (i18n package — platform-agnostic, не подключает node types в build.)
        const g = globalThis;
        if (g.console && typeof g.console.warn === 'function') {
            g.console.warn('[i18n] missing error key:', key);
        }
        const trace = params && typeof params === 'object' && 'trace' in params
            ? String(params.trace ?? '—')
            : '—';
        return t('error_unknown', { trace });
    }
    return result;
};
// ─── E-FE.6 / E-BE-A — новые error_* коды error-path (30+) ────────────
export const error_route_not_found = (p) => t('error_route_not_found', p);
export const error_route_method_not_allowed = (p) => t('error_route_method_not_allowed', p);
export const error_route_disabled = () => t('error_route_disabled');
export const error_request_timeout = () => t('error_request_timeout');
export const error_request_aborted = () => t('error_request_aborted');
export const error_request_body_too_large = () => t('error_request_body_too_large');
export const error_request_content_type_unsupported = () => t('error_request_content_type_unsupported');
export const error_validation_failed = () => t('error_validation_failed');
// S-FIX-2-FE.4.2 — schema-mismatch toast (ApiSchemaError).
export const error_schema_mismatch_title = () => t('error_schema_mismatch_title');
export const error_schema_mismatch_body = (p) => t('error_schema_mismatch_body', p);
export const error_validation_precondition_failed = () => t('error_validation_precondition_failed');
export const error_parse_failed = () => t('error_parse_failed');
export const error_network_unavailable = () => t('error_network_unavailable');
/** F2 — мутация не ушла: сторож paused-мутаций снял её и откатил оптимистик. */
export const error_network_offline_paused = () => t('error_network_offline_paused');
export const error_rate_limited = () => t('error_rate_limited');
export const error_rate_limit_tenant_quota = () => t('error_rate_limit_tenant_quota');
export const error_feature_disabled = () => t('error_feature_disabled');
export const error_maintenance_in_progress = () => t('error_maintenance_in_progress');
export const error_resource_has_children = (p) => t('error_resource_has_children', p);
export const error_resource_has_sle = (p) => t('error_resource_has_sle', p);
export const error_resource_has_stock_entries = (p) => t('error_resource_has_stock_entries', p);
export const error_resource_has_work_orders = (p) => t('error_resource_has_work_orders', p);
export const error_client_exception = () => t('error_client_exception');
export const error_unknown = (p) => t('error_unknown', p ?? { trace: '' });
// ─── E-FE.1 typed exports для renderApiError switch-а (все ErrorCode) ──
export const error_auth_missing_token = () => t('error_auth_missing_token');
export const error_auth_invalid_token = () => t('error_auth_invalid_token');
export const error_auth_expired_token = () => t('error_auth_expired_token');
export const error_auth_forbidden = () => t('error_auth_forbidden');
export const error_auth_unauthorized = () => t('error_auth_unauthorized');
export const error_auth_invalid_credentials = () => t('error_auth_invalid_credentials');
export const error_auth_password_change_required = () => t('error_auth_password_change_required');
export const error_auth_impersonation_admin_forbidden = () => t('error_auth_impersonation_admin_forbidden');
// S-FIX-5-BE.8.6 — voucher_type labels для FE drill-down (Stock Ledger).
export const voucher_type_stock_entry = () => t('voucher_type_stock_entry');
export const voucher_type_stock_reconciliation = () => t('voucher_type_stock_reconciliation');
export const voucher_type_opening_stock = () => t('voucher_type_opening_stock');
export const voucher_type_landed_cost = () => t('voucher_type_landed_cost');
export const voucher_type_work_order = () => t('voucher_type_work_order');
export const voucher_type_compensating = () => t('voucher_type_compensating');
export const error_validation_zod_shape_mismatch = () => t('error_validation_zod_shape_mismatch');
export const error_resource_not_found = () => t('error_resource_not_found');
export const error_resource_conflict = () => t('error_resource_conflict');
export const error_resource_locked = () => t('error_resource_locked');
export const error_resource_gone = () => t('error_resource_gone');
export const error_server_internal = () => t('error_server_internal');
export const error_server_timeout = () => t('error_server_timeout');
export const error_server_degraded = () => t('error_server_degraded');
export const error_integration_1c_unreachable = () => t('error_integration_1c_unreachable');
export const error_integration_bitrix_unreachable = () => t('error_integration_bitrix_unreachable');
export const error_integration_adesk_unreachable = () => t('error_integration_adesk_unreachable');
export const error_integration_temporal_unreachable = () => t('error_integration_temporal_unreachable');
export const error_db_unavailable = () => t('error_db_unavailable');
export const error_db_constraint_violation = () => t('error_db_constraint_violation');
export const error_wo_invalid_state_transition = () => t('error_wo_invalid_state_transition');
export const error_wo_material_shortage = () => t('error_wo_material_shortage');
export const error_wo_bom_not_found = () => t('error_wo_bom_not_found');
export const error_stock_insufficient_balance = () => t('error_stock_insufficient_balance');
export const error_stock_negative_balance_forbidden = () => t('error_stock_negative_balance_forbidden');
export const error_approval_tier_mismatch = () => t('error_approval_tier_mismatch');
export const error_approval_approver_insufficient = () => t('error_approval_approver_insufficient');
export const error_approval_impersonation_forbidden = () => t('error_approval_impersonation_forbidden');
export const error_valuation_unknown_method = () => t('error_valuation_unknown_method');
// ─── Root error boundary (E-FE.3) ─────────────────────────────────────
export const error_app_crashed_title = () => t('error_app_crashed_title');
export const error_app_crashed_body = (p) => t('error_app_crashed_body', p);
export const error_app_crashed_reload_btn = () => t('error_app_crashed_reload_btn');
// ─── S-FIX-1: Tier 3 dual approval (FE.A.1) ──────────────────────────
export const tier3_self_approval_forbidden_inline = () => t('tier3_self_approval_forbidden_inline');
export const error_tier3_missing_approvers_header = () => t('error_tier3_missing_approvers_header');
export const error_tier3_invalid_approvers_format = () => t('error_tier3_invalid_approvers_format');
export const error_tier3_fewer_than_two = () => t('error_tier3_fewer_than_two');
export const error_tier3_duplicate_approvers = () => t('error_tier3_duplicate_approvers');
export const error_tier3_self_approval_forbidden = () => t('error_tier3_self_approval_forbidden');
export const error_tier3_approver_not_found = () => t('error_tier3_approver_not_found');
export const error_tier3_approver_role_mismatch = () => t('error_tier3_approver_role_mismatch');
export const error_tier3_approver_role_too_low = () => t('error_tier3_approver_role_too_low');
export const error_tier3_body_header_mismatch = () => t('error_tier3_body_header_mismatch');
export const error_tier3_rate_limit_exceeded = (p) => t('error_tier3_rate_limit_exceeded', p);
// ─── S-FIX-1: IntegrationStatusBanner (FE.A.2) ───────────────────────
export const integration_shadow_pill_label = () => t('integration_shadow_pill_label');
export const integration_placeholder_pill_label = () => t('integration_placeholder_pill_label');
export const integration_shadow_mode_explainer = () => t('integration_shadow_mode_explainer');
export const integration_placeholder_mode_explainer = () => t('integration_placeholder_mode_explainer');
// ─── S-FIX-1: Warehouse + WO RBAC UI (FE.B.1) ────────────────────────
export const error_forbidden_warehouse_out_of_scope = () => t('error_forbidden_warehouse_out_of_scope');
export const error_forbidden_warehouse_out_of_scope_with_code = (p) => t('error_forbidden_warehouse_out_of_scope_with_code', p);
export const error_forbidden_work_order_executor_mismatch = (p) => t('error_forbidden_work_order_executor_mismatch', p);
export const warehouse_out_of_scope_tooltip = (p) => t('warehouse_out_of_scope_tooltip', p);
export const wo_action_disabled_warehouse_out_of_scope = () => t('wo_action_disabled_warehouse_out_of_scope');
export const wo_action_disabled_executor_mismatch = () => t('wo_action_disabled_executor_mismatch');
// ─── S-FIX-1: Bank import (FE.C.1) ────────────────────────────────────
export const error_bank_import_token_mismatch = () => t('error_bank_import_token_mismatch');
export const error_bank_import_draft_expired = () => t('error_bank_import_draft_expired');
export const error_bank_import_row_set_mismatch = () => t('error_bank_import_row_set_mismatch');
export const error_bank_import_counterparty_required = () => t('error_bank_import_counterparty_required');
export const error_bank_import_draft_not_found = () => t('error_bank_import_draft_not_found');
export const bank_import_unmatched_rows = (p) => t('bank_import_unmatched_rows', p);
export const bank_import_row_unmatched = () => t('bank_import_row_unmatched');
// ─── S-FIX-1: P&L FX (FE.D.1) ────────────────────────────────────────
export const pnl_fx_no_adjustments_in_period = () => t('pnl_fx_no_adjustments_in_period');
export const pnl_fx_unrealized_label = () => t('pnl_fx_unrealized_label');
export const pnl_fx_realized_label = () => t('pnl_fx_realized_label');
export const pnl_fx_provenance = () => t('pnl_fx_provenance');
export const pnl_fx_explainer = () => t('pnl_fx_explainer');
export const pnl_fx_partial_coverage_warning = () => t('pnl_fx_partial_coverage_warning');
export const pnl_legacy_schema_warning = () => t('pnl_legacy_schema_warning');
// ─── S-FIX-1: Admin Jobs page (FE.D.2) ───────────────────────────────
export const error_admin_jobs_scheduler_unavailable = () => t('error_admin_jobs_scheduler_unavailable');
export const error_admin_jobs_unknown_job_name = () => t('error_admin_jobs_unknown_job_name');
export const error_fx_rate_delete_nbkz_forbidden = () => t('error_fx_rate_delete_nbkz_forbidden');
export const error_fx_rate_delete_in_use = (p) => t('error_fx_rate_delete_in_use', p);
export const route_admin_jobs_title = () => t('route_admin_jobs_title');
export const nav_admin_jobs = () => t('nav_admin_jobs');
export const admin_jobs_col_name = () => t('admin_jobs_col_name');
export const admin_jobs_col_cron = () => t('admin_jobs_col_cron');
export const admin_jobs_col_next_run = () => t('admin_jobs_col_next_run');
export const admin_jobs_col_last_run = () => t('admin_jobs_col_last_run');
export const admin_jobs_col_last_status = () => t('admin_jobs_col_last_status');
export const admin_jobs_name_nbkz_fetch = () => t('admin_jobs_name_nbkz_fetch');
export const admin_jobs_name_fx_revaluation = () => t('admin_jobs_name_fx_revaluation');
export const admin_jobs_name_bank_import_cleanup = () => t('admin_jobs_name_bank_import_cleanup');
export const admin_jobs_status_ok = () => t('admin_jobs_status_ok');
export const admin_jobs_status_error = () => t('admin_jobs_status_error');
export const admin_jobs_status_skipped = () => t('admin_jobs_status_skipped');
export const admin_jobs_status_never_run = () => t('admin_jobs_status_never_run');
export const admin_jobs_scheduler_unavailable = () => t('admin_jobs_scheduler_unavailable');
export const admin_jobs_run_now = () => t('admin_jobs_run_now');
export const admin_jobs_triggered_toast = () => t('admin_jobs_triggered_toast');
export const admin_jobs_provenance = () => t('admin_jobs_provenance');
export const admin_fx_rate_delete_confirm_title = () => t('admin_fx_rate_delete_confirm_title');
export const admin_fx_rate_delete_confirm_body = (p) => t('admin_fx_rate_delete_confirm_body', p);
export const admin_fx_rate_delete_nbkz_forbidden = () => t('admin_fx_rate_delete_nbkz_forbidden');
export const admin_fx_rate_delete_create_override_cta = () => t('admin_fx_rate_delete_create_override_cta');
export const admin_fx_rate_delete_in_use = (p) => t('admin_fx_rate_delete_in_use', p);
// ─── S-FIX-1: Admin Users (FE.E.1) ───────────────────────────────────
export const error_admin_user_cannot_deactivate_last_admin = () => t('error_admin_user_cannot_deactivate_last_admin');
export const error_admin_user_already_active = () => t('error_admin_user_already_active');
export const error_admin_user_cannot_impersonate_admin = () => t('error_admin_user_cannot_impersonate_admin');
export const error_admin_user_cannot_impersonate_self = () => t('error_admin_user_cannot_impersonate_self');
export const error_admin_user_impersonate_target_not_in_entity = () => t('error_admin_user_impersonate_target_not_in_entity');
export const role_admin = () => t('role_admin');
export const role_admin_description = () => t('role_admin_description');
export const role_manager = () => t('role_manager');
export const role_manager_description = () => t('role_manager_description');
export const role_accountant = () => t('role_accountant');
export const role_accountant_description = () => t('role_accountant_description');
export const role_reviewer = () => t('role_reviewer');
export const role_reviewer_description = () => t('role_reviewer_description');
export const role_sme = () => t('role_sme');
export const role_sme_description = () => t('role_sme_description');
export const role_viewer = () => t('role_viewer');
export const role_viewer_description = () => t('role_viewer_description');
export const role_warehouse_operator = () => t('role_warehouse_operator');
export const role_warehouse_operator_description = () => t('role_warehouse_operator_description');
export const role_production_planner = () => t('role_production_planner');
export const role_production_planner_description = () => t('role_production_planner_description');
export const error_app_crashed_contact_link = () => t('error_app_crashed_contact_link');
// ─── locale switcher ─────────────────────────────────────────────────
export const locale_name = (p) => t(`locale_name_${p.code}`);
export const locale_coming_soon = () => t('locale_coming_soon');
export const preferences_section_language = () => t('preferences_section_language');
export const preferences_language_label = () => t('preferences_language_label');
export const preferences_language_hint = () => t('preferences_language_hint');
export const preferences_locale_saved = () => t('preferences_locale_saved');
// ─── page titles / tabs / buttons ─────────────────────────────────────
export const page_title_dashboard = () => t('page_title_dashboard');
export const page_title_runs = () => t('page_title_runs');
export const page_title_wo_detail = (p) => t('page_title_wo_detail', p);
export const page_title_items = () => t('page_title_items');
export const page_title_warehouses = () => t('page_title_warehouses');
export const page_title_boms = () => t('page_title_boms');
export const page_title_stock_balance = () => t('page_title_stock_balance');
export const page_title_stock_ledger = () => t('page_title_stock_ledger');
export const page_title_preferences = () => t('page_title_preferences');
export const tab_overview = () => t('tab_overview');
export const tab_materials = () => t('tab_materials');
export const tab_job_cards = () => t('tab_job_cards');
export const tab_warnings = () => t('tab_warnings');
export const tab_history = () => t('tab_history');
export const tab_audit = () => t('tab_audit');
export const tab_sles = () => t('tab_sles');
export const button_replenish = () => t('button_replenish');
export const button_approve = () => t('button_approve');
export const button_reject = () => t('button_reject');
export const button_release = () => t('button_release');
export const button_start = () => t('button_start');
export const button_complete = () => t('button_complete');
export const button_view_details = () => t('button_view_details');
// ─── pagination ──────────────────────────────────────────────────────
export const pagination_showing = (p) => t('pagination_showing', p);
export const pagination_page_of = (p) => t('pagination_page_of', p);
export const pagination_prev = () => t('pagination_prev');
export const pagination_next = () => t('pagination_next');
// ─── tool_code labels ─────────────────────────────────────────────────
export const tool_label = (p) => t(`tool_${p.code.replace(/\./g, '_')}`);
// ─── excel headers ───────────────────────────────────────────────────
export const excel_sheet_balance_title = () => t('excel_sheet_balance_title');
export const excel_sheet_ledger_title = () => t('excel_sheet_ledger_title');
export const excel_sheet_stock_entries_title = () => t('excel_sheet_stock_entries_title');
export const excel_sheet_variance_title = () => t('excel_sheet_variance_title');
export const excel_sheet_cost_draft_title = () => t('excel_sheet_cost_draft_title');
export const excel_sheet_wo_progress_title = () => t('excel_sheet_wo_progress_title');
export const excel_sheet_readme_title = () => t('excel_sheet_readme_title');
export const excel_sheet_run_summary_title = () => t('excel_sheet_run_summary_title');
export const excel_sheet_inventory_snapshot_title = () => t('excel_sheet_inventory_snapshot_title');
export const excel_sheet_writeoff_allocations_title = () => t('excel_sheet_writeoff_allocations_title');
export const excel_sheet_cost_allocations_title = () => t('excel_sheet_cost_allocations_title');
export const excel_sheet_source_conflicts_title = () => t('excel_sheet_source_conflicts_title');
export const excel_sheet_anomalies_review_title = () => t('excel_sheet_anomalies_review_title');
export const excel_sheet_evidence_index_title = () => t('excel_sheet_evidence_index_title');
export const excel_column = (p) => t(`excel_column_${p.code}`);
// ─── llm prompts ──────────────────────────────────────────────────────
export const llm_system_prompt_preamble = () => t('llm_system_prompt_preamble');
export const llm_system_prompt_language_directive = () => t('llm_system_prompt_language_directive');
export const llm_system_prompt_no_uuid_rule = () => t('llm_system_prompt_no_uuid_rule');
export const llm_system_prompt_no_decimal_rule = () => t('llm_system_prompt_no_decimal_rule');
export const llm_system_prompt_glossary_header = () => t('llm_system_prompt_glossary_header');
export const llm_response_uncertain = () => t('llm_response_uncertain');
export const llm_response_tool_needed = (p) => t('llm_response_tool_needed', p);
export const llm_response_no_data = () => t('llm_response_no_data');
export const llm_response_explaining = () => t('llm_response_explaining');
export const llm_response_insufficient_info = () => t('llm_response_insufficient_info');
export const llm_chat_intro = () => t('llm_chat_intro');
export const llm_chat_rules_header = () => t('llm_chat_rules_header');
export const llm_chat_rule_no_compute = () => t('llm_chat_rule_no_compute');
export const llm_chat_rule_read_only = () => t('llm_chat_rule_read_only');
export const llm_chat_rule_ask_if_unsure = () => t('llm_chat_rule_ask_if_unsure');
export const llm_chat_rule_short_numeric = () => t('llm_chat_rule_short_numeric');
// chat-interactive-actions B14 — сводка массового комментария в центре уведомлений.
export const tm_notif_bulk_comment_digest = () => t('tm_notif_bulk_comment_digest');
export const tm_notif_bulk_comment_body = (p) => t('tm_notif_bulk_comment_body', p);
export const llm_chat_rule_cite_doc_id = () => t('llm_chat_rule_cite_doc_id');
// chat-interactive-actions B12 (SOLUTION §4) — правила контура «предложил, а не сделал».
export const llm_chat_rule_ambiguous_verb = () => t('llm_chat_rule_ambiguous_verb');
export const llm_chat_rule_tool_error = () => t('llm_chat_rule_tool_error');
export const llm_chat_rule_stale_selection = () => t('llm_chat_rule_stale_selection');
export const llm_chat_rule_data_not_instructions = () => t('llm_chat_rule_data_not_instructions');
/*
 * Круг 33 кадра 4 (UI-2, дословно): «Перечисление состава на кадре дважды,
 * режется авторитетное. Пузырь повествования (~300 CSS px) называет все три
 * задачи поимённо; окно состава — 87px и одна запись… при карточке в том же ходе
 * повествование держит счёт, перечисление — только карточка; освобождается
 * ~230px, три записи входят и клауза исчезает».
 */
export const llm_chat_rule_no_composition_list = () => t('llm_chat_rule_no_composition_list');
export const llm_chat_rule_task_statuses = () => t('llm_chat_rule_task_statuses');
/* B33 — машинное имя инструмента в пузыре чата это та же болезнь, против которой
   стоит i18n-инвариант: человек не может ничего сделать с этим словом, а список
   инструментов меняется от захода к заходу. */
export const llm_chat_rule_no_tool_names = () => t('llm_chat_rule_no_tool_names');
export const llm_chat_rule_identity = () => t('llm_chat_rule_identity');
export const llm_chat_drafts_header = () => t('llm_chat_drafts_header');
export const llm_chat_draft_line = (p) => t('llm_chat_draft_line', p);
export const llm_chat_draft_status_proposed = () => t('llm_chat_draft_status_proposed');
export const llm_chat_draft_status_consumed = () => t('llm_chat_draft_status_consumed');
export const llm_chat_draft_status_cancelled = () => t('llm_chat_draft_status_cancelled');
export const llm_chat_draft_status_expired = () => t('llm_chat_draft_status_expired');
export const llm_chat_snapshot_label = () => t('llm_chat_snapshot_label');
export const llm_chat_question_label = () => t('llm_chat_question_label');
export const llm_chat_docs_header = () => t('llm_chat_docs_header');
export const llm_chat_docs_section_label = (p) => t('llm_chat_docs_section_label', p);
export const llm_chat_history_header = () => t('llm_chat_history_header');
export const llm_chat_history_user_prefix = () => t('llm_chat_history_user_prefix');
export const llm_chat_history_assistant_prefix = () => t('llm_chat_history_assistant_prefix');
export const llm_chat_no_docs_for_route = () => t('llm_chat_no_docs_for_route');
// CH-17 — USER CONTEXT block (route + role + fallback honesty signal).
export const llm_chat_user_context_header = () => t('llm_chat_user_context_header');
export const llm_chat_user_context_route = (p) => t('llm_chat_user_context_route', p);
export const llm_chat_user_context_role = (p) => t('llm_chat_user_context_role', p);
export const llm_chat_route_no_specific_docs = (p) => t('llm_chat_route_no_specific_docs', p);
export const llm_chat_write_intent_refusal = () => t('llm_chat_write_intent_refusal');
// ─── field labels (bulk) ──────────────────────────────────────────────
export const field_label = (p) => t(`field_${p.key}`);
export const column_label = (p) => t(`column_${p.key}`);
// ─── layout / components (sweep R-FE.5-7) ─────────────────────────────
export const layout_topbar_exports_label = () => t('layout_topbar_exports_label');
export const layout_topbar_notifications_label = () => t('layout_topbar_notifications_label');
export const layout_topbar_help_label = () => t('layout_topbar_help_label');
export const layout_async_exports_title = () => t('layout_async_exports_title');
export const layout_command_palette_no_results = () => t('layout_command_palette_no_results');
export const layout_command_palette_title = () => t('layout_command_palette_title');
export const layout_command_palette_placeholder = () => t('layout_command_palette_placeholder');
export const layout_command_palette_section_nav = () => t('layout_command_palette_section_nav');
export const layout_command_palette_section_inventory = () => t('layout_command_palette_section_inventory');
export const layout_command_palette_section_other = () => t('layout_command_palette_section_other');
export const layout_impersonation_banner_title = () => t('layout_impersonation_banner_title');
export const component_date_picker_today = () => t('component_date_picker_today');
export const component_date_picker_yesterday = () => t('component_date_picker_yesterday');
export const component_date_picker_clear = () => t('component_date_picker_clear');
export const component_date_picker_now = () => t('component_date_picker_now');
export const component_date_picker_preset_7d = () => t('component_date_picker_preset_7d');
export const component_date_picker_preset_30d = () => t('component_date_picker_preset_30d');
export const component_date_picker_preset_this_month = () => t('component_date_picker_preset_this_month');
export const component_date_picker_time_label = () => t('component_date_picker_time_label');
export const component_saved_views_label = () => t('component_saved_views_label');
export const component_saved_views_none = () => t('component_saved_views_none');
export const component_saved_views_aria_toolbar = () => t('component_saved_views_aria_toolbar');
export const component_saved_views_save = () => t('component_saved_views_save');
export const component_saved_views_placeholder = () => t('component_saved_views_placeholder');
export const component_saved_views_remove_aria = (p) => t('component_saved_views_remove_aria', p);
export const component_table_density_label = () => t('component_table_density_label');
export const component_table_density_aria = () => t('component_table_density_aria');
export const component_table_density_comfortable = () => t('component_table_density_comfortable');
export const component_table_density_compact = () => t('component_table_density_compact');
export const component_export_menu_title = () => t('component_export_menu_title');
export const component_export_menu_current_csv = () => t('component_export_menu_current_csv');
export const component_export_menu_current_xlsx = () => t('component_export_menu_current_xlsx');
export const component_export_menu_current_json = () => t('component_export_menu_current_json');
export const component_export_rate_limit_title = () => t('component_export_rate_limit_title');
export const component_export_rate_limit_description = () => t('component_export_rate_limit_description');
export const component_item_group_tree_title = () => t('component_item_group_tree_title');
export const component_bulk_import_restart = () => t('component_bulk_import_restart');
export const component_override_diff_no_changes = () => t('component_override_diff_no_changes');
export const component_wo_timeline_empty = () => t('component_wo_timeline_empty');
/** T2A-F8 — подписанты перехода Work Order на экране, а не только в журнале. */
export const component_wo_timeline_signed_by = () => t('component_wo_timeline_signed_by');
/** Раунд 2 гейта — «две подписи были обязательны», а не просто перечень имён. */
export const component_wo_timeline_signed_of = (p) => t('component_wo_timeline_signed_of', {
    count: String(p.count),
    required: String(p.required),
});
export const component_cancel_preview_title = () => t('component_cancel_preview_title');
export const component_approval_diff_field_label = () => t('component_approval_diff_field_label');
export const component_approval_diff_before = () => t('component_approval_diff_before');
export const component_approval_diff_after = () => t('component_approval_diff_after');
export const component_master_drift_loading = () => t('component_master_drift_loading');
export const component_conflict_card_accept_ours = () => t('component_conflict_card_accept_ours');
export const component_conflict_card_accept_theirs = () => t('component_conflict_card_accept_theirs');
export const component_conflict_card_merge = () => t('component_conflict_card_merge');
export const component_conflict_card_escalate = () => t('component_conflict_card_escalate');
export const component_conflict_card_actions_aria = () => t('component_conflict_card_actions_aria');
export const component_conflict_card_authoritative_aria = () => t('component_conflict_card_authoritative_aria');
export const component_conflict_card_authoritative_label = () => t('component_conflict_card_authoritative_label');
export const component_conflict_source_staiter = () => t('component_conflict_source_staiter');
export const component_conflict_source_1c = () => t('component_conflict_source_1c');
export const component_conflict_source_bitrix = () => t('component_conflict_source_bitrix');
export const component_conflict_source_adesk = () => t('component_conflict_source_adesk');
export const component_conflict_source_excel = () => t('component_conflict_source_excel');
export const component_conflict_source_manual = () => t('component_conflict_source_manual');
export const component_conflict_card_type_intra_level = () => t('component_conflict_card_type_intra_level');
export const component_conflict_card_type_master_data_drift = () => t('component_conflict_card_type_master_data_drift');
export const component_conflict_card_type_bom_explosion_loop = () => t('component_conflict_card_type_bom_explosion_loop');
export const component_conflict_card_type_valuation_mismatch = () => t('component_conflict_card_type_valuation_mismatch');
export const component_conflict_card_type_temporal_precedence_unresolved = () => t('component_conflict_card_type_temporal_precedence_unresolved');
export const component_conflict_card_type_reconciliation_variance = () => t('component_conflict_card_type_reconciliation_variance');
export const component_conflict_card_type_tolerance_exceeded = () => t('component_conflict_card_type_tolerance_exceeded');
export const component_conflict_card_type_manual_override_deferred = () => t('component_conflict_card_type_manual_override_deferred');
export const component_conflict_card_severity_low = () => t('component_conflict_card_severity_low');
export const component_conflict_card_severity_medium = () => t('component_conflict_card_severity_medium');
export const component_conflict_card_severity_high = () => t('component_conflict_card_severity_high');
export const component_conflict_card_severity_critical = () => t('component_conflict_card_severity_critical');
export const component_conflict_card_severity_unknown = () => t('component_conflict_card_severity_unknown');
export const component_conflict_card_aria_prefix = () => t('component_conflict_card_aria_prefix');
export const component_conflict_card_severity_prefix = () => t('component_conflict_card_severity_prefix');
export const component_conflict_card_subject_missing = () => t('component_conflict_card_subject_missing');
export const component_conflict_card_incomplete_aria = () => t('component_conflict_card_incomplete_aria');
export const component_conflict_card_incomplete_title = () => t('component_conflict_card_incomplete_title');
export const component_conflict_card_incomplete_body = () => t('component_conflict_card_incomplete_body');
export const component_new_run_modal_back = () => t('component_new_run_modal_back');
export const component_chat_new_conversation = () => t('component_chat_new_conversation');
export const component_impersonation_suffix = () => t('component_impersonation_suffix');
export const component_impersonation_end_session = () => t('component_impersonation_end_session');
export const component_async_exports_empty = () => t('component_async_exports_empty');
export const component_async_exports_download = () => t('component_async_exports_download');
export const export_status_queued = () => t('export_status_queued');
export const export_status_running = () => t('export_status_running');
export const export_status_done = () => t('export_status_done');
export const export_status_failed = () => t('export_status_failed');
export const export_status_cancelled = () => t('export_status_cancelled');
export const login_forgot_password = () => t('login_forgot_password');
export const toast_locale_saved = () => t('toast_locale_saved');
export const toast_locale_save_failed = () => t('toast_locale_save_failed');
export const error_fallback_forbidden_title = () => t('error_fallback_forbidden_title');
export const error_fallback_service_unavailable_title = () => t('error_fallback_service_unavailable_title');
export const error_fallback_service_unavailable_desc = () => t('error_fallback_service_unavailable_desc');
export const error_fallback_request_title = () => t('error_fallback_request_title');
export const error_fallback_unexpected_title = () => t('error_fallback_unexpected_title');
export const error_fallback_retry = () => t('error_fallback_retry');
export const error_fallback_go_home = () => t('error_fallback_go_home');
// ─── WO error codes (localizeWoError) ─────────────────────────────────
export const wo_signal_label_short = (p) => t(`wo_signal_label_${p.signal}`);
export const wo_signal_label_default = () => t('wo_signal_label_default');
export const wo_state_label_short = (p) => t(`wo_state_label_short_${p.state}`);
export const wo_state_label_short_default = () => t('wo_state_label_short_default');
export const wo_error_invalid_transition = (p) => t('wo_error_invalid_transition', p);
export const wo_error_invalid_transition_tail_allowed = (p) => t('wo_error_invalid_transition_tail_allowed', p);
export const wo_error_inactive_bom = () => t('wo_error_inactive_bom');
export const wo_error_bom_item_mismatch = () => t('wo_error_bom_item_mismatch');
export const wo_error_bom_not_found = () => t('wo_error_bom_not_found');
export const wo_error_actor_is_signer = () => t('wo_error_actor_is_signer');
export const wo_error_signature_expired = () => t('wo_error_signature_expired');
export const wo_error_tier2_approval_required = () => t('wo_error_tier2_approval_required');
export const wo_error_tier3_dual_approval_required = () => t('wo_error_tier3_dual_approval_required');
export const wo_error_insufficient_role_with_ability = (p) => t('wo_error_insufficient_role_with_ability', p);
export const wo_error_insufficient_role = () => t('wo_error_insufficient_role');
export const wo_error_cannot_delete_jc_from_completed = () => t('wo_error_cannot_delete_jc_from_completed');
export const wo_error_temporal_unavailable = () => t('wo_error_temporal_unavailable');
export const wo_error_workflow_missing = () => t('wo_error_workflow_missing');
export const wo_error_workflow_closed = (p) => t('wo_error_workflow_closed', p);
export const wo_error_job_card_wo_state = (p) => t('wo_error_job_card_wo_state', p);
export const wo_error_executor_not_found = () => t('wo_error_executor_not_found');
export const wo_error_wo_not_found = () => t('wo_error_wo_not_found');
// ─── Integration status banner ────────────────────────────────────────
export const integration_peer = (p) => t(`integration_peer_${p.peer}`);
export const integration_health_ok = () => t('integration_health_ok');
export const integration_health_degraded = () => t('integration_health_degraded');
export const integration_health_disconnected = () => t('integration_health_disconnected');
export const integration_health_label = (p) => t(`integration_health_${p.health}`);
export const integration_banner_summary_ok = (p) => t('integration_banner_summary_ok', { count: p.count });
export const integration_banner_summary_degraded = (p) => t('integration_banner_summary_degraded', p);
export const integration_banner_summary_disconnected = (p) => t('integration_banner_summary_disconnected', p);
export const integration_banner_last_push = (p) => t('integration_banner_last_push', p);
export const integration_banner_open_admin_aria = () => t('integration_banner_open_admin_aria');
export const integration_detail_last_pull = (p) => t('integration_detail_last_pull', p);
export const integration_detail_last_push = (p) => t('integration_detail_last_push', p);
export const integration_detail_mode_rw = () => t('integration_detail_mode_rw');
export const integration_detail_mode_r = () => t('integration_detail_mode_r');
export const relative_time_just_now = () => t('relative_time_just_now');
export const relative_time_minutes_ago = (p) => t('relative_time_minutes_ago', { count: p.count });
export const relative_time_hours_ago = (p) => t('relative_time_hours_ago', { count: p.count });
// ─── Job cards table ──────────────────────────────────────────────────
export const job_card_aria_table = () => t('job_card_aria_table');
export const job_card_col_operation = () => t('job_card_col_operation');
export const job_card_col_operator = () => t('job_card_col_operator');
export const job_card_col_planned_min = () => t('job_card_col_planned_min');
export const job_card_col_actual_min = () => t('job_card_col_actual_min');
export const job_card_col_planned_qty = () => t('job_card_col_planned_qty');
export const job_card_col_completed_qty = () => t('job_card_col_completed_qty');
export const job_card_col_scrap = () => t('job_card_col_scrap');
export const job_card_col_status = () => t('job_card_col_status');
export const job_card_status_label = (p) => t(`job_card_status_${p.status}`);
export const job_card_aria_saved = () => t('job_card_aria_saved');
export const job_card_aria_delete = () => t('job_card_aria_delete');
export const job_card_draft_placeholder_operation = () => t('job_card_draft_placeholder_operation');
export const job_card_aria_new_operation = () => t('job_card_aria_new_operation');
export const job_card_aria_cancel_draft = () => t('job_card_aria_cancel_draft');
export const job_card_add_button = () => t('job_card_add_button');
export const job_card_totals_planned = (p) => t('job_card_totals_planned', { value: p.value });
export const job_card_totals_actual = (p) => t('job_card_totals_actual', { value: p.value });
export const job_card_totals_variance = (p) => t('job_card_totals_variance', { sign: p.sign, value: p.value });
export const job_card_delete_dialog_title = () => t('job_card_delete_dialog_title');
export const job_card_delete_dialog_description = () => t('job_card_delete_dialog_description');
export const user_picker_placeholder = () => t('user_picker_placeholder');
export const user_picker_select_prompt = () => t('user_picker_select_prompt');
export const job_card_toast_created = () => t('job_card_toast_created');
export const job_card_toast_create_failed = () => t('job_card_toast_create_failed');
export const job_card_toast_update_failed = () => t('job_card_toast_update_failed');
export const job_card_toast_delete_failed = () => t('job_card_toast_delete_failed');
// ─── Manufacture preview dialog ───────────────────────────────────────
export const manufacture_preview_title = () => t('manufacture_preview_title');
export const manufacture_preview_shortage_banner = () => t('manufacture_preview_shortage_banner');
export const manufacture_preview_section_requirements = () => t('manufacture_preview_section_requirements');
export const manufacture_preview_aria_requirements = () => t('manufacture_preview_aria_requirements');
export const manufacture_preview_col_material = () => t('manufacture_preview_col_material');
export const manufacture_preview_col_required = () => t('manufacture_preview_col_required');
export const manufacture_preview_col_available = () => t('manufacture_preview_col_available');
export const manufacture_preview_col_shortage = () => t('manufacture_preview_col_shortage');
export const manufacture_preview_col_batch = () => t('manufacture_preview_col_batch');
export const manufacture_preview_section_warnings = () => t('manufacture_preview_section_warnings');
export const manufacture_preview_section_alternates = () => t('manufacture_preview_section_alternates');
export const manufacture_preview_alternate_swap = (p) => t('manufacture_preview_alternate_swap', p);
export const manufacture_preview_override_label = () => t('manufacture_preview_override_label');
export const manufacture_preview_approver_label = () => t('manufacture_preview_approver_label');
export const manufacture_preview_approver_hint = () => t('manufacture_preview_approver_hint');
export const manufacture_preview_reason_label = () => t('manufacture_preview_reason_label');
export const manufacture_preview_confirm_default = () => t('manufacture_preview_confirm_default');
// ─── Tier3 / batches / items / new-run / chat / drift / audit ─────────
export const tier3_confirm_push_default = () => t('tier3_confirm_push_default');
export const tier3_reason_default = () => t('tier3_reason_default');
export const tier3_banner_title = () => t('tier3_banner_title');
export const tier3_banner_suffix = () => t('tier3_banner_suffix');
export const tier3_progress_aria = (p) => t('tier3_progress_aria', { step: p.step });
export const tier3_payload_aria = () => t('tier3_payload_aria');
export const tier3_reason_label_suffix = (p) => t('tier3_reason_label_suffix', { count: p.count });
export const tier3_reason_too_short = () => t('tier3_reason_too_short');
export const tier3_signer_prefix = () => t('tier3_signer_prefix');
export const tier3_signer2_header = () => t('tier3_signer2_header');
export const tier3_status_signed = () => t('tier3_status_signed');
export const tier3_status_waiting = () => t('tier3_status_waiting');
export const tier3_status_selected = () => t('tier3_status_selected');
export const tier3_signer_note_label = () => t('tier3_signer_note_label');
export const tier3_signer_note_placeholder = () => t('tier3_signer_note_placeholder');
export const tier3_second_approver_label = () => t('tier3_second_approver_label');
export const tier3_second_approver_placeholder = () => t('tier3_second_approver_placeholder');
export const tier3_second_approver_hint = () => t('tier3_second_approver_hint');
export const tier3_same_user_error = () => t('tier3_same_user_error');
export const tier3_sign_as_button = (p) => t('tier3_sign_as_button', p);
export const batch_qc_status_label = (p) => t(`batch_qc_status_${p.status}`);
export const batch_toast_created = () => t('batch_toast_created');
export const batch_toast_create_failed = () => t('batch_toast_create_failed');
export const batch_toast_updated = () => t('batch_toast_updated');
export const batch_toast_update_failed = () => t('batch_toast_update_failed');
export const batch_dialog_create_title = () => t('batch_dialog_create_title');
export const batch_dialog_edit_title = () => t('batch_dialog_edit_title');
export const batch_field_batch_no = () => t('batch_field_batch_no');
export const batch_field_item = () => t('batch_field_item');
export const batch_field_mfg_date = () => t('batch_field_mfg_date');
export const batch_field_expiry_date = () => t('batch_field_expiry_date');
export const batch_field_qc_status = () => t('batch_field_qc_status');
export const batch_field_batch_no_placeholder = () => t('batch_field_batch_no_placeholder');
export const batch_edit_hint_batch_no_changed = () => t('batch_edit_hint_batch_no_changed');
export const batch_edit_field_item = () => t('batch_edit_field_item');
export const batch_edit_hint_readonly = () => t('batch_edit_hint_readonly');
export const batch_edit_qc_hint = () => t('batch_edit_qc_hint');
// FE-164 Sprint Q+ browser-verify: item_type enum может содержать 'wip' (legacy)
// или другие values отсутствующие в i18n. Fallback к raw type вместо [missing].
export const item_type_label = (p) => {
    const r = t(`item_type_${p.type}`);
    return typeof r === 'string' && r.startsWith('[missing:') ? p.type : r;
};
/** FE-64 Sprint Q+: type description for radio cards. */
export const item_type_desc = (p) => {
    const key = `item_type_desc_${p.type}`;
    try {
        return t(key);
    }
    catch {
        return '';
    }
};
export const item_dialog_create_title = () => t('item_dialog_create_title');
export const item_field_sku = () => t('item_field_sku');
export const item_field_sku_placeholder = () => t('item_field_sku_placeholder');
export const item_field_name = () => t('item_field_name');
export const item_field_name_placeholder = () => t('item_field_name_placeholder');
export const item_field_type = () => t('item_field_type');
export const item_field_group = () => t('item_field_group');
export const item_field_group_none = () => t('item_field_group_none');
export const item_field_default_uom = () => t('item_field_default_uom');
export const item_field_stock_uom = () => t('item_field_stock_uom');
export const item_field_description = () => t('item_field_description');
export const item_field_description_placeholder = () => t('item_field_description_placeholder');
export const item_field_has_batch = () => t('item_field_has_batch');
export const item_field_has_serial = () => t('item_field_has_serial');
export const item_toast_created = () => t('item_toast_created');
export const item_toast_create_failed = () => t('item_toast_create_failed');
export const item_toast_updated = () => t('item_toast_updated');
export const item_toast_update_failed = () => t('item_toast_update_failed');
export const item_toast_deleted = () => t('item_toast_deleted');
export const item_toast_delete_failed = () => t('item_toast_delete_failed');
export const new_run_reconciliation_title = () => t('new_run_reconciliation_title');
export const new_run_reconciliation_description = () => t('new_run_reconciliation_description');
export const new_run_writeoff_title = () => t('new_run_writeoff_title');
export const new_run_writeoff_description = () => t('new_run_writeoff_description');
export const new_run_cost_title = () => t('new_run_cost_title');
export const new_run_cost_description = () => t('new_run_cost_description');
export const new_run_period_close_title = () => t('new_run_period_close_title');
export const new_run_period_close_description = () => t('new_run_period_close_description');
export const new_run_anomaly_title = () => t('new_run_anomaly_title');
export const new_run_anomaly_description = () => t('new_run_anomaly_description');
export const new_run_modal_title = () => t('new_run_modal_title');
export const new_run_modal_step1_heading = () => t('new_run_modal_step1_heading');
export const new_run_modal_step2_heading = () => t('new_run_modal_step2_heading');
export const new_run_modal_scope_label = () => t('new_run_modal_scope_label');
export const new_run_modal_period_from = () => t('new_run_modal_period_from');
export const new_run_modal_period_to = () => t('new_run_modal_period_to');
export const new_run_modal_start = () => t('new_run_modal_start');
export const new_run_modal_next = () => t('new_run_modal_next');
export const new_run_modal_period_label = () => t('new_run_modal_period_label');
export const new_run_modal_preset_current_month = () => t('new_run_modal_preset_current_month');
export const new_run_modal_error_task_type = () => t('new_run_modal_error_task_type');
export const new_run_modal_error_create = () => t('new_run_modal_error_create');
export const login_title = () => t('login_title');
export const auth_logout_reason_entity = (p) => t('auth_logout_reason_entity', p);
export const auth_logout_reason_generic = () => t('auth_logout_reason_generic');
export const auth_logout_reason_account = () => t('auth_logout_reason_account');
export const access_entity_revoked_title = (p) => t('access_entity_revoked_title', p);
export const access_entity_revoked_title_generic = () => t('access_entity_revoked_title_generic');
export const access_entity_revoked_switched = (p) => t('access_entity_revoked_switched', p);
export const access_write_not_saved_title = () => t('access_write_not_saved_title');
export const access_write_not_saved_body = () => t('access_write_not_saved_body');
export const login_email_label = () => t('login_email_label');
export const login_password_label = () => t('login_password_label');
export const login_submit = () => t('login_submit');
export const login_network_error = () => t('login_network_error');
export const login_rate_limit = (p) => t('login_rate_limit', p);
export const login_rate_limit_until = (p) => t('login_rate_limit_until', p);
export const login_rate_limit_until_access = (p) => t('login_rate_limit_until_access', p);
export const login_rate_limit_over = () => t('login_rate_limit_over');
export const login_error_invalid_credentials = () => t('login_error_invalid_credentials');
export const login_error_account_locked = () => t('login_error_account_locked');
export const login_error_password_change_required = () => t('login_error_password_change_required');
export const login_error_generic = () => t('login_error_generic');
export const approval_matrix_title = () => t('approval_matrix_title');
export const approval_matrix_description = () => t('approval_matrix_description');
export const approval_matrix_warning = () => t('approval_matrix_warning');
export const approval_matrix_col_task = () => t('approval_matrix_col_task');
export const approval_matrix_col_tier = () => t('approval_matrix_col_tier');
export const approval_matrix_col_roles = () => t('approval_matrix_col_roles');
export const approval_matrix_col_min_signers = () => t('approval_matrix_col_min_signers');
export const approval_matrix_tier_aria = (p) => t('approval_matrix_tier_aria', p);
export const approval_matrix_signers_aria = (p) => t('approval_matrix_signers_aria', p);
export const preferences_page_title = () => t('preferences_page_title');
export const preferences_theme_label = () => t('preferences_theme_label');
export const preferences_theme_light = () => t('preferences_theme_light');
export const preferences_theme_dark = () => t('preferences_theme_dark');
export const preferences_theme_system = () => t('preferences_theme_system');
export const preferences_sidebar_label = () => t('preferences_sidebar_label');
export const preferences_sidebar_expanded = () => t('preferences_sidebar_expanded');
export const preferences_sidebar_collapsed = () => t('preferences_sidebar_collapsed');
export const preferences_timezone_label = () => t('preferences_timezone_label');
// F94 (§9.5) — подпись у поля пояса: сроки считает деловой пояс компании.
export const preferences_timezone_business_almaty = () => t('preferences_timezone_business_almaty');
export const preferences_timezone_business_other = (p) => t('preferences_timezone_business_other', p);
export const preferences_density_label = () => t('preferences_density_label');
export const preferences_run_task_label = () => t('preferences_run_task_label');
export const preferences_run_task_none = () => t('preferences_run_task_none');
export const preferences_run_task_reconcile = () => t('preferences_run_task_reconcile');
export const preferences_run_task_writeoff = () => t('preferences_run_task_writeoff');
export const preferences_run_task_cost = () => t('preferences_run_task_cost');
export const preferences_run_task_anomaly = () => t('preferences_run_task_anomaly');
export const preferences_run_task_period_close = () => t('preferences_run_task_period_close');
export const preferences_saving = () => t('preferences_saving');
export const preferences_auto_save_hint = () => t('preferences_auto_save_hint');
export const force_pwd_title = () => t('force_pwd_title');
export const force_pwd_lede = () => t('force_pwd_lede');
export const force_pwd_current = () => t('force_pwd_current');
export const force_pwd_new = () => t('force_pwd_new');
export const force_pwd_new_hint = () => t('force_pwd_new_hint');
export const force_pwd_confirm = () => t('force_pwd_confirm');
export const force_pwd_submit = () => t('force_pwd_submit');
export const force_pwd_error_generic = () => t('force_pwd_error_generic');
export const evidence_page_title = () => t('evidence_page_title');
export const evidence_page_description = () => t('evidence_page_description');
export const evidence_filters_heading = () => t('evidence_filters_heading');
export const evidence_source_label = () => t('evidence_source_label');
export const evidence_source_all = () => t('evidence_source_all');
export const evidence_doc_id_label = () => t('evidence_doc_id_label');
export const evidence_doc_id_placeholder = () => t('evidence_doc_id_placeholder');
export const evidence_hash_label = () => t('evidence_hash_label');
export const evidence_empty_title = () => t('evidence_empty_title');
export const evidence_empty_desc = () => t('evidence_empty_desc');
export const evidence_col_source = () => t('evidence_col_source');
export const evidence_col_label = () => t('evidence_col_label');
export const evidence_col_snippet = () => t('evidence_col_snippet');
export const evidence_btn_open = () => t('evidence_btn_open');
export const evidence_drawer_default = () => t('evidence_drawer_default');
export const evidence_linked_runs = () => t('evidence_linked_runs');
export const anomalies_page_title = () => t('anomalies_page_title');
export const anomalies_filters_heading = () => t('anomalies_filters_heading');
export const anomalies_severity_label = () => t('anomalies_severity_label');
export const anomalies_type_label = () => t('anomalies_type_label');
export const anomalies_filter_all = () => t('anomalies_filter_all');
export const anomalies_severity_info = () => t('anomalies_severity_info');
export const anomalies_severity_warn = () => t('anomalies_severity_warn');
export const anomalies_severity_major = () => t('anomalies_severity_major');
export const anomalies_severity_critical = () => t('anomalies_severity_critical');
export const anomalies_type_qty_outlier = () => t('anomalies_type_qty_outlier');
export const anomalies_type_rate_jump = () => t('anomalies_type_rate_jump');
export const anomalies_type_yield_variance = () => t('anomalies_type_yield_variance');
export const anomalies_type_cost_layer_imbalance = () => t('anomalies_type_cost_layer_imbalance');
export const anomalies_type_velocity_drop = () => t('anomalies_type_velocity_drop');
export const anomalies_type_duplicate_voucher = () => t('anomalies_type_duplicate_voucher');
export const anomalies_type_orphan_movement = () => t('anomalies_type_orphan_movement');
export const anomalies_type_overdue_ap = () => t('anomalies_type_overdue_ap');
export const anomalies_type_overdue_ar = () => t('anomalies_type_overdue_ar');
export const anomalies_type_reconciliation_variance = () => t('anomalies_type_reconciliation_variance');
export const anomalies_type_fx_rate_jump = () => t('anomalies_type_fx_rate_jump');
export const anomalies_type_cash_flow_negative = () => t('anomalies_type_cash_flow_negative');
export const anomalies_type_scrap_above_threshold = () => t('anomalies_type_scrap_above_threshold');
export const anomalies_type_negative_stock = () => t('anomalies_type_negative_stock');
export const anomalies_type_counterparty_missing_bin = () => t('anomalies_type_counterparty_missing_bin');
export const anomalies_type_valuation_rate_missing = () => t('anomalies_type_valuation_rate_missing');
export const anomalies_type_duplicate_payment_candidate = () => t('anomalies_type_duplicate_payment_candidate');
export const anomalies_bulk_aria = () => t('anomalies_bulk_aria');
export const anomalies_bulk_selected = (p) => t('anomalies_bulk_selected', p);
export const anomalies_bulk_review = () => t('anomalies_bulk_review');
export const anomalies_btn_dismiss = () => t('anomalies_btn_dismiss');
export const anomalies_bulk_clear = () => t('anomalies_bulk_clear');
export const anomalies_empty_title = () => t('anomalies_empty_title');
export const anomalies_empty_desc = () => t('anomalies_empty_desc');
export const anomalies_col_type = () => t('anomalies_col_type');
export const anomalies_col_subject = () => t('anomalies_col_subject');
export const anomalies_col_severity = () => t('anomalies_col_severity');
export const anomalies_col_status = () => t('anomalies_col_status');
export const anomalies_btn_review = () => t('anomalies_btn_review');
export const writeoff_page_title = () => t('writeoff_page_title');
export const writeoff_page_description = () => t('writeoff_page_description');
export const writeoff_wo_label = () => t('writeoff_wo_label');
export const writeoff_wo_placeholder = () => t('writeoff_wo_placeholder');
export const writeoff_empty_title = () => t('writeoff_empty_title');
export const writeoff_empty_desc = () => t('writeoff_empty_desc');
export const writeoff_hero_rows = () => t('writeoff_hero_rows');
export const writeoff_hero_variance = () => t('writeoff_hero_variance');
export const writeoff_hero_with_reason = () => t('writeoff_hero_with_reason');
export const writeoff_table_aria = () => t('writeoff_table_aria');
export const writeoff_no_rows = () => t('writeoff_no_rows');
export const writeoff_col_item = () => t('writeoff_col_item');
export const writeoff_col_required = () => t('writeoff_col_required');
export const writeoff_col_allocated = () => t('writeoff_col_allocated');
export const writeoff_col_variance = () => t('writeoff_col_variance');
export const writeoff_col_reason = () => t('writeoff_col_reason');
export const writeoff_col_source = () => t('writeoff_col_source');
export const writeoff_legend_title = () => t('writeoff_legend_title');
export const writeoff_legend_normative = () => t('writeoff_legend_normative');
export const writeoff_legend_scrap = () => t('writeoff_legend_scrap');
export const writeoff_legend_error = () => t('writeoff_legend_error');
export const writeoff_legend_shortage = () => t('writeoff_legend_shortage');
export const writeoff_legend_spoilage = () => t('writeoff_legend_spoilage');
export const writeoff_legend_other = () => t('writeoff_legend_other');
export const cost_page_title = () => t('cost_page_title');
export const cost_page_description = () => t('cost_page_description');
export const cost_scope_label = () => t('cost_scope_label');
export const cost_scope_period = () => t('cost_scope_period');
export const cost_scope_sku = () => t('cost_scope_sku');
export const cost_scope_work_order = () => t('cost_scope_work_order');
export const cost_value_label = () => t('cost_value_label');
export const cost_placeholder_sku = () => t('cost_placeholder_sku');
export const cost_placeholder_wo = () => t('cost_placeholder_wo');
export const cost_show = () => t('cost_show');
export const cost_empty_title = () => t('cost_empty_title');
export const cost_empty_desc = () => t('cost_empty_desc');
export const cost_hero_material = () => t('cost_hero_material');
export const cost_hero_material_hint = () => t('cost_hero_material_hint');
export const cost_hero_labor = () => t('cost_hero_labor');
export const cost_hero_labor_hint = () => t('cost_hero_labor_hint');
export const cost_hero_overhead = () => t('cost_hero_overhead');
export const cost_hero_overhead_hint = () => t('cost_hero_overhead_hint');
export const cost_hero_total = () => t('cost_hero_total');
export const cost_table_aria = () => t('cost_table_aria');
export const cost_col_label = () => t('cost_col_label');
export const cost_col_category = () => t('cost_col_category');
export const cost_col_qty = () => t('cost_col_qty');
export const cost_col_amount = () => t('cost_col_amount');
export const cost_evidence_title = () => t('cost_evidence_title');
export const drawer_close_aria = () => t('drawer_close_aria');
// ─── chat-panel-geometry: режим панели, закрепление, разворот, разделитель ───
export const chat_mode_beside = () => t('chat_mode_beside');
export const chat_mode_overlay = () => t('chat_mode_overlay');
export const chat_mode_fullscreen = () => t('chat_mode_fullscreen');
export const chat_mode_hint_beside = () => t('chat_mode_hint_beside');
export const chat_mode_hint_overlay = () => t('chat_mode_hint_overlay');
export const chat_mode_hint_at_boundary = () => t('chat_mode_hint_at_boundary');
export const chat_beside_hint_columns = () => t('chat_beside_hint_columns');
export const chat_pin_overlay = () => t('chat_pin_overlay');
export const chat_unpin_overlay = () => t('chat_unpin_overlay');
export const chat_fullscreen_enter = () => t('chat_fullscreen_enter');
export const chat_fullscreen_exit = () => t('chat_fullscreen_exit');
export const chat_fullscreen_hint = () => t('chat_fullscreen_hint');
/*
 * Живая проверка после круга 41: правка состава жива и в полном экране (орган
 * туда вернулся по UX-2 того же круга), а подпись о ней снова молчала — ровно
 * тот дефект, который круг 39 назвал у оконного режима. Барьер круга 39
 * переписан на новый носитель: полноэкранный режим больше не «сильнее» правки,
 * он с ней складывается.
 */
export const chat_fullscreen_hint_editing = () => t('chat_fullscreen_hint_editing');
export const chat_fullscreen_exit_short = () => t('chat_fullscreen_exit_short');
export const chat_close_short = () => t('chat_close_short');
export const chat_resizer_aria = () => t('chat_resizer_aria');
export const chat_boundary_guide_label = () => t('chat_boundary_guide_label');
export const chat_snap_to_boundary = () => t('chat_snap_to_boundary');
export const chat_snap_to_boundary_short = () => t('chat_snap_to_boundary_short');
/** «накрывает 116 пикселей» — сколько панель отняла у полотна прямо сейчас. */
export const chat_mode_overlap = (p) => `${t('chat_mode_overlap')} ${chat_px_count({ n: p.n })}`;
export const chat_status_beside = () => t('chat_status_beside');
export const chat_status_overlay = () => t('chat_status_overlay');
export const chat_back_to_table = () => t('chat_back_to_table');
/**
 * «N пикселей» с русскими тремя формами. Через `pluralForm`, а не ICU в JSON:
 * рантайм подставляет только `{слово}` — конструкция `{n, plural, …}` ушла бы в
 * интерфейс дословно (SOLUTION §13).
 */
export const chat_px_count = (p) => {
    const word = pluralForm(p.n, {
        one: t('chat_px_one'),
        few: t('chat_px_few'),
        many: t('chat_px_many'),
    });
    return `${p.n} ${word}`;
};
/** «Рядом — только при окне шире 1414 пикселей». */
export const chat_beside_hint_window = (p) => `${t('chat_beside_hint_window')} ${chat_px_count({ n: p.n })}`;
export const chat_open_aria = () => t('chat_open_aria');
export const chat_open_aria_shortcut = () => t('chat_open_aria_shortcut');
export const chat_open_tooltip = () => t('chat_open_tooltip');
export const chat_open_btn_label = () => t('chat_open_btn_label');
export const topbar_notifications = () => t('topbar_notifications');
export const topbar_notifications_with_overdue = (p) => t('topbar_notifications_with_overdue', p);
export const chat_drawer_title = () => t('chat_drawer_title');
export const chat_conversations_title = () => t('chat_conversations_title');
export const chat_empty_placeholder = () => t('chat_empty_placeholder');
export const chat_evidence_header = () => t('chat_evidence_header');
export const chat_suggestions_header = () => t('chat_suggestions_header');
export const chat_input_placeholder = () => t('chat_input_placeholder');
export const chat_input_aria = () => t('chat_input_aria');
export const chat_send_button = () => t('chat_send_button');
export const chat_thinking = () => t('chat_thinking');
export const chat_mentions_hint = () => t('chat_mentions_hint');
export const chat_conversations_aria = () => t('chat_conversations_aria');
export const chat_hint_readonly = () => t('chat_hint_readonly');
export const chat_input_placeholder_full = () => t('chat_input_placeholder_full');
/**
 * Круг 4 кадра К2 (UI-2, дословно): «Плейсхолдер композера переносится на две
 * строки — „Спросите агента… @run / @period / @item для контекста“ на 400 px
 * занимает две строки и упирается в рейл и в кнопку отправки».
 *
 * Перечисление упоминаний живёт в плейсхолдере ровно там, где для него есть
 * строка: ширину панели держит человек, и решает её замер, а не догадка.
 */
export const chat_input_placeholder_min = () => t('chat_input_placeholder_min');
export const chat_shortcut_open = () => t('chat_shortcut_open');
export const chat_refuse_title = () => t('chat_refuse_title');
export const chat_refuse_open_route = () => t('chat_refuse_open_route');
export const chat_context_prefix = () => t('chat_context_prefix');
export const chat_ai_answer = () => t('chat_ai_answer');
// ─── Chat NLQ — UX redesign 2026-04-27 ──────────────────────────────────
export const chat_empty_heading = () => t('chat_empty_heading');
export const chat_empty_hint = () => t('chat_empty_hint');
export const chat_suggestion_tasks_section = () => t('chat_suggestion_tasks_section');
export const chat_suggestion_tasks_overdue = () => t('chat_suggestion_tasks_overdue');
export const chat_suggestion_tasks_unassigned = () => t('chat_suggestion_tasks_unassigned');
export const chat_suggestion_tasks_week = () => t('chat_suggestion_tasks_week');
export const chat_suggestion_what_with_order = () => t('chat_suggestion_what_with_order');
export const chat_suggestion_stock_balance = () => t('chat_suggestion_stock_balance');
export const chat_suggestion_recent_conflicts = () => t('chat_suggestion_recent_conflicts');
// CH-11 — route-specific suggestions
export const chat_suggestion_se_how_to_receipt = () => t('chat_suggestion_se_how_to_receipt');
export const chat_suggestion_se_why_submit = () => t('chat_suggestion_se_why_submit');
export const chat_suggestion_se_what_is_sle = () => t('chat_suggestion_se_what_is_sle');
export const chat_suggestion_wo_how_to_close = () => t('chat_suggestion_wo_how_to_close');
export const chat_suggestion_wo_what_is_job_card = () => t('chat_suggestion_wo_what_is_job_card');
export const chat_suggestion_wo_why_blocked = () => t('chat_suggestion_wo_why_blocked');
export const chat_suggestion_recon_how_to = () => t('chat_suggestion_recon_how_to');
export const chat_suggestion_recon_opening_stock = () => t('chat_suggestion_recon_opening_stock');
export const chat_suggestion_recon_variance = () => t('chat_suggestion_recon_variance');
export const chat_suggestion_conflicts_what_is = () => t('chat_suggestion_conflicts_what_is');
export const chat_suggestion_conflicts_how_resolve = () => t('chat_suggestion_conflicts_how_resolve');
export const chat_suggestion_conflicts_tier2 = () => t('chat_suggestion_conflicts_tier2');
export const chat_suggestion_approvals_tier_diff = () => t('chat_suggestion_approvals_tier_diff');
export const chat_suggestion_approvals_who_approves = () => t('chat_suggestion_approvals_who_approves');
export const chat_suggestion_approvals_reject = () => t('chat_suggestion_approvals_reject');
export const chat_suggestion_items_create = () => t('chat_suggestion_items_create');
export const chat_suggestion_items_variants = () => t('chat_suggestion_items_variants');
export const chat_suggestion_items_valuation = () => t('chat_suggestion_items_valuation');
export const chat_suggestion_period_close_blockers = () => t('chat_suggestion_period_close_blockers');
export const chat_suggestion_period_close_steps = () => t('chat_suggestion_period_close_steps');
export const chat_suggestion_period_close_reopen = () => t('chat_suggestion_period_close_reopen');
export const chat_new_chat_n = (p) => t('chat_new_chat_n', p);
/**
 * chat-interactive-actions F10 (§6.4) — словарь карточки действий, чипа фильтра,
 * причин пропуска и кодов контура. Сервер этих текстов не шлёт: он отдаёт коды
 * (`over_cap`, `state_group_missing`, `draft_already_consumed`), а слова живут тут.
 */
export const chat_action_title_bulk = () => t('chat_action_title_bulk');
export const chat_action_title_comment = () => t('chat_action_title_comment');
export const chat_action_matched = (p) => t('chat_action_matched', p);
export const chat_action_will_apply = (p) => t('chat_action_will_apply', p);
export const chat_action_will_deny = (p) => t('chat_action_will_deny', p);
export const chat_action_sample_header = (p) => t('chat_action_sample_header', p);
export const chat_action_applied_to = () => t('chat_action_applied_to');
export const chat_action_applied_time_label = () => t('chat_action_applied_time_label');
export const chat_action_scope_header = () => t('chat_action_scope_header');
/**
 * R2 (UX-1 приёмки) — чип отбора несёт ПРЕДИКАТ со значением, а не измерение.
 * «по приоритету» не даёт сверить самое рискованное звено цепочки — истолкование
 * фразы: с какого приоритета? Значения у карточки есть, их и показываем.
 */
export const chat_action_scope_status = (p) => t('chat_action_scope_status', p);
export const chat_action_scope_priority = (p) => t('chat_action_scope_priority', p);
export const chat_action_scope_overdue = () => t('chat_action_scope_overdue');
/** Проект и исполнитель — идентификаторы: имён у карточки нет, показываем счёт. */
/** Проект — по ИМЕНИ: счётчик «выбрано: 1» не даёт сверить истолкование фразы. */
export const chat_action_scope_project = (p) => t('chat_action_scope_project', p);
export const chat_action_scope_more = (p) => t('chat_action_scope_more', p);
export const chat_action_scope_assignee = (p) => t('chat_action_scope_assignee', p);
export const chat_action_scope_no_assignee = () => t('chat_action_scope_no_assignee');
export const chat_action_scope_due = (p) => t('chat_action_scope_due', p);
export const chat_action_scope_due_has = () => t('chat_action_scope_due_has');
export const chat_action_scope_due_none = () => t('chat_action_scope_due_none');
export const chat_action_scope_text = (p) => t('chat_action_scope_text', p);
export const chat_action_scope_rules = (p) => t('chat_action_scope_rules', p);
export const chat_action_scope_selection = () => t('chat_action_scope_selection');
/** R2 (UX-4) — срок годности предложения: TTL черновика 1 час (В-5). */
export const chat_action_valid_until = () => t('chat_action_valid_until');
/** Пустое значение поля у задачи из примера («срок не задан»). */
export const chat_action_sample_value_none = () => t('chat_action_sample_value_none');
export const chat_action_open_task_aria = (p) => t('chat_action_open_task_aria', p);
/**
 * Охват называет ВСЕЛЕННУЮ отбора, а не голую дробь: «3 из 3» не отвечало, три из
 * скольких найденных — из страницы, из фильтра или из всех задач компании.
 */
export const chat_action_will_apply_of_filter = (p) => t('chat_action_will_apply_of_filter', p);
export const chat_action_will_apply_of_selection = (p) => t('chat_action_will_apply_of_selection', p);
/** Когда показаны ВСЕ задачи пачки — это уже не примеры, а состав. */
export const chat_action_composition_header = (p) => t('chat_action_composition_header', p);
export const chat_action_sample_due = (p) => t('chat_action_sample_due', p);
/**
 * Просрочка в составе — СЛОВОМ рядом с датой, а не цветом: тёплый канал карточки
 * уже занят состоянием ожидания, плёнкой необратимости и заливкой CTA, и четвёртое
 * янтарное пятно делало его неоднозначным (§1.3 — цвет только там, где он значит
 * одно). Дата при этом остаётся: решение опирается на неё не меньше, чем на факт
 * опоздания.
 */
export const chat_action_shown_in_list = () => t('chat_action_shown_in_list');
export const chat_action_sample_no_code = () => t('chat_action_sample_no_code');
export const chat_action_sample_ref_hint = () => t('chat_action_sample_ref_hint');
export const chat_action_sample_overdue = (p) => `${t('chat_action_sample_overdue_prefix')} ${p.days} ${pluralForm(p.days, {
    one: t('chat_action_sample_overdue_one'),
    few: t('chat_action_sample_overdue_few'),
    many: t('chat_action_sample_overdue_many'),
})}`;
/**
 * Сторона «сейчас», сказанная ОДИН раз: когда карточка показывает весь состав и
 * значение у всех задач одинаковое, три одинаковых перехода подряд — орнамент,
 * а не информация. Утверждение «у всех» произносится только про полный состав.
 */
export const chat_action_current_all = (p) => t('chat_action_current_all', p);
/** То же для проекта: одно имя, повторённое в каждой строке, — это шум. */
export const chat_action_project_all = (p) => t('chat_action_project_all', p);
export const chat_action_irreversible = () => t('chat_action_irreversible');
/**
 * CIA-F2 — у вычислимого подмножества операций отмена ПОЯВИЛАСЬ, и карточка
 * обязана это говорить: прежняя оговорка «массовой отмены нет» стала бы ложью
 * ровно там, где отмена работает.
 */
export const chat_action_undoable = () => t('chat_action_undoable');
/**
 * Круг 6 кадра 1 гейта захода С2, обе половины: «Нулевая дельта выглядит как
 * удача — карточка сама пишет „Сейчас у всех: Высокий“, то есть предложение не
 * меняет ничего, а кнопка остаётся полновесной „Применить (3)“». Холостой исход
 * назван словом, и применять его нечем.
 */
export const chat_action_no_delta = () => t('chat_action_no_delta');
/* Круг 8 кадра 1 (UI-1): при нулевой дельте носитель ОДИН — причина блокировки,
   и она же называет состояние, из-за которого применять нечего. */
export const chat_action_no_delta_reason = (p) => t('chat_action_no_delta_reason', p);
/* Круг 8 кадра 1 (UX-3): у холостого предложения состав свёрнут — предмет решения
   не он, а вывод «менять нечего». */
export const chat_action_show_composition = (p) => t('chat_action_show_composition', p);
/* Круг 15 кадра 1 (UX-1): остаток состава называется числом и открывается щелчком —
   немого обрыва под липким подвалом быть не должно. */
export const chat_action_show_rest = (p) => t('chat_action_show_rest', p);
/* Круг 2 кадра 3 (UI-1, BLOCKER): причина режима печатается во ВСЕХ режимах, а не
   только в развороте; текст меняется вместе с режимом (UX-2 того же круга). */
export const chat_mode_hint_line_overlay = () => t('chat_mode_hint_line_overlay');
/*
 * Круг 39 кадра 4 (UX-1, дословно): «Названа цена Esc, но не судьба правки — под
 * шапкой стоит „Esc сворачивает панель — предложение остаётся в ленте“, а
 * предикат подписи знает только `fullscreen` и `geometry.mode`. Механика правку
 * теперь бережёт, но кадр об этом молчит ровно там, где человек уже обжёгся
 * кругом 38. Направление: третья ветка по `subsetDraftId !== null`».
 */
export const chat_mode_hint_line_editing = () => t('chat_mode_hint_line_editing');
export const chat_mode_hint_line_beside = () => t('chat_mode_hint_line_beside');
/* Круг 1 кадра 3 (UI-4): «42» рядом с «Новый чат» без единицы читается свойством беседы. */
export const chat_conversations_unit = () => t('chat_conversations_unit');
/* Круг 3 кадра 3 (UI-1): у порога строка называет не событие, а цену — что
   именно закроется в ноль и чем это чинится. */
export const chat_action_deadline_consequence = (p) => t('chat_action_deadline_consequence', p);
/* Круг 9 кадра 1 (UX-2): заголовок карточки называет ОБА конца перехода и объём —
   «Средний → Высокий · 3 задачи», а не только цель. */
export const chat_action_subject_transition = (p) => t('chat_action_subject_transition', p);
export const chat_action_subject_tasks = (p) => pluralForm(p.n, {
    one: t('chat_action_subject_tasks_one').replace('{n}', String(p.n)),
    few: t('chat_action_subject_tasks_few').replace('{n}', String(p.n)),
    many: t('chat_action_subject_tasks_many').replace('{n}', String(p.n)),
});
/** Кнопка без счёта: при нулевой дельте число отбора читалось как число изменений. */
export const chat_action_apply_plain = () => t('chat_action_apply_plain');
/**
 * Круг 2 кадра 1 гейта захода С2 (UX, дословно): «Откат обещан только клавишей
 * и только на другой поверхности — после „Применить“ фокус остаётся в панели
 * чата, а Ctrl+Z живёт на экране задач; мышиному пользователю пути назад нет
 * вовсе». Кнопка отмены живёт там же, где отчёт о применении.
 */
export const chat_action_undo_now = (p) => t('chat_action_undo_now', { n: p.n });
/** Огрызок авторства в липкой группе: чьё это предложение (§4.7). */
export const chat_action_author_short = () => t('chat_action_author_short');
export const chat_action_open_tasks = () => t('chat_action_open_tasks');
export const chat_action_title_bulk_done = () => t('chat_action_title_bulk_done');
export const chat_action_title_comment_done = () => t('chat_action_title_comment_done');
export const chat_action_show_all = (p) => t('chat_action_show_all', p);
export const chat_action_apply = () => t('chat_action_apply');
export const chat_action_cancel = () => t('chat_action_cancel');
export const chat_action_applying = () => t('chat_action_applying');
export const chat_action_snapshot_note = () => t('chat_action_snapshot_note');
// F12 (дизайн-ревью, заход 2) — карточка обязана называть ПРЕДЛАГАЕМОЕ значение и
// одну арифметику: «применится X из Y», а не два числа, спорящих с предупреждением.
export const chat_action_will_apply_of = (p) => t('chat_action_will_apply_of', p);
export const chat_action_denied_short = (p) => t('chat_action_denied_short', p);
export const chat_action_denied_hint = () => t('chat_action_denied_hint');
export const chat_action_applied_at = (p) => t('chat_action_applied_at', p);
export const chat_action_sample_header_done = () => t('chat_action_sample_header_done');
export const chat_action_status_pending = () => t('chat_action_status_pending');
export const chat_action_status_applied = () => t('chat_action_status_applied');
export const chat_action_status_cancelled = () => t('chat_action_status_cancelled');
export const chat_action_status_expired = () => t('chat_action_status_expired');
export const chat_action_status_stale = () => t('chat_action_status_stale');
export const chat_action_show_all_in_list = (p) => t('chat_action_show_all_in_list', p);
export const chat_action_target_priority = (p) => t('chat_action_target_priority', p);
export const chat_action_target_status = (p) => t('chat_action_target_status', p);
export const chat_action_target_due = (p) => t('chat_action_target_due', p);
export const chat_action_target_due_clear = () => t('chat_action_target_due_clear');
export const chat_action_target_shift = (p) => t('chat_action_target_shift', p);
export const chat_action_expand_required = () => t('chat_action_expand_required');
export const chat_action_applied = (p) => t('chat_action_applied', p);
export const chat_action_denied_line = (p) => t('chat_action_denied_line', p);
export const chat_action_skipped_line = (p) => t('chat_action_skipped_line', p);
export const chat_action_cancelled = () => t('chat_action_cancelled');
export const chat_action_expired = () => t('chat_action_expired');
export const chat_action_expired_reason = () => t('chat_action_expired_reason');
export const chat_action_stale = () => t('chat_action_stale');
export const chat_action_comment_text = () => t('chat_action_comment_text');
export const chat_filter_chip = () => t('chat_filter_chip');
/*
 * Круг 37 кадра 4 (UX-3, дословно): «У одного выделения два объявленных
 * владельца в одном кадре: плашка полотна честно говорит „выделено 2 из 3 для
 * предложения чата“, а чип композера в 40 px ниже — „Задачи · ТОО Тест · 2
 * выделено“, то есть область следующего вопроса… Направление: при
 * `subsetDraftId !== null` чип композера называет заимствование».
 */
export const chat_context_borrowed = (p) => t('chat_context_borrowed', p);
/*
 * Круг 37 кадра 4 (UX-4, дословно): «Чип полотна „Срез по предложению ✕
 * Сбросить“ не называет объект глагола: в режиме правки состава „Сбросить“
 * читается как возврат состава, а снимает срез». Глагол получил объект.
 */
export const chat_filter_chip_reset = () => t('chat_filter_chip_reset');
/**
 * R1 — чип отбора по ЗАФИКСИРОВАННОМУ составу: «Состав из чата: 3 задачи».
 * Пустого фильтра здесь быть не может, поэтому чип называет число, а не «применён».
 */
/*
 * Круг 35 кадра 4 (UI-3, дословно): «„Срез по предложению: 3 задачи“ после
 * ручного исключения зовёт число, которого нет ни в карточке, ни в подсказке
 * полотна. Направление: чип без счёта либо „2 из 3“».
 * «2 из 3» чип назвать не может: он описывает СРЕЗ полотна, а в срезе по-прежнему
 * три строки — состав сузился, а фильтр нет. Поэтому число уходит из чипа: его
 * место — карточка и плашка выделения, где оно означает ровно одно.
 */
export const chat_filter_chip_ids = (_p) => t('chat_filter_chip_ids_prefix');
/**
 * F20 — остаток срока годности предложения. Форма «действительно ещё 42 мин»
 * отвечает на вопрос человека («сколько у меня времени»), а не заставляет его
 * вычитать из часов, как делало «действительно до 11:31».
 */
/**
 * Короткая форма остатка — для строки решения, где рядом стоит сам предмет
 * («Приоритет → Низкий · ещё 55 мин»). Длинный префикс «действительно ещё» там
 * переносил строку на две, и липкая группа росла ни за чем.
 */
/**
 * Совсем компактная форма — только число и единица. Стоит в строке решения рядом
 * со значком часов, который и несёт смысл «осталось»; полная фраза — в подсказке
 * и в имени для скринридера. Круг 6 кадра 2 гейта: строка решения обязана
 * помещаться в один ряд, иначе липкая группа растёт ради служебного текста.
 */
export const chat_context_filtered = () => t('chat_context_filtered');
export const chat_context_selected = (p) => `${p.n} ${pluralForm(p.n, {
    one: t('chat_context_selected_one'),
    few: t('chat_context_selected_few'),
    many: t('chat_context_selected_many'),
})}`;
/*
 * Круг 19 кадра 1 (UI-2): обратимость свёрнута в тег строки провенанса; полная
 * фраза осталась в подсказке.
 *
 * Круг 40 кадра 4 (UX-2, дословно): «Обещание обратимости без времени.
 * „Обратимо (Ctrl+Z)“ стоит над „Применить“, а `installTaskUndoHotkey` живой
 * СЕЙЧАС и адресует стек правок задач, не карточку. Исключивший №6 руками
 * прочтёт „Ctrl+Z вернёт мою правку“ — не вернёт… Направление: в правке —
 * „После применения — отмена (Ctrl+Z)“».
 * Время названо в самой подписи, а не в предикате: тег и без правки состава
 * говорит ровно о том же — о том, что будет ПОСЛЕ применения.
 */
export const chat_action_undoable_tag = () => t('chat_action_undoable_tag');
/* Дрейф выделения — см. `selectionDrifted` в ActionCard (круг 1 кадра 5 гейта F26). */
export const chat_action_selection_drifted = (p) => t('chat_action_selection_drifted', p);
/* Круг 1 кадра 3 (UI-4): цена решения разведена на величину (янтарь) и придаточное
   о последствии (основной грейд) — три тёплых пятна подряд в подвале спорили с CTA. */
/* Круг 15 кадра 3 (UI-1): абсолют срока стоит в самой строке, а не только в
   подсказке — «складывающий 15:48 + 3 получает 15:51», и мимо курсора его не было. */
export const chat_action_deadline_left = (p) => t('chat_action_deadline_left', p);
export const chat_action_deadline_clause = () => t('chat_action_deadline_clause');
/* Круг 14 кадра 3 (UI-1): у терминальной карточки риск — факт черновика, а не
   активная тревога: глиф и формулировка меняются вместе с состоянием. */
/* Круг 14 кадра 3 (UI-2): у карточки после нуля названо и время смерти. */
export const chat_action_expired_at = (p) => t('chat_action_expired_at', p);
/* Круг 4 кадра 3 (UI-4): «предлог „в“ управляет временем, не датой» — у архивной
   карточки снимок называется без предлога, потому что там стоит дата со временем. */
export const chat_action_snapshot_dated = () => t('chat_action_snapshot_dated');
/* Круг 3 кадра 3 (UX-1): в терминальной карточке причина не зовёт в обход
   собственной кнопки — действие говорит «Пересобрать».
   Круг 5 (UX-3): «Факт истечения сказан трижды… фраза-причина перестала нести
   новое. Заменить на следствие („Пересборка возьмёт текущие данные“)». */
export const chat_action_expired_reason_short = () => t('chat_action_expired_reason_short');
/* Круг 1 кадра 3 (UX-1): истёкшая карточка называет исход ДАННЫХ, а не только
   исход таймера. */
export const chat_action_expired_outcome = () => t('chat_action_expired_outcome');
/*
 * Круг 15 кадра 4 (UI-1): §1.3 требует цвет + значок + ТЕКСТ — короткое слово
 * у знака исключения, полная фраза остаётся в имени для скринридера.
 *
 * Круг 38 кадра 4 (UX-3, дословно): «„исключена · уже так“ — причина без
 * авторства: решение человека подано системным основанием. Направление:
 * „исключена вами · уже так“». Рядом с этой записью стоит второй ключ — «уже
 * так», — и он как раз системный; без указания автора два основания читались
 * одним.
 */
export const chat_action_sample_dropped_tag = () => t('chat_action_sample_dropped_tag');
/* Круг 15 кадра 4 (UI-2): в режиме подмножества шапка считает по живому составу. */
/*
 * Круг 36 кадра 4 (UX-1, 🔴 BLOCKER): пустая правка состава — своё состояние, а
 * не отсутствие правки. Причина стоит у выключенной кнопки и называет оба
 * выхода: отметить на полотне либо вернуть состав агента.
 */
export const chat_action_subset_empty = () => t('chat_action_subset_empty');
/*
 * Круг 36 кадра 4 (UX-3): в режиме правки орган уже не «изменить состав» — он
 * возвращает срез на полотно. Подпись идёт по тому же предикату, что и план
 * действия (`editCompositionPlan`).
 */
export const chat_action_show_on_canvas = () => t('chat_action_show_on_canvas');
export const chat_action_composition_header_subset = (p) => t('chat_action_composition_header_subset', p);
/* Круг 20 кадра 1 (UX-1): при дрейфе отбор объявляется ОДНИМ чипом — снимком с
   его мощностью; живое выделение к этому составу отношения уже не имеет. */
export const chat_action_scope_snapshot_n = (p) => t('chat_action_scope_snapshot_n', p);
/* Круг 18 кадра 1 (UX-2): оговорка называет МНОЖЕСТВО, а не мощность — иначе на
   карточке стоят две «2» про разные наборы. */
export const chat_action_selection_drifted_outside = (p) => t('chat_action_selection_drifted_outside', p);
/* Кадр 4 гейта захода С2 (UX-3): та же величина расхождения без тревоги — правку
   состава человек заказал сам. */
/* Круг 16 кадра 4 (UI-3): «Один и тот же счёт сказан трижды в 400 px… Счёт „X из
   Y“ оставить шапке, сводке — только „уточнён вручную“ и органы». */
export const chat_action_selection_tuned = () => t('chat_action_selection_tuned');
/* Кадр 4 (UX-2): строки, выделенные вне предложения, действие не тронет. */
export const chat_action_selection_outside = (p) => t('chat_action_selection_outside', p);
/* Кадр 4, круг 2 (UX-2): выход из ручной правки состава — назад к предложению агента. */
/*
 * Круг 33 кадра 4 (UX-3, дословно): «Прогноз „изменится N“ принадлежит кнопке,
 * которая пишет; ссылка только пересобирает состав, и число она пересчитает сама
 * после клика». Ссылка называет РАЗМЕР состава агента — и слово при числе, чтобы
 * это число не читалось прогнозом изменений, как читались голые скобки.
 */
/*
 * Круг 38 кадра 4 (UX-2): у возврата к составу агента есть свой возврат. Правка
 * состава — единственное, что человек на этом кадре производит, и терять её от
 * одного клика по соседней ссылке она не должна.
 */
export const chat_action_restore_edit = () => t('chat_action_restore_edit');
/*
 * Круг 41 кадра 4 (UX-1, дословно): «Отмена правки объявляет себя невыполненным
 * приказом. Ключ `chat_action_reset_composition` („Вернуть состав агента — 3
 * задачи“) стоит и кнопкой, и статикой блока ПОСЛЕ отката под `role="status"`…
 * человек получает императив мёртвым текстом… скринридер зачитает приказ как
 * исход. Направление: свой ключ („Состав агента возвращён — 3 задачи“)».
 */
export const chat_action_edit_undone = (p) => pluralForm(p.n, {
    one: t('chat_action_edit_undone_one').replace('{n}', String(p.n)),
    few: t('chat_action_edit_undone_few').replace('{n}', String(p.n)),
    many: t('chat_action_edit_undone_many').replace('{n}', String(p.n)),
});
export const chat_action_reset_composition = (p) => pluralForm(p.n, {
    one: t('chat_action_reset_composition_one').replace('{n}', String(p.n)),
    few: t('chat_action_reset_composition_few').replace('{n}', String(p.n)),
    many: t('chat_action_reset_composition_many').replace('{n}', String(p.n)),
});
/* Кадр 4, круг 3 (UX-1/UX-2): строка состава, которую действие не изменит, и
   строка, которой оно СНИЗИТ приоритет, помечены ключом каждая. */
export const chat_action_sample_noop = () => t('chat_action_sample_noop');
/** В-20 (F8): подпись призрака предложенного значения в ячейке таблицы — для скринридера. */
export const chat_action_proposed_value_sr = () => t('chat_action_proposed_value_sr');
/** Круг 1 кадра К1 (F8): указатель на содержимое тела карточки ниже кромки. */
export const chat_action_body_more_below = () => t('chat_action_body_more_below');
export const chat_action_sample_downgrade = () => t('chat_action_sample_downgrade');
/* Круг 16 кадра 4 (UI-3): в режиме правки состава запись говорит, входит она в
   применение или исключена. */
export const chat_action_sample_picked = () => t('chat_action_sample_picked');
export const chat_action_sample_dropped = () => t('chat_action_sample_dropped');
/* Круг 12 кадра 1 (UX-3): расхождение «Состав (N)» и «Применить (M)» называется
   прямо, а не выводится читателем. */
/*
 * Круг 32 кадра 4 (UX-1): записи, которые есть в составе, но при текущей высоте
 * панели лежат под липким подвалом. Считаются раскладкой, а не константой.
 */
/*
 * Круг 34 кадра 4 (UX-1): рез есть, а целиком скрытых записей нет — числу тут
 * взяться неоткуда, и кнопка зовёт без него.
 */
/* Имя области состава: ящик берёт фокус, и без имени скринридер объявит его
   безымянной группой (круг 20 кадра 3, UI-2). */
export const chat_action_composition_region = () => t('chat_action_composition_region');
export const chat_action_composition_show_all = () => t('chat_action_composition_show_all');
export const chat_action_composition_more_below = (p) => t('chat_action_composition_more_below', p);
/*
 * Круг 30 кадра 1 (UX-2 светлой, дословно): «Сумма не сходится: „Состав (3)“,
 * „изменится 2“, а в сводке две строки на две задачи — „1 без изменений“ и „1
 * понижение“. Строки на повышение, то есть на главный смысл предложения, нет.
 * Пользователь не может сверить „2“ ни с чем. Направление: перечислять все
 * исходы, сумма строк = составу».
 * Слово нейтральное: операция бывает не только про приоритет, и «повышение» для
 * смены состояния или архивации было бы неправдой.
 */
/*
 * Круг 31 кадра 1 (UX-1 светлой, дословно): «Пять разножанровых фактов одним
 * плоским списком — провенанс, SLA, три строки исхода и подсказка про Ctrl+Z
 * нарисованы одинаковой серой строкой с иконкой слева. Решающее содержание
 * неотличимо от метаданных и стоит ниже них… Направление: выделить три строки
 * исхода в отдельную группу с тонким заголовком („Что произойдёт“) и
 * разделителем, поставить её сразу над кнопками».
 */
export const chat_action_outcome_heading = () => t('chat_action_outcome_heading');
/*
 * Круг 33 кадра 1 (UX-2 светлой, дословно): «Счёт „Применить (2)“ против трёх
 * строк „Что произойдёт“ — строкой долга, перестановку не делаю. Ваш довод
 * сильнее моего: риск обязан стоять последним, ближе к кнопке. Дешевле примирить
 * счёт подписью в шапке блока — „Что произойдёт · применится к 2 из 3“, порядок
 * строк не трогая». Взято дословно: порядок исходов остаётся, счёт сходится в
 * заголовке.
 */
export const chat_action_outcome_heading_count = (p) => t('chat_action_outcome_heading_count', p);
export const chat_action_plain_applied = (p) => pluralForm(p.n, {
    one: t('chat_action_plain_applied_one').replace('{n}', String(p.n)),
    few: t('chat_action_plain_applied_few').replace('{n}', String(p.n)),
    many: t('chat_action_plain_applied_many').replace('{n}', String(p.n)),
});
export const chat_action_noop_skipped = (p) => pluralForm(p.n, {
    one: t('chat_action_noop_skipped_one').replace('{n}', String(p.n)),
    few: t('chat_action_noop_skipped_few').replace('{n}', String(p.n)),
    many: t('chat_action_noop_skipped_many').replace('{n}', String(p.n)),
});
/* Круг 4 кадра 4 (UX-4): «Настаиваю, чтобы понижение было названо словом в самой
   кнопке… Подпись, не подтверждение». */
export const chat_action_downgrades = (p) => pluralForm(p.n, {
    one: t('chat_action_downgrades_one'),
    few: t('chat_action_downgrades_few'),
    many: t('chat_action_downgrades_many'),
});
/* Когда наборы разошлись, основное действие называет, к ЧЕМУ применится: число в
   «Применить (3)» совпадало с «Выделено 3» в плашке и читалось подтверждением. */
export const chat_action_apply_composition = (p) => t('chat_action_apply_composition', p);
/*
 * Круг 43 кадра 4 (UX-2, дословно): «спор был не про синоним, а про то, что чип
 * называет снимком СОСТАВ („снимок выделения · 3“), подвал — МОМЕНТ ВРЕМЕНИ
 * („снимок в 20:45“). Слово остаётся за составом, время называется временем:
 * „Предложение сформировано в 20:45 · e2e-admin@staiter.test · истечёт через 1
 * ч“. Второго слова для той же вещи не заводит».
 * Так снимается и коллизия, и надобность в отдельном подлежащем: предложение
 * названо самой формулировкой.
 */
export const chat_action_snapshot_short = () => t('chat_action_snapshot_short');
/**
 * R11 (кадр 5 гейта F26): «чип „Отбор“ в состоянии дрейфа». Пока выделение в
 * таблице живое, а состав предложения заморожен, строка отбора обязана сама
 * говорить, что она о СНИМКЕ, — иначе «выделенные задачи» читается как «те,
 * что выделены сейчас».
 */
export const chat_action_scope_snapshot = () => t('chat_action_scope_snapshot');
/**
 * CIA-F22 и CIA-F6 (кадр 2 круг 3 и кадр 1 UX-4 гейта F26): карточка не
 * называет действующее лицо и факт записи в журнал; исполненная — не называет
 * автора решения. Черновики видит только их владелец (`chat.ts` фильтрует по
 * `userId` беседы), поэтому лицо здесь всегда одно и известно фронту.
 */
export const chat_action_actor_live = () => t('chat_action_actor_live');
export const chat_action_actor_journal = () => t('chat_action_actor_journal');
export const chat_action_actor_done = () => t('chat_action_actor_done');
/**
 * CIA-F17 — подпись аргумента шага ленты. Ключи технические (`project_ids`,
 * `state_groups`), и словарь покрывает те, что реально приходят от tool-схем;
 * незнакомый ключ показывается как есть — это латиница, и она честнее
 * выдуманного перевода.
 */
/**
 * Круг 4 кадра 2 гейта захода С2 (UI-2): значение-перечисление в провенансе шага
 * называется подписью, а не считается. Незнакомое значение остаётся как есть —
 * лучше латиница, чем счётчик вместо смысла.
 */
export const chat_tool_arg_value = (value) => {
    const label = t(`chat_tool_argv_${value}`);
    return label.startsWith('[missing:') ? value : label;
};
export const chat_tool_arg_label = (key) => {
    const value = t(`chat_tool_arg_${key}`);
    // Незнакомый ключ рантайм отдаёт маркером `[missing: …]` — вместо него имя как есть.
    return value.startsWith('[missing:') ? key : value;
};
/**
 * CIA-F18 — исход хода. Провал, который агент обошёл и всё-таки ответил, и
 * провал, оборвавший ход без ответа, — разные события; лента обязана называть
 * их разными словами, а не одним янтарным треугольником.
 */
export const chat_turn_aborted = () => t('chat_turn_aborted');
/**
 * CIA-F25 (пак `tails-closeout`, F6) — подпись провала хода по его исходу
 * (`chat.final.outcome`, B11). Неизвестный исход — прежняя общая подпись.
 */
export const chat_turn_outcome = (outcome) => {
    if (!outcome)
        return t('chat_turn_aborted');
    const text = t(`chat_turn_outcome_${outcome}`);
    return text.startsWith('[missing:') ? t('chat_turn_aborted') : text;
};
export const chat_turn_retry = () => t('chat_turn_retry');
/** LH-B9 (пак `llm-haiku-5-5`, гейт Л-2) — пометка ответа, оборванного на пределе длины. */
export const chat_answer_incomplete = () => t('chat_answer_incomplete');
/** WTU-F19 (пак `tails-closeout`, F10) — выход «сменить компанию» с экрана модульного отказа. */
export const module_forbidden_switch_company = () => t('module_forbidden_switch_company');
export const module_forbidden_desc_company = (p) => t('module_forbidden_desc_company', p);
export const module_forbidden_chat_hint = () => t('module_forbidden_chat_hint');
/** Кадр К2 (пак `tails-closeout`, F9) — шаг не запущен из-за лимита обращений. */
export const chat_step_not_run = (p) => t('chat_step_not_run', p);
export const chat_turn_edit = () => t('chat_turn_edit');
/**
 * CIA-F28 (пак `tails-closeout`, F7, круг 2 гейта) — подпись происхождения ответа
 * по его основанию: какие шаги хода дали данные. Кадр F7, круг 1, UX-3: «Из текста
 * „AI-сгенерировано“ правило не выводится» — плашка называет основание.
 */
export const chat_tool_source = (tool) => {
    const text = t(`chat_tool_${tool}_source`);
    return text.startsWith('[missing:') ? t('chat_tool_source_fallback') : text;
};
export const chat_provenance_data = (p) => t('chat_provenance_data', p);
export const chat_provenance_data_partial = (p) => t('chat_provenance_data_partial', p);
export const chat_provenance_none = () => t('chat_provenance_none');
export const chat_turn_trace_label = () => t('chat_turn_trace_label');
export const chat_turn_trace_copy = () => t('chat_turn_trace_copy');
/** CIA-F28 / К2-23 (пак `tails-closeout`, F7) — строки `AIInsightCard`, прежде захардкоженные по-русски. */
export const component_ai_insight_disclaimer = () => t('component_ai_insight_disclaimer');
export const component_ai_insight_title_default = () => t('component_ai_insight_title_default');
export const component_ai_insight_sources = () => t('component_ai_insight_sources');
export const component_ai_insight_confidence = (p) => t('component_ai_insight_confidence', p);
/** CIA-F19 (пак `tails-closeout`, F6) — причина отказа шага по коду из `chat.tool_result`. */
export const chat_step_reason = (code) => {
    const text = t(`chat_step_reason_${code}`);
    return text.startsWith('[missing:') ? t('chat_step_reason_unknown') : text;
};
/**
 * G8 (кадр К2 пака панели): «счётчик бесед без единицы — на пустом „Новом чате“
 * он читается как счёт чего-то ВНУТРИ этой беседы». Единица живёт в имени для
 * скринридера и в подсказке; на кадре её место занимает само число.
 */
export const chat_conversations_count = (p) => `${p.n} ${t('chat_conversations_count')}`;
/** CIA-F26 (пак `tails-closeout`, F4) — отказ загрузки списка бесед в панели чата. */
export const chat_conversations_load_failed = () => t('chat_conversations_load_failed');
export const chat_tool_step_bypassed = () => t('chat_tool_step_bypassed');
/* Кадр 2 гейта захода С2 (UX-1): подпись исхода шага — «найдено: N». */
export const chat_tool_step_found = () => t('chat_tool_step_found');
/*
 * F28 — последняя минута называется словами, а не «1 мин»: округлять остаток
 * вверх нельзя нигде, а перед самым истечением — тем более.
 *
 * Круг 35 кадра 4 (UX-2, дословно): «Срок решения назван без подлежащего. „ещё
 * 57 мин“ — это TTL предложения… а фраза „предложение истечёт через…“ живёт
 * только в `aria-label`/подсказке. Видимый сегмент стоит четвёртым после
 * „Обратимо (Ctrl+Z) · автор · снимок в 18:10“ и по ближайшему антецеденту
 * читается как окно отката… Направление: видимая форма самоназывающаяся
 * („истечёт через 57 мин“)».
 * Глагол несёт сама форма — ни значка, ни второй строки на это не тратится.
 */
export const chat_action_time_left_compact_under_minute = () => t('chat_action_time_left_compact_under_minute');
/* Та же величина без глагола: сказуемое стоит в голове фразы порога (круг 18
   кадра 3, UI-3). Отдельный ключ, чтобы компактная форма провенанса глагола не
   лишилась. */
export const chat_action_time_left_value_under_minute = () => t('chat_action_time_left_value_under_minute');
/**
 * G10 (круг 10 кадра К3 пака `chat-panel-geometry`, UX-1, дословно): «Таймер
 * называет остаток, но не исход — „ещё 4 мин“ торопит, не сказав, что случится
 * в 0 (истечёт предложение? состав пересоберётся?)… карточку нельзя отдавать
 * пользователям, пока строка не называет последствие».
 *
 * Последствие названо тем же словом, которым карточка встречает ноль:
 * «истечёт через 4 мин» → «Срок подтверждения истёк». Отдельной строки под
 * кнопками не появилось: на 360 px её негде взять.
 */
export const chat_action_time_left_short_under_minute = () => t('chat_action_time_left_short_under_minute_full');
/*
 * Круг 26 кадра 1 (UX-1, дословно): «Компактный остаток жизни двусмыслен в ряду
 * провенанса — „снимок в 14:25 · 59 мин“ соседствует с меткой времени, и „59 мин“
 * одинаково читается как „осталось“ и как „назад“… Направление: слово „ещё“ в
 * компактной форме». Глагол вернулся, значок из строки ушёл — колонка знака в
 * подвале одна (круг 19 кадра 4).
 */
/*
 * Величина остатка без глагола — для фраз, где подлежащее уже названо.
 * Круг 18 кадра 3 (UI-3, дословно): «„Срок подтверждения — истечёт…“: тире стоит
 * между подлежащим и сказуемым… только лексика: „Срок подтверждения истечёт
 * меньше чем через минуту, до 22:59“». Сказуемое переехало в голову фразы, и
 * величина стала обстоятельством времени: «через 2 мин», «через 1 ч»,
 * «меньше чем через минуту».
 */
export const chat_action_time_left_value_minutes = (p) => `${t('chat_action_time_left_in')} ${p.n} ${pluralForm(p.n, {
    one: t('chat_action_time_left_minutes_one'),
    few: t('chat_action_time_left_minutes_few'),
    many: t('chat_action_time_left_minutes_many'),
})}`;
export const chat_action_time_left_value_hours = (p) => `${t('chat_action_time_left_in')} ${p.n} ${pluralForm(p.n, {
    one: t('chat_action_time_left_hours_one'),
    few: t('chat_action_time_left_hours_few'),
    many: t('chat_action_time_left_hours_many'),
})}`;
export const chat_action_time_left_compact_minutes = (p) => `${t('chat_action_time_left_compact_prefix')} ${p.n} ${pluralForm(p.n, {
    one: t('chat_action_time_left_minutes_one'),
    few: t('chat_action_time_left_minutes_few'),
    many: t('chat_action_time_left_minutes_many'),
})}`;
export const chat_action_time_left_compact_hours = (p) => `${t('chat_action_time_left_compact_prefix')} ${p.n} ${pluralForm(p.n, {
    one: t('chat_action_time_left_hours_one'),
    few: t('chat_action_time_left_hours_few'),
    many: t('chat_action_time_left_hours_many'),
})}`;
export const chat_action_time_left_short_minutes = (p) => `${t('chat_action_time_left_short_prefix')} ${p.n} ${pluralForm(p.n, {
    one: t('chat_action_time_left_minutes_one'),
    few: t('chat_action_time_left_minutes_few'),
    many: t('chat_action_time_left_minutes_many'),
})}`;
export const chat_action_time_left_short_hours = (p) => `${t('chat_action_time_left_short_prefix')} ${p.n} ${pluralForm(p.n, {
    one: t('chat_action_time_left_hours_one'),
    few: t('chat_action_time_left_hours_few'),
    many: t('chat_action_time_left_hours_many'),
})}`;
/** F21 — пересобрать истёкшее предложение (серверная ручка, не сообщение в чат). */
export const chat_action_rebuild = () => t('chat_action_rebuild');
export const chat_action_rebuild_permissions_changed = (p) => t('chat_action_rebuild_permissions_changed', p);
/**
 * F22 — честность состава по строке: кому задача, применится ли к ней операция
 * и что всё это — снимок на момент предложения, а не факт на сейчас.
 */
export const chat_action_sample_no_permission = () => t('chat_action_sample_no_permission');
export const chat_action_snapshot_at_proposal = () => t('chat_action_snapshot_at_proposal');
export const chat_action_assignees_more = (p) => t('chat_action_assignees_more', p);
/**
 * F23 — строка контекста композера на поверхности задач.
 *
 * Заменяет список протокольных токенов (`@run:` и прочие бесполезны там, где в
 * пути нет ни запуска, ни периода) и отвечает на вопрос «что агент про меня
 * сейчас знает»: сколько выделено и сужен ли список.
 */
/* Приглашение в поле композера на поверхности задач. Числа контекста несёт чип
   под полем, а не плейсхолдер (круг 1 кадра 5 гейта F26, UI-5). */
export const chat_composer_task_context_none = () => t('chat_composer_task_context_none');
export const chat_composer_task_context = (p) => {
    const parts = [t('chat_composer_task_context_none')];
    if (p.selected > 0) {
        parts.push(t('chat_composer_task_context_selected', { selected: p.selected }));
    }
    if (p.filtered > 0)
        parts.push(t('chat_composer_task_context_filtered'));
    return parts.join(' · ');
};
export const chat_mentions_label = () => t('chat_mentions_label');
/**
 * Круг 1 гейта F26 (UX-2) — «шапка решения не со-видима с кнопками».
 *
 * Карточка выросла, и предмет решения уезжал за кромку панели: человек видел
 * кнопку «Применить», не видя, к скольким задачам она применится. Число теперь
 * стоит В САМОЙ кнопке — оно доедет до глаза вместе с ней при любой высоте.
 */
export const chat_action_apply_count = (p) => t('chat_action_apply_count', p);
/**
 * F19 — третье действие ряда решения и его следствия.
 *
 * «Применить к выделенным (k из N)» называет ОБА числа: без знаменателя человек
 * не видит, от чего он отказался, а именно это и есть содержание решения.
 */
export const chat_action_edit_composition = () => t('chat_action_edit_composition');
export const chat_action_apply_selected = (p) => t('chat_action_apply_selected', p);
export const chat_action_applied_manual = (p) => t('chat_action_applied_manual', p);
export const chat_action_composition_hint = () => t('chat_action_composition_hint');
export const tm_bulk_bar_composition_mode = (p) => t('tm_bulk_bar_composition_mode', p);
/**
 * R18 (кадр 5 гейта F26) — «ссылка из плашки массовых действий в карточку».
 * Человек правит состав в таблице, а решение принимается в панели: обратной
 * дороги из плашки не было вовсе.
 */
export const tm_bulk_bar_to_proposal = () => t('tm_bulk_bar_to_proposal');
/** Предупреждение о выделении вне состава: русская плюрализация тремя формами. */
export const chat_action_selection_outside_warning = (p) => `${t('chat_action_selection_outside_warning_prefix')}: ${p.count} ${pluralForm(p.count, {
    one: t('chat_action_selection_outside_one'),
    few: t('chat_action_selection_outside_few'),
    many: t('chat_action_selection_outside_many'),
})}`;
/**
 * F18 [Х18] — состав длиннее страницы: чип называет ОБА числа.
 *
 * Показать сотню строк из ста двадцати и оставить в чипе «120 задач» значило бы
 * соврать ровно там, где человек проверяет набор перед необратимым действием.
 */
export const tm_page_composition_truncated = (p) => t('tm_page_composition_truncated', p);
export const tm_bulk_comment = () => t('tm_bulk_comment');
export const tm_bulk_comment_placeholder = () => t('tm_bulk_comment_placeholder');
export const tm_bulk_comment_send = () => t('tm_bulk_comment_send');
export const tm_bulk_comment_done = (p) => t('tm_bulk_comment_done', p);
/** Лейбл операции пачки по её коду (`TaskBulkOpType` + `comment`). */
export const chat_action_op_label = (op) => {
    const localised = t(`chat_action_op_${op}`);
    return localised.startsWith('[missing:') ? op : localised;
};
/** Текст предупреждения превью по коду (`ChatActionWarningCode`). */
export const chat_action_warning = (code) => {
    const localised = t(`chat_action_warn_${code}`);
    return localised.startsWith('[missing:') ? code : localised;
};
/** Причина, по которой задача выпала из пачки (`TaskBulkSkipReason`). */
export const tm_bulk_skip_reason = (reason) => {
    const localised = t(`tm_bulk_skip_${reason}`);
    return localised.startsWith('[missing:') ? reason : localised;
};
export const chat_active_default = () => t('chat_active_default');
export const chat_input_shortcut_hint = () => t('chat_input_shortcut_hint');
export const chat_input_placeholder_short = () => t('chat_input_placeholder_short');
/**
 * F29 — счётчик одинаковых подряд идущих шагов ленты: «Читаю справочники задач ·
 * 4 раза».
 *
 * Круг 1 кадра гейта (UI-2, дословно): «„× 8“ в конце строки конфликтует с ×
 * закрытия панели в шапке того же дровера, пробел в `"× {n}"` дробит идиому.
 * Либо `×8` без пробела рядом с подписью, либо словесная форма („8 раз“)».
 * Выбрана словесная: глиф «×» в панели, у которой в шапке стоит крест закрытия,
 * читается как «убрать», и никакой отступ этого не лечит.
 */
export const chat_tool_step_repeat_word = (p) => pluralForm(p.n, {
    one: t('chat_tool_step_repeat_one'),
    few: t('chat_tool_step_repeat_few'),
    many: t('chat_tool_step_repeat_many'),
});
/**
 * Lookup label для tool name (read_transactional_records, query_stock_balance, etc.).
 * Если ключ chat_tool_$X не найден — fallback к raw tool name.
 */
export const chat_tool_label = (tool) => {
    const key = `chat_tool_${tool}`;
    const localised = t(key);
    return localised.startsWith('[missing:') ? tool : localised;
};
/**
 * F29, круг 4 кадра гейта (UX-1, дословно): «Завершённый шаг говорит в настоящем
 * времени — „Читаю справочники задач“, „Ищу задачи 5 раз“ стоят рядом с `✓`
 * после того, как ответ уже пришёл. Весь вес состояния несёт крошечный глиф;
 * текст ему противоречит».
 *
 * Отсюда парная подпись: настоящее время остаётся идущему шагу со спиннером,
 * законченный получает `chat_tool_<tool>_done`. Fallback — на подпись идущего
 * шага: новый инструмент без парного ключа не превратится в машинное имя.
 */
export const chat_tool_label_done = (tool) => {
    const done = t(`chat_tool_${tool}_done`);
    if (!done.startsWith('[missing:'))
        return done;
    return chat_tool_label(tool);
};
/**
 * F29 — тот же шаг, но не удавшийся. Отдельная подпись, а не «получилось» плюс
 * приписка: пара «Поиск задач выполнен» + «не удалось» в одной строке спорит
 * сама с собой. Fallback тот же — на подпись идущего шага.
 */
export const chat_tool_label_failed = (tool) => {
    const failed = t(`chat_tool_${tool}_failed`);
    if (!failed.startsWith('[missing:'))
        return failed;
    return chat_tool_label(tool);
};
/**
 * Lookup label для route segment (work-orders, items, etc.).
 * Slash → underscore для JSON-key. Fallback к raw segment.
 */
export const chat_route_label = (routeName) => {
    const key = `chat_route_${routeName.replace(/-/g, '_')}`;
    const localised = t(key);
    return localised.startsWith('[missing:') ? routeName : localised;
};
export const master_drift_empty = () => t('master_drift_empty');
export const master_drift_table_aria = () => t('master_drift_table_aria');
export const master_drift_col_field = () => t('master_drift_col_field');
export const master_drift_col_ours = () => t('master_drift_col_ours');
export const master_drift_col_theirs = () => t('master_drift_col_theirs');
export const master_drift_col_pick = () => t('master_drift_col_pick');
export const master_drift_pick_aria = (p) => t('master_drift_pick_aria', p);
export const master_drift_pick_ours = () => t('master_drift_pick_ours');
export const master_drift_pick_theirs = () => t('master_drift_pick_theirs');
export const master_drift_pick_merge = () => t('master_drift_pick_merge');
export const master_drift_merge_aria = (p) => t('master_drift_merge_aria', p);
export const master_drift_push_label = () => t('master_drift_push_label');
export const master_drift_note_label = () => t('master_drift_note_label');
export const master_drift_note_placeholder = () => t('master_drift_note_placeholder');
export const master_drift_apply = () => t('master_drift_apply');
export const master_drift_saving = () => t('master_drift_saving');
export const override_toast_decision_failed = () => t('override_toast_decision_failed');
export const override_toast_bulk_applied = () => t('override_toast_bulk_applied');
export const override_toast_bulk_failed = () => t('override_toast_bulk_failed');
export const override_toast_finalized = () => t('override_toast_finalized');
export const override_toast_finalize_failed = () => t('override_toast_finalize_failed');
export const override_summary_total = () => t('override_summary_total');
export const override_summary_locked = () => t('override_summary_locked');
export const override_locked_banner = (p) => t('override_locked_banner', p);
export const override_bulk_selected = (p) => t('override_bulk_selected', p);
export const override_bulk_accept_all = () => t('override_bulk_accept_all');
export const override_bulk_defer_all = () => t('override_bulk_defer_all');
export const override_bulk_reject_all = () => t('override_bulk_reject_all');
export const override_bulk_clear = () => t('override_bulk_clear');
export const override_table_aria = () => t('override_table_aria');
export const override_col_select_all = () => t('override_col_select_all');
export const override_col_sheet = () => t('override_col_sheet');
export const override_col_cell = () => t('override_col_cell');
export const override_col_field = () => t('override_col_field');
export const override_col_before = () => t('override_col_before');
export const override_col_after = () => t('override_col_after');
export const override_col_decision = () => t('override_col_decision');
export const override_col_actions = () => t('override_col_actions');
export const override_select_row_aria = (p) => t('override_select_row_aria', p);
export const override_locked_cell_aria = () => t('override_locked_cell_aria');
export const override_action_reject_aria = () => t('override_action_reject_aria');
export const override_finalize_finalized = () => t('override_finalize_finalized');
export const override_finalize_pending = (p) => t('override_finalize_pending', p);
export const override_finalize_ready = () => t('override_finalize_ready');
export const bulk_import_resource = (p) => t(`bulk_import_resource_${p.code}`);
export const bulk_import_resource_counterparties_disabled = () => t('bulk_import_resource_counterparties_disabled');
export const bulk_import_step_upload = () => t('bulk_import_step_upload');
export const bulk_import_step_preview = () => t('bulk_import_step_preview');
export const bulk_import_step_commit = () => t('bulk_import_step_commit');
export const bulk_import_dialog_title = () => t('bulk_import_dialog_title');
export const bulk_import_steps_aria = () => t('bulk_import_steps_aria');
export const bulk_import_analyzing = () => t('bulk_import_analyzing');
export const bulk_import_check = () => t('bulk_import_check');
export const bulk_import_starting = () => t('bulk_import_starting');
export const bulk_import_import_n = (p) => t('bulk_import_import_n', p);
export const bulk_import_import_empty = () => t('bulk_import_import_empty');
export const bulk_import_import_errors_tooltip = () => t('bulk_import_import_errors_tooltip');
export const bulk_import_order_hint = () => t('bulk_import_order_hint');
export const bulk_import_resource_label = () => t('bulk_import_resource_label');
export const bulk_import_template_download = () => t('bulk_import_template_download');
export const bulk_import_template_unavailable = () => t('bulk_import_template_unavailable');
export const bulk_import_bom_note = () => t('bulk_import_bom_note');
export const bulk_import_drop_prompt = () => t('bulk_import_drop_prompt');
export const bulk_import_drop_hint = () => t('bulk_import_drop_hint');
export const bulk_import_remove_file_aria = () => t('bulk_import_remove_file_aria');
export const bulk_import_file_unit_b = (p) => t('bulk_import_file_unit_b', p);
export const bulk_import_file_unit_kb = (p) => t('bulk_import_file_unit_kb', p);
export const bulk_import_file_unit_mb = (p) => t('bulk_import_file_unit_mb', p);
export const bulk_import_upload_error = (p) => t('bulk_import_upload_error', p);
export const bulk_import_unsupported_format = (p) => t('bulk_import_unsupported_format', p);
export const bulk_import_file_too_large = (p) => t('bulk_import_file_too_large', p);
export const bulk_import_summary_create = () => t('bulk_import_summary_create');
export const bulk_import_summary_update = () => t('bulk_import_summary_update');
export const bulk_import_summary_skip = () => t('bulk_import_summary_skip');
export const bulk_import_issues_heading = (p) => t('bulk_import_issues_heading', p);
export const bulk_import_issues_table_aria = () => t('bulk_import_issues_table_aria');
export const bulk_import_issues_col_row = () => t('bulk_import_issues_col_row');
export const bulk_import_issues_col_column = () => t('bulk_import_issues_col_column');
export const bulk_import_issues_col_severity = () => t('bulk_import_issues_col_severity');
export const bulk_import_issues_col_message = () => t('bulk_import_issues_col_message');
export const bulk_import_severity_error = () => t('bulk_import_severity_error');
export const bulk_import_severity_warning = () => t('bulk_import_severity_warning');
export const bulk_import_done_title = () => t('bulk_import_done_title');
export const bulk_import_done_sub = (p) => t('bulk_import_done_sub', p);
export const bulk_import_failed_title = () => t('bulk_import_failed_title');
export const bulk_import_unknown_error = () => t('bulk_import_unknown_error');
export const bulk_import_progress_with_total = (p) => t('bulk_import_progress_with_total', p);
export const bulk_import_progress_indef = () => t('bulk_import_progress_indef');
export const bulk_import_async_warning = () => t('bulk_import_async_warning');
export const bulk_import_close_confirm_title = () => t('bulk_import_close_confirm_title');
export const bulk_import_close_confirm_desc = () => t('bulk_import_close_confirm_desc');
export const bulk_import_close_confirm_button = () => t('bulk_import_close_confirm_button');
export const bulk_import_toast_done = () => t('bulk_import_toast_done');
export const bulk_import_toast_failed = () => t('bulk_import_toast_failed');
export const item_group_tree_all = () => t('item_group_tree_all');
export const item_group_tree_new_placeholder = () => t('item_group_tree_new_placeholder');
export const item_group_tree_new_aria = () => t('item_group_tree_new_aria');
export const item_group_tree_create_aria = () => t('item_group_tree_create_aria');
export const item_group_tree_delete_title = () => t('item_group_tree_delete_title');
export const item_group_tree_delete_desc = () => t('item_group_tree_delete_desc');
export const item_group_tree_delete_aria = (p) => t('item_group_tree_delete_aria', p);
export const valuation_dialog_title = () => t('valuation_dialog_title');
export const valuation_calculating = () => t('valuation_calculating');
export const valuation_preview_button = () => t('valuation_preview_button');
export const valuation_applying = () => t('valuation_applying');
export const valuation_apply_tier2 = () => t('valuation_apply_tier2');
export const valuation_current_prefix = () => t('valuation_current_prefix');
export const valuation_new_label = () => t('valuation_new_label');
export const valuation_summary_sle = () => t('valuation_summary_sle');
export const valuation_summary_delta = () => t('valuation_summary_delta');
export const valuation_summary_rows = () => t('valuation_summary_rows');
export const valuation_table_aria = () => t('valuation_table_aria');
export const valuation_col_batch = () => t('valuation_col_batch');
export const valuation_col_qty = () => t('valuation_col_qty');
export const valuation_col_current_rate = () => t('valuation_col_current_rate');
export const valuation_col_new_rate = () => t('valuation_col_new_rate');
export const valuation_col_delta = () => t('valuation_col_delta');
export const valuation_col_delta_pct = () => t('valuation_col_delta_pct');
export const valuation_approver_label = () => t('valuation_approver_label');
export const valuation_approver_placeholder = () => t('valuation_approver_placeholder');
export const valuation_reason_label = () => t('valuation_reason_label');
export const valuation_reason_placeholder = () => t('valuation_reason_placeholder');
export const valuation_backfill_prefix = () => t('valuation_backfill_prefix');
export const variants_empty_title = () => t('variants_empty_title');
export const variants_empty_desc = () => t('variants_empty_desc');
export const variants_save_count = (p) => t('variants_save_count', p);
export const variants_count = (p) => t('variants_count', p);
export const variants_matrix_aria = () => t('variants_matrix_aria');
export const variants_cell_aria = (p) => t('variants_cell_aria', p);
export const variants_bulk_csv_label = (p) => t('variants_bulk_csv_label', p);
export const variants_bulk_import = () => t('variants_bulk_import');
export const saved_views_default_aria = () => t('saved_views_default_aria');
export const saved_views_default_placeholder = () => t('saved_views_default_placeholder');
export const saved_views_rename_input_aria = (p) => t('saved_views_rename_input_aria', p);
export const saved_views_rename_aria = (p) => t('saved_views_rename_aria', p);
export const saved_views_unshare_aria = (p) => t('saved_views_unshare_aria', p);
export const saved_views_share_aria = (p) => t('saved_views_share_aria', p);
export const saved_views_delete_aria = (p) => t('saved_views_delete_aria', p);
export const saved_views_save_confirm_aria = () => t('saved_views_save_confirm_aria');
export const saved_views_new_placeholder = () => t('saved_views_new_placeholder');
export const saved_views_reset = () => t('saved_views_reset');
export const cancel_preview_comp_aria = () => t('cancel_preview_comp_aria');
export const cancel_preview_comp_empty = () => t('cancel_preview_comp_empty');
export const cancel_preview_col_item = () => t('cancel_preview_col_item');
export const cancel_preview_col_warehouse = () => t('cancel_preview_col_warehouse');
export const cancel_preview_col_batch = () => t('cancel_preview_col_batch');
export const cancel_preview_col_qty_delta = () => t('cancel_preview_col_qty_delta');
export const cancel_preview_col_rate = () => t('cancel_preview_col_rate');
export const cancel_preview_col_value_delta = () => t('cancel_preview_col_value_delta');
export const cancel_preview_recompute_title = (p) => t('cancel_preview_recompute_title', p);
export const cancel_preview_recompute_none = () => t('cancel_preview_recompute_none');
export const cancel_preview_recompute_aria = () => t('cancel_preview_recompute_aria');
export const cancel_preview_col_posting_at = () => t('cancel_preview_col_posting_at');
export const cancel_preview_col_prev_rate = () => t('cancel_preview_col_prev_rate');
export const cancel_preview_col_new_rate = () => t('cancel_preview_col_new_rate');
export const cancel_preview_collapse = (p) => t('cancel_preview_collapse', p);
export const cancel_preview_expand = (p) => t('cancel_preview_expand', p);
export const cancel_preview_affected_title = (p) => t('cancel_preview_affected_title', p);
export const cancel_preview_affected_none = () => t('cancel_preview_affected_none');
export const cancel_preview_col_doc_type = () => t('cancel_preview_col_doc_type');
export const cancel_preview_col_doc = () => t('cancel_preview_col_doc');
export const cancel_preview_col_reason = () => t('cancel_preview_col_reason');
export const cancel_preview_warnings_title = () => t('cancel_preview_warnings_title');
export const cancel_preview_blocking_suffix = () => t('cancel_preview_blocking_suffix');
export const cancel_preview_acknowledge = () => t('cancel_preview_acknowledge');
export const cancel_preview_recompute_required = () => t('cancel_preview_recompute_required');
export const cancel_preview_recompute_not_required = () => t('cancel_preview_recompute_not_required');
/** F1 — подпись предупреждения отмены по коду типа (см. правило №19). */
export const cancel_preview_warning_by_type = (type) => {
    const key = `cancel_preview_warning_${type}`;
    const result = t(key);
    if (typeof result === 'string' && result.startsWith('[missing:')) {
        return t('cancel_preview_warning_other');
    }
    return result;
};
export const entity_audit_title = () => t('entity_audit_title');
export const entity_audit_no_events = () => t('entity_audit_no_events');
export const entity_audit_aria_prefix = () => t('entity_audit_aria_prefix');
export const entity_audit_open_in_audit_log = () => t('entity_audit_open_in_audit_log');
export const audit_tool_work_order_create = () => t('audit_tool_work_order_create');
export const audit_tool_work_order_update = () => t('audit_tool_work_order_update');
export const audit_tool_work_order_reassign = () => t('audit_tool_work_order_reassign');
export const audit_tool_work_order_job_card_create = () => t('audit_tool_work_order_job_card_create');
export const audit_tool_work_order_job_card_update = () => t('audit_tool_work_order_job_card_update');
export const audit_tool_work_order_job_card_delete = () => t('audit_tool_work_order_job_card_delete');
export const audit_tool_stock_entry_create_draft = () => t('audit_tool_stock_entry_create_draft');
export const audit_tool_stock_entry_update = () => t('audit_tool_stock_entry_update');
export const audit_tool_stock_entry_submit = () => t('audit_tool_stock_entry_submit');
export const audit_tool_stock_entry_cancel = () => t('audit_tool_stock_entry_cancel');
export const audit_tool_landed_cost_create_draft = () => t('audit_tool_landed_cost_create_draft');
export const audit_tool_landed_cost_submit = () => t('audit_tool_landed_cost_submit');
export const audit_tool_landed_cost_cancel = () => t('audit_tool_landed_cost_cancel');
export const audit_tool_item_create = () => t('audit_tool_item_create');
export const audit_tool_item_update = () => t('audit_tool_item_update');
export const audit_tool_item_delete = () => t('audit_tool_item_delete');
export const audit_tool_item_variants_upsert = () => t('audit_tool_item_variants_upsert');
export const audit_tool_item_valuation_method_change = () => t('audit_tool_item_valuation_method_change');
export const audit_tool_bom_create = () => t('audit_tool_bom_create');
export const audit_tool_bom_update = () => t('audit_tool_bom_update');
export const audit_tool_bom_delete = () => t('audit_tool_bom_delete');
export const audit_tool_bom_clone = () => t('audit_tool_bom_clone');
export const audit_tool_bom_activate = () => t('audit_tool_bom_activate');
export const audit_tool_bom_item_create = () => t('audit_tool_bom_item_create');
export const audit_tool_bom_item_update = () => t('audit_tool_bom_item_update');
export const audit_tool_bom_item_delete = () => t('audit_tool_bom_item_delete');
export const audit_tool_other = () => t('audit_tool_other');
export const entity_audit_shown = (p) => t('entity_audit_shown', p);
export const entity_audit_shown_around = (p) => t('entity_audit_shown_around', p);
export const entity_audit_load_newer = () => t('entity_audit_load_newer');
export const entity_audit_load_older = () => t('entity_audit_load_older');
export const entity_audit_show_latest = () => t('entity_audit_show_latest');
export const entity_audit_back = () => t('entity_audit_back');
export const entity_audit_signed_tier = (p) => t('entity_audit_signed_tier', p);
export const entity_audit_highlight_missing = () => t('entity_audit_highlight_missing');
export const entity_audit_action_transition = (p) => t('entity_audit_action_transition', p);
export const entity_audit_status_failed = () => t('entity_audit_status_failed');
export const entity_audit_system = () => t('entity_audit_system');
export const entity_audit_highlight_aria = () => t('entity_audit_highlight_aria');
// ─── Route: admin audit ──────────────────────────────────────────────
export const route_admin_audit_title = () => t('route_admin_audit_title');
export const route_admin_audit_description = () => t('route_admin_audit_description');
export const route_admin_audit_filter_actor = () => t('route_admin_audit_filter_actor');
export const route_admin_audit_filter_resource = () => t('route_admin_audit_filter_resource');
export const route_admin_audit_filter_action = () => t('route_admin_audit_filter_action');
export const route_admin_audit_filter_entity_type = () => t('route_admin_audit_filter_entity_type');
export const route_admin_audit_filter_entity_id = () => t('route_admin_audit_filter_entity_id');
export const route_admin_audit_filter_period = () => t('route_admin_audit_filter_period');
export const route_admin_audit_empty_title = () => t('route_admin_audit_empty_title');
export const route_admin_audit_empty_desc = () => t('route_admin_audit_empty_desc');
export const route_admin_audit_col_time = () => t('route_admin_audit_col_time');
export const route_admin_audit_col_entity = () => t('route_admin_audit_col_entity');
export const route_admin_audit_col_status = () => t('route_admin_audit_col_status');
export const route_admin_audit_status_ok = () => t('route_admin_audit_status_ok');
export const route_admin_audit_status_error = () => t('route_admin_audit_status_error');
export const route_admin_audit_btn_details = () => t('route_admin_audit_btn_details');
export const route_admin_audit_drawer_trace_id = () => t('route_admin_audit_drawer_trace_id');
export const route_admin_audit_drawer_latency = () => t('route_admin_audit_drawer_latency');
export const route_admin_audit_drawer_latency_ms = (p) => t('route_admin_audit_drawer_latency_ms', p);
export const route_admin_audit_drawer_input_hash = () => t('route_admin_audit_drawer_input_hash');
export const route_admin_audit_drawer_output_hash = () => t('route_admin_audit_drawer_output_hash');
export const route_admin_audit_drawer_payload_heading = () => t('route_admin_audit_drawer_payload_heading');
// ─── Route: admin integrations ───────────────────────────────────────
export const route_admin_integrations_title = () => t('route_admin_integrations_title');
export const route_admin_integrations_description = () => t('route_admin_integrations_description');
// multi-tenancy-rls F1 — RLS-mode индикатор (admin-видимость).
export const route_admin_integrations_rls_label = () => t('route_admin_integrations_rls_label');
export const route_admin_integrations_rls_on = () => t('route_admin_integrations_rls_on');
export const route_admin_integrations_rls_off = () => t('route_admin_integrations_rls_off');
export const route_admin_integrations_rls_hint = () => t('route_admin_integrations_rls_hint');
export const route_admin_integrations_btn_add = () => t('route_admin_integrations_btn_add');
export const route_admin_integrations_col_peer = () => t('route_admin_integrations_col_peer');
export const route_admin_integrations_col_health = () => t('route_admin_integrations_col_health');
export const route_admin_integrations_col_sync_mode = () => t('route_admin_integrations_col_sync_mode');
export const route_admin_integrations_col_last_pull = () => t('route_admin_integrations_col_last_pull');
export const route_admin_integrations_col_last_push = () => t('route_admin_integrations_col_last_push');
export const route_admin_integrations_col_credentials = () => t('route_admin_integrations_col_credentials');
export const route_admin_integrations_health_badge_ok = () => t('route_admin_integrations_health_badge_ok');
export const route_admin_integrations_health_badge_degraded = () => t('route_admin_integrations_health_badge_degraded');
export const route_admin_integrations_health_badge_disconnected = () => t('route_admin_integrations_health_badge_disconnected');
export const route_admin_integrations_sync_pull = () => t('route_admin_integrations_sync_pull');
export const route_admin_integrations_sync_push = () => t('route_admin_integrations_sync_push');
export const route_admin_integrations_sync_bidirectional = () => t('route_admin_integrations_sync_bidirectional');
export const route_admin_integrations_sync_disabled = () => t('route_admin_integrations_sync_disabled');
export const route_admin_integrations_btn_health_history = () => t('route_admin_integrations_btn_health_history');
export const route_admin_integrations_btn_master = () => t('route_admin_integrations_btn_master');
export const route_admin_integrations_btn_master_tooltip = () => t('route_admin_integrations_btn_master_tooltip');
export const route_admin_integrations_btn_documents = () => t('route_admin_integrations_btn_documents');
export const route_admin_integrations_btn_documents_tooltip = () => t('route_admin_integrations_btn_documents_tooltip');
export const route_admin_integrations_btn_aggregates = () => t('route_admin_integrations_btn_aggregates');
export const route_admin_integrations_btn_aggregates_tooltip = () => t('route_admin_integrations_btn_aggregates_tooltip');
export const route_admin_integrations_btn_push_history = () => t('route_admin_integrations_btn_push_history');
export const route_admin_integrations_btn_rollback = () => t('route_admin_integrations_btn_rollback');
export const route_admin_integrations_toast_master_pushed = () => t('route_admin_integrations_toast_master_pushed');
export const route_admin_integrations_toast_docs_pushed = () => t('route_admin_integrations_toast_docs_pushed');
export const route_admin_integrations_toast_aggr_pushed = () => t('route_admin_integrations_toast_aggr_pushed');
export const route_admin_integrations_toast_push_failed = () => t('route_admin_integrations_toast_push_failed');
export const route_admin_integrations_toast_rollback_ok = () => t('route_admin_integrations_toast_rollback_ok');
export const route_admin_integrations_toast_rollback_failed = () => t('route_admin_integrations_toast_rollback_failed');
export const route_admin_integrations_push_master_title = () => t('route_admin_integrations_push_master_title');
export const route_admin_integrations_push_master_summary = () => t('route_admin_integrations_push_master_summary');
export const route_admin_integrations_push_master_confirm = () => t('route_admin_integrations_push_master_confirm');
export const route_admin_integrations_push_docs_title = () => t('route_admin_integrations_push_docs_title');
export const route_admin_integrations_push_docs_summary = (p) => t('route_admin_integrations_push_docs_summary', p);
export const route_admin_integrations_push_docs_confirm = () => t('route_admin_integrations_push_docs_confirm');
export const route_admin_integrations_push_aggr_title = () => t('route_admin_integrations_push_aggr_title');
export const route_admin_integrations_push_aggr_summary = (p) => t('route_admin_integrations_push_aggr_summary', p);
export const route_admin_integrations_push_aggr_confirm = () => t('route_admin_integrations_push_aggr_confirm');
export const route_admin_integrations_docs_input_label = () => t('route_admin_integrations_docs_input_label');
export const route_admin_integrations_docs_input_hint = () => t('route_admin_integrations_docs_input_hint');
export const route_admin_integrations_docs_apply_n = (p) => t('route_admin_integrations_docs_apply_n', p);
export const route_admin_integrations_period_input_label = () => t('route_admin_integrations_period_input_label');
export const route_admin_integrations_push_history_title = () => t('route_admin_integrations_push_history_title');
export const route_admin_integrations_push_history_empty = () => t('route_admin_integrations_push_history_empty');
export const route_admin_integrations_history_meta = (p) => t('route_admin_integrations_history_meta', p);
export const route_admin_integrations_history_period = (p) => t('route_admin_integrations_history_period', p);
export const route_admin_integrations_history_rolled_back = (p) => t('route_admin_integrations_history_rolled_back', p);
export const route_admin_integrations_rollback_title = () => t('route_admin_integrations_rollback_title');
export const route_admin_integrations_rollback_description = () => t('route_admin_integrations_rollback_description');
export const route_admin_integrations_rollback_reason_label = () => t('route_admin_integrations_rollback_reason_label');
export const route_admin_integrations_health_history_title = () => t('route_admin_integrations_health_history_title');
export const route_admin_integrations_health_history_empty = () => t('route_admin_integrations_health_history_empty');
export const route_admin_integrations_wizard_title = () => t('route_admin_integrations_wizard_title');
export const route_admin_integrations_wizard_source_type = () => t('route_admin_integrations_wizard_source_type');
export const route_admin_integrations_wizard_name = () => t('route_admin_integrations_wizard_name');
export const route_admin_integrations_wizard_name_placeholder = () => t('route_admin_integrations_wizard_name_placeholder');
export const route_admin_integrations_wizard_creds_hint = () => t('route_admin_integrations_wizard_creds_hint');
export const route_admin_integrations_wizard_test_conn = () => t('route_admin_integrations_wizard_test_conn');
export const route_admin_integrations_wizard_testing = () => t('route_admin_integrations_wizard_testing');
export const route_admin_integrations_wizard_test_ok = () => t('route_admin_integrations_wizard_test_ok');
export const route_admin_integrations_wizard_test_error = (p) => t('route_admin_integrations_wizard_test_error', p);
export const route_admin_integrations_wizard_ready_to_save = () => t('route_admin_integrations_wizard_ready_to_save');
export const route_admin_integrations_wizard_reveal_hint = () => t('route_admin_integrations_wizard_reveal_hint');
export const route_admin_integrations_peer_1c_label = () => t('route_admin_integrations_peer_1c_label');
export const route_admin_integrations_peer_bitrix_label = () => t('route_admin_integrations_peer_bitrix_label');
export const route_admin_integrations_peer_adesk_label = () => t('route_admin_integrations_peer_adesk_label');
// ─── Route: admin memory ─────────────────────────────────────────────
export const route_admin_memory_title = () => t('route_admin_memory_title');
export const route_admin_memory_description = () => t('route_admin_memory_description');
export const route_admin_memory_empty_title = () => t('route_admin_memory_empty_title');
export const route_admin_memory_empty_description = () => t('route_admin_memory_empty_description');
export const route_admin_memory_stat_total = () => t('route_admin_memory_stat_total');
export const route_admin_memory_stat_avg_age = () => t('route_admin_memory_stat_avg_age');
export const route_admin_memory_stat_recall_hit = () => t('route_admin_memory_stat_recall_hit');
export const route_admin_memory_stat_scope = (p) => t('route_admin_memory_stat_scope', p);
export const route_admin_memory_table_aria = () => t('route_admin_memory_table_aria');
export const route_admin_memory_col_scope = () => t('route_admin_memory_col_scope');
export const route_admin_memory_col_lifecycle = () => t('route_admin_memory_col_lifecycle');
export const route_admin_memory_col_sensitivity = () => t('route_admin_memory_col_sensitivity');
export const route_admin_memory_col_provenance = () => t('route_admin_memory_col_provenance');
export const route_admin_memory_col_fresh_until = () => t('route_admin_memory_col_fresh_until');
export const route_admin_memory_col_version = () => t('route_admin_memory_col_version');
export const route_admin_memory_col_summary = () => t('route_admin_memory_col_summary');
export const route_admin_memory_btn_promote = () => t('route_admin_memory_btn_promote');
export const route_admin_memory_btn_demote = () => t('route_admin_memory_btn_demote');
export const route_admin_memory_btn_expire = () => t('route_admin_memory_btn_expire');
// ─── Route: admin sync-staging ───────────────────────────────────────
export const route_admin_sync_staging_title = () => t('route_admin_sync_staging_title');
export const route_admin_sync_staging_description = () => t('route_admin_sync_staging_description');
export const route_admin_sync_staging_btn_pull = () => t('route_admin_sync_staging_btn_pull');
export const route_admin_sync_staging_btn_push = () => t('route_admin_sync_staging_btn_push');
export const route_admin_sync_staging_filters_heading = () => t('route_admin_sync_staging_filters_heading');
export const route_admin_sync_staging_filter_source = () => t('route_admin_sync_staging_filter_source');
export const route_admin_sync_staging_filter_resource = () => t('route_admin_sync_staging_filter_resource');
export const route_admin_sync_staging_filter_resolution = () => t('route_admin_sync_staging_filter_resolution');
export const route_admin_sync_staging_filter_all = () => t('route_admin_sync_staging_filter_all');
export const route_admin_sync_staging_source_1c = () => t('route_admin_sync_staging_source_1c');
export const route_admin_sync_staging_source_bitrix = () => t('route_admin_sync_staging_source_bitrix');
export const route_admin_sync_staging_source_adesk = () => t('route_admin_sync_staging_source_adesk');
export const route_admin_sync_staging_source_excel = () => t('route_admin_sync_staging_source_excel');
export const route_admin_sync_staging_resource_item = () => t('route_admin_sync_staging_resource_item');
export const route_admin_sync_staging_resource_warehouse = () => t('route_admin_sync_staging_resource_warehouse');
export const route_admin_sync_staging_resource_batch = () => t('route_admin_sync_staging_resource_batch');
export const route_admin_sync_staging_resource_bom = () => t('route_admin_sync_staging_resource_bom');
export const route_admin_sync_staging_resource_counterparty = () => t('route_admin_sync_staging_resource_counterparty');
export const route_admin_sync_staging_resolution_unresolved = () => t('route_admin_sync_staging_resolution_unresolved');
export const route_admin_sync_staging_resolution_ours = () => t('route_admin_sync_staging_resolution_ours');
export const route_admin_sync_staging_resolution_theirs = () => t('route_admin_sync_staging_resolution_theirs');
export const route_admin_sync_staging_resolution_merged = () => t('route_admin_sync_staging_resolution_merged');
export const route_admin_sync_staging_resolution_escalated = () => t('route_admin_sync_staging_resolution_escalated');
export const route_admin_sync_staging_table_aria = () => t('route_admin_sync_staging_table_aria');
export const route_admin_sync_staging_col_operation = () => t('route_admin_sync_staging_col_operation');
export const route_admin_sync_staging_col_subject = () => t('route_admin_sync_staging_col_subject');
export const route_admin_sync_staging_col_detected = () => t('route_admin_sync_staging_col_detected');
export const route_admin_sync_staging_btn_resolve = () => t('route_admin_sync_staging_btn_resolve');
export const route_admin_sync_staging_drawer_title = () => t('route_admin_sync_staging_drawer_title');
// ─── Route: Admin Warehouse Access (B.7) ────────────────────────────
export const route_admin_warehouse_access_title = () => t('route_admin_warehouse_access_title');
export const route_admin_warehouse_access_user_label = () => t('route_admin_warehouse_access_user_label');
export const route_admin_warehouse_access_user_option = (p) => t('route_admin_warehouse_access_user_option', p);
export const route_admin_warehouse_access_user_placeholder = () => t('route_admin_warehouse_access_user_placeholder');
export const route_admin_warehouse_access_unrestricted_hint = () => t('route_admin_warehouse_access_unrestricted_hint');
export const route_admin_warehouse_access_user_not_found = () => t('route_admin_warehouse_access_user_not_found');
export const route_admin_warehouse_access_count = (params) => t('route_admin_warehouse_access_count', params);
// ─── Route: Admin Bank Import (C.6) ─────────────────────────────────
export const route_admin_bank_import_title = () => t('route_admin_bank_import_title');
export const route_admin_bank_import_format_label = () => t('route_admin_bank_import_format_label');
export const route_admin_bank_import_upload = () => t('route_admin_bank_import_upload');
export const route_admin_bank_import_rows_count = (params) => t('route_admin_bank_import_rows_count', params);
export const route_admin_bank_import_col_date = () => t('route_admin_bank_import_col_date');
export const route_admin_bank_import_col_amount = () => t('route_admin_bank_import_col_amount');
export const route_admin_bank_import_col_cp = () => t('route_admin_bank_import_col_cp');
export const route_admin_bank_import_col_confidence = () => t('route_admin_bank_import_col_confidence');
export const route_admin_bank_import_commit = () => t('route_admin_bank_import_commit');
export const payment_source_bank_import = () => t('payment_source_bank_import');
// ─── Route: Admin FX Rates (D.9) ─────────────────────────────────────
export const route_admin_fx_rates_title = () => t('route_admin_fx_rates_title');
export const route_admin_fx_rates_filter_currency = () => t('route_admin_fx_rates_filter_currency');
export const route_admin_fx_rates_nbkz_fetch = () => t('route_admin_fx_rates_nbkz_fetch');
export const route_admin_fx_rates_reval = () => t('route_admin_fx_rates_reval');
export const route_admin_fx_rates_new_override = () => t('route_admin_fx_rates_new_override');
export const route_admin_fx_rates_col_currency = () => t('route_admin_fx_rates_col_currency');
export const route_admin_fx_rates_col_rate = () => t('route_admin_fx_rates_col_rate');
export const route_admin_fx_rates_col_source = () => t('route_admin_fx_rates_col_source');
// ─── P&L FX rows (D.10) ───────────────────────────────────────────────
export const pnl_section_fx_gain_loss = () => t('pnl_section_fx_gain_loss');
export const pnl_fx_unrealized = () => t('pnl_fx_unrealized');
export const pnl_fx_realized = () => t('pnl_fx_realized');
// ─── Route: Admin Users ──────────────────────────────────────────────
export const route_admin_users_title = () => t('route_admin_users_title');
export const route_admin_users_title_super = () => t('route_admin_users_title_super');
export const route_admin_users_title_holding = () => t('route_admin_users_title_holding');
export const route_admin_users_title_company = () => t('route_admin_users_title_company');
export const route_admin_users_restore_locked_short = () => t('route_admin_users_restore_locked_short');
// С-I8, гейт круг 1 (ACC-F37): руководитель компании не меняет доступы человека с ролью выше своей.
export const route_admin_users_access_locked_short = () => t('route_admin_users_access_locked_short');
// С-I8, гейт круг 2 (ACC-F38): руководителю компании — кто вернёт отключённый аккаунт.
export const route_admin_users_restore_by_platform = () => t('route_admin_users_restore_by_platform');
export const route_admin_users_disabled_by_name = () => t('route_admin_users_disabled_by_name');
export const route_admin_users_disabled_by_outside_line = () => t('route_admin_users_disabled_by_outside_line');
export const route_admin_users_restore_locked = () => t('route_admin_users_restore_locked');
export const route_admin_users_restore_account_title = () => t('route_admin_users_restore_account_title');
export const route_admin_users_restore_account_body = (p) => t('route_admin_users_restore_account_body', p);
export const route_admin_users_title_company_named = (p) => t('route_admin_users_title_company_named', p);
export const route_admin_users_menu_access = () => t('route_admin_users_menu_access');
export const route_admin_users_disabled_account = () => t('route_admin_users_disabled_account');
export const route_admin_users_disabled_membership = () => t('route_admin_users_disabled_membership');
export const route_admin_users_disabled_unknown_date = () => t('route_admin_users_disabled_unknown_date');
export const route_admin_users_disabled_title_account = () => t('route_admin_users_disabled_title_account');
export const route_admin_users_disabled_title_membership = () => t('route_admin_users_disabled_title_membership');
export const route_admin_users_description = () => t('route_admin_users_description');
export const route_admin_users_btn_bulk_invite = () => t('route_admin_users_btn_bulk_invite');
export const route_admin_users_btn_invite = () => t('route_admin_users_btn_invite');
export const route_admin_users_col_email = () => t('route_admin_users_col_email');
export const route_admin_users_col_role = () => t('route_admin_users_col_role');
export const route_admin_users_col_reset = () => t('route_admin_users_col_reset');
export const route_admin_users_col_last_login = () => t('route_admin_users_col_last_login');
/** T2A-F3/SR-F2 — колонка живости аккаунта вместо несуществовавшего «последнего входа». */
export const route_admin_users_col_last_activity = () => t('route_admin_users_col_last_activity');
export const route_admin_users_last_activity_hint = () => t('route_admin_users_last_activity_hint');
export const user_drawer_last_activity = () => t('user_drawer_last_activity');
export const user_drawer_last_activity_never = () => t('user_drawer_last_activity_never');
/** Раунд 2 гейта T2A/SR — порог живости: интеграция молчит дольше суток. */
export const route_admin_users_activity_silent = () => t('route_admin_users_activity_silent');
export const route_admin_users_activity_silent_days = (p) => t('route_admin_users_activity_silent_days', { days: String(p.days) });
export const route_admin_users_activity_never = () => t('route_admin_users_activity_never');
/** Раунд 6 гейта — у человека и у машины отсутствие обращений значит РАЗНОЕ. */
export const route_admin_users_activity_no_login = () => t('route_admin_users_activity_no_login');
/** Раунд 5 гейта — правило колонки живости объявлено, а не выводится из строк. */
export const route_admin_users_activity_rule = () => t('route_admin_users_activity_rule');
/** Раунд 4 гейта T2A/SR — состояние связи не кодируется одним цветом (§4.1b). */
export const route_wo_detail_sse_word_open = () => t('route_wo_detail_sse_word_open');
export const route_wo_detail_sse_word_pending = () => t('route_wo_detail_sse_word_pending');
export const route_wo_detail_sse_word_closed = () => t('route_wo_detail_sse_word_closed');
export const route_admin_users_reset_required = () => t('route_admin_users_reset_required');
export const route_admin_users_btn_reset = () => t('route_admin_users_btn_reset');
export const route_admin_users_btn_reset_tooltip = () => t('route_admin_users_btn_reset_tooltip');
export const route_admin_users_btn_deactivate = () => t('route_admin_users_btn_deactivate');
export const route_admin_users_btn_impersonate = () => t('route_admin_users_btn_impersonate');
export const route_admin_users_btn_impersonate_tooltip = () => t('route_admin_users_btn_impersonate_tooltip');
export const route_admin_users_impersonate_title = () => t('route_admin_users_impersonate_title');
export const route_admin_users_impersonate_launching = () => t('route_admin_users_impersonate_launching');
export const route_admin_users_impersonate_confirm = () => t('route_admin_users_impersonate_confirm');
export const route_admin_users_impersonate_warning_prefix = () => t('route_admin_users_impersonate_warning_prefix');
export const route_admin_users_impersonate_warning_suffix = () => t('route_admin_users_impersonate_warning_suffix');
export const route_admin_users_invite_title = () => t('route_admin_users_invite_title');
export const route_admin_users_invite_submit = () => t('route_admin_users_invite_submit');
export const route_admin_users_invite_target_label = () => t('route_admin_users_invite_target_label');
export const route_admin_users_invite_target_entity = () => t('route_admin_users_invite_target_entity');
export const route_admin_users_invite_target_holding = () => t('route_admin_users_invite_target_holding');
export const route_admin_users_invite_entity_label = () => t('route_admin_users_invite_entity_label');
export const route_admin_users_invite_add_company = () => t('route_admin_users_invite_add_company');
export const tm_visibility_section_title = () => t('tm_visibility_section_title');
export const tm_visibility_make_private = () => t('tm_visibility_make_private');
export const tm_visibility_make_open = () => t('tm_visibility_make_open');
export const tm_visibility_current_open = () => t('tm_visibility_current_open');
export const tm_visibility_current_private = () => t('tm_visibility_current_private');
export const tm_visibility_dialog_private_title = () => t('tm_visibility_dialog_private_title');
export const tm_visibility_dialog_open_title = () => t('tm_visibility_dialog_open_title');
export const tm_visibility_members_line = (p) => t('tm_visibility_members_line', p);
export const tm_visibility_whole_company_warn = () => t('tm_visibility_whole_company_warn');
export const tm_visibility_auto_add_title = () => t('tm_visibility_auto_add_title');
export const tm_visibility_reason_task_assignee = () => t('tm_visibility_reason_task_assignee');
export const tm_visibility_reason_claimed_assignee = () => t('tm_visibility_reason_claimed_assignee');
export const tm_visibility_reason_draft_author = () => t('tm_visibility_reason_draft_author');
export const tm_visibility_reason_project_owner = () => t('tm_visibility_reason_project_owner');
export const tm_visibility_unchecked_warn = () => t('tm_visibility_unchecked_warn');
export const tm_visibility_role_queues_warn = (p) => t('tm_visibility_role_queues_warn', p);
export const tm_visibility_authors_note = () => t('tm_visibility_authors_note');
export const tm_visibility_open_confirm_body = () => t('tm_visibility_open_confirm_body');
export const tm_visibility_confirm = () => t('tm_visibility_confirm');
export const tm_visibility_toast_changed = () => t('tm_visibility_toast_changed');
export const tm_visibility_applies_immediately = () => t('tm_visibility_applies_immediately');
export const project_visibility_label = () => t('project_visibility_label');
export const project_visibility_open = () => t('project_visibility_open');
export const project_visibility_private = () => t('project_visibility_private');
export const project_visibility_open_hint = () => t('project_visibility_open_hint');
export const project_visibility_private_hint = () => t('project_visibility_private_hint');
export const project_visibility_owner_note = () => t('project_visibility_owner_note');
export const tmpl_visibility_copy_label = () => t('tmpl_visibility_copy_label');
export const tmpl_template_private_warn = () => t('tmpl_template_private_warn');
export const tmpl_materialize_visibility_label = () => t('tmpl_materialize_visibility_label');
export const project_members_section_title = () => t('project_members_section_title');
export const project_members_hint = () => t('project_members_hint');
export const project_members_lead_you = () => t('project_members_lead_you');
export const project_members_search_label = () => t('project_members_search_label');
export const project_members_search_ph = () => t('project_members_search_ph');
export const project_members_no_results = () => t('project_members_no_results');
export const project_members_all_added = () => t('project_members_all_added');
export const project_members_count = (p) => t('project_members_count', p);
export const project_members_manage_link = () => t('project_members_manage_link');
export const project_badge_private = () => t('project_badge_private');
export const project_badge_private_title = () => t('project_badge_private_title');
export const project_visibility_filter_all = () => t('project_visibility_filter_all');
export const project_chip_unavailable = () => t('project_chip_unavailable');
export const task_not_found_title = () => t('task_not_found_title');
export const task_not_found_body = () => t('task_not_found_body');
export const project_workspace_not_found_title = () => t('project_workspace_not_found_title');
export const project_workspace_not_found_body = () => t('project_workspace_not_found_body');
export const project_workspace_back_to_list = () => t('project_workspace_back_to_list');
export const error_task_module_mixed_visibility_membership = () => t('error_task_module_mixed_visibility_membership');
export const error_task_module_inbox_cannot_be_private = () => t('error_task_module_inbox_cannot_be_private');
export const error_task_module_assignee_not_project_member = () => t('error_task_module_assignee_not_project_member');
export const route_admin_users_invite_remove_company = () => t('route_admin_users_invite_remove_company');
export const admin_invites_col_companies = () => t('admin_invites_col_companies');
export const accept_invite_label_companies = () => t('accept_invite_label_companies');
export const route_admin_users_invite_entity_placeholder = () => t('route_admin_users_invite_entity_placeholder');
export const route_admin_users_invite_holding_label = () => t('route_admin_users_invite_holding_label');
export const route_admin_users_invite_holding_placeholder = () => t('route_admin_users_invite_holding_placeholder');
export const route_admin_users_invite_no_companies = () => t('route_admin_users_invite_no_companies');
export const route_admin_users_invite_rights_hint = () => t('route_admin_users_invite_rights_hint');
export const route_admin_users_invite_options_loading = () => t('route_admin_users_invite_options_loading');
export const route_admin_users_invite_options_failed = () => t('route_admin_users_invite_options_failed');
export const route_admin_users_invite_options_retry = () => t('route_admin_users_invite_options_retry');
export const route_admin_users_invite_role_changed = (p) => t('route_admin_users_invite_role_changed', p);
export const admin_invite_reason_company_admin_required = () => t('admin_invite_reason_company_admin_required');
export const admin_invite_bulk_reason_no_target = () => t('admin_invite_bulk_reason_no_target');
export const admin_invite_bulk_reason_row_failed = () => t('admin_invite_bulk_reason_row_failed');
export const route_admin_users_bulk_title = () => t('route_admin_users_bulk_title');
export const route_admin_users_bulk_sending = () => t('route_admin_users_bulk_sending');
export const route_admin_users_bulk_submit = (p) => t('route_admin_users_bulk_submit', p);
export const route_admin_users_bulk_dropzone_aria = () => t('route_admin_users_bulk_dropzone_aria');
export const route_admin_users_bulk_dropzone_hint = () => t('route_admin_users_bulk_dropzone_hint');
export const route_admin_users_bulk_role_for_all = () => t('route_admin_users_bulk_role_for_all');
export const route_admin_users_bulk_target_company = (p) => t('route_admin_users_bulk_target_company', p);
export const route_admin_users_bulk_paste_placeholder = () => t('route_admin_users_bulk_paste_placeholder');
export const route_admin_users_bulk_paste_aria = () => t('route_admin_users_bulk_paste_aria');
export const route_admin_users_bulk_preview_aria = () => t('route_admin_users_bulk_preview_aria');
export const route_admin_users_bulk_col_role = () => t('route_admin_users_bulk_col_role');
export const route_admin_users_bulk_col_check = () => t('route_admin_users_bulk_col_check');
export const route_admin_users_bulk_check_ok = () => t('route_admin_users_bulk_check_ok');
export const route_admin_users_bulk_check_invalid = () => t('route_admin_users_bulk_check_invalid');
export const route_admin_users_bulk_result_created = (p) => t('route_admin_users_bulk_result_created', p);
export const route_admin_users_bulk_result_failed = (p) => t('route_admin_users_bulk_result_failed', p);
export const route_admin_users_bulk_reason_invalid_email = () => t('route_admin_users_bulk_reason_invalid_email');
export const route_admin_users_bulk_reason_invalid_role = () => t('route_admin_users_bulk_reason_invalid_role');
export const route_admin_users_bulk_reason_duplicate = () => t('route_admin_users_bulk_reason_duplicate');
export const route_admin_users_bulk_reason_role_above = () => t('route_admin_users_bulk_reason_role_above');
export const route_admin_users_bulk_no_rights = (p) => t('route_admin_users_bulk_no_rights', p);
export const route_admin_users_bulk_reason_invalid = () => t('route_admin_users_bulk_reason_invalid');
// ─── Route: Onboarding ───────────────────────────────────────────────
export const route_onboarding_title = () => t('route_onboarding_title');
export const route_onboarding_description = () => t('route_onboarding_description');
export const route_onboarding_step_label = (p) => t(`route_onboarding_step_${p.step.replace(/-/g, '_')}`);
export const route_onboarding_welcome_heading = (p) => t('route_onboarding_welcome_heading', p);
export const route_onboarding_welcome_fallback_name = () => t('route_onboarding_welcome_fallback_name');
export const route_onboarding_welcome_p1 = () => t('route_onboarding_welcome_p1');
export const route_onboarding_welcome_p2 = () => t('route_onboarding_welcome_p2');
export const route_onboarding_integrations_heading = () => t('route_onboarding_integrations_heading');
export const route_onboarding_integrations_p = () => t('route_onboarding_integrations_p');
export const route_onboarding_integrations_link = () => t('route_onboarding_integrations_link');
export const route_onboarding_opening_heading = () => t('route_onboarding_opening_heading');
export const route_onboarding_opening_p = () => t('route_onboarding_opening_p');
export const route_onboarding_opening_date_label = () => t('route_onboarding_opening_date_label');
export const route_onboarding_opening_date_hint = () => t('route_onboarding_opening_date_hint');
export const route_onboarding_opening_footnote = () => t('route_onboarding_opening_footnote');
export const route_onboarding_summary_heading = () => t('route_onboarding_summary_heading');
export const route_onboarding_summary_p = () => t('route_onboarding_summary_p');
export const route_onboarding_summary_link = () => t('route_onboarding_summary_link');
export const route_onboarding_btn_finish = () => t('route_onboarding_btn_finish');
// ─── Route: Approvals ────────────────────────────────────────────────
export const route_approvals_title = () => t('route_approvals_title');
export const route_approvals_description = () => t('route_approvals_description');
export const route_approvals_filters_heading = () => t('route_approvals_filters_heading');
export const route_approvals_filter_task_type = () => t('route_approvals_filter_task_type');
export const route_approvals_filter_all = () => t('route_approvals_filter_all');
export const route_approvals_task_run_result = () => t('route_approvals_task_run_result');
export const route_approvals_task_stock_entry = () => t('route_approvals_task_stock_entry');
export const route_approvals_task_stock_recon = () => t('route_approvals_task_stock_recon');
export const route_approvals_task_wo_transition = () => t('route_approvals_task_wo_transition');
export const route_approvals_task_landed_cost = () => t('route_approvals_task_landed_cost');
export const route_approvals_task_regulatory = () => t('route_approvals_task_regulatory');
export const route_approvals_filter_status = () => t('route_approvals_filter_status');
export const route_approvals_status_pending = () => t('route_approvals_status_pending');
export const route_approvals_status_approved = () => t('route_approvals_status_approved');
export const route_approvals_status_rejected = () => t('route_approvals_status_rejected');
export const route_approvals_status_expired = () => t('route_approvals_status_expired');
export const route_approvals_empty_title = () => t('route_approvals_empty_title');
export const route_approvals_empty_description = () => t('route_approvals_empty_description');
export const route_approvals_table_aria = () => t('route_approvals_table_aria');
export const route_approvals_col_subject = () => t('route_approvals_col_subject');
export const route_approvals_col_task = () => t('route_approvals_col_task');
export const route_approvals_col_tier = () => t('route_approvals_col_tier');
export const route_approvals_tier_aria = (p) => t('route_approvals_tier_aria', p);
export const route_approvals_tier_aria_dual = (p) => t('route_approvals_tier_aria_dual', p);
export const route_approvals_col_sla = () => t('route_approvals_col_sla');
export const route_approvals_sla_aria = (p) => t('route_approvals_sla_aria', p);
export const route_approvals_sla_hours = (p) => t('route_approvals_sla_hours', p);
export const route_approvals_col_status = () => t('route_approvals_col_status');
export const route_approvals_btn_open = () => t('route_approvals_btn_open');
export const route_approvals_drawer_title_fallback = () => t('route_approvals_drawer_title_fallback');
export const route_approvals_btn_reject = () => t('route_approvals_btn_reject');
export const route_approvals_btn_approve = () => t('route_approvals_btn_approve');
export const route_approvals_toast_approved = () => t('route_approvals_toast_approved');
export const route_approvals_toast_approve_failed = () => t('route_approvals_toast_approve_failed');
export const route_approvals_toast_rejected = () => t('route_approvals_toast_rejected');
export const route_approvals_toast_reject_failed = () => t('route_approvals_toast_reject_failed');
export const route_approvals_tier3_banner_title = () => t('route_approvals_tier3_banner_title');
export const route_approvals_tier3_banner_body = (p) => t('route_approvals_tier3_banner_body', p);
export const route_approvals_meta_task = () => t('route_approvals_meta_task');
export const route_approvals_meta_tier = () => t('route_approvals_meta_tier');
export const route_approvals_meta_requested_by = () => t('route_approvals_meta_requested_by');
export const route_approvals_meta_age = () => t('route_approvals_meta_age');
export const route_approvals_meta_age_hours = (p) => t('route_approvals_meta_age_hours', p);
export const route_approvals_meta_run = () => t('route_approvals_meta_run');
export const route_approvals_section_diff = () => t('route_approvals_section_diff');
export const route_approvals_section_evidence = () => t('route_approvals_section_evidence');
export const route_approvals_section_signatures = () => t('route_approvals_section_signatures');
export const route_approvals_section_pending = (p) => t('route_approvals_section_pending', p);
export const route_approvals_pending_slot = (p) => t('route_approvals_pending_slot', p);
export const route_approvals_you_signing_prefix = () => t('route_approvals_you_signing_prefix');
export const route_approvals_reject_dialog_title = () => t('route_approvals_reject_dialog_title');
export const route_approvals_reject_reason_label = () => t('route_approvals_reject_reason_label');
export const route_approvals_reject_reason_placeholder = () => t('route_approvals_reject_reason_placeholder');
export const route_approvals_reject_reason_hint = () => t('route_approvals_reject_reason_hint');
// ─── Route: BOMs index ───────────────────────────────────────────────
export const route_boms_index_description = () => t('route_boms_index_description');
export const route_boms_index_btn_new = () => t('route_boms_index_btn_new');
export const route_boms_index_empty_title = () => t('route_boms_index_empty_title');
export const route_boms_index_empty_description = () => t('route_boms_index_empty_description');
export const route_boms_index_empty_action = () => t('route_boms_index_empty_action');
export const route_boms_index_col_code = () => t('route_boms_index_col_code');
export const route_boms_index_col_name = () => t('route_boms_index_col_name');
export const route_boms_index_col_item = () => t('route_boms_index_col_item');
export const route_boms_index_col_version = () => t('route_boms_index_col_version');
export const route_boms_index_col_active = () => t('route_boms_index_col_active');
export const route_boms_index_col_created = () => t('route_boms_index_col_created');
// ─── Route: Dashboard ────────────────────────────────────────────────
export const route_dashboard_greeting_fallback = () => t('route_dashboard_greeting_fallback');
export const route_dashboard_btn_new_run = () => t('route_dashboard_btn_new_run');
export const route_dashboard_open_approvals_count = (p) => t('route_dashboard_open_approvals_count', p);
export const route_dashboard_open_approvals_queue = () => t('route_dashboard_open_approvals_queue');
export const route_dashboard_sla_breach_days = (p) => t('route_dashboard_sla_breach_days', p);
export const route_dashboard_shortcut_new_run = () => t('route_dashboard_shortcut_new_run');
export const route_dashboard_onboarding_banner_title = () => t('route_dashboard_onboarding_banner_title');
export const route_dashboard_onboarding_banner_body = () => t('route_dashboard_onboarding_banner_body');
export const route_dashboard_onboarding_banner_link = () => t('route_dashboard_onboarding_banner_link');
export const route_dashboard_metric_open_conflicts = () => t('route_dashboard_metric_open_conflicts');
export const route_dashboard_metric_delta_24h = () => t('route_dashboard_metric_delta_24h');
export const route_dashboard_metric_pending_approvals = () => t('route_dashboard_metric_pending_approvals');
export const route_dashboard_metric_oldest_age = (p) => t('route_dashboard_metric_oldest_age', p);
export const route_dashboard_metric_oldest_age_days = (p) => t('route_dashboard_metric_oldest_age_days', p);
export const route_dashboard_last_run_just_now = () => t('route_dashboard_last_run_just_now');
export const route_dashboard_last_run_hours_ago = (p) => t('route_dashboard_last_run_hours_ago', p);
export const route_dashboard_last_run_yesterday = () => t('route_dashboard_last_run_yesterday');
export const route_dashboard_last_run_days_ago = (p) => t('route_dashboard_last_run_days_ago', p);
export const route_finance_period_preset_month = () => t('route_finance_period_preset_month');
export const route_finance_period_preset_quarter = () => t('route_finance_period_preset_quarter');
export const route_finance_period_preset_year = () => t('route_finance_period_preset_year');
export const route_admin_audit_filter_cat_all = () => t('route_admin_audit_filter_cat_all');
export const route_admin_audit_filter_cat_read = () => t('route_admin_audit_filter_cat_read');
export const route_admin_audit_filter_cat_write = () => t('route_admin_audit_filter_cat_write');
export const route_admin_audit_filter_cat_approve = () => t('route_admin_audit_filter_cat_approve');
export const route_dashboard_metric_last_run = () => t('route_dashboard_metric_last_run');
export const route_dashboard_card_recent_runs = () => t('route_dashboard_card_recent_runs');
export const route_dashboard_card_recent_runs_all = () => t('route_dashboard_card_recent_runs_all');
export const route_dashboard_card_recent_runs_all_aria = () => t('route_dashboard_card_recent_runs_all_aria');
export const route_dashboard_empty_title = () => t('route_dashboard_empty_title');
export const route_dashboard_empty_description = () => t('route_dashboard_empty_description');
export const route_dashboard_card_quick_actions = () => t('route_dashboard_card_quick_actions');
export const route_dashboard_quick_conflicts = () => t('route_dashboard_quick_conflicts');
export const route_dashboard_quick_anomalies = () => t('route_dashboard_quick_anomalies');
export const route_dashboard_quick_evidence = () => t('route_dashboard_quick_evidence');
export const route_dashboard_card_integrations = () => t('route_dashboard_card_integrations');
export const route_dashboard_integrations_hint = () => t('route_dashboard_integrations_hint');
// ─── Route: Items index ──────────────────────────────────────────────
export const route_items_index_description = () => t('route_items_index_description');
export const route_items_index_btn_bulk_import = () => t('route_items_index_btn_bulk_import');
export const route_items_index_btn_new = () => t('route_items_index_btn_new');
export const route_items_index_search_label = () => t('route_items_index_search_label');
export const route_items_index_search_placeholder = () => t('route_items_index_search_placeholder');
export const route_items_index_empty_tenant_title = () => t('route_items_index_empty_tenant_title');
export const route_items_index_empty_tenant_description = () => t('route_items_index_empty_tenant_description');
export const route_items_index_empty_tenant_create = () => t('route_items_index_empty_tenant_create');
export const route_items_index_empty_filter_title = () => t('route_items_index_empty_filter_title');
export const route_items_index_empty_filter_description = () => t('route_items_index_empty_filter_description');
export const route_items_index_col_name = () => t('route_items_index_col_name');
export const route_items_index_col_type = () => t('route_items_index_col_type');
export const route_items_index_col_uom = () => t('route_items_index_col_uom');
export const route_items_index_col_source = () => t('route_items_index_col_source');
// ─── Route: Conflicts ────────────────────────────────────────────────
export const route_conflicts_filters_heading = () => t('route_conflicts_filters_heading');
export const route_conflicts_shortcut_next = () => t('route_conflicts_shortcut_next');
export const route_conflicts_shortcut_prev = () => t('route_conflicts_shortcut_prev');
export const route_conflicts_shortcut_open = () => t('route_conflicts_shortcut_open');
export const route_conflicts_shortcut_resolve = () => t('route_conflicts_shortcut_resolve');
export const route_conflicts_filter_all = () => t('route_conflicts_filter_all');
export const route_conflicts_filter_severity = () => t('route_conflicts_filter_severity');
export const route_conflicts_severity_low = () => t('route_conflicts_severity_low');
export const route_conflicts_severity_medium = () => t('route_conflicts_severity_medium');
export const route_conflicts_severity_high = () => t('route_conflicts_severity_high');
export const route_conflicts_severity_critical = () => t('route_conflicts_severity_critical');
export const route_conflicts_filter_type = () => t('route_conflicts_filter_type');
export const route_conflicts_type_intra_level = () => t('route_conflicts_type_intra_level');
export const route_conflicts_type_master_data_drift = () => t('route_conflicts_type_master_data_drift');
export const route_conflicts_type_valuation_mismatch = () => t('route_conflicts_type_valuation_mismatch');
export const route_conflicts_filter_status = () => t('route_conflicts_filter_status');
export const route_conflicts_status_open = () => t('route_conflicts_status_open');
export const route_conflicts_status_resolved = () => t('route_conflicts_status_resolved');
export const route_conflicts_status_escalated = () => t('route_conflicts_status_escalated');
export const route_conflicts_empty_title = () => t('route_conflicts_empty_title');
export const route_conflicts_empty_description = () => t('route_conflicts_empty_description');
export const route_conflicts_col_subject = () => t('route_conflicts_col_subject');
export const route_conflicts_col_type = () => t('route_conflicts_col_type');
export const route_conflicts_col_severity = () => t('route_conflicts_col_severity');
export const route_conflicts_col_sources = () => t('route_conflicts_col_sources');
export const route_conflicts_btn_open = () => t('route_conflicts_btn_open');
export const route_conflicts_drawer_fallback_title = () => t('route_conflicts_drawer_fallback_title');
export const route_conflicts_btn_accept_a = () => t('route_conflicts_btn_accept_a');
export const route_conflicts_btn_accept_b = () => t('route_conflicts_btn_accept_b');
export const route_conflicts_section_drift_resolver = () => t('route_conflicts_section_drift_resolver');
// FE-1 honest-audit: broken-state primitives + shortcuts + approver context.
export const route_conflicts_broken_title = () => t('route_conflicts_broken_title');
export const route_conflicts_broken_description = () => t('route_conflicts_broken_description');
export const route_conflicts_broken_copy_trace = () => t('route_conflicts_broken_copy_trace');
export const route_conflicts_broken_open_audit = () => t('route_conflicts_broken_open_audit');
export const route_conflicts_broken_close = () => t('route_conflicts_broken_close');
export const route_conflicts_trace_copied = () => t('route_conflicts_trace_copied');
export const route_conflicts_trace_copied_bulk = (p) => t('route_conflicts_trace_copied_bulk', p);
export const route_conflicts_shortcut_copy_trace = () => t('route_conflicts_shortcut_copy_trace');
export const route_conflicts_approver_context_title = () => t('route_conflicts_approver_context_title');
export const route_conflicts_approver_context_body = (p) => t('route_conflicts_approver_context_body', p);
// ─── Route: Stock (balance + ledger shared) ──────────────────────────
export const route_stock_balance_description = () => t('route_stock_balance_description');
export const route_stock_filters_heading = () => t('route_stock_filters_heading');
export const route_stock_filter_as_of = () => t('route_stock_filter_as_of');
export const route_stock_filter_item_id = () => t('route_stock_filter_item_id');
export const route_stock_filter_item_placeholder = () => t('route_stock_filter_item_placeholder');
export const route_stock_filter_warehouse_id = () => t('route_stock_filter_warehouse_id');
export const route_stock_filter_warehouse_placeholder = () => t('route_stock_filter_warehouse_placeholder');
export const route_stock_filter_batch = () => t('route_stock_filter_batch');
export const route_stock_filter_batch_placeholder = () => t('route_stock_filter_batch_placeholder');
export const route_stock_balance_empty_title = () => t('route_stock_balance_empty_title');
export const route_stock_balance_empty_description = () => t('route_stock_balance_empty_description');
export const route_stock_col_item = () => t('route_stock_col_item');
export const route_stock_col_warehouse = () => t('route_stock_col_warehouse');
export const route_stock_col_batch = () => t('route_stock_col_batch');
export const route_stock_col_qty = () => t('route_stock_col_qty');
export const route_stock_col_uom = () => t('route_stock_col_uom');
export const route_stock_col_rate = () => t('route_stock_col_rate');
export const route_stock_col_value = () => t('route_stock_col_value');
export const route_stock_ledger_description = () => t('route_stock_ledger_description');
export const route_stock_ledger_filter_item = () => t('route_stock_ledger_filter_item');
export const route_stock_ledger_filter_warehouse = () => t('route_stock_ledger_filter_warehouse');
export const route_stock_ledger_filter_period = () => t('route_stock_ledger_filter_period');
export const route_stock_ledger_empty_title = () => t('route_stock_ledger_empty_title');
export const route_stock_ledger_empty_description = () => t('route_stock_ledger_empty_description');
export const route_stock_ledger_aria = () => t('route_stock_ledger_aria');
export const route_stock_ledger_col_at = () => t('route_stock_ledger_col_at');
export const route_stock_ledger_col_delta = () => t('route_stock_ledger_col_delta');
export const route_stock_ledger_col_after = () => t('route_stock_ledger_col_after');
export const route_stock_ledger_col_voucher = () => t('route_stock_ledger_col_voucher');
// ─── Route: Runs index ───────────────────────────────────────────────
export const route_runs_index_filter_status = () => t('route_runs_index_filter_status');
export const route_runs_index_filter_task_type = () => t('route_runs_index_filter_task_type');
export const route_runs_index_filter_all = () => t('route_runs_index_filter_all');
export const route_runs_index_empty_title = () => t('route_runs_index_empty_title');
export const route_runs_index_empty_description = () => t('route_runs_index_empty_description');
export const route_runs_index_btn_new = () => t('route_runs_index_btn_new');
export const route_runs_index_aria = () => t('route_runs_index_aria');
export const route_runs_index_col_task = () => t('route_runs_index_col_task');
export const route_runs_index_col_period = () => t('route_runs_index_col_period');
export const route_runs_index_col_runtime = () => t('route_runs_index_col_runtime');
export const route_runs_index_col_business = () => t('route_runs_index_col_business');
export const route_runs_index_col_started = () => t('route_runs_index_col_started');
// ─── Route: Landed cost (list + shared) ──────────────────────────────
export const route_lc_list_description = () => t('route_lc_list_description');
export const route_lc_list_btn_new = () => t('route_lc_list_btn_new');
export const route_lc_status_label = (p) => t(`route_lc_status_${p.status}`);
export const route_lc_list_empty_title = () => t('route_lc_list_empty_title');
export const route_lc_list_empty_description = () => t('route_lc_list_empty_description');
export const route_lc_list_aria = () => t('route_lc_list_aria');
export const route_lc_list_col_code = () => t('route_lc_list_col_code');
export const route_lc_list_col_status = () => t('route_lc_list_col_status');
export const route_lc_list_col_overhead = () => t('route_lc_list_col_overhead');
export const route_lc_list_col_created = () => t('route_lc_list_col_created');
// ─── Route: Stock entries (list) ─────────────────────────────────────
export const route_se_list_btn_new = () => t('route_se_list_btn_new');
export const route_se_list_empty_title = () => t('route_se_list_empty_title');
export const route_se_list_empty_description = () => t('route_se_list_empty_description');
export const route_se_list_col_type = () => t('route_se_list_col_type');
export const route_se_list_col_posting_at = () => t('route_se_list_col_posting_at');
export const route_se_list_col_lines = () => t('route_se_list_col_lines');
export const route_se_list_col_value = () => t('route_se_list_col_value');
export const route_se_list_col_status = () => t('route_se_list_col_status');
// ─── Route: Stock reconciliation (list + preview + cancel) ───────────
export const route_sr_severity_label = (p) => t(`route_sr_severity_${p.severity}`);
export const route_sr_list_description = () => t('route_sr_list_description');
export const route_sr_list_btn_new = () => t('route_sr_list_btn_new');
export const route_sr_list_empty_title = () => t('route_sr_list_empty_title');
export const route_sr_list_empty_description = () => t('route_sr_list_empty_description');
export const route_sr_list_aria = () => t('route_sr_list_aria');
export const route_sr_list_col_kind = () => t('route_sr_list_col_kind');
export const route_sr_list_col_warehouse = () => t('route_sr_list_col_warehouse');
export const route_sr_list_col_created = () => t('route_sr_list_col_created');
export const route_sr_list_col_lines = () => t('route_sr_list_col_lines');
export const route_sr_list_col_status = () => t('route_sr_list_col_status');
export const route_sr_btn_preview = () => t('route_sr_btn_preview');
export const route_sr_btn_cancel_doc = () => t('route_sr_btn_cancel_doc');
export const route_sr_preview_title = () => t('route_sr_preview_title');
export const route_sr_toast_submitted = () => t('route_sr_toast_submitted');
export const route_sr_toast_submit_failed = () => t('route_sr_toast_submit_failed');
export const route_sr_toast_cancelled = () => t('route_sr_toast_cancelled');
export const route_sr_toast_cancel_failed = () => t('route_sr_toast_cancel_failed');
export const route_sr_submit_btn = () => t('route_sr_submit_btn');
export const route_sr_summary_items_affected = () => t('route_sr_summary_items_affected');
export const route_sr_summary_total_variance = () => t('route_sr_summary_total_variance');
export const route_sr_summary_max_variance = () => t('route_sr_summary_max_variance');
export const route_sr_reasons_missing_alert = () => t('route_sr_reasons_missing_alert');
export const route_sr_preview_lines_aria = () => t('route_sr_preview_lines_aria');
export const route_sr_col_item = () => t('route_sr_col_item');
export const route_sr_col_batch = () => t('route_sr_col_batch');
export const route_sr_col_expected = () => t('route_sr_col_expected');
export const route_sr_col_physical = () => t('route_sr_col_physical');
export const route_sr_col_variance_qty = () => t('route_sr_col_variance_qty');
export const route_sr_col_variance_value = () => t('route_sr_col_variance_value');
export const route_sr_col_severity = () => t('route_sr_col_severity');
export const route_sr_cancel_title = () => t('route_sr_cancel_title');
export const route_sr_cancel_confirm = () => t('route_sr_cancel_confirm');
export const route_sr_cancel_approver_label = () => t('route_sr_cancel_approver_label');
export const route_sr_cancel_reason_label = () => t('route_sr_cancel_reason_label');
export const route_sr_cancel_reason_min_error = () => t('route_sr_cancel_reason_min_error');
// ─── Route: Warehouses tree ──────────────────────────────────────────
export const route_warehouses_description = () => t('route_warehouses_description');
export const route_warehouses_btn_bulk_import = () => t('route_warehouses_btn_bulk_import');
export const route_warehouses_btn_new = () => t('route_warehouses_btn_new');
export const route_warehouses_empty_title = () => t('route_warehouses_empty_title');
export const route_warehouses_empty_description = () => t('route_warehouses_empty_description');
export const route_warehouses_reason_label = (p) => t(`route_warehouses_reason_${p.reason}`);
export const route_warehouses_reason_fallback = () => t('route_warehouses_reason_fallback');
export const route_warehouses_announce_move_start = () => t('route_warehouses_announce_move_start');
export const route_warehouses_announce_moved = () => t('route_warehouses_announce_moved');
export const route_warehouses_announce_move_rejected = (p) => t('route_warehouses_announce_move_rejected', p);
export const route_warehouses_announce_move_impossible = (p) => t('route_warehouses_announce_move_impossible', p);
export const route_warehouses_announce_reorder_todo = () => t('route_warehouses_announce_reorder_todo');
export const route_warehouses_toast_deleted = () => t('route_warehouses_toast_deleted');
export const route_warehouses_toast_delete_failed = () => t('route_warehouses_toast_delete_failed');
export const route_warehouses_toast_delete_in_use_title = () => t('route_warehouses_toast_delete_in_use_title');
export const route_warehouses_toast_delete_in_use_body = () => t('route_warehouses_toast_delete_in_use_body');
export const route_warehouses_toast_unknown_error = () => t('route_warehouses_toast_unknown_error');
export const route_warehouses_loading = () => t('route_warehouses_loading');
export const route_warehouses_confirm_move_title = () => t('route_warehouses_confirm_move_title');
export const route_warehouses_confirm_move_btn = () => t('route_warehouses_confirm_move_btn');
export const route_warehouses_confirm_affected = () => t('route_warehouses_confirm_affected');
export const route_warehouses_confirm_new_path = (p) => t('route_warehouses_confirm_new_path', p);
export const route_warehouses_delete_title = () => t('route_warehouses_delete_title');
export const route_warehouses_delete_checking = () => t('route_warehouses_delete_checking');
export const route_warehouses_delete_has_children = (p) => t('route_warehouses_delete_has_children', p);
export const route_warehouses_delete_in_use = () => t('route_warehouses_delete_in_use');
export const route_warehouses_delete_impossible = () => t('route_warehouses_delete_impossible');
export const route_warehouses_delete_confirm_prefix = () => t('route_warehouses_delete_confirm_prefix');
export const route_warehouses_delete_confirm_suffix = () => t('route_warehouses_delete_confirm_suffix');
export const route_warehouses_delete_child_count = () => t('route_warehouses_delete_child_count');
export const route_warehouses_delete_sle_count = () => t('route_warehouses_delete_sle_count');
export const route_warehouses_delete_se_count = () => t('route_warehouses_delete_se_count');
export const route_warehouses_aria_collapse = () => t('route_warehouses_aria_collapse');
export const route_warehouses_aria_expand = () => t('route_warehouses_aria_expand');
export const route_warehouses_node_is_group = () => t('route_warehouses_node_is_group');
export const route_warehouses_hover_affected = (p) => t('route_warehouses_hover_affected', p);
export const route_warehouses_node_btn_add_child = () => t('route_warehouses_node_btn_add_child');
export const route_warehouses_node_btn_rename = () => t('route_warehouses_node_btn_rename');
export const route_warehouses_add_child_title = () => t('route_warehouses_add_child_title');
export const route_warehouses_add_root_title = () => t('route_warehouses_add_root_title');
export const route_warehouses_field_code = () => t('route_warehouses_field_code');
export const route_warehouses_field_name = () => t('route_warehouses_field_name');
export const route_warehouses_field_is_group = () => t('route_warehouses_field_is_group');
export const route_warehouses_rename_title = () => t('route_warehouses_rename_title');
// ─── Route: Work orders index ────────────────────────────────────────
export const route_wo_index_description = () => t('route_wo_index_description');
export const route_wo_index_btn_new = () => t('route_wo_index_btn_new');
export const route_wo_index_error_wo_not_found_title = () => t('route_wo_index_error_wo_not_found_title');
export const route_wo_index_error_wo_not_found_desc = () => t('route_wo_index_error_wo_not_found_desc');
export const route_wo_index_bulk_label = (p) => t(`route_wo_index_bulk_label_${p.action}`);
export const route_wo_warn_label = (p) => t(`route_wo_warn_${p.type}`);
export const route_wo_index_toast_bulk_result = (p) => t('route_wo_index_toast_bulk_result', p);
export const route_wo_index_toast_bulk_failed = () => t('route_wo_index_toast_bulk_failed');
export const route_wo_index_toast_reason_required = () => t('route_wo_index_toast_reason_required');
export const route_wo_index_toast_executor_required = () => t('route_wo_index_toast_executor_required');
export const route_wo_index_filter_state = () => t('route_wo_index_filter_state');
export const route_wo_state_label_list = (p) => t(`route_wo_state_${p.state}`);
export const route_wo_index_filter_all = () => t('route_wo_index_filter_all');
export const route_wo_index_filter_item = () => t('route_wo_index_filter_item');
export const route_wo_index_filter_start_mode_legend = () => t('route_wo_index_filter_start_mode_legend');
export const route_wo_index_filter_start_mode_planned = () => t('route_wo_index_filter_start_mode_planned');
export const route_wo_index_filter_start_mode_actual = () => t('route_wo_index_filter_start_mode_actual');
export const route_wo_index_filter_start_period = () => t('route_wo_index_filter_start_period');
export const route_wo_index_filter_executor = () => t('route_wo_index_filter_executor');
export const route_wo_index_filter_bom_version = () => t('route_wo_index_filter_bom_version');
export const route_wo_index_bulk_toolbar_aria = () => t('route_wo_index_bulk_toolbar_aria');
export const route_wo_index_bulk_selected_count = (p) => t('route_wo_index_bulk_selected_count', p);
export const route_wo_index_btn_release = () => t('route_wo_index_btn_release');
export const route_wo_index_btn_cancel = () => t('route_wo_index_btn_cancel');
export const route_wo_index_btn_reassign = () => t('route_wo_index_btn_reassign');
export const route_wo_index_btn_clear_selection = () => t('route_wo_index_btn_clear_selection');
export const route_wo_index_empty_title = () => t('route_wo_index_empty_title');
export const route_wo_index_empty_description = () => t('route_wo_index_empty_description');
export const route_wo_index_aria = () => t('route_wo_index_aria');
export const route_wo_index_col_code = () => t('route_wo_index_col_code');
export const route_wo_index_col_item = () => t('route_wo_index_col_item');
export const route_wo_index_col_qty = () => t('route_wo_index_col_qty');
export const route_wo_index_col_state = () => t('route_wo_index_col_state');
export const route_wo_index_col_plan_start = () => t('route_wo_index_col_plan_start');
export const route_wo_index_col_plan_end = () => t('route_wo_index_col_plan_end');
export const route_wo_index_col_executor = () => t('route_wo_index_col_executor');
export const route_wo_index_col_warn = () => t('route_wo_index_col_warn');
export const route_wo_index_warn_tooltip = (p) => t('route_wo_index_warn_tooltip', p);
export const route_wo_index_bulk_dialog_title = (p) => t('route_wo_index_bulk_dialog_title', p);
export const route_wo_index_bulk_reason_label = () => t('route_wo_index_bulk_reason_label');
export const route_wo_index_bulk_reason_placeholder = () => t('route_wo_index_bulk_reason_placeholder');
export const route_wo_index_bulk_new_executor_label = () => t('route_wo_index_bulk_new_executor_label');
export const route_wo_index_bulk_approver_label = () => t('route_wo_index_bulk_approver_label');
export const route_wo_index_bulk_no_signature_notice = () => t('route_wo_index_bulk_no_signature_notice');
export const route_wo_index_bulk_confirm_unsigned = () => t('route_wo_index_bulk_confirm_unsigned');
export const route_wo_index_bulk_approver_placeholder = () => t('route_wo_index_bulk_approver_placeholder');
export const route_wo_index_bulk_approver_hint = (p) => t('route_wo_index_bulk_approver_hint', p);
export const route_wo_index_bulk_approver_mixed_hint = (p) => t('route_wo_index_bulk_approver_mixed_hint', p);
export const route_wo_index_bulk_approver_unknown_hint = () => t('route_wo_index_bulk_approver_unknown_hint');
// ─── Route: Period close ─────────────────────────────────────────────
export const route_pc_description = () => t('route_pc_description');
export const route_pc_step_label = (p) => t(`route_pc_step_${p.step}`);
export const route_pc_select_heading = () => t('route_pc_select_heading');
export const route_pc_period_label = () => t('route_pc_period_label');
export const route_pc_period_hint = () => t('route_pc_period_hint');
export const route_pc_btn_next = () => t('route_pc_btn_next');
export const route_pc_btn_prev = () => t('route_pc_btn_prev');
export const route_pc_checklist_heading = (p) => t('route_pc_checklist_heading', p);
export const route_pc_loading = () => t('route_pc_loading');
export const route_pc_review_heading = () => t('route_pc_review_heading');
export const route_pc_review_period = () => t('route_pc_review_period');
export const route_pc_review_state = () => t('route_pc_review_state');
export const route_pc_review_blockers = () => t('route_pc_review_blockers');
export const route_pc_review_can_lock = () => t('route_pc_review_can_lock');
export const route_pc_yes = () => t('route_pc_yes');
export const route_pc_no = () => t('route_pc_no');
export const route_pc_note_label = () => t('route_pc_note_label');
export const route_pc_note_placeholder = () => t('route_pc_note_placeholder');
export const route_pc_btn_lock_tier2 = () => t('route_pc_btn_lock_tier2');
export const route_pc_blocked_warn = () => t('route_pc_blocked_warn');
export const route_pc_locked_title = () => t('route_pc_locked_title');
export const route_pc_toast_locked = () => t('route_pc_toast_locked');
export const route_pc_toast_lock_failed = () => t('route_pc_toast_lock_failed');
export const route_pc_locked_period = () => t('route_pc_locked_period');
export const route_pc_locked_at = () => t('route_pc_locked_at');
export const route_pc_locked_by = () => t('route_pc_locked_by');
export const route_pc_audit_heading = () => t('route_pc_audit_heading');
export const route_pc_audit_empty = () => t('route_pc_audit_empty');
export const route_pc_already_locked_prefix = () => t('route_pc_already_locked_prefix');
export const route_pc_already_locked_link = () => t('route_pc_already_locked_link');
export const route_pc_stepper_aria = () => t('route_pc_stepper_aria');
export const route_pc_blockers_ok = () => t('route_pc_blockers_ok');
export const route_pc_blocker_deeplink = () => t('route_pc_blocker_deeplink');
// ─── Route: Reconciliation workspace ─────────────────────────────────
export const route_recon_description = () => t('route_recon_description');
export const route_recon_btn_run = () => t('route_recon_btn_run');
export const route_recon_btn_create_doc = () => t('route_recon_btn_create_doc');
export const route_recon_intro_aria = () => t('route_recon_intro_aria');
export const route_recon_intro_heading = () => t('route_recon_intro_heading');
export const route_recon_intro_track_agent_title = () => t('route_recon_intro_track_agent_title');
export const route_recon_intro_track_agent_body = () => t('route_recon_intro_track_agent_body');
export const route_recon_intro_track_manual_title = () => t('route_recon_intro_track_manual_title');
export const route_recon_intro_track_manual_body = () => t('route_recon_intro_track_manual_body');
export const route_recon_filter_period = () => t('route_recon_filter_period');
export const route_recon_metric_items = () => t('route_recon_metric_items');
export const route_recon_metric_value = () => t('route_recon_metric_value');
export const route_recon_metric_variance = () => t('route_recon_metric_variance');
export const route_recon_agent_col_aria = () => t('route_recon_agent_col_aria');
export const route_recon_agent_draft_title = () => t('route_recon_agent_draft_title');
export const route_recon_agent_empty_title = () => t('route_recon_agent_empty_title');
export const route_recon_agent_empty_description = () => t('route_recon_agent_empty_description');
export const route_recon_data_col_aria = () => t('route_recon_data_col_aria');
export const route_recon_data_heading = () => t('route_recon_data_heading');
export const route_recon_data_empty_title = () => t('route_recon_data_empty_title');
export const route_recon_data_empty_description = () => t('route_recon_data_empty_description');
export const route_recon_data_col_wh = () => t('route_recon_data_col_wh');
export const route_recon_data_col_lines = () => t('route_recon_data_col_lines');
export const route_recon_data_col_variance = () => t('route_recon_data_col_variance');
export const route_recon_data_col_status = () => t('route_recon_data_col_status');
export const route_recon_actions_col_aria = () => t('route_recon_actions_col_aria');
export const route_recon_actions_heading = () => t('route_recon_actions_heading');
export const route_recon_action_create_doc = () => t('route_recon_action_create_doc');
export const route_recon_action_new_run = () => t('route_recon_action_new_run');
export const route_recon_action_conflicts = () => t('route_recon_action_conflicts');
export const route_recon_action_anomalies = () => t('route_recon_action_anomalies');
// ─── Route: Excel browser ────────────────────────────────────────────
export const route_excel_description = () => t('route_excel_description');
export const route_excel_btn_download_latest = () => t('route_excel_btn_download_latest');
export const route_excel_btn_publish = () => t('route_excel_btn_publish');
export const route_excel_filter_period = () => t('route_excel_filter_period');
export const route_excel_job_status = (p) => t('route_excel_job_status', p);
export const route_excel_empty_title = () => t('route_excel_empty_title');
export const route_excel_empty_description = () => t('route_excel_empty_description');
export const route_excel_aria = () => t('route_excel_aria');
export const route_excel_col_sha = () => t('route_excel_col_sha');
export const route_excel_col_created = () => t('route_excel_col_created');
export const route_excel_col_by = () => t('route_excel_col_by');
export const route_excel_col_size = () => t('route_excel_col_size');
export const route_excel_size_kb = (p) => t('route_excel_size_kb', p);
export const route_excel_col_schema = () => t('route_excel_col_schema');
export const route_excel_col_actions = () => t('route_excel_col_actions');
export const route_excel_btn_download = () => t('route_excel_btn_download');
export const route_excel_btn_upload_edits = () => t('route_excel_btn_upload_edits');
export const route_excel_upload_title = () => t('route_excel_upload_title');
export const route_excel_upload_analyze = () => t('route_excel_upload_analyze');
export const route_excel_upload_hint = (p) => t('route_excel_upload_hint', p);
export const route_excel_file_input_aria = () => t('route_excel_file_input_aria');
export const route_excel_file_size_row = (p) => t('route_excel_file_size_row', p);
export const route_excel_section_review = () => t('route_excel_section_review');
export const route_excel_publish_confirm_title = () => t('route_excel_publish_confirm_title');
export const route_excel_publish_confirm_desc = (p) => t('route_excel_publish_confirm_desc', p);
export const route_excel_publish_confirm_btn = () => t('route_excel_publish_confirm_btn');
export const route_excel_toast_publish_started = () => t('route_excel_toast_publish_started');
export const route_excel_toast_publish_failed = () => t('route_excel_toast_publish_failed');
export const route_excel_toast_analyzed = () => t('route_excel_toast_analyzed');
export const route_excel_toast_analyzed_desc = (p) => t('route_excel_toast_analyzed_desc', p);
export const route_excel_toast_upload_failed = () => t('route_excel_toast_upload_failed');
export const route_excel_toast_download_failed = () => t('route_excel_toast_download_failed');
// ─── Route: Batches list ─────────────────────────────────────────────
export const route_batches_transition_label = (p) => t(`route_batches_transition_${p.transition.replace(/-/g, '_')}`);
export const route_batches_toast_deleted = () => t('route_batches_toast_deleted');
export const route_batches_toast_delete_conflict_title = () => t('route_batches_toast_delete_conflict_title');
export const route_batches_toast_delete_conflict_body = () => t('route_batches_toast_delete_conflict_body');
export const route_batches_toast_delete_failed = () => t('route_batches_toast_delete_failed');
export const route_batches_toast_unknown_error = () => t('route_batches_toast_unknown_error');
export const route_batches_btn_recall_selected = (p) => t('route_batches_btn_recall_selected', p);
export const route_batches_btn_bulk_import = () => t('route_batches_btn_bulk_import');
export const route_batches_btn_new = () => t('route_batches_btn_new');
export const route_batches_filter_item_id = () => t('route_batches_filter_item_id');
export const route_batches_filter_item_placeholder = () => t('route_batches_filter_item_placeholder');
export const route_batches_filter_expiring_days = () => t('route_batches_filter_expiring_days');
export const route_batches_filter_sort_expiry = () => t('route_batches_filter_sort_expiry');
export const route_batches_sort_none = () => t('route_batches_sort_none');
export const route_batches_sort_asc = () => t('route_batches_sort_asc');
export const route_batches_sort_desc = () => t('route_batches_sort_desc');
export const route_batches_filter_expired_only = () => t('route_batches_filter_expired_only');
export const route_batches_empty_title = () => t('route_batches_empty_title');
export const route_batches_empty_description = () => t('route_batches_empty_description');
export const route_batches_empty_btn_create = () => t('route_batches_empty_btn_create');
export const route_batches_aria = () => t('route_batches_aria');
export const route_batches_aria_select = (p) => t('route_batches_aria_select', p);
export const route_batches_col_no = () => t('route_batches_col_no');
export const route_batches_col_item = () => t('route_batches_col_item');
export const route_batches_col_mfg = () => t('route_batches_col_mfg');
export const route_batches_col_exp = () => t('route_batches_col_exp');
export const route_batches_col_qc = () => t('route_batches_col_qc');
export const route_batches_col_actions = () => t('route_batches_col_actions');
export const route_batches_aria_edit = (p) => t('route_batches_aria_edit', p);
export const route_batches_aria_delete = (p) => t('route_batches_aria_delete', p);
export const route_batches_btn_history = () => t('route_batches_btn_history');
export const route_batches_transition_dialog_title = (p) => t('route_batches_transition_dialog_title', p);
export const route_batches_dialog_confirm = () => t('route_batches_dialog_confirm');
export const route_batches_field_reason_label = () => t('route_batches_field_reason_label');
export const route_batches_field_approver_label = () => t('route_batches_field_approver_label');
export const route_batches_history_title = () => t('route_batches_history_title');
export const route_batches_delete_title = () => t('route_batches_delete_title');
export const route_batches_delete_checking = () => t('route_batches_delete_checking');
export const route_batches_delete_confirm_prefix = () => t('route_batches_delete_confirm_prefix');
export const route_batches_delete_in_use = () => t('route_batches_delete_in_use');
export const route_batches_delete_sle_count = () => t('route_batches_delete_sle_count');
export const route_batches_delete_se_count = () => t('route_batches_delete_se_count');
export const route_batches_recall_title = (p) => t('route_batches_recall_title', p);
export const route_batches_recall_close = () => t('route_batches_recall_close');
export const route_batches_recall_btn = () => t('route_batches_recall_btn');
export const route_batches_recall_intro = () => t('route_batches_recall_intro');
export const route_batches_recall_reason_label = () => t('route_batches_recall_reason_label');
export const route_batches_recall_approver_label = () => t('route_batches_recall_approver_label');
export const route_batches_recall_stats = (p) => t('route_batches_recall_stats', p);
export const route_batches_recall_failed_heading = (p) => t('route_batches_recall_failed_heading', p);
export const route_batches_recall_failed_unknown = () => t('route_batches_recall_failed_unknown');
export const route_batches_history_loading = () => t('route_batches_history_loading');
export const route_batches_history_empty = () => t('route_batches_history_empty');
export const route_batches_qc_badge_label = (p) => t(`route_batches_qc_badge_${p.status}`);
// ─── Route: Landed cost (new form) ───────────────────────────────────
export const route_lc_new_err_title_required = () => t('route_lc_new_err_title_required');
export const route_lc_new_err_amount_required = () => t('route_lc_new_err_amount_required');
export const route_lc_new_err_amount_format = () => t('route_lc_new_err_amount_format');
export const route_lc_new_err_amount_gt_zero = () => t('route_lc_new_err_amount_gt_zero');
export const route_lc_new_err_source_id_required = () => t('route_lc_new_err_source_id_required');
export const route_lc_new_err_source_min = () => t('route_lc_new_err_source_min');
export const route_lc_new_err_charges_min = () => t('route_lc_new_err_charges_min');
export const route_lc_new_title = () => t('route_lc_new_title');
export const route_lc_new_description = () => t('route_lc_new_description');
export const route_lc_new_posting_at_label = () => t('route_lc_new_posting_at_label');
export const route_lc_new_currency_label = () => t('route_lc_new_currency_label');
export const route_lc_new_sources_heading = () => t('route_lc_new_sources_heading');
export const route_lc_new_source_item_label = (p) => t('route_lc_new_source_item_label', p);
export const route_lc_new_source_item_placeholder = () => t('route_lc_new_source_item_placeholder');
export const route_lc_new_aria_remove_source = () => t('route_lc_new_aria_remove_source');
export const route_lc_new_btn_add_source = () => t('route_lc_new_btn_add_source');
export const route_lc_new_charges_heading = () => t('route_lc_new_charges_heading');
export const route_lc_new_charge_title_label = () => t('route_lc_new_charge_title_label');
export const route_lc_new_charge_title_placeholder = () => t('route_lc_new_charge_title_placeholder');
export const route_lc_new_charge_amount_label = () => t('route_lc_new_charge_amount_label');
export const route_lc_new_charge_currency_label = () => t('route_lc_new_charge_currency_label');
export const route_lc_new_charge_basis_label = () => t('route_lc_new_charge_basis_label');
export const route_lc_new_basis_label = (p) => t(`route_lc_new_basis_${p.basis}`);
export const route_lc_new_aria_remove_charge = () => t('route_lc_new_aria_remove_charge');
export const route_lc_new_btn_add_charge = () => t('route_lc_new_btn_add_charge');
export const route_lc_new_notes_label = () => t('route_lc_new_notes_label');
export const route_lc_new_btn_create = () => t('route_lc_new_btn_create');
export const route_lc_new_toast_created = () => t('route_lc_new_toast_created');
export const route_lc_new_toast_failed = () => t('route_lc_new_toast_failed');
// ─── Route: Stock reconciliation (new wizard) ────────────────────────
export const route_sr_new_variance_label = (p) => t(`route_sr_new_variance_${p.reason}`);
export const route_sr_new_err_item_required = () => t('route_sr_new_err_item_required');
export const route_sr_new_err_warehouse_required = () => t('route_sr_new_err_warehouse_required');
export const route_sr_new_title_opening = () => t('route_sr_new_title_opening');
export const route_sr_new_title_standard = () => t('route_sr_new_title_standard');
export const route_sr_new_description = () => t('route_sr_new_description');
export const route_sr_new_stepper_aria = () => t('route_sr_new_stepper_aria');
export const route_sr_new_step_label = (p) => t(`route_sr_new_step_${p.step}`);
export const route_sr_new_params_heading = () => t('route_sr_new_params_heading');
export const route_sr_new_onboarding_title = () => t('route_sr_new_onboarding_title');
export const route_sr_new_onboarding_body = () => t('route_sr_new_onboarding_body');
export const route_sr_new_field_kind = () => t('route_sr_new_field_kind');
export const route_sr_new_kind_standard = () => t('route_sr_new_kind_standard');
export const route_sr_new_kind_opening_stock = () => t('route_sr_new_kind_opening_stock');
export const route_sr_new_opening_already_set = () => t('route_sr_new_opening_already_set');
export const route_sr_new_field_warehouse = () => t('route_sr_new_field_warehouse');
export const route_sr_new_wh_placeholder = () => t('route_sr_new_wh_placeholder');
export const route_sr_new_field_period = () => t('route_sr_new_field_period');
export const route_sr_new_period_hint = () => t('route_sr_new_period_hint');
export const route_sr_new_lines_heading = () => t('route_sr_new_lines_heading');
export const route_sr_new_lines_hint = () => t('route_sr_new_lines_hint');
export const route_sr_new_col_item = () => t('route_sr_new_col_item');
export const route_sr_new_col_batch = () => t('route_sr_new_col_batch');
export const route_sr_new_col_expected = () => t('route_sr_new_col_expected');
export const route_sr_new_col_actual = () => t('route_sr_new_col_actual');
export const route_sr_new_col_variance = () => t('route_sr_new_col_variance');
export const route_sr_new_col_uom = () => t('route_sr_new_col_uom');
export const route_sr_new_col_reason = () => t('route_sr_new_col_reason');
export const route_sr_new_col_remove_aria = () => t('route_sr_new_col_remove_aria');
export const route_sr_new_row_remove_aria = () => t('route_sr_new_row_remove_aria');
export const route_sr_new_btn_add_row = () => t('route_sr_new_btn_add_row');
export const route_sr_new_notes_label = () => t('route_sr_new_notes_label');
export const route_sr_new_notes_placeholder = () => t('route_sr_new_notes_placeholder');
export const route_sr_new_preview_heading = () => t('route_sr_new_preview_heading');
export const route_sr_new_summary_rows = () => t('route_sr_new_summary_rows');
export const route_sr_new_summary_total_variance = () => t('route_sr_new_summary_total_variance');
export const route_sr_new_summary_severity_map = () => t('route_sr_new_summary_severity_map');
export const route_sr_new_severity_match_chip = (p) => t('route_sr_new_severity_match', p);
export const route_sr_new_severity_warn_chip = (p) => t('route_sr_new_severity_warn', p);
export const route_sr_new_severity_danger_chip = (p) => t('route_sr_new_severity_danger', p);
export const route_sr_new_preview_footnote = () => t('route_sr_new_preview_footnote');
export const route_sr_new_draft_created_heading = () => t('route_sr_new_draft_created_heading');
export const route_sr_new_saving_hint = () => t('route_sr_new_saving_hint');
export const route_sr_new_save_failed = () => t('route_sr_new_save_failed');
export const route_sr_new_preview_items_affected = () => t('route_sr_new_preview_items_affected');
export const route_sr_new_preview_total_variance = () => t('route_sr_new_preview_total_variance');
export const route_sr_new_preview_max_variance = () => t('route_sr_new_preview_max_variance');
export const route_sr_new_preview_lines_aria = () => t('route_sr_new_preview_lines_aria');
export const route_sr_new_toast_draft_created = () => t('route_sr_new_toast_draft_created');
export const route_sr_new_toast_draft_created_desc = () => t('route_sr_new_toast_draft_created_desc');
export const route_sr_new_toast_create_failed = () => t('route_sr_new_toast_create_failed');
export const route_sr_new_btn_to_list = () => t('route_sr_new_btn_to_list');
export const route_sr_new_btn_done = () => t('route_sr_new_btn_done');
export const route_se_new_type_material_receipt_title = () => t('route_se_new_type_material_receipt_title');
export const route_se_new_type_material_receipt_desc = () => t('route_se_new_type_material_receipt_desc');
export const route_se_new_type_material_issue_title = () => t('route_se_new_type_material_issue_title');
export const route_se_new_type_material_issue_desc = () => t('route_se_new_type_material_issue_desc');
export const route_se_new_type_material_transfer_title = () => t('route_se_new_type_material_transfer_title');
export const route_se_new_type_material_transfer_desc = () => t('route_se_new_type_material_transfer_desc');
export const route_se_new_type_manufacture_title = () => t('route_se_new_type_manufacture_title');
export const route_se_new_type_manufacture_desc = () => t('route_se_new_type_manufacture_desc');
export const route_se_new_type_repack_title = () => t('route_se_new_type_repack_title');
export const route_se_new_type_repack_desc = () => t('route_se_new_type_repack_desc');
export const route_se_new_type_wip_transfer_title = () => t('route_se_new_type_wip_transfer_title');
export const route_se_new_type_wip_transfer_desc = () => t('route_se_new_type_wip_transfer_desc');
export const route_se_new_err_item_required = () => t('route_se_new_err_item_required');
export const route_se_new_err_qty_required = () => t('route_se_new_err_qty_required');
export const route_se_new_err_qty_format = () => t('route_se_new_err_qty_format');
export const route_se_new_err_uom_required = () => t('route_se_new_err_uom_required');
export const route_se_new_err_date_required = () => t('route_se_new_err_date_required');
export const route_se_new_err_lines_min = () => t('route_se_new_err_lines_min');
export const route_se_new_title = () => t('route_se_new_title');
export const route_se_new_subtitle = (p) => t('route_se_new_subtitle', p);
export const route_se_new_subtitle_type_placeholder = () => t('route_se_new_subtitle_type_placeholder');
export const route_se_new_step1_heading = () => t('route_se_new_step1_heading');
export const route_se_new_typegroup_aria = () => t('route_se_new_typegroup_aria');
export const route_se_new_posting_at_label = () => t('route_se_new_posting_at_label');
export const route_se_new_supplier_label = () => t('route_se_new_supplier_label');
export const route_se_new_supplier_placeholder = () => t('route_se_new_supplier_placeholder');
export const route_se_new_supplier_hint = () => t('route_se_new_supplier_hint');
export const route_se_new_cost_center_label = () => t('route_se_new_cost_center_label');
export const route_se_new_cost_center_placeholder = () => t('route_se_new_cost_center_placeholder');
export const route_se_new_wo_label = () => t('route_se_new_wo_label');
export const route_se_new_wo_placeholder = () => t('route_se_new_wo_placeholder');
export const route_se_new_wo_hint = () => t('route_se_new_wo_hint');
export const route_se_new_strict_expiry_label = () => t('route_se_new_strict_expiry_label');
export const route_se_new_lines_heading = () => t('route_se_new_lines_heading');
export const route_se_new_line_item_label = () => t('route_se_new_line_item_label');
export const route_se_new_line_source_label = () => t('route_se_new_line_source_label');
export const route_se_new_line_target_label = () => t('route_se_new_line_target_label');
export const route_se_new_line_qty_label = () => t('route_se_new_line_qty_label');
export const route_se_new_line_uom_label = () => t('route_se_new_line_uom_label');
export const route_se_new_line_batch_label = () => t('route_se_new_line_batch_label');
export const route_se_new_line_batch_placeholder = () => t('route_se_new_line_batch_placeholder');
export const route_se_new_line_batch_hint_strict = () => t('route_se_new_line_batch_hint_strict');
export const route_se_new_line_batch_hint_loose = () => t('route_se_new_line_batch_hint_loose');
export const route_se_new_line_serial_label = () => t('route_se_new_line_serial_label');
export const route_se_new_line_serials_label = () => t('route_se_new_line_serials_label');
export const route_se_new_aria_remove_line = () => t('route_se_new_aria_remove_line');
export const route_se_new_btn_add_line = () => t('route_se_new_btn_add_line');
export const route_se_new_notes_label = () => t('route_se_new_notes_label');
export const route_se_new_btn_preview = () => t('route_se_new_btn_preview');
export const route_se_new_step3_heading = () => t('route_se_new_step3_heading');
export const route_se_new_preview_loading = () => t('route_se_new_preview_loading');
export const route_se_new_preview_aria = () => t('route_se_new_preview_aria');
export const route_se_new_preview_empty = () => t('route_se_new_preview_empty');
export const route_se_new_col_item = () => t('route_se_new_col_item');
export const route_se_new_col_wh = () => t('route_se_new_col_wh');
export const route_se_new_col_batch = () => t('route_se_new_col_batch');
export const route_se_new_col_qty_delta = () => t('route_se_new_col_qty_delta');
export const route_se_new_col_rate = () => t('route_se_new_col_rate');
export const route_se_new_col_value_delta = () => t('route_se_new_col_value_delta');
export const route_se_new_violations_heading = () => t('route_se_new_violations_heading');
export const route_se_new_violation_row = (p) => t('route_se_new_violation_row', p);
export const route_se_new_violation_blocked_suffix = () => t('route_se_new_violation_blocked_suffix');
export const route_se_new_violation_allowed_suffix = () => t('route_se_new_violation_allowed_suffix');
export const route_se_new_warnings_heading = () => t('route_se_new_warnings_heading');
export const route_se_new_warning_block_suffix = () => t('route_se_new_warning_block_suffix');
export const route_se_new_btn_submit = () => t('route_se_new_btn_submit');
export const route_se_new_toast_draft_failed = () => t('route_se_new_toast_draft_failed');
export const route_se_new_toast_submitted = () => t('route_se_new_toast_submitted');
export const route_se_new_toast_submit_failed = () => t('route_se_new_toast_submit_failed');
export const route_se_new_uom_pcs = () => t('route_se_new_uom_pcs');
export const route_se_new_uom_kg = () => t('route_se_new_uom_kg');
export const route_se_new_uom_m = () => t('route_se_new_uom_m');
export const route_se_new_uom_m2 = () => t('route_se_new_uom_m2');
export const route_se_new_uom_l = () => t('route_se_new_uom_l');
export const route_se_new_btn_back = () => t('route_se_new_btn_back');
export const route_wo_new_err_qty_required = () => t('route_wo_new_err_qty_required');
export const route_wo_new_err_qty_format = () => t('route_wo_new_err_qty_format');
export const route_wo_new_err_qty_positive = () => t('route_wo_new_err_qty_positive');
export const route_wo_new_err_item_required = () => t('route_wo_new_err_item_required');
export const route_wo_new_err_bom_required = () => t('route_wo_new_err_bom_required');
export const route_wo_new_err_end_after_start = () => t('route_wo_new_err_end_after_start');
export const route_wo_new_toast_created = (p) => t('route_wo_new_toast_created', p);
export const route_wo_new_toast_create_failed = () => t('route_wo_new_toast_create_failed');
export const route_wo_new_title = () => t('route_wo_new_title');
export const route_wo_new_description = () => t('route_wo_new_description');
export const route_wo_new_item_label = () => t('route_wo_new_item_label');
export const route_wo_new_bom_label = () => t('route_wo_new_bom_label');
export const route_wo_new_bom_hint = () => t('route_wo_new_bom_hint');
export const route_wo_new_qty_label = () => t('route_wo_new_qty_label');
export const route_wo_new_planned_start_label = () => t('route_wo_new_planned_start_label');
export const route_wo_new_planned_end_label = () => t('route_wo_new_planned_end_label');
export const route_wo_new_executor_label = () => t('route_wo_new_executor_label');
export const route_wo_new_source_wh_label = () => t('route_wo_new_source_wh_label');
export const route_wo_new_source_wh_hint = () => t('route_wo_new_source_wh_hint');
export const route_wo_new_wip_wh_label = () => t('route_wo_new_wip_wh_label');
export const route_wo_new_fg_wh_label = () => t('route_wo_new_fg_wh_label');
export const route_wo_new_scrap_wh_label = () => t('route_wo_new_scrap_wh_label');
export const route_wo_new_scrap_wh_hint = () => t('route_wo_new_scrap_wh_hint');
export const route_wo_new_notes_label = () => t('route_wo_new_notes_label');
export const route_wo_new_notes_placeholder = () => t('route_wo_new_notes_placeholder');
export const route_wo_new_stay_on_page = () => t('route_wo_new_stay_on_page');
export const route_wo_new_btn_submit = () => t('route_wo_new_btn_submit');
export const route_wo_new_btn_open = () => t('route_wo_new_btn_open');
export const route_wo_new_preview_heading = () => t('route_wo_new_preview_heading');
export const route_wo_new_preview_insufficient = () => t('route_wo_new_preview_insufficient');
export const route_wo_new_preview_table_aria = () => t('route_wo_new_preview_table_aria');
export const route_wo_new_col_material = () => t('route_wo_new_col_material');
export const route_wo_new_col_required = () => t('route_wo_new_col_required');
export const route_wo_new_col_available = () => t('route_wo_new_col_available');
export const route_wo_new_col_shortage = () => t('route_wo_new_col_shortage');
export const route_wo_new_warnings_heading = () => t('route_wo_new_warnings_heading');
export const route_bom_new_err_required = () => t('route_bom_new_err_required');
export const route_bom_new_err_format = () => t('route_bom_new_err_format');
export const route_bom_new_err_item_required = () => t('route_bom_new_err_item_required');
export const route_bom_new_err_operation_name_required = () => t('route_bom_new_err_operation_name_required');
export const route_bom_new_err_code_required = () => t('route_bom_new_err_code_required');
export const route_bom_new_err_name_required = () => t('route_bom_new_err_name_required');
export const route_bom_new_err_fg_required = () => t('route_bom_new_err_fg_required');
export const route_bom_new_err_materials_min = () => t('route_bom_new_err_materials_min');
export const route_bom_new_uom_pcs = () => t('route_bom_new_uom_pcs');
export const route_bom_new_uom_kg = () => t('route_bom_new_uom_kg');
export const route_bom_new_uom_g = () => t('route_bom_new_uom_g');
export const route_bom_new_uom_m = () => t('route_bom_new_uom_m');
export const route_bom_new_uom_m2 = () => t('route_bom_new_uom_m2');
export const route_bom_new_uom_l = () => t('route_bom_new_uom_l');
export const route_bom_new_uom_set = () => t('route_bom_new_uom_set');
export const route_bom_new_toast_created = () => t('route_bom_new_toast_created');
export const route_bom_new_toast_create_failed = () => t('route_bom_new_toast_create_failed');
export const route_bom_new_title = () => t('route_bom_new_title');
export const route_bom_new_description = () => t('route_bom_new_description');
export const route_bom_new_stepper_aria = () => t('route_bom_new_stepper_aria');
export const route_bom_new_step1_label = () => t('route_bom_new_step1_label');
export const route_bom_new_step2_label = () => t('route_bom_new_step2_label');
export const route_bom_new_step3_label = () => t('route_bom_new_step3_label');
export const route_bom_new_step1_heading = () => t('route_bom_new_step1_heading');
export const route_bom_new_field_code_label = () => t('route_bom_new_field_code_label');
export const route_bom_new_field_code_placeholder = () => t('route_bom_new_field_code_placeholder');
export const route_bom_new_field_code_hint = () => t('route_bom_new_field_code_hint');
export const route_bom_new_field_name_label = () => t('route_bom_new_field_name_label');
export const route_bom_new_field_name_placeholder = () => t('route_bom_new_field_name_placeholder');
export const route_bom_new_field_fg_label = () => t('route_bom_new_field_fg_label');
export const route_bom_new_field_output_qty_label = () => t('route_bom_new_field_output_qty_label');
export const route_bom_new_field_output_qty_hint = () => t('route_bom_new_field_output_qty_hint');
export const route_bom_new_field_uom_label = () => t('route_bom_new_field_uom_label');
export const route_bom_new_field_version_label = () => t('route_bom_new_field_version_label');
export const route_bom_new_field_version_hint = () => t('route_bom_new_field_version_hint');
export const route_bom_new_materials_heading = () => t('route_bom_new_materials_heading');
export const route_bom_new_col_item = () => t('route_bom_new_col_item');
export const route_bom_new_col_qty = () => t('route_bom_new_col_qty');
export const route_bom_new_col_uom = () => t('route_bom_new_col_uom');
export const route_bom_new_col_scrap = () => t('route_bom_new_col_scrap');
export const route_bom_new_col_alt = () => t('route_bom_new_col_alt');
export const route_bom_new_alt_label = () => t('route_bom_new_alt_label');
export const route_bom_new_btn_remove = () => t('route_bom_new_btn_remove');
export const route_bom_new_btn_add_material = () => t('route_bom_new_btn_add_material');
export const route_bom_new_operations_heading = () => t('route_bom_new_operations_heading');
export const route_bom_new_operations_hint = () => t('route_bom_new_operations_hint');
export const route_bom_new_col_op_name = () => t('route_bom_new_col_op_name');
export const route_bom_new_col_time = () => t('route_bom_new_col_time');
export const route_bom_new_col_station = () => t('route_bom_new_col_station');
export const route_bom_new_col_cost = () => t('route_bom_new_col_cost');
export const route_bom_new_op_name_placeholder = () => t('route_bom_new_op_name_placeholder');
export const route_bom_new_workstation_placeholder = () => t('route_bom_new_workstation_placeholder');
export const route_bom_new_btn_add_operation = () => t('route_bom_new_btn_add_operation');
export const route_bom_new_review_heading = () => t('route_bom_new_review_heading');
export const route_bom_new_review_code = () => t('route_bom_new_review_code');
export const route_bom_new_review_name = () => t('route_bom_new_review_name');
export const route_bom_new_review_output_qty = () => t('route_bom_new_review_output_qty');
export const route_bom_new_review_version = () => t('route_bom_new_review_version');
export const route_bom_new_review_materials = () => t('route_bom_new_review_materials');
export const route_bom_new_review_operations = () => t('route_bom_new_review_operations');
export const route_bom_new_review_draft_notice_prefix = () => t('route_bom_new_review_draft_notice_prefix');
export const route_bom_new_review_draft_notice_emphasis = () => t('route_bom_new_review_draft_notice_emphasis');
export const route_bom_new_review_draft_notice_suffix = () => t('route_bom_new_review_draft_notice_suffix');
export const route_bom_new_btn_back = () => t('route_bom_new_btn_back');
export const route_bom_new_btn_cancel = () => t('route_bom_new_btn_cancel');
export const route_bom_new_btn_next = () => t('route_bom_new_btn_next');
export const route_bom_new_btn_submit = () => t('route_bom_new_btn_submit');
export const route_runs_detail_title_fallback = () => t('route_runs_detail_title_fallback');
export const route_runs_detail_title_with_id = (p) => t('route_runs_detail_title_with_id', p);
export const route_runs_detail_description = (p) => t('route_runs_detail_description', p);
export const route_runs_detail_btn_approve = () => t('route_runs_detail_btn_approve');
export const route_runs_detail_btn_reject = () => t('route_runs_detail_btn_reject');
export const route_runs_detail_tab_source_conflicts = () => t('route_runs_detail_tab_source_conflicts');
export const route_runs_detail_tab_anomalies = () => t('route_runs_detail_tab_anomalies');
export const route_runs_detail_tab_recommended_actions = () => t('route_runs_detail_tab_recommended_actions');
export const route_runs_detail_tab_evidence = () => t('route_runs_detail_tab_evidence');
export const route_runs_detail_tab_observability = () => t('route_runs_detail_tab_observability');
export const route_runs_detail_tab_activity_log = () => t('route_runs_detail_tab_activity_log');
export const route_runs_detail_tab_inventory_result = () => t('route_runs_detail_tab_inventory_result');
export const route_runs_detail_tab_writeoff_result = () => t('route_runs_detail_tab_writeoff_result');
export const route_runs_detail_tab_cost_result = () => t('route_runs_detail_tab_cost_result');
export const route_runs_detail_empty_conflicts = () => t('route_runs_detail_empty_conflicts');
export const route_runs_detail_empty_anomalies = () => t('route_runs_detail_empty_anomalies');
export const route_runs_detail_empty_recommendations = () => t('route_runs_detail_empty_recommendations');
export const route_runs_detail_empty_evidence = () => t('route_runs_detail_empty_evidence');
export const route_runs_detail_tier_suffix = (p) => t('route_runs_detail_tier_suffix', p);
export const route_runs_detail_approve_title = () => t('route_runs_detail_approve_title');
export const route_runs_detail_approve_description = () => t('route_runs_detail_approve_description');
export const route_runs_detail_approve_input_label = () => t('route_runs_detail_approve_input_label');
export const route_runs_detail_approve_input_placeholder = () => t('route_runs_detail_approve_input_placeholder');
export const route_runs_detail_reject_title = () => t('route_runs_detail_reject_title');
export const route_runs_detail_reject_description = () => t('route_runs_detail_reject_description');
export const route_runs_detail_reject_input_label = () => t('route_runs_detail_reject_input_label');
export const route_runs_detail_activity_aria = () => t('route_runs_detail_activity_aria');
export const route_runs_detail_col_time = () => t('route_runs_detail_col_time');
export const route_runs_detail_col_event = () => t('route_runs_detail_col_event');
export const route_runs_detail_col_payload = () => t('route_runs_detail_col_payload');
export const route_runs_detail_col_trace = () => t('route_runs_detail_col_trace');
export const route_lc_detail_title_fallback = () => t('route_lc_detail_title_fallback');
export const route_lc_detail_title_with_code = (p) => t('route_lc_detail_title_with_code', p);
export const route_lc_detail_description = (p) => t('route_lc_detail_description', p);
export const route_lc_detail_btn_submit = () => t('route_lc_detail_btn_submit');
export const route_lc_detail_btn_cancel = () => t('route_lc_detail_btn_cancel');
export const route_lc_detail_charges_heading = () => t('route_lc_detail_charges_heading');
export const route_lc_detail_charges_aria = () => t('route_lc_detail_charges_aria');
export const route_lc_detail_col_title = () => t('route_lc_detail_col_title');
export const route_lc_detail_col_amount = () => t('route_lc_detail_col_amount');
export const route_lc_detail_col_currency = () => t('route_lc_detail_col_currency');
export const route_lc_detail_col_basis = () => t('route_lc_detail_col_basis');
export const route_lc_detail_sources_heading = () => t('route_lc_detail_sources_heading');
export const route_lc_detail_sources_aria = () => t('route_lc_detail_sources_aria');
export const route_lc_detail_sources_empty = () => t('route_lc_detail_sources_empty');
export const route_lc_detail_col_entry = () => t('route_lc_detail_col_entry');
export const route_lc_detail_col_item = () => t('route_lc_detail_col_item');
export const route_lc_detail_col_qty = () => t('route_lc_detail_col_qty');
export const route_lc_detail_col_weight = () => t('route_lc_detail_col_weight');
export const route_lc_detail_allocations_heading = () => t('route_lc_detail_allocations_heading');
export const route_lc_detail_allocations_aria = () => t('route_lc_detail_allocations_aria');
export const route_lc_detail_allocations_empty = () => t('route_lc_detail_allocations_empty');
export const route_lc_detail_col_prev_rate = () => t('route_lc_detail_col_prev_rate');
export const route_lc_detail_col_delta = () => t('route_lc_detail_col_delta');
export const route_lc_detail_col_new_rate = () => t('route_lc_detail_col_new_rate');
export const route_lc_detail_col_total = () => t('route_lc_detail_col_total');
export const route_lc_detail_submit_title = () => t('route_lc_detail_submit_title');
export const route_lc_detail_submit_cancel = () => t('route_lc_detail_submit_cancel');
export const route_lc_detail_submit_confirm = () => t('route_lc_detail_submit_confirm');
export const route_lc_detail_submit_hint = () => t('route_lc_detail_submit_hint');
export const route_lc_detail_approver_label_opt = () => t('route_lc_detail_approver_label_opt');
export const route_lc_detail_approver_self = () => t('route_lc_detail_approver_self');
export const route_lc_detail_approver_role_user = (p) => t('route_lc_detail_approver_role_user', p);
export const route_lc_detail_cancel_title = () => t('route_lc_detail_cancel_title');
export const route_lc_detail_cancel_back = () => t('route_lc_detail_cancel_back');
export const route_lc_detail_cancel_confirm = () => t('route_lc_detail_cancel_confirm');
export const route_lc_detail_approver_label = () => t('route_lc_detail_approver_label');
export const route_lc_detail_approver_choose = () => t('route_lc_detail_approver_choose');
export const route_lc_detail_reason_label = () => t('route_lc_detail_reason_label');
export const route_lc_detail_reason_err_min = () => t('route_lc_detail_reason_err_min');
export const route_lc_detail_toast_submitted = () => t('route_lc_detail_toast_submitted');
export const route_lc_detail_toast_submit_failed = () => t('route_lc_detail_toast_submit_failed');
export const route_lc_detail_toast_cancelled = () => t('route_lc_detail_toast_cancelled');
export const route_lc_detail_toast_cancel_failed = () => t('route_lc_detail_toast_cancel_failed');
export const route_se_detail_title_fallback = () => t('route_se_detail_title_fallback');
export const route_se_detail_title_with_id = (p) => t('route_se_detail_title_with_id', p);
export const route_se_detail_description = (p) => t('route_se_detail_description', p);
export const route_se_detail_btn_submit = () => t('route_se_detail_btn_submit');
export const route_se_detail_btn_cancel = () => t('route_se_detail_btn_cancel');
export const route_se_detail_period_locked_title = () => t('route_se_detail_period_locked_title');
export const route_se_detail_period_locked_link = () => t('route_se_detail_period_locked_link');
export const route_se_detail_lines_aria = () => t('route_se_detail_lines_aria');
export const route_se_detail_col_item = () => t('route_se_detail_col_item');
export const route_se_detail_col_source = () => t('route_se_detail_col_source');
export const route_se_detail_col_target = () => t('route_se_detail_col_target');
export const route_se_detail_col_qty = () => t('route_se_detail_col_qty');
export const route_se_detail_col_uom = () => t('route_se_detail_col_uom');
export const route_se_detail_col_batch = () => t('route_se_detail_col_batch');
export const route_se_detail_submit_title = () => t('route_se_detail_submit_title');
export const route_se_detail_submit_back = () => t('route_se_detail_submit_back');
export const route_se_detail_submit_blocked = () => t('route_se_detail_submit_blocked');
export const route_se_detail_submit_confirm = () => t('route_se_detail_submit_confirm');
export const route_se_detail_submit_hint = () => t('route_se_detail_submit_hint');
export const route_se_detail_submit_preview_aria = () => t('route_se_detail_submit_preview_aria');
export const route_se_detail_preview_empty = () => t('route_se_detail_preview_empty');
export const route_se_detail_col_wh = () => t('route_se_detail_col_wh');
export const route_se_detail_col_qty_delta = () => t('route_se_detail_col_qty_delta');
export const route_se_detail_col_rate = () => t('route_se_detail_col_rate');
export const route_se_detail_col_value_delta = () => t('route_se_detail_col_value_delta');
export const route_se_detail_violations_heading = () => t('route_se_detail_violations_heading');
export const route_se_detail_violation_row = (p) => t('route_se_detail_violation_row', p);
export const route_se_detail_violation_block_suffix = () => t('route_se_detail_violation_block_suffix');
export const route_se_detail_cancel_title = () => t('route_se_detail_cancel_title');
export const route_se_detail_cancel_back = () => t('route_se_detail_cancel_back');
export const route_se_detail_cancel_confirm = () => t('route_se_detail_cancel_confirm');
export const route_se_detail_approver_label = () => t('route_se_detail_approver_label');
export const route_se_detail_approver_choose = () => t('route_se_detail_approver_choose');
export const route_se_detail_approver_hint = () => t('route_se_detail_approver_hint');
export const route_se_detail_approver_role_user = (p) => t('route_se_detail_approver_role_user', p);
export const route_se_detail_reason_label = () => t('route_se_detail_reason_label');
export const route_se_detail_reason_placeholder = () => t('route_se_detail_reason_placeholder');
export const route_se_detail_reason_err_min = () => t('route_se_detail_reason_err_min');
export const route_se_detail_override_label = () => t('route_se_detail_override_label');
export const route_se_detail_toast_submitted = () => t('route_se_detail_toast_submitted');
export const route_se_detail_toast_submit_failed = () => t('route_se_detail_toast_submit_failed');
export const route_se_detail_toast_cancelled = () => t('route_se_detail_toast_cancelled');
export const route_se_detail_toast_cancel_failed = () => t('route_se_detail_toast_cancel_failed');
export const route_item_detail_title_fallback = () => t('route_item_detail_title_fallback');
export const route_item_detail_title_with_sku = (p) => t('route_item_detail_title_with_sku', p);
export const route_item_detail_description = (p) => t('route_item_detail_description', p);
export const route_item_detail_btn_edit = () => t('route_item_detail_btn_edit');
export const route_item_detail_btn_cancel = () => t('route_item_detail_btn_cancel');
export const route_item_detail_btn_delete = () => t('route_item_detail_btn_delete');
export const route_item_detail_tab_general = () => t('route_item_detail_tab_general');
export const route_item_detail_tab_variants = () => t('route_item_detail_tab_variants');
export const route_item_detail_tab_uom = () => t('route_item_detail_tab_uom');
export const route_item_detail_tab_used_in_boms = () => t('route_item_detail_tab_used_in_boms');
export const route_item_detail_tab_stock = () => t('route_item_detail_tab_stock');
export const route_item_detail_tab_valuation = () => t('route_item_detail_tab_valuation');
export const route_item_detail_uom_placeholder = () => t('route_item_detail_uom_placeholder');
export const route_item_detail_delete_title = () => t('route_item_detail_delete_title');
export const route_item_detail_delete_blocked = () => t('route_item_detail_delete_blocked');
export const route_item_detail_usage_bom = () => t('route_item_detail_usage_bom');
export const route_item_detail_usage_batch = () => t('route_item_detail_usage_batch');
export const route_item_detail_usage_stock_entry = () => t('route_item_detail_usage_stock_entry');
export const route_item_detail_usage_sle = () => t('route_item_detail_usage_sle');
export const route_item_detail_delete_confirm = () => t('route_item_detail_delete_confirm');
export const route_item_detail_delete_no_usage = () => t('route_item_detail_delete_no_usage');
export const route_item_detail_toast_deleted = () => t('route_item_detail_toast_deleted');
export const route_item_detail_toast_delete_failed = () => t('route_item_detail_toast_delete_failed');
export const route_item_detail_type_raw_material = () => t('route_item_detail_type_raw_material');
export const route_item_detail_type_sub_assembly = () => t('route_item_detail_type_sub_assembly');
export const route_item_detail_type_finished_good = () => t('route_item_detail_type_finished_good');
export const route_item_detail_type_consumable = () => t('route_item_detail_type_consumable');
export const route_item_detail_type_service = () => t('route_item_detail_type_service');
export const route_item_detail_field_sku = () => t('route_item_detail_field_sku');
export const route_item_detail_field_name = () => t('route_item_detail_field_name');
export const route_item_detail_field_type = () => t('route_item_detail_field_type');
export const route_item_detail_field_default_uom = () => t('route_item_detail_field_default_uom');
export const route_item_detail_field_default_uom_edit = () => t('route_item_detail_field_default_uom_edit');
export const route_item_detail_field_source = () => t('route_item_detail_field_source');
export const route_item_detail_field_is_stock_item = () => t('route_item_detail_field_is_stock_item');
export const route_item_detail_yes = () => t('route_item_detail_yes');
export const route_item_detail_no = () => t('route_item_detail_no');
export const route_item_detail_field_not_editable = () => t('route_item_detail_field_not_editable');
export const route_item_detail_field_description = () => t('route_item_detail_field_description');
export const route_item_detail_field_description_placeholder = () => t('route_item_detail_field_description_placeholder');
export const route_item_detail_btn_save = () => t('route_item_detail_btn_save');
export const route_item_detail_toast_saved = () => t('route_item_detail_toast_saved');
export const route_item_detail_toast_save_failed = () => t('route_item_detail_toast_save_failed');
export const route_item_detail_valuation_current = () => t('route_item_detail_valuation_current');
export const route_item_detail_valuation_moving_avg = () => t('route_item_detail_valuation_moving_avg');
export const route_item_detail_valuation_fifo = () => t('route_item_detail_valuation_fifo');
export const route_item_detail_valuation_lifo = () => t('route_item_detail_valuation_lifo');
export const route_item_detail_valuation_btn_change = () => t('route_item_detail_valuation_btn_change');
export const route_item_detail_valuation_hint = () => t('route_item_detail_valuation_hint');
export const route_item_detail_used_in_boms_empty = () => t('route_item_detail_used_in_boms_empty');
export const route_item_detail_used_in_boms_link = () => t('route_item_detail_used_in_boms_link');
export const route_item_detail_used_in_boms_aria = () => t('route_item_detail_used_in_boms_aria');
export const route_item_detail_col_bom = () => t('route_item_detail_col_bom');
export const route_item_detail_col_version = () => t('route_item_detail_col_version');
export const route_item_detail_col_active = () => t('route_item_detail_col_active');
export const route_item_detail_col_qty_used = () => t('route_item_detail_col_qty_used');
export const route_item_detail_col_scrap_pct = () => t('route_item_detail_col_scrap_pct');
export const route_item_detail_col_scrap_pct_val = (p) => t('route_item_detail_col_scrap_pct_val', p);
export const route_item_detail_col_depth = () => t('route_item_detail_col_depth');
export const route_item_detail_stock_aria = () => t('route_item_detail_stock_aria');
export const route_item_detail_col_wh = () => t('route_item_detail_col_wh');
export const route_item_detail_col_batch = () => t('route_item_detail_col_batch');
export const route_item_detail_col_qty = () => t('route_item_detail_col_qty');
export const route_item_detail_col_rate = () => t('route_item_detail_col_rate');
export const route_item_detail_col_value = () => t('route_item_detail_col_value');
export const route_wo_detail_title_fallback = () => t('route_wo_detail_title_fallback');
export const route_wo_detail_title_with_code = (p) => t('route_wo_detail_title_with_code', p);
export const route_wo_detail_description = (p) => t('route_wo_detail_description', p);
export const route_wo_detail_tab_overview = () => t('route_wo_detail_tab_overview');
export const route_wo_detail_tab_materials = () => t('route_wo_detail_tab_materials');
export const route_wo_detail_tab_job_cards = () => t('route_wo_detail_tab_job_cards');
export const route_wo_detail_tab_warnings = () => t('route_wo_detail_tab_warnings');
export const route_wo_detail_tab_timeline = () => t('route_wo_detail_tab_timeline');
export const route_wo_detail_tab_audit = () => t('route_wo_detail_tab_audit');
export const route_wo_detail_tab_sles = () => t('route_wo_detail_tab_sles');
export const route_wo_detail_state_heading = (p) => t('route_wo_detail_state_heading', p);
export const route_wo_detail_cancelled_alert = () => t('route_wo_detail_cancelled_alert');
export const route_wo_detail_sse_aria = (p) => t('route_wo_detail_sse_aria', p);
export const route_wo_detail_sse_title = (p) => t('route_wo_detail_sse_title', p);
export const route_wo_detail_metric_planned_start = () => t('route_wo_detail_metric_planned_start');
export const route_wo_detail_metric_actual_start = () => t('route_wo_detail_metric_actual_start');
export const route_wo_detail_metric_planned_end = () => t('route_wo_detail_metric_planned_end');
export const route_wo_detail_metric_actual_end = () => t('route_wo_detail_metric_actual_end');
export const route_wo_detail_variance_banner = (p) => t('route_wo_detail_variance_banner', p);
export const route_wo_detail_toolbar_aria = () => t('route_wo_detail_toolbar_aria');
export const route_wo_detail_btn_release = () => t('route_wo_detail_btn_release');
export const route_wo_detail_btn_start = () => t('route_wo_detail_btn_start');
export const route_wo_detail_btn_pause = () => t('route_wo_detail_btn_pause');
export const route_wo_detail_btn_complete = () => t('route_wo_detail_btn_complete');
export const route_wo_detail_btn_resume = () => t('route_wo_detail_btn_resume');
export const route_wo_detail_btn_close = () => t('route_wo_detail_btn_close');
export const route_wo_detail_toast_close_wip = () => t('route_wo_detail_toast_close_wip');
export const route_wo_detail_btn_cancel_t3 = () => t('route_wo_detail_btn_cancel_t3');
export const route_wo_detail_btn_cancel_t2 = () => t('route_wo_detail_btn_cancel_t2');
export const route_wo_detail_shortcut_key_r = () => t('route_wo_detail_shortcut_key_r');
export const route_wo_detail_shortcut_key_s = () => t('route_wo_detail_shortcut_key_s');
export const route_wo_detail_shortcut_key_c = () => t('route_wo_detail_shortcut_key_c');
export const route_wo_detail_tabs_aria = () => t('route_wo_detail_tabs_aria');
export const route_wo_detail_field_bom = () => t('route_wo_detail_field_bom');
export const route_wo_detail_bom_label_named = (p) => t('route_wo_detail_bom_label_named', p);
export const route_wo_detail_bom_label_short = (p) => t('route_wo_detail_bom_label_short', p);
export const route_wo_detail_field_executor = () => t('route_wo_detail_field_executor');
export const route_wo_detail_field_source_wh = () => t('route_wo_detail_field_source_wh');
export const route_wo_detail_field_wip_wh = () => t('route_wo_detail_field_wip_wh');
export const route_wo_detail_field_fg_wh = () => t('route_wo_detail_field_fg_wh');
export const route_wo_detail_field_scrap_wh = () => t('route_wo_detail_field_scrap_wh');
export const route_wo_detail_field_warnings = () => t('route_wo_detail_field_warnings');
export const route_wo_detail_warnings_value = (p) => t('route_wo_detail_warnings_value', p);
export const route_wo_detail_field_progress = () => t('route_wo_detail_field_progress');
export const route_wo_detail_progress_value = (p) => t('route_wo_detail_progress_value', p);
export const route_wo_detail_materials_loading = () => t('route_wo_detail_materials_loading');
export const route_wo_detail_materials_empty = () => t('route_wo_detail_materials_empty');
export const route_wo_detail_materials_aria = () => t('route_wo_detail_materials_aria');
export const route_wo_detail_col_item = () => t('route_wo_detail_col_item');
export const route_wo_detail_col_req = () => t('route_wo_detail_col_req');
export const route_wo_detail_col_avail = () => t('route_wo_detail_col_avail');
export const route_wo_detail_col_shortage = () => t('route_wo_detail_col_shortage');
export const route_wo_detail_col_src_wh = () => t('route_wo_detail_col_src_wh');
export const route_wo_detail_col_batch = () => t('route_wo_detail_col_batch');
export const route_wo_detail_item_label_dual = (p) => t('route_wo_detail_item_label_dual', p);
export const route_wo_detail_sles_aria = () => t('route_wo_detail_sles_aria');
export const route_wo_detail_col_sle_id = () => t('route_wo_detail_col_sle_id');
export const route_wo_detail_col_time = () => t('route_wo_detail_col_time');
export const route_wo_detail_col_wh = () => t('route_wo_detail_col_wh');
export const route_wo_detail_col_qty_delta = () => t('route_wo_detail_col_qty_delta');
export const route_wo_detail_col_rate = () => t('route_wo_detail_col_rate');
export const route_wo_detail_release_confirm = () => t('route_wo_detail_release_confirm');
export const route_wo_detail_cancel_t2_title = (p) => t('route_wo_detail_cancel_t2_title', p);
export const route_wo_detail_process_stopped_operator = (p) => t('route_wo_detail_process_stopped_operator', p);
export const route_wo_detail_process_stopped_admin = (p) => t('route_wo_detail_process_stopped_admin', p);
export const route_wo_detail_state_stopped_suffix = () => t('route_wo_detail_state_stopped_suffix');
export const route_wo_detail_toast_process_stopped = () => t('route_wo_detail_toast_process_stopped');
export const route_wo_detail_cancel_reason_missing = () => t('route_wo_detail_cancel_reason_missing');
export const route_wo_detail_cancel_approver_missing = () => t('route_wo_detail_cancel_approver_missing');
export const wo_stepper_skipped = () => t('wo_stepper_skipped');
export const route_wo_detail_cancelled_lead_reversal_pending = () => t('route_wo_detail_cancelled_lead_reversal_pending');
export const route_wo_detail_cancelled_open_reversal = () => t('route_wo_detail_cancelled_open_reversal');
export const route_wo_detail_cancelled_output_reversal_pending = () => t('route_wo_detail_cancelled_output_reversal_pending');
export const route_wo_detail_cancelled_output_posters = (p) => t('route_wo_detail_cancelled_output_posters', p);
export const route_wo_detail_produced_reversal_draft = () => t('route_wo_detail_produced_reversal_draft');
export const route_wo_detail_cancelled_label_at = () => t('route_wo_detail_cancelled_label_at');
export const route_wo_detail_cancelled_label_by = () => t('route_wo_detail_cancelled_label_by');
export const route_wo_detail_cancelled_label_signed = () => t('route_wo_detail_cancelled_label_signed');
export const route_wo_detail_cancelled_label_reason = () => t('route_wo_detail_cancelled_label_reason');
export const route_wo_detail_cancelled_label_output = () => t('route_wo_detail_cancelled_label_output');
export const route_wo_detail_cancelled_output_reversed = () => t('route_wo_detail_cancelled_output_reversed');
export const route_wo_detail_cancelled_output_none = () => t('route_wo_detail_cancelled_output_none');
export const route_wo_detail_cancelled_output_not_reversed = () => t('route_wo_detail_cancelled_output_not_reversed');
export const component_wo_timeline_reason = (p) => t('component_wo_timeline_reason', p);
export const component_wo_timeline_system = () => t('component_wo_timeline_system');
export const component_wo_timeline_role_unknown = () => t('component_wo_timeline_role_unknown');
export const wo_stepper_unreachable = () => t('wo_stepper_unreachable');
export const route_wo_detail_qty_ordered = () => t('route_wo_detail_qty_ordered');
export const route_wo_detail_qty_produced = () => t('route_wo_detail_qty_produced');
export const route_wo_detail_cancelled_lead = () => t('route_wo_detail_cancelled_lead');
export const route_wo_detail_cancelled_new_order = () => t('route_wo_detail_cancelled_new_order');
export const route_wo_detail_metric_plan_void = () => t('route_wo_detail_metric_plan_void');
export const component_wo_timeline_initiator = () => t('component_wo_timeline_initiator');
export const component_wo_timeline_aria = () => t('component_wo_timeline_aria');
export const component_wo_timeline_sle_aria = () => t('component_wo_timeline_sle_aria');
export const component_wo_timeline_payload_show = () => t('component_wo_timeline_payload_show');
export const component_wo_timeline_payload_hide = () => t('component_wo_timeline_payload_hide');
export const route_wo_detail_warehouse_not_set = () => t('route_wo_detail_warehouse_not_set');
export const route_wo_detail_complete_blocked_title = (p) => t('route_wo_detail_complete_blocked_title', p);
export const route_wo_detail_complete_blocked_cancel_order = () => t('route_wo_detail_complete_blocked_cancel_order');
export const route_wo_detail_complete_blocked_close = () => t('route_wo_detail_complete_blocked_close');
export const route_wo_detail_complete_blocked_reason = () => t('route_wo_detail_complete_blocked_reason');
export const route_wo_detail_complete_confirm_blocked_warehouses = (p) => t('route_wo_detail_complete_confirm_blocked_warehouses', p);
export const route_wo_detail_toast_completed_draft = () => t('route_wo_detail_toast_completed_draft');
export const route_wo_detail_complete_confirm_meta = (p) => t('route_wo_detail_complete_confirm_meta', p);
export const route_wo_detail_complete_confirm_effect = (p) => t('route_wo_detail_complete_confirm_effect', p);
export const route_wo_detail_complete_confirm_effect_wh = (p) => t('route_wo_detail_complete_confirm_effect_wh', p);
export const route_wo_detail_complete_confirm_warn_warnings = (p) => t('route_wo_detail_complete_confirm_warn_warnings', p);
export const route_wo_detail_complete_confirm_open_warnings = () => t('route_wo_detail_complete_confirm_open_warnings');
export const route_wo_detail_complete_confirm_title = (p) => t('route_wo_detail_complete_confirm_title', p);
export const route_wo_detail_complete_confirm_description = () => t('route_wo_detail_complete_confirm_description');
export const route_wo_detail_complete_confirm_cancel = () => t('route_wo_detail_complete_confirm_cancel');
export const wo_stepper_aria_pause_branch = () => t('wo_stepper_aria_pause_branch');
export const route_wo_detail_checked_at = (p) => t('route_wo_detail_checked_at', p);
export const wo_stepper_aria = (p) => t('wo_stepper_aria', p);
export const wo_stepper_aria_cancelled_at = (p) => t('wo_stepper_aria_cancelled_at', p);
export const wo_stepper_aria_branch = () => t('wo_stepper_aria_branch');
export const route_wo_detail_cancel_approver_journal = () => t('route_wo_detail_cancel_approver_journal');
export const route_wo_detail_execution_recheck = () => t('route_wo_detail_execution_recheck');
export const route_wo_detail_cancel_unknown_warning = () => t('route_wo_detail_cancel_unknown_warning');
export const route_wo_detail_cancel_approver_self_suffix = () => t('route_wo_detail_cancel_approver_self_suffix');
export const route_wo_detail_cancel_approver_selected = (p) => t('route_wo_detail_cancel_approver_selected', p);
export const route_wo_detail_cancel_consequence = () => t('route_wo_detail_cancel_consequence');
export const route_wo_detail_execution_unknown = (p) => t('route_wo_detail_execution_unknown', p);
export const route_wo_detail_cancel_approver_not_found = () => t('route_wo_detail_cancel_approver_not_found');
export const route_wo_detail_cancel_self_note = () => t('route_wo_detail_cancel_self_note');
export const route_wo_detail_actions_stopped = () => t('route_wo_detail_actions_stopped');
export const admin_invite_bulk_retry_failed_some = (p) => t('admin_invite_bulk_retry_failed_some', p);
export const admin_invite_bulk_reissue_links = (p) => t('admin_invite_bulk_reissue_links', p);
export const admin_invite_links_copy_failed_body = () => t('admin_invite_links_copy_failed_body');
export const admin_invite_bulk_links_mail_down = () => t('admin_invite_bulk_links_mail_down');
export const admin_invite_bulk_links_replaced_body = (p) => t('admin_invite_bulk_links_replaced_body', p);
export const admin_invite_bulk_reissue_confirm_title = () => t('admin_invite_bulk_reissue_confirm_title');
export const admin_invite_bulk_reissue_confirm_body = (p) => t('admin_invite_bulk_reissue_confirm_body', p);
export const admin_invite_bulk_reissue_confirm_ok = () => t('admin_invite_bulk_reissue_confirm_ok');
export const admin_invite_toast_mail_down_title = () => t('admin_invite_toast_mail_down_title');
export const admin_invite_toast_mail_down_body = (p) => t('admin_invite_toast_mail_down_body', p);
export const admin_invite_toast_email_failed_body_link = (p) => t('admin_invite_toast_email_failed_body_link', p);
export const admin_invite_copy_all_and_close = () => t('admin_invite_copy_all_and_close');
export const admin_invite_links_field_label = () => t('admin_invite_links_field_label');
export const admin_invite_link_field_label = () => t('admin_invite_link_field_label');
export const admin_invite_link_copy_failed_body = () => t('admin_invite_link_copy_failed_body');
export const admin_invite_toast_to_invites = () => t('admin_invite_toast_to_invites');
export const admin_invite_copy_all_links = () => t('admin_invite_copy_all_links');
export const admin_invite_links_copied_all = () => t('admin_invite_links_copied_all');
export const admin_invite_link_copied_short = () => t('admin_invite_link_copied_short');
export const admin_invite_bulk_close_title = () => t('admin_invite_bulk_close_title');
export const admin_invite_bulk_close_body = (p) => t('admin_invite_bulk_close_body', p);
export const admin_invite_bulk_close_confirm = () => t('admin_invite_bulk_close_confirm');
export const admin_invite_toast_copy_link = () => t('admin_invite_toast_copy_link');
export const admin_invite_toast_sent_body_link = (p) => t('admin_invite_toast_sent_body_link', p);
export const admin_invite_toast_resent_body_link = (p) => t('admin_invite_toast_resent_body_link', p);
export const admin_invite_link_copied = () => t('admin_invite_link_copied');
export const admin_invite_link_copied_body = () => t('admin_invite_link_copied_body');
export const admin_invite_bulk_links_once = () => t('admin_invite_bulk_links_once');
export const tm_notif_access_card_how = () => t('tm_notif_access_card_how');
export const tm_notif_access_card_dismiss_hint = () => t('tm_notif_access_card_dismiss_hint');
export const tm_notif_access_card_requester = (p) => t('tm_notif_access_card_requester', p);
export const tm_notif_access_card_rule = () => t('tm_notif_access_card_rule');
export const tm_notif_access_card_dismiss = () => t('tm_notif_access_card_dismiss');
export const tm_notif_access_subject = (p) => t('tm_notif_access_subject', p);
export const tm_notif_access_card_body = (p) => t('tm_notif_access_card_body', p);
export const tm_notif_access_card_someone = () => t('tm_notif_access_card_someone');
export const tm_notif_access_card_go = () => t('tm_notif_access_card_go');
export const user_drawer_sessions_disabled_account = (p) => t('user_drawer_sessions_disabled_account', p);
export const tm_notif_access_requested_module = (p) => t('tm_notif_access_requested_module', p);
export const user_drawer_disable_confirm = () => t('user_drawer_disable_confirm');
export const user_drawer_discard_changes = () => t('user_drawer_discard_changes');
export const user_drawer_account_locked = () => t('user_drawer_account_locked');
export const route_admin_users_disabled_chip_membership = (p) => t('route_admin_users_disabled_chip_membership', p);
export const route_admin_users_disabled_chip_account = () => t('route_admin_users_disabled_chip_account');
export const route_admin_users_disabled_by_you = () => t('route_admin_users_disabled_by_you');
export const route_admin_users_btn_disable_in_company = () => t('route_admin_users_btn_disable_in_company');
export const route_admin_users_btn_disable_account = () => t('route_admin_users_btn_disable_account');
export const route_admin_users_restore_in_company = () => t('route_admin_users_restore_in_company');
export const route_admin_users_disabled_by_platform_line = () => t('route_admin_users_disabled_by_platform_line');
export const route_admin_users_disabled_by_other_company_line = () => t('route_admin_users_disabled_by_other_company_line');
export const user_drawer_disable_account_title = () => t('user_drawer_disable_account_title');
export const user_drawer_disable_account_body = (p) => t('user_drawer_disable_account_body', p);
export const user_drawer_disable_in_company_title = () => t('user_drawer_disable_in_company_title');
export const user_drawer_disable_in_company_body = (p) => t('user_drawer_disable_in_company_body', p);
export const error_admin_user_account_status_platform_only = () => t('error_admin_user_account_status_platform_only');
export const error_admin_user_role_platform_only = () => t('error_admin_user_role_platform_only');
export const audit_person_platform_admin = () => t('audit_person_platform_admin');
export const audit_person_other_company = () => t('audit_person_other_company');
export const audit_person_disabled_membership = () => t('audit_person_disabled_membership');
export const audit_person_disabled_account = () => t('audit_person_disabled_account');
export const audit_person_hidden_reason = () => t('audit_person_hidden_reason');
export const route_wo_detail_process_stopped_planned = (p) => t('route_wo_detail_process_stopped_planned', p);
export const route_wo_detail_release_stopped = () => t('route_wo_detail_release_stopped');
export const route_wo_detail_stopped_cancel_cta = () => t('route_wo_detail_stopped_cancel_cta');
export const route_wo_detail_process_stopped_when = (p) => t('route_wo_detail_process_stopped_when', p);
export const route_wo_detail_process_stopped_cause_only = (p) => t('route_wo_detail_process_stopped_cause_only', p);
export const route_wo_detail_copy_ref = () => t('route_wo_detail_copy_ref');
export const wo_stop_cause_completed = () => t('wo_stop_cause_completed');
export const wo_stop_cause_failed = () => t('wo_stop_cause_failed');
export const wo_stop_cause_cancelled = () => t('wo_stop_cause_cancelled');
export const wo_stop_cause_terminated = () => t('wo_stop_cause_terminated');
export const wo_stop_cause_timed_out = () => t('wo_stop_cause_timed_out');
export const wo_stop_cause_not_found = () => t('wo_stop_cause_not_found');
export const route_wo_detail_cancel_approver_placeholder = () => t('route_wo_detail_cancel_approver_placeholder');
export const route_wo_detail_cancel_approver_option = (p) => t('route_wo_detail_cancel_approver_option', p);
export const route_wo_detail_cancel_approvers_failed = () => t('route_wo_detail_cancel_approvers_failed');
export const route_wo_detail_cancel_no_approvers = (p) => t('route_wo_detail_cancel_no_approvers', p);
export const route_wo_detail_cancel_t2_approver_label = () => t('route_wo_detail_cancel_t2_approver_label');
export const route_wo_detail_cancel_t2_approver_hint = (p) => t('route_wo_detail_cancel_t2_approver_hint', p);
export const route_wo_detail_cancel_reason_label = () => t('route_wo_detail_cancel_reason_label');
export const route_wo_detail_cancel_t2_cancel = () => t('route_wo_detail_cancel_t2_cancel');
export const route_wo_detail_cancel_t2_confirm = () => t('route_wo_detail_cancel_t2_confirm');
export const route_wo_detail_cancel_t3_title = () => t('route_wo_detail_cancel_t3_title');
export const route_wo_detail_cancel_t3_summary = (p) => t('route_wo_detail_cancel_t3_summary', p);
export const route_wo_detail_cancel_t3_confirm = () => t('route_wo_detail_cancel_t3_confirm');
export const route_wo_detail_cancel_t3_reason_label = () => t('route_wo_detail_cancel_t3_reason_label');
export const route_wo_detail_cancel_t3_reason_placeholder = () => t('route_wo_detail_cancel_t3_reason_placeholder');
export const route_wo_detail_pause_title = () => t('route_wo_detail_pause_title');
export const route_wo_detail_pause_error = (p) => t('route_wo_detail_pause_error', p);
export const route_wo_detail_pause_description = () => t('route_wo_detail_pause_description');
export const route_wo_detail_pause_confirm_pending = () => t('route_wo_detail_pause_confirm_pending');
export const route_wo_detail_pause_confirm = () => t('route_wo_detail_pause_confirm');
export const route_wo_detail_pause_reason_label = () => t('route_wo_detail_pause_reason_label');
export const route_wo_detail_pause_reason_placeholder = () => t('route_wo_detail_pause_reason_placeholder');
export const route_wo_detail_shortcut_help_title = () => t('route_wo_detail_shortcut_help_title');
export const route_wo_detail_shortcut_r_desc = () => t('route_wo_detail_shortcut_r_desc');
export const route_wo_detail_shortcut_s_desc = () => t('route_wo_detail_shortcut_s_desc');
export const route_wo_detail_shortcut_c_desc = () => t('route_wo_detail_shortcut_c_desc');
export const route_wo_detail_shortcut_arrows_desc = () => t('route_wo_detail_shortcut_arrows_desc');
export const route_wo_detail_shortcut_help_desc = () => t('route_wo_detail_shortcut_help_desc');
export const route_wo_detail_toast_released = () => t('route_wo_detail_toast_released');
export const route_wo_detail_toast_release_failed = () => t('route_wo_detail_toast_release_failed');
export const route_wo_detail_toast_started = () => t('route_wo_detail_toast_started');
export const route_wo_detail_toast_start_failed = () => t('route_wo_detail_toast_start_failed');
export const route_wo_detail_toast_paused = () => t('route_wo_detail_toast_paused');
export const route_wo_detail_toast_pause_failed = () => t('route_wo_detail_toast_pause_failed');
export const route_wo_detail_toast_resumed = () => t('route_wo_detail_toast_resumed');
export const route_wo_detail_toast_resume_failed = () => t('route_wo_detail_toast_resume_failed');
export const route_wo_detail_toast_completed = () => t('route_wo_detail_toast_completed');
export const route_wo_detail_toast_complete_failed = () => t('route_wo_detail_toast_complete_failed');
export const route_wo_detail_toast_cancelled_t2 = () => t('route_wo_detail_toast_cancelled_t2');
export const route_wo_detail_toast_cancel_failed_t2 = () => t('route_wo_detail_toast_cancel_failed_t2');
export const route_wo_detail_toast_cancelled_t3 = () => t('route_wo_detail_toast_cancelled_t3');
export const route_wo_detail_toast_cancel_failed_t3 = () => t('route_wo_detail_toast_cancel_failed_t3');
export const route_bom_detail_uom_pcs = () => t('route_bom_detail_uom_pcs');
export const route_bom_detail_uom_kg = () => t('route_bom_detail_uom_kg');
export const route_bom_detail_uom_g = () => t('route_bom_detail_uom_g');
export const route_bom_detail_uom_m = () => t('route_bom_detail_uom_m');
export const route_bom_detail_uom_m2 = () => t('route_bom_detail_uom_m2');
export const route_bom_detail_uom_l = () => t('route_bom_detail_uom_l');
export const route_bom_detail_uom_set = () => t('route_bom_detail_uom_set');
export const route_bom_detail_title_fallback = () => t('route_bom_detail_title_fallback');
export const route_bom_detail_title_with_code = (p) => t('route_bom_detail_title_with_code', p);
export const route_bom_detail_desc_active = () => t('route_bom_detail_desc_active');
export const route_bom_detail_desc_inactive = () => t('route_bom_detail_desc_inactive');
export const route_bom_detail_btn_edit = () => t('route_bom_detail_btn_edit');
export const route_bom_detail_btn_history = () => t('route_bom_detail_btn_history');
export const route_bom_detail_btn_clone_pending = () => t('route_bom_detail_btn_clone_pending');
export const route_bom_detail_btn_clone = () => t('route_bom_detail_btn_clone');
export const route_bom_detail_btn_activate = () => t('route_bom_detail_btn_activate');
export const route_bom_detail_btn_delete = () => t('route_bom_detail_btn_delete');
export const route_bom_detail_field_code = () => t('route_bom_detail_field_code');
export const route_bom_detail_field_name = () => t('route_bom_detail_field_name');
export const route_bom_detail_field_fg = () => t('route_bom_detail_field_fg');
export const route_bom_detail_fg_label_dual = (p) => t('route_bom_detail_fg_label_dual', p);
export const route_bom_detail_field_output_qty = () => t('route_bom_detail_field_output_qty');
export const route_bom_detail_field_uom = () => t('route_bom_detail_field_uom');
export const route_bom_detail_field_version = () => t('route_bom_detail_field_version');
export const route_bom_detail_version_prefix = (p) => t('route_bom_detail_version_prefix', p);
export const route_bom_detail_field_active = () => t('route_bom_detail_field_active');
export const route_bom_detail_yes = () => t('route_bom_detail_yes');
export const route_bom_detail_no = () => t('route_bom_detail_no');
export const route_bom_detail_edit_heading = () => t('route_bom_detail_edit_heading');
export const route_bom_detail_edit_code = () => t('route_bom_detail_edit_code');
export const route_bom_detail_edit_fg = () => t('route_bom_detail_edit_fg');
export const route_bom_detail_edit_not_editable = () => t('route_bom_detail_edit_not_editable');
export const route_bom_detail_edit_name = () => t('route_bom_detail_edit_name');
export const route_bom_detail_edit_output_qty = () => t('route_bom_detail_edit_output_qty');
export const route_bom_detail_edit_uom = () => t('route_bom_detail_edit_uom');
export const route_bom_detail_edit_active = () => t('route_bom_detail_edit_active');
export const route_bom_detail_btn_save = () => t('route_bom_detail_btn_save');
export const route_bom_detail_btn_cancel = () => t('route_bom_detail_btn_cancel');
export const route_bom_detail_toast_header_saved = () => t('route_bom_detail_toast_header_saved');
export const route_bom_detail_toast_save_failed = () => t('route_bom_detail_toast_save_failed');
export const route_bom_detail_materials_heading = () => t('route_bom_detail_materials_heading');
export const route_bom_detail_btn_add_material = () => t('route_bom_detail_btn_add_material');
export const route_bom_detail_empty_title = () => t('route_bom_detail_empty_title');
export const route_bom_detail_empty_desc = () => t('route_bom_detail_empty_desc');
export const route_bom_detail_explode_heading = () => t('route_bom_detail_explode_heading');
export const route_bom_detail_explode_qty_label = () => t('route_bom_detail_explode_qty_label');
export const route_bom_detail_btn_explode = () => t('route_bom_detail_btn_explode');
export const route_bom_detail_explode_aria = () => t('route_bom_detail_explode_aria');
export const route_bom_detail_col_item = () => t('route_bom_detail_col_item');
export const route_bom_detail_col_qty = () => t('route_bom_detail_col_qty');
export const route_bom_detail_col_uom = () => t('route_bom_detail_col_uom');
export const route_bom_detail_col_depth = () => t('route_bom_detail_col_depth');
export const route_bom_detail_add_item_title = () => t('route_bom_detail_add_item_title');
export const route_bom_detail_add_item_confirm = () => t('route_bom_detail_add_item_confirm');
export const route_bom_detail_add_item_picker_label = () => t('route_bom_detail_add_item_picker_label');
export const route_bom_detail_add_item_qty_label = () => t('route_bom_detail_add_item_qty_label');
export const route_bom_detail_add_item_uom_label = () => t('route_bom_detail_add_item_uom_label');
export const route_bom_detail_add_item_scrap_label = () => t('route_bom_detail_add_item_scrap_label');
export const route_bom_detail_add_item_alt_label = () => t('route_bom_detail_add_item_alt_label');
export const route_bom_detail_toast_material_added = () => t('route_bom_detail_toast_material_added');
export const route_bom_detail_toast_material_add_failed = () => t('route_bom_detail_toast_material_add_failed');
export const route_bom_detail_toast_material_removed = () => t('route_bom_detail_toast_material_removed');
export const route_bom_detail_toast_material_remove_failed = () => t('route_bom_detail_toast_material_remove_failed');
export const route_bom_detail_history_title = () => t('route_bom_detail_history_title');
export const route_bom_detail_history_loading = () => t('route_bom_detail_history_loading');
export const route_bom_detail_history_aria = () => t('route_bom_detail_history_aria');
export const route_bom_detail_col_version = () => t('route_bom_detail_col_version');
export const route_bom_detail_col_active = () => t('route_bom_detail_col_active');
export const route_bom_detail_col_open_wo = () => t('route_bom_detail_col_open_wo');
export const route_bom_detail_col_created_at = () => t('route_bom_detail_col_created_at');
export const route_bom_detail_col_created_by = () => t('route_bom_detail_col_created_by');
export const route_bom_detail_activate_title = () => t('route_bom_detail_activate_title');
export const route_bom_detail_activate_confirm = () => t('route_bom_detail_activate_confirm');
export const route_bom_detail_activate_hint = () => t('route_bom_detail_activate_hint');
export const route_bom_detail_approver_label = () => t('route_bom_detail_approver_label');
export const route_bom_detail_approver_placeholder = () => t('route_bom_detail_approver_placeholder');
export const route_bom_detail_activate_err_conflict = () => t('route_bom_detail_activate_err_conflict');
export const route_bom_detail_activate_err_generic = () => t('route_bom_detail_activate_err_generic');
export const route_bom_detail_delete_title = () => t('route_bom_detail_delete_title');
export const route_bom_detail_delete_usage_loading = () => t('route_bom_detail_delete_usage_loading');
export const route_bom_detail_delete_confirm_prefix = () => t('route_bom_detail_delete_confirm_prefix');
export const route_bom_detail_delete_confirm_suffix = () => t('route_bom_detail_delete_confirm_suffix');
export const route_bom_detail_delete_blocked_wo = (p) => t('route_bom_detail_delete_blocked_wo', p);
export const route_bom_detail_delete_blocked_generic = () => t('route_bom_detail_delete_blocked_generic');
export const route_bom_detail_toast_deleted = () => t('route_bom_detail_toast_deleted');
export const route_bom_detail_toast_delete_used = () => t('route_bom_detail_toast_delete_used');
export const route_bom_detail_toast_delete_used_desc = () => t('route_bom_detail_toast_delete_used_desc');
export const route_bom_detail_toast_delete_failed = () => t('route_bom_detail_toast_delete_failed');
export const route_bom_detail_toast_delete_failed_generic = () => t('route_bom_detail_toast_delete_failed_generic');
export const route_bom_detail_item_label_dual = (p) => t('route_bom_detail_item_label_dual', p);
export const route_bom_detail_inline_qty_label = () => t('route_bom_detail_inline_qty_label');
export const route_bom_detail_inline_scrap_label = () => t('route_bom_detail_inline_scrap_label');
export const route_bom_detail_alt_short = () => t('route_bom_detail_alt_short');
export const route_bom_detail_aria_remove = () => t('route_bom_detail_aria_remove');
// ─── S17 Finance Dashboard ──────────────────────────────────────────
export const nav_finance_dashboard = () => t('nav_finance_dashboard');
export const finance_dashboard_title = () => t('finance_dashboard_title');
export const dashboard_secondary_kpi_title = () => t('dashboard_secondary_kpi_title');
export const filter_preset_today = () => t('filter_preset_today');
export const filter_preset_wtd = () => t('filter_preset_wtd');
export const filter_preset_mtd = () => t('filter_preset_mtd');
export const filter_preset_qtd = () => t('filter_preset_qtd');
export const filter_preset_ytd = () => t('filter_preset_ytd');
export const filter_preset_last_12m = () => t('filter_preset_last_12m');
export const filter_preset_custom = () => t('filter_preset_custom');
export const filter_compare_to_previous = () => t('filter_compare_to_previous');
export const filter_compare_hint_length_matched = () => t('filter_compare_hint_length_matched');
export const filter_cost_center_label = () => t('filter_cost_center_label');
export const filter_cost_center_placeholder = () => t('filter_cost_center_placeholder');
export const filter_project_label = () => t('filter_project_label');
export const filter_project_placeholder = () => t('filter_project_placeholder');
export const filter_currency_label = () => t('filter_currency_label');
export const currency_kzt = () => t('currency_kzt');
export const currency_usd = () => t('currency_usd');
export const currency_eur = () => t('currency_eur');
export const currency_rub = () => t('currency_rub');
export const filter_counterparty_group_label = () => t('filter_counterparty_group_label');
export const filter_counterparty_group_placeholder = () => t('filter_counterparty_group_placeholder');
export const filter_budget_label = () => t('filter_budget_label');
export const filter_budget_placeholder = () => t('filter_budget_placeholder');
export const filter_budget_none = () => t('filter_budget_none');
export const filter_reset = () => t('filter_reset');
export const fx_rate_as_of = (p) => t('fx_rate_as_of', p);
export const kpi_revenue = () => t('kpi_revenue');
export const kpi_gross_profit = () => t('kpi_gross_profit');
export const kpi_gross_margin = () => t('kpi_gross_margin');
export const kpi_net_profit = () => t('kpi_net_profit');
export const kpi_net_margin = () => t('kpi_net_margin');
export const kpi_operating_cash_flow = () => t('kpi_operating_cash_flow');
export const kpi_cash_position = () => t('kpi_cash_position');
export const kpi_ar_outstanding = () => t('kpi_ar_outstanding');
export const kpi_ap_outstanding = () => t('kpi_ap_outstanding');
export const kpi_budget_variance = () => t('kpi_budget_variance');
export const kpi_margin_pct_suffix = () => t('kpi_margin_pct_suffix');
export const kpi_cash_position_approximation_hint = () => t('kpi_cash_position_approximation_hint');
export const kpi_sort_by_value = () => t('kpi_sort_by_value');
export const kpi_sort_by_delta = () => t('kpi_sort_by_delta');
export const kpi_sort_by_name = () => t('kpi_sort_by_name');
export const kpi_sort_label = () => t('kpi_sort_label');
export const hero_metric_aria = (p) => t('hero_metric_aria', p);
export const chart_revenue_cost_trend_title = () => t('chart_revenue_cost_trend_title');
export const chart_revenue_cost_trend_x_label = () => t('chart_revenue_cost_trend_x_label');
export const chart_revenue_cost_trend_y_label = () => t('chart_revenue_cost_trend_y_label');
export const chart_pnl_breakdown_title = () => t('chart_pnl_breakdown_title');
export const chart_pnl_breakdown_x_label = () => t('chart_pnl_breakdown_x_label');
export const chart_pnl_breakdown_y_label = () => t('chart_pnl_breakdown_y_label');
export const chart_cash_flow_title = () => t('chart_cash_flow_title');
export const chart_cash_flow_x_label = () => t('chart_cash_flow_x_label');
export const chart_cash_flow_y_label = () => t('chart_cash_flow_y_label');
export const chart_ar_aging_title = () => t('chart_ar_aging_title');
export const chart_ar_aging_x_label = () => t('chart_ar_aging_x_label');
export const chart_budget_variance_title = () => t('chart_budget_variance_title');
export const chart_legend_revenue = () => t('chart_legend_revenue');
export const chart_legend_cogs = () => t('chart_legend_cogs');
export const chart_legend_opex = () => t('chart_legend_opex');
export const chart_legend_gross_profit = () => t('chart_legend_gross_profit');
export const chart_legend_net_profit = () => t('chart_legend_net_profit');
export const chart_legend_cash_inflow = () => t('chart_legend_cash_inflow');
export const chart_legend_cash_outflow = () => t('chart_legend_cash_outflow');
export const chart_legend_cash_balance = () => t('chart_legend_cash_balance');
export const chart_legend_net_positive = () => t('chart_legend_net_positive');
export const chart_legend_net_negative = () => t('chart_legend_net_negative');
export const chart_cash_flow_hint = () => t('chart_cash_flow_hint');
export const chart_ar_aging_empty_hint = () => t('chart_ar_aging_empty_hint');
export const chart_legend_actual = () => t('chart_legend_actual');
export const chart_legend_forecast = () => t('chart_legend_forecast');
export const chart_legend_ar_outstanding = () => t('chart_legend_ar_outstanding');
export const chart_legend_ap_outstanding = () => t('chart_legend_ap_outstanding');
export const aging_bucket_0_30 = () => t('aging_bucket_0_30');
export const aging_bucket_31_60 = () => t('aging_bucket_31_60');
export const aging_bucket_61_90 = () => t('aging_bucket_61_90');
export const aging_bucket_90_plus = () => t('aging_bucket_90_plus');
export const chart_empty_state = () => t('chart_empty_state');
export const budget_dashboard_select_budget = () => t('budget_dashboard_select_budget');
export const top_counterparties_title = () => t('top_counterparties_title');
export const top_counterparties_metric_revenue = () => t('top_counterparties_metric_revenue');
export const top_counterparties_metric_ar_balance = () => t('top_counterparties_metric_ar_balance');
export const top_counterparties_metric_ap_balance = () => t('top_counterparties_metric_ap_balance');
export const top_counterparties_metric_gross_margin = () => t('top_counterparties_metric_gross_margin');
export const metric_unavailable_hint = (p) => t('metric_unavailable_hint', p);
export const top_counterparties_col_rank = () => t('top_counterparties_col_rank');
export const top_counterparties_col_counterparty = () => t('top_counterparties_col_counterparty');
export const top_counterparties_col_value = () => t('top_counterparties_col_value');
export const top_counterparties_col_share = () => t('top_counterparties_col_share');
export const top_counterparties_coverage = (p) => t('top_counterparties_coverage', p);
export const top_counterparties_empty = () => t('top_counterparties_empty');
export const dashboard_export_excel = () => t('dashboard_export_excel');
export const dashboard_export_started = () => t('dashboard_export_started');
export const dashboard_export_failed = () => t('dashboard_export_failed');
export const dashboard_copy_link = () => t('dashboard_copy_link');
export const dashboard_link_copied = () => t('dashboard_link_copied');
export const error_fx_engine_unavailable = (p) => t('error_fx_engine_unavailable', p);
export const error_period_too_long_for_granularity = (p) => t('error_period_too_long_for_granularity', p);
export const error_budget_not_found = () => t('error_budget_not_found');
export const error_metric_unavailable = (p) => t('error_metric_unavailable', p);
export const pnl_basis_label = () => t('pnl_basis_label');
export const pnl_basis_accrual = () => t('pnl_basis_accrual');
export const pnl_basis_cash = () => t('pnl_basis_cash');
export const pnl_basis_cash_hint = () => t('pnl_basis_cash_hint');
// ─── Wizard sections (S-FIX-2-FE.1.6 — i18n inline strings cleanup) ───
export const wizard_section_confirm = () => t('wizard_section_confirm');
export const wizard_section_invoice_lines = () => t('wizard_section_invoice_lines');
export const wizard_section_preview = () => t('wizard_section_preview');
export const wizard_section_settlement_lines = () => t('wizard_section_settlement_lines');
export const wizard_button_skip_to_manual = () => t('wizard_button_skip_to_manual');
export const wizard_button_add_invoice_line = () => t('wizard_button_add_invoice_line');
export const wizard_button_add_settlement_line = () => t('wizard_button_add_settlement_line');
export const wizard_summary_lines_count = () => t('wizard_summary_lines_count');
export const wizard_summary_invoice_lines_count = () => t('wizard_summary_invoice_lines_count');
export const wizard_settlement_propose_empty = () => t('wizard_settlement_propose_empty');
// ─── Payments column (S-FIX-2-FE.1.6) ───
export const payment_column_source = () => t('payment_column_source');
// ─── i18n russian-coverage cleanup (S-FIX-3) ───
export const route_admin_fx_rates_col_date = () => t('route_admin_fx_rates_col_date');
export const route_admin_fx_rates_col_note = () => t('route_admin_fx_rates_col_note');
export const invoice_new_subtotal = () => t('invoice_new_subtotal');
export const invoice_new_total = () => t('invoice_new_total');
// ─── S17–S22 Sales Order + Project Lifecycle Management ─────────────
export const nav_sales_orders = () => t('nav_sales_orders');
export const nav_work_tasks = () => t('nav_work_tasks');
export const nav_work_blueprints = () => t('nav_work_blueprints');
export const nav_projects = () => t('nav_projects');
export const so_status_draft = () => t('so_status_draft');
export const so_status_quotation = () => t('so_status_quotation');
export const so_status_confirmed = () => t('so_status_confirmed');
export const so_status_in_progress = () => t('so_status_in_progress');
export const so_status_delivered = () => t('so_status_delivered');
export const so_status_invoiced = () => t('so_status_invoiced');
export const so_status_closed = () => t('so_status_closed');
export const so_status_cancelled = () => t('so_status_cancelled');
export const so_filter_all_statuses = () => t('so_filter_all_statuses');
export const common_filter_all_directions = () => t('common_filter_all_directions');
export const pnl_section_breakdown = () => t('pnl_section_breakdown');
export const project_form_planned_start = () => t('project_form_planned_start');
export const project_form_planned_end = () => t('project_form_planned_end');
export const so_title_list = () => t('so_title_list');
export const so_title_new = () => t('so_title_new');
export const so_title_detail = () => t('so_title_detail');
export const so_cta_create = () => t('so_cta_create');
export const so_cta_create_project = () => t('so_cta_create_project');
export const so_col_code = () => t('so_col_code');
export const so_col_customer = () => t('so_col_customer');
export const so_col_status = () => t('so_col_status');
export const so_col_order_date = () => t('so_col_order_date');
export const so_col_delivery_date = () => t('so_col_delivery_date');
export const so_col_total = () => t('so_col_total');
export const so_col_position = () => t('so_col_position');
export const so_col_item = () => t('so_col_item');
export const so_col_qty = () => t('so_col_qty');
export const so_col_uom = () => t('so_col_uom');
export const so_col_unit_price = () => t('so_col_unit_price');
export const so_col_line_total = () => t('so_col_line_total');
export const so_form_currency = () => t('so_form_currency');
export const so_form_contract_number = () => t('so_form_contract_number');
export const so_form_contract_date = () => t('so_form_contract_date');
export const so_form_notes = () => t('so_form_notes');
export const so_section_lines = () => t('so_section_lines');
export const so_section_state_machine = () => t('so_section_state_machine');
export const so_locked_after_confirmed = () => t('so_locked_after_confirmed');
export const so_transition_to = () => t('so_transition_to');
export const project_status_open = () => t('project_status_open');
export const project_status_completed = () => t('project_status_completed');
export const project_status_cancelled = () => t('project_status_cancelled');
export const project_stage_planning = () => t('project_stage_planning');
export const project_stage_active = () => t('project_stage_active');
export const project_stage_on_hold = () => t('project_stage_on_hold');
export const project_stage_completed = () => t('project_stage_completed');
export const project_stage_closed = () => t('project_stage_closed');
export const project_health_on_track = () => t('project_health_on_track');
export const project_health_at_risk = () => t('project_health_at_risk');
export const project_health_overdue = () => t('project_health_overdue');
export const project_health_on_hold = () => t('project_health_on_hold');
export const project_pct_method_manual = () => t('project_pct_method_manual');
export const project_pct_method_task_completion = () => t('project_pct_method_task_completion');
export const project_pct_method_task_progress = () => t('project_pct_method_task_progress');
export const project_pct_method_task_weight = () => t('project_pct_method_task_weight');
export const project_task_status_open = () => t('project_task_status_open');
export const project_task_status_working = () => t('project_task_status_working');
export const project_task_status_pending_review = () => t('project_task_status_pending_review');
export const project_task_status_overdue = () => t('project_task_status_overdue');
export const project_task_status_completed = () => t('project_task_status_completed');
export const project_task_status_cancelled = () => t('project_task_status_cancelled');
export const project_title_list = () => t('project_title_list');
export const project_title_new = () => t('project_title_new');
export const project_title_detail = () => t('project_title_detail');
export const project_filters_btn = () => t('project_filters_btn');
export const project_page_size_label = () => t('project_page_size_label');
export const project_page_shown = (p) => t('project_page_shown', p);
export const project_page_shown_incl_cancelled = (p) => t('project_page_shown_incl_cancelled', p);
export const project_page_hidden_by_filters = (p) => t('project_page_hidden_by_filters', p);
export const project_page_hidden_completed = (p) => t('project_page_hidden_completed', p);
/**
 * D3.6a — сколько записей списка не открылось. Плюрализация обязательна: без неё
 * по-русски выходило «1 не открылись», что читается как небрежность продукта
 * ровно там, где он признаётся в потере данных.
 */
/** D4.8 (CC-3) — превью процента рядом с вариантом метода в списке. */
export const pc_method_preview_becomes = (p) => t('pc_method_preview_becomes', p);
export const pc_method_preview_stays = (p) => t('pc_method_preview_stays', p);
/** D4.9 (CC-4) — причина, по которой метод расчёта неприменим (решение владельца: disabled с причиной). */
/** D4.9 (раунд 2 UX-гейта) — маркировка состояния поля: сохранённый метод не считается. */
export const pc_method_current_unavailable = () => t('pc_method_current_unavailable');
/** D4.9 (раунд 2 гейта) — причина + ДЕЙСТВИЕ: «что мне теперь делать» вместо одной констатации. */
export const pc_method_unavailable_action = () => t('pc_method_unavailable_action');
export const pc_method_unavailable_action_weights = () => t('pc_method_unavailable_action_weights');
/** D4.9 — короткая пометка в списке; полная причина живёт под полем. */
export const pc_method_unavailable_short = () => t('pc_method_unavailable_short');
export const pc_method_unavailable_no_weights = () => t('pc_method_unavailable_no_weights');
export const pc_method_unavailable_no_tasks = () => t('pc_method_unavailable_no_tasks');
export const project_page_dropped_rows = (p) => pluralForm(p.count, {
    one: t('project_page_dropped_one', { count: p.count }),
    few: t('project_page_dropped_few', { count: p.count }),
    many: t('project_page_dropped_many', { count: p.count }),
});
export const project_filter_empty_title = () => t('project_filter_empty_title');
export const project_filter_empty_hint = () => t('project_filter_empty_hint');
export const project_filter_reset = () => t('project_filter_reset');
export const project_filter_search_chip = (p) => t('project_filter_search_chip', p);
export const project_view_mode_aria = () => t('project_view_mode_aria');
export const project_view_cards_aria = () => t('project_view_cards_aria');
export const project_view_table_aria = () => t('project_view_table_aria');
export const project_delta_vs_week = () => t('project_delta_vs_week');
export const project_delta_week_title = () => t('project_delta_week_title');
export const project_cta_create = () => t('project_cta_create');
export const project_cta_edit = () => t('project_cta_edit');
export const project_cta_save_changes = () => t('project_cta_save_changes');
export const project_col_code = () => t('project_col_code');
export const project_col_name = () => t('project_col_name');
export const project_col_customer = () => t('project_col_customer');
export const project_col_stage = () => t('project_col_stage');
export const project_stage_cancelled_from = (p) => t('project_stage_cancelled_from', p);
export const project_stage_cancelled_from_title = (p) => t('project_stage_cancelled_from_title', p);
// PPC-F22 (F13 tails-closeout) — та же конвенция у завершённого: подпись = стадия на момент исхода.
export const project_stage_completed_at_title = (p) => t('project_stage_completed_at_title', p);
export const project_progress_cancelled_title = () => t('project_progress_cancelled_title');
export const project_progress_completed_below_full_title = (p) => t('project_progress_completed_below_full_title', p);
export const project_stage_terminal_muted_title = (p) => t('project_stage_terminal_muted_title', p);
export const tm_subscription = () => t('tm_subscription');
export const error_unknown_no_trace = () => t('error_unknown_no_trace');
export const tm_task_request_access = () => t('tm_task_request_access');
export const tm_task_request_access_retry = () => t('tm_task_request_access_retry');
export const tm_task_request_access_sent_to = (p) => t('tm_task_request_access_sent_to', p);
export const tm_task_request_access_sent_nobody = () => t('tm_task_request_access_sent_nobody');
export const tm_notif_access_requested = () => t('tm_notif_access_requested');
export const tm_notif_access_granted = () => t('tm_notif_access_granted');
export const tm_worklog_time_edited_from = (p) => t('tm_worklog_time_edited_from', p);
export const module_forbidden_desc_named = (p) => t('module_forbidden_desc_named', p);
export const module_forbidden_grantors = (p) => t('module_forbidden_grantors', p);
export const module_forbidden_grantors_title = () => t('module_forbidden_grantors_title');
export const module_forbidden_desc_everywhere = (p) => t('module_forbidden_desc_everywhere', p);
export const module_forbidden_more_companies = (p) => t('module_forbidden_more_companies', p);
export const module_forbidden_grantors_company = (p) => t('module_forbidden_grantors_company', p);
export const module_forbidden_switched = (p) => t('module_forbidden_switched', p);
export const module_forbidden_switch_back = (p) => t('module_forbidden_switch_back', p);
export const module_forbidden_open_in_title = () => t('module_forbidden_open_in_title');
export const module_forbidden_go_to_company = (p) => t('module_forbidden_go_to_company', p);
export const project_page_hidden_by_query = (p) => t('project_page_hidden_by_query', p);
export const project_page_clear_query = () => t('project_page_clear_query');
export const project_progress_completed_open_tasks_title = (p) => t('project_progress_completed_open_tasks_title', p);
export const project_stage_unknown = () => t('project_stage_unknown');
export const project_col_health = () => t('project_col_health');
export const project_col_pm = () => t('project_col_pm');
export const project_col_end_date = () => t('project_col_end_date');
export const project_col_progress = () => t('project_col_progress');
// projects-by-company — колонка/чип «Компания» в мультикомпанийном охвате.
export const project_col_entity = () => t('project_col_entity');
// projects-by-company P4 — группировка списка проектов по компании.
export const project_group_by_company = () => t('project_group_by_company');
// tasks-by-company-fixes T5 — CTA дискаверабилити агрегата (single-scope при мульти-доступе).
export const tasks_show_all_companies = () => t('tasks_show_all_companies');
export const tasks_show_all_companies_cta = () => t('tasks_show_all_companies_cta');
export const tasks_show_all_companies_hint = () => t('tasks_show_all_companies_hint');
export const tasks_scope_holding = () => t('tasks_scope_holding');
export const tasks_scope_back_to_single = () => t('tasks_scope_back_to_single');
export const tasks_scope_count_title = (p) => t('tasks_scope_count_title', p);
export const tasks_scope_back_to_named = (p) => t('tasks_scope_back_to_named', p);
export const project_filter_customer_id_placeholder = () => t('project_filter_customer_id_placeholder');
export const project_filter_code_placeholder = () => t('project_filter_code_placeholder');
export const project_form_name = () => t('project_form_name');
export const project_form_so = () => t('project_form_so');
// task-quick-capture F1 — аккордеон производство/финансы в форме создания проекта.
export const project_form_advanced_section = () => t('project_form_advanced_section');
export const project_form_advanced_optional = () => t('project_form_advanced_optional');
export const project_form_advanced_note = () => t('project_form_advanced_note');
// task-quick-capture F2 — inline-захват задачи в пустом состоянии.
export const tasks_quickadd_placeholder = () => t('tasks_quickadd_placeholder');
export const tasks_quickadd_inbox_hint = () => t('tasks_quickadd_inbox_hint');
export const task_quick_add_keyboard_hint = () => t('task_quick_add_keyboard_hint');
export const tasks_empty_title = () => t('tasks_empty_title');
export const tasks_create_project = () => t('tasks_create_project');
export const tasks_open_projects = () => t('tasks_open_projects');
// task-quick-capture F5 — бейдж «Финансовый/PM»-проекта (is_financial из B3).
export const project_badge_financial = () => t('project_badge_financial');
export const project_badge_financial_title = () => t('project_badge_financial_title');
// task-quick-capture F3 — глобальный quick-add задачи (шапка + Ctrl+K).
export const quickadd_button = () => t('quickadd_button');
export const quickadd_project_hint = () => t('quickadd_project_hint');
export const quickadd_tooltip = () => t('quickadd_tooltip');
export const quickadd_title_placeholder = () => t('quickadd_title_placeholder');
export const quickadd_submit = () => t('quickadd_submit');
export const quickadd_created_toast = () => t('quickadd_created_toast');
export const quickadd_open_task = () => t('quickadd_open_task');
// task-quick-capture F4 — экран «Мой день».
export const myday_title = () => t('myday_title');
export const myday_empty_title = () => t('myday_empty_title');
export const myday_empty_hint = () => t('myday_empty_hint');
export const myday_quickadd_placeholder = () => t('myday_quickadd_placeholder');
export const nav_my_day = () => t('nav_my_day');
export const project_form_project_type = () => t('project_form_project_type');
export const project_form_project_type_hint = () => t('project_form_project_type_hint');
export const project_form_locked_hint = () => t('project_form_locked_hint');
export const project_form_dates = () => t('project_form_dates');
export const project_form_estimated_cost = () => t('project_form_estimated_cost');
export const project_hero_cost_absent = () => t('project_hero_cost_absent');
export const project_hero_cost_add = () => t('project_hero_cost_add');
export const project_form_pm = () => t('project_form_pm');
export const project_form_notes = () => t('project_form_notes');
export const project_form_pct_method = () => t('project_form_pct_method');
export const project_form_select_company = () => t('project_form_select_company');
export const project_form_select_customer = () => t('project_form_select_customer');
export const project_form_select_so = () => t('project_form_select_so');
export const project_form_select_type = () => t('project_form_select_type');
// access-go-live 2026-07-08 — перенос проекта между компаниями.
export const project_move_confirm_title = () => t('project_move_confirm_title');
export const project_move_confirm_body = (p) => t('project_move_confirm_body', p);
export const project_move_confirm_detach_note = () => t('project_move_confirm_detach_note');
export const project_move_confirm_cta = () => t('project_move_confirm_cta');
export const project_move_success = (p) => t('project_move_success', p);
export const project_type_supply = () => t('project_type_supply');
export const project_type_manufacturing = () => t('project_type_manufacturing');
export const project_type_rnd = () => t('project_type_rnd');
export const project_type_modernization = () => t('project_type_modernization');
export const project_type_other = () => t('project_type_other');
export const project_tab_overview = () => t('project_tab_overview');
export const project_tab_tasks = () => t('project_tab_tasks');
export const project_tab_gantt = () => t('project_tab_gantt');
export const project_tab_milestones = () => t('project_tab_milestones');
export const project_tab_financial = () => t('project_tab_financial');
export const project_tab_links = () => t('project_tab_links');
export const project_tab_task_log = () => t('project_tab_task_log');
export const project_tab_status_journal = () => t('project_tab_status_journal');
export const project_tab_audit = () => t('project_tab_audit');
export const project_tabs_journals_group = () => t('project_tabs_journals_group');
export const project_stage_action_start = () => t('project_stage_action_start');
export const project_stage_action_pause = () => t('project_stage_action_pause');
export const project_stage_action_resume = () => t('project_stage_action_resume');
export const project_stage_action_complete = () => t('project_stage_action_complete');
export const project_stage_action_close = () => t('project_stage_action_close');
export const project_stage_action_cancel = () => t('project_stage_action_cancel');
export const project_stage_action_update_health = () => t('project_stage_action_update_health');
export const project_financial_gross_margin = () => t('project_financial_gross_margin');
export const project_margin_basis = (p) => t('project_margin_basis', p);
// N5 — раньше собиралось из ключа и литерала: `${m.…gross_margin()} (план)`. Хвост
// «(план)»/«(факт)» не переводился, и в en-локали выходило «Margin (план)».
export const project_financial_gross_margin_plan = () => t('project_financial_gross_margin_plan');
export const project_financial_gross_margin_fact = () => t('project_financial_gross_margin_fact');
export const project_financial_cost_actual_vs_plan = () => t('project_financial_cost_actual_vs_plan');
export const project_financial_billed = () => t('project_financial_billed');
export const project_financial_cost_breakdown = () => t('project_financial_cost_breakdown');
export const project_financial_cost_labor = () => t('project_financial_cost_labor');
export const project_financial_cost_materials = () => t('project_financial_cost_materials');
export const project_financial_cost_purchase = () => t('project_financial_cost_purchase');
export const project_financial_cost_landed = () => t('project_financial_cost_landed');
export const project_financial_variance = () => t('project_financial_variance');
export const project_financial_cost_variance = () => t('project_financial_cost_variance');
export const project_financial_schedule_variance = () => t('project_financial_schedule_variance');
export const project_financial_refresh = () => t('project_financial_refresh');
export const project_financial_edit_baseline = () => t('project_financial_edit_baseline');
export const project_financial_empty = () => t('project_financial_empty');
export const task_tree_col_subject = () => t('task_tree_col_subject');
export const task_tree_col_status = () => t('task_tree_col_status');
export const task_tree_col_assignee = () => t('task_tree_col_assignee');
export const task_tree_col_start = () => t('task_tree_col_start');
export const task_tree_col_end = () => t('task_tree_col_end');
export const task_tree_col_progress = () => t('task_tree_col_progress');
export const task_tree_col_dependencies = () => t('task_tree_col_dependencies');
export const task_tree_col_weight = () => t('task_tree_col_weight');
export const task_tree_col_actions = () => t('task_tree_col_actions');
export const task_tree_add = () => t('task_tree_add');
export const task_tree_aria_select = (p) => t('task_tree_aria_select', p);
export const task_tree_aria_expand = () => t('task_tree_aria_expand');
export const task_tree_aria_collapse = () => t('task_tree_aria_collapse');
export const task_tree_aria_delete = () => t('task_tree_aria_delete');
export const task_tree_aria_edit = () => t('task_tree_aria_edit');
export const task_tree_aria_progress = () => t('task_tree_aria_progress');
export const task_tree_form_kind = () => t('task_tree_form_kind');
export const task_tree_kind_regular = () => t('task_tree_kind_regular');
export const task_tree_kind_group = () => t('task_tree_kind_group');
export const task_tree_kind_milestone = () => t('task_tree_kind_milestone');
export const task_tree_form_advanced = () => t('task_tree_form_advanced');
export const task_tree_hint_disabled_on_group = () => t('task_tree_hint_disabled_on_group');
export const task_tree_hint_depends_on_single = () => t('task_tree_hint_depends_on_single');
export const task_tree_hint_weight = () => t('task_tree_hint_weight');
export const task_tree_form_subject = () => t('task_tree_form_subject');
export const task_tree_form_description = () => t('task_tree_form_description');
export const task_tree_form_status = () => t('task_tree_form_status');
export const task_tree_form_is_group = () => t('task_tree_form_is_group');
export const task_tree_form_is_milestone = () => t('task_tree_form_is_milestone');
export const task_tree_form_parent = () => t('task_tree_form_parent');
export const task_tree_form_milestone_link = () => t('task_tree_form_milestone_link');
export const task_tree_form_exp_start = () => t('task_tree_form_exp_start');
export const task_tree_form_exp_end = () => t('task_tree_form_exp_end');
export const task_tree_form_expected_hours = () => t('task_tree_form_expected_hours');
export const task_tree_form_task_weight = () => t('task_tree_form_task_weight');
export const task_tree_form_assignee = () => t('task_tree_form_assignee');
export const task_tree_form_depends_on = () => t('task_tree_form_depends_on');
export const task_tree_empty = () => t('task_tree_empty');
export const milestone_title = () => t('milestone_title');
export const milestone_list_title = () => t('milestone_list_title');
export const milestone_add = () => t('milestone_add');
export const milestone_form_name = () => t('milestone_form_name');
export const milestone_form_deadline = () => t('milestone_form_deadline');
export const milestone_form_billing_pct = () => t('milestone_form_billing_pct');
export const milestone_form_notes = () => t('milestone_form_notes');
export const milestone_mark_reached = () => t('milestone_mark_reached');
export const milestone_draft_invoice = () => t('milestone_draft_invoice');
export const milestone_draft_invoice_confirm = (p) => t('milestone_draft_invoice_confirm', p);
export const milestone_no_so_tooltip = () => t('milestone_no_so_tooltip');
export const milestone_reached_at = () => t('milestone_reached_at');
export const milestone_billed_link = () => t('milestone_billed_link');
export const milestone_expected_amount = () => t('milestone_expected_amount');
export const milestone_invoice_created = () => t('milestone_invoice_created');
export const task_log_title = () => t('task_log_title');
export const task_log_add = () => t('task_log_add');
export const task_log_col_date = () => t('task_log_col_date');
export const task_log_col_task = () => t('task_log_col_task');
export const task_log_col_user = () => t('task_log_col_user');
export const task_log_col_hours = () => t('task_log_col_hours');
export const task_log_col_description = () => t('task_log_col_description');
export const task_log_form_task = () => t('task_log_form_task');
export const task_log_form_date = () => t('task_log_form_date');
export const task_log_form_hours = () => t('task_log_form_hours');
export const task_log_form_description = () => t('task_log_form_description');
export const task_log_empty = () => t('task_log_empty');
export const gantt_view_day = () => t('gantt_view_day');
export const gantt_view_week = () => t('gantt_view_week');
export const gantt_view_month = () => t('gantt_view_month');
export const gantt_view_quarter = () => t('gantt_view_quarter');
export const gantt_view_year = () => t('gantt_view_year');
export const gantt_today = () => t('gantt_today');
export const gantt_filter_overdue_only = () => t('gantt_filter_overdue_only');
export const gantt_empty = () => t('gantt_empty');
export const gantt_loading = () => t('gantt_loading');
export const gantt_period_label = () => t('gantt_period_label');
export const gantt_days_count = (p) => t('gantt_days_count', p);
export const gantt_today_in_range = (p) => t('gantt_today_in_range', p);
export const gantt_today_outside_before = (p) => t('gantt_today_outside_before', p);
export const gantt_today_outside_after = (p) => t('gantt_today_outside_after', p);
export const gantt_today_outside_before_short = (p) => t('gantt_today_outside_before_short', p);
export const gantt_today_outside_after_short = (p) => t('gantt_today_outside_after_short', p);
export const gantt_view_mode_label = () => t('gantt_view_mode_label');
export const gantt_legend_label = () => t('gantt_legend_label');
export const gantt_legend_group = () => t('gantt_legend_group');
export const gantt_legend_task = () => t('gantt_legend_task');
export const gantt_legend_milestone_reached = () => t('gantt_legend_milestone_reached');
export const gantt_legend_milestone_pending = () => t('gantt_legend_milestone_pending');
export const gantt_group_expand = (p) => t('gantt_group_expand', p);
export const gantt_group_collapse = (p) => t('gantt_group_collapse', p);
// S21 polish — локализация tooltip/aria ганта (раньше inline RU; EN/KK/ZH видели русский).
export const gantt_zoom_label = () => t('gantt_zoom_label');
export const gantt_add_task = () => t('gantt_add_task');
export const gantt_create_folder = () => t('gantt_create_folder');
export const gantt_drag_task = () => t('gantt_drag_task');
export const gantt_assign_dates_hint = () => t('gantt_assign_dates_hint');
export const gantt_show_on_timeline = () => t('gantt_show_on_timeline');
export const gantt_drag_to_timeline = () => t('gantt_drag_to_timeline');
export const gantt_schedule_from_today = () => t('gantt_schedule_from_today');
export const gantt_table_width = () => t('gantt_table_width');
// UX18 — DisabledWithReason wrapper
export const rbac_disabled_tooltip_generic = () => t('rbac_disabled_tooltip_generic');
export const rbac_disabled_tooltip_with_role = (p) => t('rbac_disabled_tooltip_with_role', p);
export const rbac_role_admin = () => t('rbac_role_admin');
export const rbac_role_manager = () => t('rbac_role_manager');
// UX5 — Cancel project flow
export const project_cancel_cta = () => t('project_cancel_cta');
export const project_cancel_title = () => t('project_cancel_title');
export const project_cancel_impact_title = () => t('project_cancel_impact_title');
/** D5.4 — «3 открытых задач» было согласовано неверно: русский требует трёх форм. */
export const project_cancel_impact_tasks = (p) => `${p.count} ${pluralForm(p.count, {
    one: t('project_cancel_impact_tasks_one'),
    few: t('project_cancel_impact_tasks_few'),
    many: t('project_cancel_impact_tasks_many'),
})}`;
export const project_cancel_impact_invoices = (p) => `${p.count} ${pluralForm(p.count, {
    one: t('project_cancel_impact_invoices_one'),
    few: t('project_cancel_impact_invoices_few'),
    many: t('project_cancel_impact_invoices_many'),
})}`;
export const project_cancel_impact_stage = (p) => t('project_cancel_impact_stage', p);
/**
 * F2 (project-actions-parity §2.3) — четыре строки последствий вместо одной.
 * Прежняя `project_cancel_impact_tasks` («N открытых задач останутся незакрытыми»)
 * описывала исход, которого после B3 не бывает, и осталась в словаре только ради
 * чтения старых снимков — новый диалог её не вызывает.
 */
export const project_cancel_impact_cancelled = (p) => `${p.count} ${pluralForm(p.count, {
    one: t('project_cancel_impact_cancelled_one'),
    few: t('project_cancel_impact_cancelled_few'),
    many: t('project_cancel_impact_cancelled_many'),
})}`;
export const project_cancel_impact_keep_shared = (p) => `${p.count} ${pluralForm(p.count, {
    one: t('project_cancel_impact_keep_shared_one'),
    few: t('project_cancel_impact_keep_shared_few'),
    many: t('project_cancel_impact_keep_shared_many'),
})}`;
export const project_cancel_impact_keep_no_state = (p) => `${p.count} ${pluralForm(p.count, {
    one: t('project_cancel_impact_keep_no_state_one'),
    few: t('project_cancel_impact_keep_no_state_few'),
    many: t('project_cancel_impact_keep_no_state_many'),
})}`;
export const project_cancel_impact_keep_done = (p) => `${p.count} ${pluralForm(p.count, {
    one: t('project_cancel_impact_keep_done_one'),
    few: t('project_cancel_impact_keep_done_few'),
    many: t('project_cancel_impact_keep_done_many'),
})}`;
export const project_cancel_impact_stays_listed = () => t('project_cancel_impact_stays_listed');
export const project_cancel_impact_no_tasks = () => t('project_cancel_impact_no_tasks');
export const project_cancel_impact_pending = () => t('project_cancel_impact_pending');
/** F8 — диалог разотмены. */
export const project_uncancel_title = () => t('project_uncancel_title');
export const project_uncancel_cta = () => t('project_uncancel_cta');
export const project_uncancel_impact_status = (p) => t('project_uncancel_impact_status', p);
export const project_uncancel_impact_tasks_stay = () => t('project_uncancel_impact_tasks_stay');
/** Своя подпись поля и своя строка о немедленности: у возврата они не про отмену. */
export const project_uncancel_reason_label = () => t('project_uncancel_reason_label');
export const project_uncancel_impact_immediate = () => t('project_uncancel_impact_immediate');
/**
 * Гейт фазы B — выход из диалога ВОЗВРАТА подписан своим словом.
 * Скопированное «Не отменять» читалось как «не отменять возврат»: дословный
 * остаток чужого диалога ровно в момент решения (оба ревьюера, BLOCKER).
 */
export const project_uncancel_keep = () => t('project_uncancel_keep');
/**
 * «Исход снимется» вместо «вернётся в состояние «Активный»»: слова «Активный»
 * в колонке «Состояние» нет ни в одной строке списка — она показывает здоровье
 * («В графике»). Диалог называл значение, которого пользователь потом не найдёт.
 */
export const project_uncancel_impact_outcome_cleared = () => t('project_uncancel_impact_outcome_cleared');
/** Число задач, которые обратно НЕ откроются — доказательство вместо обещания. */
export const project_uncancel_impact_tasks_stay_count = (p) => `${p.count} ${pluralForm(p.count, {
    one: t('project_uncancel_impact_tasks_stay_count_one'),
    few: t('project_uncancel_impact_tasks_stay_count_few'),
    many: t('project_uncancel_impact_tasks_stay_count_many'),
})}`;
export const ctx_uncancel_project = () => t('ctx_uncancel_project');
/** F11/F5 — aria-подпись видимой кнопки-носителя меню проекта. */
export const ctx_project_menu_aria = () => t('ctx_project_menu_aria');
/** F7 (project-actions-parity §3.2) — архивация под тем же Tier 2, что отмена. */
export const project_archive_title = () => t('project_archive_title');
export const project_archive_cta = () => t('project_archive_cta');
export const project_archive_keep = () => t('project_archive_keep');
export const project_archive_reason_label = () => t('project_archive_reason_label');
export const project_archive_impact_reversible = () => t('project_archive_impact_reversible');
/**
 * Гейт фаз C/D — своя строка о немедленности. В диалоге архивации стояла
 * `project_cancel_impact_immediate` («Отмена применится сразу»): чужое слово ровно
 * там, где различие «отменить»/«архивировать» обязано читаться.
 */
export const project_archive_impact_immediate = () => t('project_archive_impact_immediate');
/** F10 — пометка архивной строки в списке: отсутствие пометки — не признак. */
export const project_row_archived_badge = () => t('project_row_archived_badge');
/** F9 — возврат из архива. */
export const project_restore_title = () => t('project_restore_title');
export const project_restore_cta = () => t('project_restore_cta');
export const project_restore_keep = () => t('project_restore_keep');
export const project_restore_reason_label = () => t('project_restore_reason_label');
export const project_restore_impact_tasks = () => t('project_restore_impact_tasks');
export const project_restore_impact_partial = () => t('project_restore_impact_partial');
export const ctx_restore_project = () => t('ctx_restore_project');
/** F10 — тумблер архивных на /projects. */
export const project_show_archived = () => t('project_show_archived');
export const project_cancel_reason_label = () => t('project_cancel_reason_label');
export const project_cancel_reason_hint = (p) => t('project_cancel_reason_hint', p);
export const project_cancel_approver_label = () => t('project_cancel_approver_label');
export const project_cancel_approver_placeholder = () => t('project_cancel_approver_placeholder');
export const project_cancel_approver_empty = () => t('project_cancel_approver_empty');
export const project_cancel_approver_hint = () => t('project_cancel_approver_hint');
export const project_cancel_block_reason = () => t('project_cancel_block_reason');
export const project_cancel_block_reason_short = (p) => t('project_cancel_block_reason_short', p);
export const project_cancel_block_approver = () => t('project_cancel_block_approver');
export const project_cancel_impact_immediate = () => t('project_cancel_impact_immediate');
export const project_cancel_block_no_approver = () => t('project_cancel_block_no_approver');
export const project_cancel_keep = () => t('project_cancel_keep');
export const project_cancel_confirm_title_tier1 = () => t('project_cancel_confirm_title_tier1');
export const project_cancel_confirm_title_tier2 = () => t('project_cancel_confirm_title_tier2');
export const project_cancel_reason_required = () => t('project_cancel_reason_required');
export const project_cancel_success = () => t('project_cancel_success');
export const project_cancel_terminal_tooltip = () => t('project_cancel_terminal_tooltip');
// UX10 — SSE connection states
export const sse_connecting_short = () => t('sse_connecting_short');
export const sse_reconnecting_long = () => t('sse_reconnecting_long');
export const sse_offline = () => t('sse_offline');
export const sse_manual_reconnect = () => t('sse_manual_reconnect');
// UX17 — SSE aria-live announce
export const sse_event_task_changed = () => t('sse_event_task_changed');
export const sse_event_milestone_reached = () => t('sse_event_milestone_reached');
export const sse_event_stage_transitioned = () => t('sse_event_stage_transitioned');
export const sse_event_metrics_recomputed = () => t('sse_event_metrics_recomputed');
// UX4 — header overflow menu items
export const project_header_more_actions = () => t('project_header_more_actions');
export const project_action_health_update = () => t('project_action_health_update');
// UX14 — log tabs tooltips
export const project_tab_task_log_tooltip = () => t('project_tab_task_log_tooltip');
export const project_tab_status_journal_tooltip = () => t('project_tab_status_journal_tooltip');
export const project_tab_audit_tooltip = () => t('project_tab_audit_tooltip');
// UX16 — Keyboard shortcuts overlay
export const keyboard_shortcuts_title = () => t('keyboard_shortcuts_title');
export const keyboard_shortcut_new = () => t('keyboard_shortcut_new');
export const keyboard_shortcut_delete = () => t('keyboard_shortcut_delete');
export const keyboard_shortcut_expand_all = () => t('keyboard_shortcut_expand_all');
export const keyboard_shortcut_search = () => t('keyboard_shortcut_search');
export const keyboard_shortcut_row_nav = () => t('keyboard_shortcut_row_nav');
export const keyboard_shortcut_close = () => t('keyboard_shortcut_close');
export const keyboard_shortcut_help = () => t('keyboard_shortcut_help');
export const linkages_section_so = () => t('linkages_section_so');
export const linkages_section_wo = () => t('linkages_section_wo');
export const linkages_section_ar = () => t('linkages_section_ar');
export const linkages_section_ap = () => t('linkages_section_ap');
export const linkages_section_stock_entries = () => t('linkages_section_stock_entries');
export const linkages_section_landed_costs = () => t('linkages_section_landed_costs');
export const linkages_section_payments = () => t('linkages_section_payments');
export const linkages_bind_cta = () => t('linkages_bind_cta');
export const linkages_count_total = (p) => t('linkages_count_total', p);
export const linkages_empty_no_data = () => t('linkages_empty_no_data');
export const linkages_empty_auto_generated = () => t('linkages_empty_auto_generated');
export const audit_tab_empty = () => t('audit_tab_empty');
export const project_chip_label = () => t('project_chip_label');
export const audit_col_timestamp = () => t('audit_col_timestamp');
export const audit_col_action = () => t('audit_col_action');
export const audit_col_user = () => t('audit_col_user');
export const audit_col_tier = () => t('audit_col_tier');
export const audit_col_status = () => t('audit_col_status');
export const tier2_approver_label = () => t('tier2_approver_label');
export const tier2_approver_placeholder = () => t('tier2_approver_placeholder');
export const tier2_actor_is_approver = () => t('tier2_actor_is_approver');
export const tier2_reason_label = () => t('tier2_reason_label');
export const tier2_confirm = () => t('tier2_confirm');
export const tier2_dialog_description = () => t('tier2_dialog_description');
export const project_bucket_all = () => t('project_bucket_all');
export const project_buckets_basis_hint = () => t('project_buckets_basis_hint');
// F15 (В-13) — легенда над рядом чипов; вариант при сужении выдачи.
export const project_buckets_legend = (p) => t('project_buckets_legend', p);
export const project_buckets_scope_live = () => t('project_buckets_scope_live');
// F15 круг 2 — пустой поиск без архивных предлагает расширить охват.
export const project_search_include_archived = () => t('project_search_include_archived');
export const project_search_empty_title = (p) => t('project_search_empty_title', p);
export const project_search_empty_hint = () => t('project_search_empty_hint');
export const project_search_clear = () => t('project_search_clear');
export const project_buckets_scope_with_archived = () => t('project_buckets_scope_with_archived');
// NC-38 (F17) — имя полосы прогресса в шапке карточки проекта.
export const project_header_progress_label = () => t('project_header_progress_label');
// PPC-F6 (F17) — факты отмены на «Обзоре».
export const project_overview_cancel_reason = (p) => t('project_overview_cancel_reason', p);
export const project_overview_cancel_no_details = () => t('project_overview_cancel_no_details');
// PLM-5 (F17) — маржа портфеля называет проекты в других валютах.
export const project_margin_excluded_currency = (p) => t('project_margin_excluded_currency', p);
// F82 (PPC-F7) — мастер спецификации называет активную версию кода.
export const route_bom_new_active_exists = (p) => t('route_bom_new_active_exists', p);
// F19 (SR-F15) — строка отбора реестра пользователей.
export const route_admin_users_filter_aria = () => t('route_admin_users_filter_aria');
export const route_admin_users_filter_search = () => t('route_admin_users_filter_search');
export const route_admin_users_filter_search_placeholder = () => t('route_admin_users_filter_search_placeholder');
export const route_admin_users_filter_company = () => t('route_admin_users_filter_company');
export const route_admin_users_filter_active = () => t('route_admin_users_filter_active');
export const route_admin_users_filter_active_yes = () => t('route_admin_users_filter_active_yes');
export const route_admin_users_filter_active_no = () => t('route_admin_users_filter_active_no');
export const route_admin_users_filter_any = () => t('route_admin_users_filter_any');
export const route_admin_users_filter_reset = () => t('route_admin_users_filter_reset');
export const route_admin_users_col_companies = () => t('route_admin_users_col_companies');
// F21 (SR-F7, SR-F11) — правило видимой компании и признак расхождения роли.
export const route_admin_users_companies_rule = () => t('route_admin_users_companies_rule');
export const route_admin_users_role_mixed = () => t('route_admin_users_role_mixed');
export const route_admin_users_role_mixed_title = (p) => t('route_admin_users_role_mixed_title', p);
export const route_admin_users_role_rule_company = () => t('route_admin_users_role_rule_company');
export const route_admin_users_role_rule_aggregate = () => t('route_admin_users_role_rule_aggregate');
export const route_admin_users_role_mixed_item = (p) => t('route_admin_users_role_mixed_item', p);
export const route_admin_users_role_not_member = () => t('route_admin_users_role_not_member');
export const route_admin_users_filter_total = (p) => t('route_admin_users_filter_total', p);
export const route_admin_users_restore_access = () => t('route_admin_users_restore_access');
// F26 (хвост U-2) — карточка сотрудника: сессии и холдинги.
export const user_drawer_sessions_revoked = (p) => t('user_drawer_sessions_revoked', p);
export const user_drawer_title = () => t('user_drawer_title');
export const user_drawer_no_changes = () => t('user_drawer_no_changes');
export const user_drawer_unsaved_fields = (p) => t('user_drawer_unsaved_fields', p);
export const user_drawer_field_name = () => t('user_drawer_field_name');
export const user_drawer_field_phone = () => t('user_drawer_field_phone');
export const user_drawer_field_salary = () => t('user_drawer_field_salary');
export const user_drawer_instant_saved = () => t('user_drawer_instant_saved');
export const user_drawer_impersonate_locked = () => t('user_drawer_impersonate_locked');
export const role_abilities_preview_named = (p) => t('role_abilities_preview_named', p);
export const user_drawer_rate_after_save = () => t('user_drawer_rate_after_save');
export const user_drawer_discard_save = () => t('user_drawer_discard_save');
export const user_drawer_changed_name = (p) => t('user_drawer_changed_name', p);
export const user_drawer_changed_phone = (p) => t('user_drawer_changed_phone', p);
export const user_drawer_changed_salary = (p) => t('user_drawer_changed_salary', p);
export const user_drawer_changed_empty = () => t('user_drawer_changed_empty');
export const user_drawer_holdings_empty = () => t('user_drawer_holdings_empty');
export const user_drawer_unsaved = () => t('user_drawer_unsaved');
export const user_drawer_saved = () => t('user_drawer_saved');
export const user_drawer_save_failed = () => t('user_drawer_save_failed');
export const user_drawer_instant_hint = () => t('user_drawer_instant_hint');
export const user_drawer_discard_title = () => t('user_drawer_discard_title');
export const user_drawer_discard_body = () => t('user_drawer_discard_body');
export const user_drawer_discard_confirm = () => t('user_drawer_discard_confirm');
export const user_drawer_discard_cancel = () => t('user_drawer_discard_cancel');
export const user_drawer_sessions_revoke_failed = () => t('user_drawer_sessions_revoke_failed');
export const user_drawer_sessions_active = (p) => t('user_drawer_sessions_active', p);
export const user_drawer_sessions_none = () => t('user_drawer_sessions_none');
export const user_drawer_holding_added = () => t('user_drawer_holding_added');
// F20 (SR-F4/F5/F13) — панель доступа к модулям.
export const module_grants_count_overrides = (p) => t('module_grants_count_overrides', p);
export const module_grants_count_pins = (p) => t('module_grants_count_pins', p);
export const module_grants_count_resets = (p) => t('module_grants_count_resets', p);
export const module_grants_chip_legend = () => t('module_grants_chip_legend');
export const module_grants_switch_confirm = (p) => t('module_grants_switch_confirm', p);
export const module_grants_count_unsaved = (p) => t('module_grants_count_unsaved', p);
export const module_grants_switch_title = () => t('module_grants_switch_title');
export const module_grants_switch_go = () => t('module_grants_switch_go');
export const module_grants_stay = () => t('module_grants_stay');
export const module_grants_leave_title = () => t('module_grants_leave_title');
export const module_grants_leave_confirm_n = (p) => t('module_grants_leave_confirm_n', p);
export const module_grants_leave_go = () => t('module_grants_leave_go');
export const module_grant_role_vs_here = (p) => t('module_grant_role_vs_here', p);
export const module_grants_leave_confirm = () => t('module_grants_leave_confirm');
export const module_grants_panel_title = (p) => t('module_grants_panel_title', p);
export const module_grant_reason_required = () => t('module_grant_reason_required');
export const user_drawer_holding_remove_aria = (p) => t('user_drawer_holding_remove_aria', p);
export const route_admin_users_filter_count = (p) => t('route_admin_users_filter_count', p);
export const route_admin_users_filter_invites_note = () => t('route_admin_users_filter_invites_note');
export const route_bom_new_activate_blocked = (p) => t('route_bom_new_activate_blocked', p);
// PPC-F22 (F18) — стадия отменённого проекта подписью в шапке.
export const project_stage_at_cancel = (p) => t('project_stage_at_cancel', p);
export const project_buckets_legend_narrowed = (p) => t('project_buckets_legend_narrowed', p);
export const project_bucket_active = () => t('project_bucket_active');
export const project_bucket_at_risk = () => t('project_bucket_at_risk');
export const project_bucket_overdue = () => t('project_bucket_overdue');
export const project_bucket_on_hold = () => t('project_bucket_on_hold');
export const project_bucket_closed = () => t('project_bucket_closed');
export const project_bucket_cancelled = () => t('project_bucket_cancelled');
export const project_filter_status_label = () => t('project_filter_status_label');
export const project_stage_transition_title = (p) => t('project_stage_transition_title', p);
export const project_open_invoice_link = () => t('project_open_invoice_link');
export const milestone_invoice_amount_label = () => t('milestone_invoice_amount_label');
export const milestone_invoice_pct_of_so = (p) => t('milestone_invoice_pct_of_so', p);
export const task_log_owner_only = () => t('task_log_owner_only');
export const route_admin_users_col_costing_rate = () => t('route_admin_users_col_costing_rate');
export const route_admin_users_costing_rate_placeholder = () => t('route_admin_users_costing_rate_placeholder');
/** PLM-1 (пак `tails-closeout`, B13/F89; В-28) — оклад вместо ставки в карточке сотрудника. */
export const route_admin_users_monthly_salary = () => t('route_admin_users_monthly_salary');
export const route_admin_users_monthly_salary_placeholder = () => t('route_admin_users_monthly_salary_placeholder');
export const route_admin_users_hourly_rate_derived = (p) => t('route_admin_users_hourly_rate_derived', p);
export const route_admin_users_hourly_rate_none = () => t('route_admin_users_hourly_rate_none');
export const route_admin_users_costing_rate_saved = () => t('route_admin_users_costing_rate_saved');
export const project_health_update_title = () => t('project_health_update_title');
export const project_health_update_narrative_placeholder = () => t('project_health_update_narrative_placeholder');
export const project_health_update_submit = () => t('project_health_update_submit');
export const project_not_found_redirect_toast = () => t('project_not_found_redirect_toast');
export const task_bulk_actions_label = () => t('task_bulk_actions_label');
export const task_bulk_set_status = () => t('task_bulk_set_status');
export const task_bulk_delete = () => t('task_bulk_delete');
export const task_bulk_assign = () => t('task_bulk_assign');
export const task_bulk_select_all = () => t('task_bulk_select_all');
export const task_bulk_clear = () => t('task_bulk_clear');
export const task_hotkey_hint = () => t('task_hotkey_hint');
export const sse_connection_open = () => t('sse_connection_open');
export const sse_connection_reconnecting = () => t('sse_connection_reconnecting');
export const sse_connection_closed = () => t('sse_connection_closed');
// ─── accept-invite page ────────────────────────────────────────────────
export const accept_invite_title = () => t('accept_invite_title');
export const accept_invite_lede = () => t('accept_invite_lede');
export const accept_invite_label_email = () => t('accept_invite_label_email');
export const accept_invite_label_role = () => t('accept_invite_label_role');
export const accept_invite_label_invited_by = () => t('accept_invite_label_invited_by');
export const accept_invite_new_password = () => t('accept_invite_new_password');
export const accept_invite_password_hint = () => t('accept_invite_password_hint');
export const accept_invite_confirm_password = () => t('accept_invite_confirm_password');
export const accept_invite_submit = () => t('accept_invite_submit');
export const accept_invite_error_generic = () => t('accept_invite_error_generic');
export const accept_invite_no_token_title = () => t('accept_invite_no_token_title');
export const accept_invite_no_token_body = () => t('accept_invite_no_token_body');
export const accept_invite_expired_title = () => t('accept_invite_expired_title');
export const accept_invite_expired_body = () => t('accept_invite_expired_body');
export const accept_invite_taken_title = () => t('accept_invite_taken_title');
export const accept_invite_taken_body = () => t('accept_invite_taken_body');
export const accept_invite_network_title = () => t('accept_invite_network_title');
export const accept_invite_network_body = () => t('accept_invite_network_body');
export const accept_invite_back_to_login = () => t('accept_invite_back_to_login');
// ─── forgot-password (request link) ────────────────────────────────────
export const forgot_password_title = () => t('forgot_password_title');
export const forgot_password_lede = () => t('forgot_password_lede');
export const forgot_password_submit = () => t('forgot_password_submit');
export const forgot_password_sent = () => t('forgot_password_sent');
// ─── reset-password (confirm with token) ───────────────────────────────
export const reset_password_title = () => t('reset_password_title');
export const reset_password_lede = () => t('reset_password_lede');
export const reset_password_new = () => t('reset_password_new');
export const reset_password_hint = () => t('reset_password_hint');
export const reset_password_confirm = () => t('reset_password_confirm');
export const reset_password_submit = () => t('reset_password_submit');
export const reset_password_error_generic = () => t('reset_password_error_generic');
export const reset_password_success_title = () => t('reset_password_success_title');
export const reset_password_success_body = () => t('reset_password_success_body');
export const reset_password_no_token_title = () => t('reset_password_no_token_title');
export const reset_password_no_token_body = () => t('reset_password_no_token_body');
export const reset_password_expired_title = () => t('reset_password_expired_title');
export const reset_password_expired_body = () => t('reset_password_expired_body');
export const reset_password_network_title = () => t('reset_password_network_title');
export const reset_password_network_body = () => t('reset_password_network_body');
export const reset_password_request_new = () => t('reset_password_request_new');
// ─── admin: invites section ────────────────────────────────────────────
export const admin_invites_section_title = () => t('admin_invites_section_title');
export const admin_invites_section_description = () => t('admin_invites_section_description');
export const admin_invites_empty = () => t('admin_invites_empty');
export const admin_invites_col_email = () => t('admin_invites_col_email');
export const admin_invites_col_role = () => t('admin_invites_col_role');
export const admin_invites_col_status = () => t('admin_invites_col_status');
export const admin_invites_col_invited_by = () => t('admin_invites_col_invited_by');
export const admin_invites_invited_by_line = (p) => t('admin_invites_invited_by_line', p);
export const admin_invites_col_expires_at = () => t('admin_invites_col_expires_at');
export const admin_invites_status_pending = () => t('admin_invites_status_pending');
export const admin_invites_status_accepted = () => t('admin_invites_status_accepted');
export const admin_invites_status_expired = () => t('admin_invites_status_expired');
export const admin_invites_btn_resend = () => t('admin_invites_btn_resend');
export const admin_invites_btn_issue_link = () => t('admin_invites_btn_issue_link');
export const admin_invites_reissue_confirm_title = () => t('admin_invites_reissue_confirm_title');
export const admin_invites_reissue_confirm_body = (p) => t('admin_invites_reissue_confirm_body', p);
export const admin_invites_btn_revoke = () => t('admin_invites_btn_revoke');
export const admin_invites_revoke_confirm = (p) => t('admin_invites_revoke_confirm', p);
export const admin_invites_revoke_confirm_title = () => t('admin_invites_revoke_confirm_title');
// ─── admin: invite toasts ──────────────────────────────────────────────
export const admin_invite_toast_sent_title = () => t('admin_invite_toast_sent_title');
export const admin_invite_toast_sent_body = (p) => t('admin_invite_toast_sent_body', p);
export const admin_invite_toast_email_failed_title = () => t('admin_invite_toast_email_failed_title');
export const admin_invite_toast_email_failed_body = (p) => t('admin_invite_toast_email_failed_body', p);
export const admin_invite_toast_error_title = () => t('admin_invite_toast_error_title');
export const admin_invite_already_member = (p) => t('admin_invite_already_member', p);
export const admin_invite_open_card = () => t('admin_invite_open_card');
export const admin_invite_toast_error_body = (p) => t('admin_invite_toast_error_body', p);
export const admin_invite_toast_bulk_title = () => t('admin_invite_toast_bulk_title');
export const admin_invite_toast_bulk_body = (p) => t('admin_invite_toast_bulk_body', p);
export const admin_invite_toast_resent_title = () => t('admin_invite_toast_resent_title');
export const admin_invite_toast_resent_body = (p) => t('admin_invite_toast_resent_body', p);
export const admin_invite_toast_revoked_title = () => t('admin_invite_toast_revoked_title');
export const admin_invite_toast_revoked_body = (p) => t('admin_invite_toast_revoked_body', p);
export const invite_delivery_sent = () => t('invite_delivery_sent');
export const invite_delivery_failed = () => t('invite_delivery_failed');
export const invite_delivery_failed_hint = () => t('invite_delivery_failed_hint');
export const invite_delivery_queued = () => t('invite_delivery_queued');
export const invite_delivery_queued_down = () => t('invite_delivery_queued_down');
export const invite_delivery_unknown = () => t('invite_delivery_unknown');
export const invite_delivery_unknown_hint = () => t('invite_delivery_unknown_hint');
export const invite_delivery_untracked = () => t('invite_delivery_untracked');
export const invite_channel_down = (p) => t('invite_channel_down', p);
export const invite_channel_down_list_hint = () => t('invite_channel_down_list_hint');
export const admin_invites_resend_title = () => t('admin_invites_resend_title');
export const admin_invite_toast_show = () => t('admin_invite_toast_show');
export const invite_delivery_sent_at = (p) => t('invite_delivery_sent_at', p);
export const invite_delivery_sent_title = () => t('invite_delivery_sent_title');
export const admin_invites_status_replaced = () => t('admin_invites_status_replaced');
export const admin_invites_created_at = (p) => t('admin_invites_created_at', p);
export const admin_invites_replaced_at = (p) => t('admin_invites_replaced_at', p);
export const route_admin_users_bulk_delivery_sent_of = (p) => t('route_admin_users_bulk_delivery_sent_of', p);
export const route_admin_users_bulk_retry_failed = (p) => t('route_admin_users_bulk_retry_failed', p);
export const admin_invites_col_email_delivery = () => t('admin_invites_col_email_delivery');
export const route_admin_users_bulk_delivery_queued = (p) => t('route_admin_users_bulk_delivery_queued', p);
export const route_admin_users_bulk_delivery_queued_down = (p) => t('route_admin_users_bulk_delivery_queued_down', p);
export const route_admin_users_bulk_delivery_failed = (p) => t('route_admin_users_bulk_delivery_failed', p);
export const route_admin_users_bulk_delivery_unknown = (p) => t('route_admin_users_bulk_delivery_unknown', p);
// Task-module — карточка задачи (FE-2/FE-3/FE-7).
export const task_assignee_add = () => t('task_assignee_add');
export const task_assignee_edit_aria = () => t('task_assignee_edit_aria');
export const task_assignee_add_aria = () => t('task_assignee_add_aria');
export const task_audit_tab = () => t('task_audit_tab');
export const task_audit_count_aria = (p) => t('task_audit_count_aria', p);
export const task_audit_empty_title = () => t('task_audit_empty_title');
export const task_audit_empty_hint = () => t('task_audit_empty_hint');
export const task_timeline_show = () => t('task_timeline_show');
// FE-24 — email-инвайт участника проекта.
export const task_invite_button = () => t('task_invite_button');
export const task_invite_dialog_title = () => t('task_invite_dialog_title');
export const task_invite_email_label = () => t('task_invite_email_label');
export const task_invite_email_placeholder = () => t('task_invite_email_placeholder');
export const task_invite_role_label = () => t('task_invite_role_label');
export const task_invite_submit = () => t('task_invite_submit');
export const task_invite_cancel = () => t('task_invite_cancel');
export const task_invite_sent_toast = (p) => t('task_invite_sent_toast', p);
export const task_invite_pending_badge = () => t('task_invite_pending_badge');
// BE-6 error codes (error_by_code lookup).
export const error_task_module_email_is_existing_user = () => t('error_task_module_email_is_existing_user');
export const error_task_module_project_lead_required = () => t('error_task_module_project_lead_required');
// ─── access-control: module-grants admin matrix (F3) ──────────────────
export const module_grants_title = () => t('module_grants_title');
export const module_grants_hint = () => t('module_grants_hint');
export const module_grants_company_label = () => t('module_grants_company_label');
export const module_grant_col_module = () => t('module_grant_col_module');
export const module_grant_col_access = () => t('module_grant_col_access');
export const module_grant_state_default = () => t('module_grant_state_default');
export const module_grant_state_allow = () => t('module_grant_state_allow');
export const module_grant_state_deny = () => t('module_grant_state_deny');
export const module_grant_effective_on = () => t('module_grant_effective_on');
export const module_grant_effective_off = () => t('module_grant_effective_off');
export const module_grant_reason_label = () => t('module_grant_reason_label');
export const module_grant_reason_placeholder = () => t('module_grant_reason_placeholder');
export const module_grant_save = () => t('module_grant_save');
export const module_grant_saved = () => t('module_grant_saved');
export const module_grant_self_lockout = () => t('module_grant_self_lockout');
export const module_name_agent_review = () => t('module_name_agent_review');
export const module_name_inventory = () => t('module_name_inventory');
export const module_name_production = () => t('module_name_production');
export const module_name_finance = () => t('module_name_finance');
export const module_name_projects = () => t('module_name_projects');
export const module_name_tasks = () => t('module_name_tasks');
export const module_name_reporting = () => t('module_name_reporting');
export const module_name_admin = () => t('module_name_admin');
export const module_forbidden_title = () => t('module_forbidden_title');
export const module_forbidden_desc = () => t('module_forbidden_desc');
export const module_forbidden_cta_dashboard = () => t('module_forbidden_cta_dashboard');
export const module_grants_select_company = () => t('module_grants_select_company');
/** T2A-F12(б) — подпись к числу переопределений на чипе компании. */
export const module_grants_chip_overrides = () => t('module_grants_chip_overrides');
/** Раунд 2 гейта — разрешения и запреты раздельно: запрет это риск, сумма его прячет. */
export const module_grants_chip_title = (p) => t('module_grants_chip_title', { allow: String(p.allow), deny: String(p.deny) });
export const module_grant_overridden = () => t('module_grant_overridden');
/** Явный грант, совпавший с ролью: подсветки не заслуживает, но назван быть должен. */
export const module_grant_explicit_same_as_role = () => t('module_grant_explicit_same_as_role');
/** Строка с несохранённым изменением: состояние названо словом, а не одним цветом. */
export const module_grant_unsaved = () => t('module_grant_unsaved');
/** Переход от полосы черновика к подвалу с полем причины и сохранением. */
export const module_grant_reason_hint = () => t('module_grant_reason_hint');
export const module_grant_role_prefix = () => t('module_grant_role_prefix');
export const cp_field_company = () => t('cp_field_company');
export const cp_company_context = () => t('cp_company_context');
export const cp_filter_all_companies = () => t('cp_filter_all_companies');
export const cp_move_button = () => t('cp_move_button');
export const cp_move_title = () => t('cp_move_title');
export const cp_move_target = () => t('cp_move_target');
export const cp_move_reason = () => t('cp_move_reason');
export const cp_move_submit = () => t('cp_move_submit');
export const cp_move_success = () => t('cp_move_success');
export const cp_move_blocked = () => t('cp_move_blocked');
export const cp_move_consequence = () => t('cp_move_consequence');
export const cp_move_blocked_inline = () => t('cp_move_blocked_inline');
export const cp_move_disabled_hint = () => t('cp_move_disabled_hint');
export const cp_dep_invoices = () => t('cp_dep_invoices');
export const cp_dep_payments = () => t('cp_dep_payments');
export const cp_dep_settlements = () => t('cp_dep_settlements');
export const module_grant_reset = () => t('module_grant_reset');
// ─── integrations platform (пак integrations — F1 каталог+wizard, F2 дашборд) ──
export const integ_platform_title = () => t('integ_platform_title');
export const integ_platform_desc = () => t('integ_platform_desc');
export const integ_platform_add = () => t('integ_platform_add');
export const integ_platform_empty_title = () => t('integ_platform_empty_title');
export const integ_platform_empty_desc = () => t('integ_platform_empty_desc');
export const integ_platform_col_provider = () => t('integ_platform_col_provider');
export const integ_platform_col_status = () => t('integ_platform_col_status');
export const integ_platform_col_health = () => t('integ_platform_col_health');
export const integ_platform_col_schedule = () => t('integ_platform_col_schedule');
export const integ_platform_col_last_pull = () => t('integ_platform_col_last_pull');
export const integ_platform_action_test = () => t('integ_platform_action_test');
export const integ_platform_action_disable = () => t('integ_platform_action_disable');
export const integ_platform_link = () => t('integ_platform_link');
export const integ_platform_status_label = (p) => t(`integ_platform_status_${p.status}`);
export const integ_platform_schedule_label = (p) => t(`integ_platform_schedule_${p.schedule}`);
export const integ_platform_wizard_title = () => t('integ_platform_wizard_title');
export const integ_platform_step_provider = () => t('integ_platform_step_provider');
export const integ_platform_step_creds = () => t('integ_platform_step_creds');
export const integ_platform_step_test = () => t('integ_platform_step_test');
export const integ_platform_step_done = () => t('integ_platform_step_done');
export const integ_platform_catalog_hint = () => t('integ_platform_catalog_hint');
export const integ_platform_coming_soon = () => t('integ_platform_coming_soon');
export const integ_platform_caps_label = () => t('integ_platform_caps_label');
export const integ_platform_multi_entity = () => t('integ_platform_multi_entity');
export const integ_platform_creds_hint = () => t('integ_platform_creds_hint');
export const integ_platform_field_label = (p) => t(`integ_platform_field_${p.field}`);
export const integ_platform_btn_create = () => t('integ_platform_btn_create');
export const integ_platform_btn_test = () => t('integ_platform_btn_test');
export const integ_platform_testing = () => t('integ_platform_testing');
export const integ_platform_test_ok = () => t('integ_platform_test_ok');
export const integ_platform_test_counterparties = (p) => t('integ_platform_test_counterparties', p);
export const integ_platform_test_operations = (p) => t('integ_platform_test_operations', p);
export const integ_platform_test_entities = (p) => t('integ_platform_test_entities', p);
export const integ_platform_test_failed = () => t('integ_platform_test_failed');
export const integ_platform_done_title = () => t('integ_platform_done_title');
export const integ_platform_done_desc = () => t('integ_platform_done_desc');
export const integ_platform_capability_label = (p) => t(`integ_platform_capability_${p.capability}`);
export const integ_platform_toast_created = () => t('integ_platform_toast_created');
export const integ_platform_toast_create_failed = () => t('integ_platform_toast_create_failed');
export const integ_platform_toast_disabled = () => t('integ_platform_toast_disabled');
export const integ_platform_disable_confirm_title = () => t('integ_platform_disable_confirm_title');
export const integ_platform_disable_confirm_desc = () => t('integ_platform_disable_confirm_desc');
export const integ_platform_hint_label = (p) => t(`integ_platform_hint_${p.field}`);
// integrations F7 — counterparty provenance / scope / needs_review
export const cp_column_source = () => t('cp_column_source');
export const cp_scope_holding = () => t('cp_scope_holding');
export const cp_scope_entity = () => t('cp_scope_entity');
export const cp_badge_needs_review = () => t('cp_badge_needs_review');
export const cp_filter_source = () => t('cp_filter_source');
export const cp_filter_all_sources = () => t('cp_filter_all_sources');
export const cp_filter_scope = () => t('cp_filter_scope');
export const cp_filter_all_scopes = () => t('cp_filter_all_scopes');
export const cp_filter_needs_review = () => t('cp_filter_needs_review');
export const cp_detail_external_refs = () => t('cp_detail_external_refs');
export const cp_review_hint = () => t('cp_review_hint');
// integrations F10/F4/F5/F3 — connection detail
export const integ_detail_overview = () => t('integ_detail_overview');
export const integ_detail_tab_scope = () => t('integ_detail_tab_scope');
export const integ_detail_tab_schedule = () => t('integ_detail_tab_schedule');
export const integ_detail_tab_routing = () => t('integ_detail_tab_routing');
export const integ_detail_test = () => t('integ_detail_test');
export const integ_detail_sync = () => t('integ_detail_sync');
export const integ_detail_syncing = () => t('integ_detail_syncing');
export const integ_detail_testing = () => t('integ_detail_testing');
export const integ_detail_sync_summary = (p) => t('integ_detail_sync_summary', p);
export const integ_detail_status = () => t('integ_detail_status');
export const integ_detail_back = () => t('integ_detail_back');
export const integ_scope_hint = () => t('integ_scope_hint');
export const integ_save = () => t('integ_save');
export const integ_saved = () => t('integ_saved');
export const integ_schedule_hint = () => t('integ_schedule_hint');
export const integ_routing_hint = () => t('integ_routing_hint');
export const integ_routing_unmapped = () => t('integ_routing_unmapped');
export const integ_routing_company = () => t('integ_routing_company');
export const integ_routing_empty = () => t('integ_routing_empty');
export const integ_health_not_synced = () => t('integ_health_not_synced');
export const integ_next_step_test = () => t('integ_next_step_test');
// integrations F8 — payment cash-flow dimensions
export const payment_column_cf = () => t('payment_column_cf');
export const payment_badge_transfer = () => t('payment_badge_transfer');
export const payment_filter_source = () => t('payment_filter_source');
export const payment_filter_all_sources = () => t('payment_filter_all_sources');
export const payment_project_tag = () => t('payment_project_tag');
// integrations F9 — payment reconcile status
export const payment_reconcile_filter = () => t('payment_reconcile_filter');
export const payment_reconcile_all = () => t('payment_reconcile_all');
export const payment_reconcile_live = () => t('payment_reconcile_live');
export const payment_reconcile_banner = (p) => t('payment_reconcile_banner', p);
export const payment_reconcile_match_hint = () => t('payment_reconcile_match_hint');
export const payment_reconcile_label = (p) => t(`payment_reconcile_${p.status}`);
// integrations F6 — article mapping
export const integ_detail_tab_mapping = () => t('integ_detail_tab_mapping');
export const integ_mapping_hint = () => t('integ_mapping_hint');
export const integ_mapping_external = () => t('integ_mapping_external');
export const integ_mapping_target = () => t('integ_mapping_target');
export const integ_mapping_empty = () => t('integ_mapping_empty');
export const integ_mapping_count = (p) => t('integ_mapping_count', p);
export const integ_mapping_search = () => t('integ_mapping_search');
export const integ_mapping_only_overridden = () => t('integ_mapping_only_overridden');
export const integ_mapping_default_badge = () => t('integ_mapping_default_badge');
export const integ_mapping_overridden_badge = () => t('integ_mapping_overridden_badge');
// ─── task-module enum labels (I1.1 — миграция lib/tasks/i18n/labels.ts) ───
export const task_priority_urgent = () => t('task_priority_urgent');
export const task_priority_high = () => t('task_priority_high');
export const task_priority_medium = () => t('task_priority_medium');
export const task_priority_low = () => t('task_priority_low');
export const task_priority_none = () => t('task_priority_none');
export const task_ref_role_mentions = () => t('task_ref_role_mentions');
export const task_ref_role_affects = () => t('task_ref_role_affects');
export const task_ref_role_evidence = () => t('task_ref_role_evidence');
export const state_group_backlog = () => t('state_group_backlog');
export const state_group_unstarted = () => t('state_group_unstarted');
export const state_group_started = () => t('state_group_started');
/** B1 (§3.2) — группа «Ожидание»: работа приостановлена, но не снята. */
export const state_group_paused = () => t('state_group_paused');
export const state_group_completed = () => t('state_group_completed');
export const state_group_cancelled = () => t('state_group_cancelled');
export const state_group_triage = () => t('state_group_triage');
export const subject_type_work_order = () => t('subject_type_work_order');
export const subject_type_job_card = () => t('subject_type_job_card');
export const subject_type_anomaly = () => t('subject_type_anomaly');
export const subject_type_source_conflict = () => t('subject_type_source_conflict');
export const subject_type_approval = () => t('subject_type_approval');
export const subject_type_stock_entry = () => t('subject_type_stock_entry');
export const subject_type_stock_reconciliation = () => t('subject_type_stock_reconciliation');
export const subject_type_invoice_ar = () => t('subject_type_invoice_ar');
export const subject_type_invoice_ap = () => t('subject_type_invoice_ap');
export const subject_type_payment = () => t('subject_type_payment');
export const subject_type_counterparty = () => t('subject_type_counterparty');
export const subject_type_item = () => t('subject_type_item');
export const subject_type_batch = () => t('subject_type_batch');
export const subject_type_period_close = () => t('subject_type_period_close');
export const subject_type_esf_filing = () => t('subject_type_esf_filing');
export const subject_type_kpn_filing = () => t('subject_type_kpn_filing');
export const subject_type_integration = () => t('subject_type_integration');
export const dependency_type_blocked_by = () => t('dependency_type_blocked_by');
export const dependency_type_relates_to = () => t('dependency_type_relates_to');
export const dependency_type_duplicate = () => t('dependency_type_duplicate');
export const dependency_type_start_before = () => t('dependency_type_start_before');
export const dependency_type_finish_before = () => t('dependency_type_finish_before');
export const dependency_type_implemented_by = () => t('dependency_type_implemented_by');
export const field_type_text = () => t('field_type_text');
export const field_type_rich_text = () => t('field_type_rich_text');
export const field_type_number = () => t('field_type_number');
export const field_type_decimal = () => t('field_type_decimal');
export const field_type_money = () => t('field_type_money');
export const field_type_date = () => t('field_type_date');
export const field_type_datetime = () => t('field_type_datetime');
export const field_type_single_select = () => t('field_type_single_select');
export const field_type_multi_select = () => t('field_type_multi_select');
export const field_type_user_picker = () => t('field_type_user_picker');
export const field_type_entity_ref = () => t('field_type_entity_ref');
export const field_type_checkbox = () => t('field_type_checkbox');
export const field_type_url = () => t('field_type_url');
export const project_kind_free = () => t('project_kind_free');
export const project_kind_work_order_execution = () => t('project_kind_work_order_execution');
export const project_kind_period_close = () => t('project_kind_period_close');
export const project_kind_onboarding_client = () => t('project_kind_onboarding_client');
export const project_kind_sku_launch = () => t('project_kind_sku_launch');
export const project_kind_warehouse_opening = () => t('project_kind_warehouse_opening');
export const project_kind_integration_setup = () => t('project_kind_integration_setup');
export const project_kind_audit_prep = () => t('project_kind_audit_prep');
export const project_kind_tax_filing = () => t('project_kind_tax_filing');
export const project_kind_physical_count = () => t('project_kind_physical_count');
export const project_kind_budget_cycle = () => t('project_kind_budget_cycle');
export const project_kind_production_sprint = () => t('project_kind_production_sprint');
export const project_status_planning = () => t('project_status_planning');
export const project_status_active = () => t('project_status_active');
export const project_status_on_hold = () => t('project_status_on_hold');
export const project_status_archived = () => t('project_status_archived');
export const estimate_uom_hours = () => t('estimate_uom_hours');
export const estimate_uom_story_points = () => t('estimate_uom_story_points');
export const estimate_uom_days = () => t('estimate_uom_days');
export const due_bucket_overdue = () => t('due_bucket_overdue');
export const due_bucket_today = () => t('due_bucket_today');
export const due_bucket_this_week = () => t('due_bucket_this_week');
export const due_bucket_next_week = () => t('due_bucket_next_week');
export const due_bucket_later = () => t('due_bucket_later');
export const due_bucket_no_date = () => t('due_bucket_no_date');
// ─── task-module error codes (I1.3 — M2M пак + last_default_state) ────
export const error_task_module_last_default_state = () => t('error_task_module_last_default_state');
export const error_task_module_already_member = () => t('error_task_module_already_member');
export const error_task_module_cannot_remove_primary = () => t('error_task_module_cannot_remove_primary');
export const error_task_module_membership_cross_entity = () => t('error_task_module_membership_cross_entity');
export const error_task_module_not_member = () => t('error_task_module_not_member');
export const error_task_module_endpoints_not_members = () => t('error_task_module_endpoints_not_members');
export const error_task_module_dependency_project_forbidden = (p) => t('error_task_module_dependency_project_forbidden', p);
// ─── task-module i18n (I2 миграция компонентов) ───
export const tm_cancel = () => t('tm_cancel');
export const tm_save = () => t('tm_save');
export const tm_create = () => t('tm_create');
export const tm_delete = () => t('tm_delete');
export const tm_remove = () => t('tm_remove');
export const tm_close = () => t('tm_close');
export const tm_send = () => t('tm_send');
export const tm_apply = () => t('tm_apply');
export const tm_clear = () => t('tm_clear');
export const tm_reset = () => t('tm_reset');
export const tm_add = () => t('tm_add');
export const tm_rename = () => t('tm_rename');
export const tm_expand = () => t('tm_expand');
export const tm_collapse = () => t('tm_collapse');
export const tm_done = () => t('tm_done');
export const tm_go_to = () => t('tm_go_to');
export const tm_share = () => t('tm_share');
export const tm_complete = () => t('tm_complete');
export const tm_unblock = () => t('tm_unblock');
export const tm_archive = () => t('tm_archive');
export const tm_add_inline = () => t('tm_add_inline');
export const tm_name = () => t('tm_name');
export const tm_description = () => t('tm_description');
export const tm_status = () => t('tm_status');
export const tm_state_pending = () => t('tm_state_pending');
export const tm_priority = () => t('tm_priority');
export const tm_due = () => t('tm_due');
export const tm_start = () => t('tm_start');
export const tm_date = () => t('tm_date');
export const tm_time = () => t('tm_time');
export const tm_estimate = () => t('tm_estimate');
export const tm_project = () => t('tm_project');
/**
 * FND-18 — заголовок колонки порядкового номера строки.
 *
 * Раунд 1 гейта (UX): «№» сталкивался с «№» внутри названий задач
 * («Перф-задача №102») — в одной строке два разных числа под одной подписью.
 * «Стр.» называет адрес строки и с нумерацией внутри имён не путается.
 */
export const tm_col_ordinal = () => t('tm_col_ordinal');
/** Счётчик просрочки без ведущего интерпункта — для пилюли со значком. */
export const tm_n_overdue_plain = (p) => t('tm_n_overdue_plain', p);
/**
 * WTU-FND-I — та же пилюля, когда рядом стоит знаменатель по всему набору.
 * «11 из 128 · 2 просроч.» читается так, будто просрочка тоже из 128; она из 11.
 */
export const tm_n_overdue_on_page = (p) => t('tm_n_overdue_on_page', p);
/** WTU-FND-H — подпись поля перехода к строке по её номеру. */
export const tm_goto_row_label = () => t('tm_goto_row_label');
/** WTU-FND-H — кнопка перехода. */
export const tm_goto_row_action = () => t('tm_goto_row_action');
/** WTU-FND-H — какие номера доступны: переход работает по загруженному набору. */
export const tm_goto_row_out_of_range = (p) => t('tm_goto_row_out_of_range', p);
/**
 * WTU-FND-G — в постраничном режиме положение называет ПЕЙДЖЕР, а плашка сверху
 * остаётся при том, чего он не говорит: порядок сортировки. Прежняя подпись
 * повторяла «из 2068» на другом конце экрана.
 */
export const tm_order_only = (p) => t('tm_order_only', p);
/** Раунд 4 гейта захода 8 — подтверждение массовой архивации (§11.4). */
export const tm_bulk_archive_confirm_title = (p) => t('tm_bulk_archive_confirm_title', p);
/** Хвост списка опознания: «и ещё N». */
export const tm_bulk_archive_confirm_rest = (p) => t('tm_bulk_archive_confirm_rest', p);
/** Что произойдёт и как вернуть — отдельно от «что именно архивирую». */
export const tm_bulk_archive_confirm_body = () => t('tm_bulk_archive_confirm_body');
/** Доступное имя колонки номера: сам заголовок — глиф «#», словами он в `aria-label`. */
export const tm_col_ordinal_aria = () => t('tm_col_ordinal_aria');
/** Оговорка счётчика группы, когда на экране срез выдачи, а не вся она. */
export const tm_group_count_on_page = () => t('tm_group_count_on_page');
export const tm_projects = () => t('tm_projects');
export const tm_group = () => t('tm_group');
export const tm_role = () => t('tm_role');
export const tm_labels = () => t('tm_labels');
export const tm_fields = () => t('tm_fields');
export const tm_field = () => t('tm_field');
export const tm_value = () => t('tm_value');
export const tm_key = () => t('tm_key');
export const tm_type = () => t('tm_type');
export const tm_order = () => t('tm_order');
export const tm_parent = () => t('tm_parent');
export const tm_assignees = () => t('tm_assignees');
export const tm_assignee = () => t('tm_assignee');
export const tm_author = () => t('tm_author');
export const tm_hours = () => t('tm_hours');
export const tm_color = () => t('tm_color');
export const tm_view = () => t('tm_view');
export const tm_list = () => t('tm_list');
export const tm_graph = () => t('tm_graph');
export const tm_company = () => t('tm_company');
export const tm_member = () => t('tm_member');
export const tm_members = () => t('tm_members');
export const tm_watcher = () => t('tm_watcher');
export const tm_lead = () => t('tm_lead');
export const tm_user = () => t('tm_user');
export const tm_users = () => t('tm_users');
export const tm_roles = () => t('tm_roles');
export const tm_groups = () => t('tm_groups');
export const tm_todo = () => t('tm_todo');
export const tm_completed_f = () => t('tm_completed_f');
export const tm_cancelled_f = () => t('tm_cancelled_f');
export const tm_triage = () => t('tm_triage');
export const tm_risk = () => t('tm_risk');
export const tm_today = () => t('tm_today');
export const tm_tomorrow = () => t('tm_tomorrow');
export const tm_yesterday = () => t('tm_yesterday');
export const tm_yesterday_lc = () => t('tm_yesterday_lc');
export const tm_just_now = () => t('tm_just_now');
export const tm_day = () => t('tm_day');
export const tm_days_cap = () => t('tm_days_cap');
export const tm_week = () => t('tm_week');
export const tm_month = () => t('tm_month');
export const tm_quarter = () => t('tm_quarter');
export const tm_day_lc = () => t('tm_day_lc');
export const tm_day_gen = () => t('tm_day_gen');
export const tm_days_gen = () => t('tm_days_gen');
export const tm_days_short = () => t('tm_days_short');
export const tm_hours_short = () => t('tm_hours_short');
export const tm_no_due = () => t('tm_no_due');
export const tm_no_dates = () => t('tm_no_dates');
export const tm_wd_mon = () => t('tm_wd_mon');
export const tm_wd_tue = () => t('tm_wd_tue');
export const tm_wd_wed = () => t('tm_wd_wed');
export const tm_wd_thu = () => t('tm_wd_thu');
export const tm_wd_fri = () => t('tm_wd_fri');
export const tm_wd_sat = () => t('tm_wd_sat');
export const tm_wd_sun = () => t('tm_wd_sun');
export const tm_mon_jan = () => t('tm_mon_jan');
export const tm_mon_feb = () => t('tm_mon_feb');
export const tm_mon_mar = () => t('tm_mon_mar');
export const tm_mon_apr = () => t('tm_mon_apr');
export const tm_mon_may = () => t('tm_mon_may');
export const tm_mon_jun = () => t('tm_mon_jun');
export const tm_mon_jul = () => t('tm_mon_jul');
export const tm_mon_aug = () => t('tm_mon_aug');
export const tm_mon_sep = () => t('tm_mon_sep');
export const tm_mon_oct = () => t('tm_mon_oct');
export const tm_mon_nov = () => t('tm_mon_nov');
export const tm_mon_dec = () => t('tm_mon_dec');
export const tm_loading = () => t('tm_loading');
/** F7 — экран «задачи нет»: deep-link на удалённую задачу (404). */
export const tm_task_not_found_title = () => t('tm_task_not_found_title');
export const tm_task_not_found_body = () => t('tm_task_not_found_body');
export const tm_task_not_found_cta = () => t('tm_task_not_found_cta');
/** F7 / UX-A1 — отказ в доступе (403): задача есть, права нет; названо, у кого просить. */
export const tm_task_forbidden_title = () => t('tm_task_forbidden_title');
export const tm_task_forbidden_body = () => t('tm_task_forbidden_body');
/** F7 / UX-A2 — третья ветка: 500 / сетевой сбой, повторяемо. */
export const tm_task_load_failed_title = () => t('tm_task_load_failed_title');
export const tm_task_load_failed_body = () => t('tm_task_load_failed_body');
/**
 * Гейт A раунд 19 — после второго отказа подряд совет обязан следовать за
 * состоянием: «попробуйте ещё раз» уже отработано и ведёт в разжалованное
 * действие.
 */
export const tm_task_load_failed_body_escalated = () => t('tm_task_load_failed_body_escalated');
/** F7 / UX-A4 — второй выход, когда контекст проекта известен. */
export const tm_task_back_to_project = () => t('tm_task_back_to_project');
/** F7 / гейт A раунд 5 — отказ буфера обмена не должен быть немым no-op. */
export const tm_task_trace_copy_failed = () => t('tm_task_trace_copy_failed');
/**
 * Гейт A раунд 12 — та же инструкция там, где клавиатуры нет. «Ctrl+C» на
 * сенсорной поверхности невыполним, а именно этот текст остаётся единственным
 * рабочим путём, когда буфер обмена недоступен.
 */
export const tm_task_trace_copy_failed_touch = () => t('tm_task_trace_copy_failed_touch');
export const tm_searching = () => t('tm_searching');
export const tm_search_ph = () => t('tm_search_ph');
export const tm_nothing_found = () => t('tm_nothing_found');
export const tm_not_found = () => t('tm_not_found');
export const tm_no_matches = () => t('tm_no_matches');
export const tm_no_tasks = () => t('tm_no_tasks');
export const tm_add_task = () => t('tm_add_task');
export const tm_new_task = () => t('tm_new_task');
export const tm_new_task_ph = () => t('tm_new_task_ph');
export const tm_task_name = () => t('tm_task_name');
export const tm_remove_from_project = () => t('tm_remove_from_project');
export const tm_add_to_project = () => t('tm_add_to_project');
export const tm_add_field = () => t('tm_add_field');
export const tm_no_assignees = () => t('tm_no_assignees');
/** FE-42 — группа задач, чей статус не резолвится ни пулом, ни денормом. */
export const tm_state_unknown = () => t('tm_state_unknown');
export const tm_undo_failed = () => t('tm_undo_failed');
// work-tasks-performance F23 — отмена свежей правки задачи и адресный тост ошибки.
export const tm_undo_changed = (p) => t('tm_undo_changed', p);
export const tm_undo_changed_named = (p) => t('tm_undo_changed_named', p);
export const tm_undo_reverted = (p) => t('tm_undo_reverted', p);
export const tm_undo_reverted_named = (p) => t('tm_undo_reverted_named', p);
export const tm_undo_action = () => t('tm_undo_action');
/** CIA-F2 — в тосте отмены у пачки нет имени задачи, есть её размер. */
export const tm_undo_bulk_scope = (p) => t('tm_undo_bulk_scope', { n: p.n });
export const tm_undo_changed_transition = (p) => t('tm_undo_changed_transition', p);
export const tm_undo_hint_window = () => t('tm_undo_hint_window');
export const tm_task_error_named = (p) => t('tm_task_error_named', p);
export const tm_retry_action = () => t('tm_retry_action');
// ── F13 (work-tasks-user-feedback) — каскадная отмена этапа ────────────
export const tm_cancel_subtree_title = (p) => t('tm_cancel_subtree_title', p);
export const tm_cancel_subtree_loading = () => t('tm_cancel_subtree_loading');
export const tm_cancel_subtree_nothing = () => t('tm_cancel_subtree_nothing');
export const tm_cancel_subtree_will = (p) => pluralForm(p.count, {
    one: t('tm_cancel_subtree_will_one', p),
    few: t('tm_cancel_subtree_will_few', p),
    many: t('tm_cancel_subtree_will_many', p),
});
export const tm_cancel_subtree_include = (p) => pluralForm(p.count, {
    one: t('tm_cancel_subtree_include_one', p),
    few: t('tm_cancel_subtree_include_few', p),
    many: t('tm_cancel_subtree_include_many', p),
});
export const tm_cancel_subtree_done = (p) => pluralForm(p.count, {
    one: t('tm_cancel_subtree_done_one', p),
    few: t('tm_cancel_subtree_done_few', p),
    many: t('tm_cancel_subtree_done_many', p),
});
export const tm_cancel_subtree_already = (p) => pluralForm(p.count, {
    one: t('tm_cancel_subtree_already_one', p),
    few: t('tm_cancel_subtree_already_few', p),
    many: t('tm_cancel_subtree_already_many', p),
});
export const tm_cancel_subtree_multi = (p) => pluralForm(p.count, {
    one: t('tm_cancel_subtree_multi_one', p),
    few: t('tm_cancel_subtree_multi_few', p),
    many: t('tm_cancel_subtree_multi_many', p),
});
export const tm_cancel_subtree_skipped_title = () => t('tm_cancel_subtree_skipped_title');
export const tm_cancel_subtree_skipped_row = (p) => t('tm_cancel_subtree_skipped_row', p);
export const tm_cancel_subtree_confirm = () => t('tm_cancel_subtree_confirm');
/** Раунд 1 гейта B: «Отменить» рядом с «Отменить этап» — один глагол на выход и на разрушение. */
export const tm_cancel_subtree_keep = () => t('tm_cancel_subtree_keep');
export const tm_cancel_subtree_untouched_title = () => t('tm_cancel_subtree_untouched_title');
export const tm_cancel_subtree_toast = (p) => pluralForm(p.count, {
    one: t('tm_cancel_subtree_toast_one', p),
    few: t('tm_cancel_subtree_toast_few', p),
    many: t('tm_cancel_subtree_toast_many', p),
});
/** B8 — триггер уведомления о каскаде: словарь центра и панели. */
// ─── Фаза E (work-tasks-user-feedback §3.8) — таймер ─────────────────
export const tm_timer_indicator_multi_long = (p) => pluralForm(p.count, {
    one: t('tm_timer_indicator_multi_long_one', p),
    few: t('tm_timer_indicator_multi_long_few', p),
    many: t('tm_timer_indicator_multi_long_many', p),
});
export const tm_timer_over_threshold_short = (p) => t('tm_timer_over_threshold_short', p);
export const tm_timer_stop_and_edit = () => t('tm_timer_stop_and_edit');
export const tm_timer_stop_and_record = () => t('tm_timer_stop_and_record');
export const tm_timer_will_record_prefix = () => t('tm_timer_will_record_prefix');
export const tm_timer_instead_of = (p) => t('tm_timer_instead_of', p);
export const tm_timer_instead_of_prefix = () => t('tm_timer_instead_of_prefix');
export const tm_timer_stop_and_edit_with = (p) => t('tm_timer_stop_and_edit_with', p);
export const tm_timer_chip_hint = () => t('tm_timer_chip_hint');
export const tm_timer_toast_edit_action = () => t('tm_timer_toast_edit_action');
export const tm_timer_head_running = (p) => t('tm_timer_head_running', p);
export const tm_timer_head_logged = (p) => t('tm_timer_head_logged', p);
export const tm_timer_running_here = (p) => t('tm_timer_running_here', p);
export const tm_timer_stopped_on_complete = () => t('tm_timer_stopped_on_complete');
export const tm_timer_head_title = (p) => t('tm_timer_head_title', p);
export const tm_timer_list_title_mixed = () => t('tm_timer_list_title_mixed');
export const tm_timer_parallel_note = () => t('tm_timer_parallel_note');
export const tm_timer_running_long = (p) => t('tm_timer_running_long', p);
export const tm_timer_will_record = (p) => t('tm_timer_will_record', p);
export const tm_timer_wont_record = () => t('tm_timer_wont_record');
export const tm_worklog_source_timer_edited = () => t('tm_worklog_source_timer_edited');
export const tm_worklog_source_timer_edited_title = () => t('tm_worklog_source_timer_edited_title');
export const tm_timer_list_title = () => t('tm_timer_list_title');
export const tm_timer_start = () => t('tm_timer_start');
export const tm_timer_pause = () => t('tm_timer_pause');
export const tm_timer_resume = () => t('tm_timer_resume');
export const tm_timer_stop = () => t('tm_timer_stop');
export const tm_timer_state_running = () => t('tm_timer_state_running');
export const tm_timer_state_paused = () => t('tm_timer_state_paused');
export const tm_timer_stopped_title = () => t('tm_timer_stopped_title');
export const tm_timer_stopped_hint = (p) => t('tm_timer_stopped_hint', p);
export const tm_timer_too_short_title = () => t('tm_timer_too_short_title');
export const tm_timer_too_short_hint = (p) => t('tm_timer_too_short_hint', p);
export const tm_timer_suggest_started_title = () => t('tm_timer_suggest_started_title');
export const tm_timer_suggest_started_hint = () => t('tm_timer_suggest_started_hint');
export const tm_timer_suggest_started_action = () => t('tm_timer_suggest_started_action');
export const tm_timer_indicator_multi = (p) => pluralForm(p.count, {
    one: t('tm_timer_indicator_multi_one', p),
    few: t('tm_timer_indicator_multi_few', p),
    many: t('tm_timer_indicator_multi_many', p),
});
export const tm_timer_indicator_multi_aria = (p) => pluralForm(p.count, {
    one: t('tm_timer_indicator_multi_aria_one', p),
    few: t('tm_timer_indicator_multi_aria_few', p),
    many: t('tm_timer_indicator_multi_aria_many', p),
});
export const tm_worklog_source_timer = () => t('tm_worklog_source_timer');
export const tm_worklog_source_timer_title = () => t('tm_worklog_source_timer_title');
export const tm_worklog_edit_time = () => t('tm_worklog_edit_time');
export const tm_worklog_time_edited_self = (p) => t('tm_worklog_time_edited_self', p);
export const tm_worklog_time_edited = (p) => t('tm_worklog_time_edited', p);
export const tm_notif_timer_reminder = () => t('tm_notif_timer_reminder');
export const tm_notif_timer_reminder_body = (p) => pluralForm(p.hours, {
    one: t('tm_notif_timer_reminder_body_one', p),
    few: t('tm_notif_timer_reminder_body_few', p),
    many: t('tm_notif_timer_reminder_body_many', p),
});
export const tm_notif_subtree_cancelled = () => t('tm_notif_subtree_cancelled');
export const tm_notif_subtree_cancelled_body = (p) => pluralForm(p.count, {
    one: t('tm_notif_subtree_cancelled_body_one', p),
    few: t('tm_notif_subtree_cancelled_body_few', p),
    many: t('tm_notif_subtree_cancelled_body_many', p),
});
/** Гейт A раунд 14 — у повторной попытки обязан быть видимый ход, а не немой клик. */
export const tm_retrying = () => t('tm_retrying');
/**
 * Гейт A раунд 15 — и видимый ИСХОД: без него повторный отказ возвращал
 * байт-в-байт тот же кадр и был неотличим от промаха мимо кнопки.
 */
export const tm_task_retry_failed = () => t('tm_task_retry_failed');
export const tm_task_retry_failed_n = (p) => pluralForm(p.count, {
    one: t('tm_task_retry_failed_n_one', p),
    few: t('tm_task_retry_failed_n_few', p),
    many: t('tm_task_retry_failed_n_many', p),
});
export const tm_ctrl_z_undo = () => t('tm_ctrl_z_undo');
export const tm_move_toast_done = (p) => t('tm_move_toast_done', p);
export const tm_move_toast_undone = (p) => t('tm_move_toast_undone', p);
export const tm_move_status_remap = (p) => t('tm_move_status_remap', p);
export const tm_move_status_remap_default = (p) => t('tm_move_status_remap_default', p);
export const tm_move_declassify_warn = () => t('tm_move_declassify_warn');
export const tm_move_classify_warn = (p) => t('tm_move_classify_warn', p);
export const error_move_declassify_lead_required = () => t('error_move_declassify_lead_required');
export const tm_field_key_ph = () => t('tm_field_key_ph');
export const tm_default_cap = () => t('tm_default_cap');
export const tm_default_lc = () => t('tm_default_lc');
export const tm_group_none = () => t('tm_group_none');
export const tm_grouping = () => t('tm_grouping');
export const tm_subgrouping = () => t('tm_subgrouping');
export const tm_subgroup_none = () => t('tm_subgroup_none');
export const tm_sorting = () => t('tm_sorting');
export const tm_properties = () => t('tm_properties');
export const tm_more = () => t('tm_more');
export const tm_by_project = () => t('tm_by_project');
export const tm_by_status = () => t('tm_by_status');
export const tm_by_priority = () => t('tm_by_priority');
export const tm_by_due = () => t('tm_by_due');
export const tm_by_assignee = () => t('tm_by_assignee');
export const tm_by_author = () => t('tm_by_author');
export const tm_by_label = () => t('tm_by_label');
export const tm_by_company = () => t('tm_by_company');
export const tm_sort_name_az = () => t('tm_sort_name_az');
export const tm_sort_due_asc = () => t('tm_sort_due_asc');
export const tm_sort_due_desc = () => t('tm_sort_due_desc');
export const tm_manual = () => t('tm_manual');
export const tm_plus_week = () => t('tm_plus_week');
export const tm_not_set = () => t('tm_not_set');
export const tm_estimate_verb = () => t('tm_estimate_verb');
export const tm_set_priority = () => t('tm_set_priority');
export const tm_move = () => t('tm_move');
export const tm_target_project = () => t('tm_target_project');
export const tm_search_project = () => t('tm_search_project');
export const tm_search_project_ph = () => t('tm_search_project_ph');
export const tm_no_projects = () => t('tm_no_projects');
export const tm_primary_lc = () => t('tm_primary_lc');
export const tm_picker_group_current_company = () => t('tm_picker_group_current_company');
export const tm_picker_group_other_companies = () => t('tm_picker_group_other_companies');
export const tm_move_entity_change_title = () => t('tm_move_entity_change_title');
export const tm_move_entity_change_body = (p) => t('tm_move_entity_change_body', p);
export const tm_move_wo_blocked = (p) => t('tm_move_wo_blocked', p);
/** F33 (решение владельца В-2 от 25.07) — правило запрета прямым текстом
    и следующий шаг: отвязку/удаление списаний не строим никогда. */
export const tm_move_wo_rule = () => t('tm_move_wo_rule');
export const tm_move_wo_next_step = () => t('tm_move_wo_next_step');
export const tm_move_blocked_lead = () => t('tm_move_blocked_lead');
export const tm_move_blocked_title = () => t('tm_move_blocked_title');
export const tm_move_wo_where = () => t('tm_move_wo_where');
export const tm_move_wo_where_prefix = () => t('tm_move_wo_where_prefix');
export const tm_move_wo_where_link = () => t('tm_move_wo_where_link');
export const tm_move_wo_where_suffix = () => t('tm_move_wo_where_suffix');
export const tm_move_wo_list_lead = () => t('tm_move_wo_list_lead');
export const tm_move_wo_row = (p) => t('tm_move_wo_row', p);
export const tm_move_wo_more = (p) => t('tm_move_wo_more', p);
export const tm_move_preview_forbidden = () => t('tm_move_preview_forbidden');
export const tm_move_drop_assignees = (p) => t('tm_move_drop_assignees', p);
export const tm_move_drop_watchers = (p) => t('tm_move_drop_watchers', p);
export const tm_move_drop_prefix = () => t('tm_move_drop_prefix');
export const tm_move_preview_failed = () => t('tm_move_preview_failed');
export const tm_move_preview_missing = () => t('tm_move_preview_missing');
export const tm_move_preview_retry = () => t('tm_move_preview_retry');
export const tm_move_toast_done_company = (p) => t('tm_move_toast_done_company', p);
export const tm_move_undo_failed = (p) => t('tm_move_undo_failed', p);
export const tm_move_from_card = () => t('tm_move_from_card');
export const tm_bridge_title = () => t('tm_bridge_title');
export const tm_bridge_body = (p) => t('tm_bridge_body', p);
export const tm_bridge_offer = () => t('tm_bridge_offer');
export const tm_bridge_confirm = () => t('tm_bridge_confirm');
export const tm_primary_cap = () => t('tm_primary_cap');
export const tm_make_primary = () => t('tm_make_primary');
export const tm_current_lc = () => t('tm_current_lc');
export const tm_current_cap = () => t('tm_current_cap');
export const tm_ent_anomaly = () => t('tm_ent_anomaly');
export const tm_ent_conflict = () => t('tm_ent_conflict');
export const tm_ent_counterparty = () => t('tm_ent_counterparty');
export const tm_ent_item = () => t('tm_ent_item');
export const tm_ent_batch = () => t('tm_ent_batch');
export const tm_ent_work_order = () => t('tm_ent_work_order');
export const tm_entity_type = () => t('tm_entity_type');
export const tm_not_selected = () => t('tm_not_selected');
export const tm_select_ph = () => t('tm_select_ph');
export const tm_required = () => t('tm_required');
export const tm_field_type = () => t('tm_field_type');
export const tm_dependencies = () => t('tm_dependencies');
export const tm_add_dependency = () => t('tm_add_dependency');
export const tm_attachments = () => t('tm_attachments');
export const tm_comments = () => t('tm_comments');
export const tm_activity = () => t('tm_activity');
export const tm_history = () => t('tm_history');
export const tm_settings = () => t('tm_settings');
export const tm_task_menu = () => t('tm_task_menu');
export const tm_open_profile = () => t('tm_open_profile');
export const tm_mark_all_read = () => t('tm_mark_all_read');
export const tm_hide = () => t('tm_hide');
export const tm_notifications = () => t('tm_notifications');
export const tm_no_notifications = () => t('tm_no_notifications');
export const tm_views = () => t('tm_views');
export const tm_save_view = () => t('tm_save_view');
export const tm_view_name = () => t('tm_view_name');
export const tm_view_name_ph = () => t('tm_view_name_ph');
export const tm_saved_views = () => t('tm_saved_views');
export const tm_delete_view = () => t('tm_delete_view');
export const tm_open_filters = () => t('tm_open_filters');
export const tm_personal = () => t('tm_personal');
export const tm_team = () => t('tm_team');
export const tm_filter = () => t('tm_filter');
export const tm_filters_and_views = () => t('tm_filters_and_views');
export const tm_active_filters = () => t('tm_active_filters');
export const tm_reset_all = () => t('tm_reset_all');
export const tm_reset_due = () => t('tm_reset_due');
export const tm_only_overdue = () => t('tm_only_overdue');
export const tm_overdue = () => t('tm_overdue');
export const tm_this_week = () => t('tm_this_week');
export const tm_next_week = () => t('tm_next_week');
export const tm_period = () => t('tm_period');
export const tm_from_prep = () => t('tm_from_prep');
export const tm_to_prep = () => t('tm_to_prep');
export const tm_any = () => t('tm_any');
export const tm_no_values = () => t('tm_no_values');
export const tm_link_entity = () => t('tm_link_entity');
export const tm_link_entity_add = () => t('tm_link_entity_add');
export const tm_link_role = () => t('tm_link_role');
export const tm_code_or_name_min2 = () => t('tm_code_or_name_min2');
export const tm_search_entity = () => t('tm_search_entity');
export const tm_start_typing_code_or_name = () => t('tm_start_typing_code_or_name');
export const tm_act_task_created = () => t('tm_act_task_created');
export const tm_act_task_updated = () => t('tm_act_task_updated');
export const tm_act_task_archived = () => t('tm_act_task_archived');
export const tm_act_task_closed = () => t('tm_act_task_closed');
export const tm_act_task_claimed = () => t('tm_act_task_claimed');
export const tm_act_task_unclaimed = () => t('tm_act_task_unclaimed');
export const tm_act_task_moved = () => t('tm_act_task_moved');
export const tm_act_assignees_changed = () => t('tm_act_assignees_changed');
export const tm_act_project_added = () => t('tm_act_project_added');
export const tm_act_project_removed = () => t('tm_act_project_removed');
export const tm_act_project_primary_changed = () => t('tm_act_project_primary_changed');
export const tm_act_dependency_created = () => t('tm_act_dependency_created');
export const tm_act_dependency_deleted = () => t('tm_act_dependency_deleted');
export const tm_act_watcher_subscribed = () => t('tm_act_watcher_subscribed');
export const tm_act_watcher_unsubscribed = () => t('tm_act_watcher_unsubscribed');
export const tm_act_ref_added = () => t('tm_act_ref_added');
export const tm_act_ref_removed = () => t('tm_act_ref_removed');
export const tm_act_worklog_created = () => t('tm_act_worklog_created');
export const tm_act_worklog_updated = () => t('tm_act_worklog_updated');
export const tm_act_worklog_deleted = () => t('tm_act_worklog_deleted');
/** F32 — чип «ПЗ» на строке списания, привязанного к производственному заказу. */
export const tm_worklog_wo_chip = () => t('tm_worklog_wo_chip');
export const tm_worklog_wo_chip_title = () => t('tm_worklog_wo_chip_title');
/** F22-хвост — события вложений в ленте активности задачи. */
export const tm_act_attachment_uploaded = () => t('tm_act_attachment_uploaded');
export const tm_act_attachment_deleted = () => t('tm_act_attachment_deleted');
export const tm_act_comment_created = () => t('tm_act_comment_created');
export const tm_fverb_state = () => t('tm_fverb_state');
export const tm_fverb_priority = () => t('tm_fverb_priority');
export const tm_fverb_due = () => t('tm_fverb_due');
export const tm_fverb_start = () => t('tm_fverb_start');
export const tm_fverb_title = () => t('tm_fverb_title');
export const tm_fverb_estimate = () => t('tm_fverb_estimate');
export const tm_verb_changed_assignees = () => t('tm_verb_changed_assignees');
export const tm_fverb_author = () => t('tm_fverb_author');
export const tm_fverb_description = () => t('tm_fverb_description');
export const tm_fverb_custom_fields = () => t('tm_fverb_custom_fields');
export const tm_fverb_order = () => t('tm_fverb_order');
export const tm_fverb_parent = () => t('tm_fverb_parent');
export const tm_averb_created = () => t('tm_averb_created');
export const tm_averb_archived = () => t('tm_averb_archived');
export const tm_averb_closed = () => t('tm_averb_closed');
export const tm_averb_claimed = () => t('tm_averb_claimed');
export const tm_averb_unclaimed = () => t('tm_averb_unclaimed');
export const tm_averb_moved = () => t('tm_averb_moved');
export const tm_averb_moved_route = (p) => t('tm_averb_moved_route', p);
export const tm_averb_labels_changed = () => t('tm_averb_labels_changed');
export const tm_averb_project_added = () => t('tm_averb_project_added');
export const tm_averb_project_removed = () => t('tm_averb_project_removed');
export const tm_averb_project_primary = () => t('tm_averb_project_primary');
export const tm_averb_dependency_created = () => t('tm_averb_dependency_created');
export const tm_averb_dependency_deleted = () => t('tm_averb_dependency_deleted');
export const tm_averb_watcher_subscribed = () => t('tm_averb_watcher_subscribed');
export const tm_averb_watcher_unsubscribed = () => t('tm_averb_watcher_unsubscribed');
export const tm_averb_ref_added = () => t('tm_averb_ref_added');
export const tm_averb_ref_removed = () => t('tm_averb_ref_removed');
export const tm_averb_worklog_created = () => t('tm_averb_worklog_created');
export const tm_averb_worklog_updated = () => t('tm_averb_worklog_updated');
export const tm_averb_worklog_deleted = () => t('tm_averb_worklog_deleted');
export const tm_averb_attachment_uploaded = () => t('tm_averb_attachment_uploaded');
export const tm_averb_attachment_deleted = () => t('tm_averb_attachment_deleted');
export const tm_verb_updated_short = () => t('tm_verb_updated_short');
export const tm_verb_updated_task = () => t('tm_verb_updated_task');
export const tm_verb_cleared_field = (p) => t('tm_verb_cleared_field', p);
export const tm_verb_changed_field = (p) => t('tm_verb_changed_field', p);
export const tm_commented = () => t('tm_commented');
export const tm_no_activity_yet = () => t('tm_no_activity_yet');
export const tm_system = () => t('tm_system');
export const tm_min_ago = (p) => t('tm_min_ago', p);
export const tm_hours_ago = (p) => t('tm_hours_ago', p);
export const tm_no_changes = () => t('tm_no_changes');
export const tm_another_task = () => t('tm_another_task');
export const tm_bytes = (p) => t('tm_bytes', p);
export const tm_kilobytes = (p) => t('tm_kilobytes', p);
export const tm_megabytes = (p) => t('tm_megabytes', p);
export const tm_add_attachment = () => t('tm_add_attachment');
export const tm_upload_failed = () => t('tm_upload_failed');
export const tm_no_attachments = () => t('tm_no_attachments');
export const tm_infected = () => t('tm_infected');
export const tm_download_file = (p) => t('tm_download_file', p);
export const tm_delete_file = (p) => t('tm_delete_file', p);
export const tm_describe_task_ph = () => t('tm_describe_task_ph');
export const tm_bold = () => t('tm_bold');
export const tm_italic = () => t('tm_italic');
export const tm_strikethrough = () => t('tm_strikethrough');
export const tm_bullet_list = () => t('tm_bullet_list');
export const tm_ordered_list = () => t('tm_ordered_list');
export const tm_code = () => t('tm_code');
export const tm_formatting = () => t('tm_formatting');
export const tm_autosave = () => t('tm_autosave');
export const tm_tab_all_hint = () => t('tm_tab_all_hint');
export const tm_tab_audit_hint = () => t('tm_tab_audit_hint');
export const tm_tab_comments_hint = () => t('tm_tab_comments_hint');
export const tm_copy_link = () => t('tm_copy_link');
export const tm_convert_to_task = () => t('tm_convert_to_task');
export const tm_convert_to_folder = () => t('tm_convert_to_folder');
export const tm_blocked_f = () => t('tm_blocked_f');
export const tm_project_word_one = () => t('tm_project_word_one');
export const tm_project_word_few = () => t('tm_project_word_few');
export const tm_project_word_many = () => t('tm_project_word_many');
/**
 * F24, раунд 7 гейта (UX): «+1 проект» в шапке говорил «добавить», а делал
 * «раскрыть» — плюс в этой же карточке шесть раз означает добавление, и шапочный
 * маркер конкурировал с настоящими кнопками. Отсюда «ещё N проектов» без плюса.
 */
export const tm_project_more_count = (p) => `${t('tm_project_more_prefix')} ${p.count} ${pluralForm(p.count, {
    one: t('tm_project_word_one'),
    few: t('tm_project_word_few'),
    many: t('tm_project_word_many'),
})}`;
export const tm_reschedule_due = () => t('tm_reschedule_due');
export const tm_blocked_by_task = () => t('tm_blocked_by_task');
export const tm_and_more = (p) => t('tm_and_more', p);
export const tm_go_to_blocker = () => t('tm_go_to_blocker');
export const tm_remove_block_q = () => t('tm_remove_block_q');
export const tm_remove_block = () => t('tm_remove_block');
export const tm_remove_block_desc = (p) => t('tm_remove_block_desc', p);
export const tm_convert_to_folder_q = () => t('tm_convert_to_folder_q');
export const tm_cleared_due = (p) => t('tm_cleared_due', p);
export const tm_cleared_start = (p) => t('tm_cleared_start', p);
export const tm_cleared_estimate = (p) => t('tm_cleared_estimate', p);
export const tm_convert_folder_desc_pre = () => t('tm_convert_folder_desc_pre');
export const tm_convert_folder_desc_post = () => t('tm_convert_folder_desc_post');
export const tm_claim = () => t('tm_claim');
export const tm_claimed_by_you = () => t('tm_claimed_by_you');
export const tm_unclaim = () => t('tm_unclaim');
export const tm_subtasks_summary = () => t('tm_subtasks_summary');
export const tm_related_to = () => t('tm_related_to');
export const tm_remove_link = () => t('tm_remove_link');
export const tm_dependency_view = () => t('tm_dependency_view');
export const tm_no_dependencies = () => t('tm_no_dependencies');
export const tm_no_refs = () => t('tm_no_refs');
export const tm_no_worklog = () => t('tm_no_worklog');
export const tm_no_custom_fields = () => t('tm_no_custom_fields');
export const tm_add_link = () => t('tm_add_link');
export const tm_custom_fields = () => t('tm_custom_fields');
export const tm_subscribe_task = () => t('tm_subscribe_task');
export const tm_unsubscribe_task = () => t('tm_unsubscribe_task');
export const tm_blocks = () => t('tm_blocks');
export const tm_open_task_named = (p) => t('tm_open_task_named', p);
export const tm_remove_dependency = () => t('tm_remove_dependency');
export const tm_comment_ph = () => t('tm_comment_ph');
/**
 * F25, раунд 2 гейта (обе половины): восстановленный черновик был неотличим от
 * только что набранного — ни времени, ни того, что он живёт только в этом
 * браузере. Черновик недельной давности всплывал бы как свежий.
 */
export const tm_comment_draft_label = () => t('tm_comment_draft_label');
export const tm_comment_draft_local = () => t('tm_comment_draft_local');
export const tm_comment_draft_yesterday = () => t('tm_comment_draft_yesterday');
/**
 * Очистка черновика — единственное на панели действие, умеющее ровно тот
 * сценарий потери, от которого F25 и защищает. Поэтому у неё есть возврат в той
 * же строке (раунд 3 гейта, обе половины).
 */
export const tm_comment_draft_cleared = () => t('tm_comment_draft_cleared');
export const tm_comment_draft_undo = () => t('tm_comment_draft_undo');
export const tm_comment_draft_clear = () => t('tm_comment_draft_clear');
export const tm_composer_hint = () => t('tm_composer_hint');
export const tm_attach_file = () => t('tm_attach_file');
export const tm_no_comments_yet = () => t('tm_no_comments_yet');
export const tm_delete_comment = () => t('tm_delete_comment');
export const tm_block_us = () => t('tm_block_us');
export const tm_we_hold = () => t('tm_we_hold');
export const tm_related_plural = () => t('tm_related_plural');
export const tm_project_actions = () => t('tm_project_actions');
// F1/F2 (task-card-nav-and-paste) — навигация из карточки задачи в проект.
export const tm_open_project = () => t('tm_open_project');
export const tm_project_card = () => t('tm_project_card');
export const tm_project_already_open = () => t('tm_project_already_open');
export const tm_you_are_here = () => t('tm_you_are_here');
// F3–F6 (task-card-nav-and-paste) — вставка файлов через Ctrl+V.
export const tm_attachment = () => t('tm_attachment');
// F21 — удаление/отвязка вложений.
export const tm_delete_attachment_hint = () => t('tm_delete_attachment_hint');
export const tm_delete_attachment_failed = () => t('tm_delete_attachment_failed');
export const tm_detach_attachment = () => t('tm_detach_attachment');
export const tm_attachment_detached = () => t('tm_attachment_detached');
export const tm_attachments_who_can_delete = () => t('tm_attachments_who_can_delete');
export const tm_you_lc = () => t('tm_you_lc');
export const tm_uploaded_by = (p) => t('tm_uploaded_by', p);
export const tm_download = () => t('tm_download');
export const tm_open_original = () => t('tm_open_original');
export const tm_drop_files_here = () => t('tm_drop_files_here');
export const tm_attachments_link = () => t('tm_attachments_link');
export const tm_delete_verb = () => t('tm_delete_verb');
export const tm_detach_attachment_failed = () => t('tm_detach_attachment_failed');
export const tm_delete_attachment_q = (p) => t('tm_delete_attachment_q', p);
export const tm_unit_kb = () => t('tm_unit_kb');
export const tm_attachments_staged = () => t('tm_attachments_staged');
export const tm_attachment_remove_hint = () => t('tm_attachment_remove_hint');
export const tm_attachments_will_send = () => t('tm_attachments_will_send');
export const tm_attachment_render_failed = () => t('tm_attachment_render_failed');
export const tm_attachment_remove = () => t('tm_attachment_remove');
export const tm_attachment_uploading = (p) => t('tm_attachment_uploading', p);
export const tm_attachment_unavailable = (p) => t('tm_attachment_unavailable', p);
export const tm_attachment_too_large = (p) => t('tm_attachment_too_large', p);
export const tm_attachment_mime_forbidden = (p) => t('tm_attachment_mime_forbidden', p);
export const tm_attachment_upload_failed = (p) => t('tm_attachment_upload_failed', p);
export const tm_project_tasks_nav = () => t('tm_project_tasks_nav');
export const tm_open_project_aria = (p) => t('tm_open_project_aria', p);
export const tm_remove_from_project_q = (p) => t('tm_remove_from_project_q', p);
export const tm_deps_will_be_removed = (p) => t('tm_deps_will_be_removed', p);
export const tm_subtasks_in_project = (p) => t('tm_subtasks_in_project', p);
export const tm_hours_of = (p) => t('tm_hours_of', p);
export const tm_hours_value = (p) => t('tm_hours_value', p);
export const tm_set_estimate = () => t('tm_set_estimate');
export const tm_time_spent = () => t('tm_time_spent');
export const tm_edited_by = (p) => t('tm_edited_by', p);
export const tm_delete_entry = () => t('tm_delete_entry');
export const tm_add_time = () => t('tm_add_time');
export const tm_filters_no_assignees = () => t('tm_filters_no_assignees');
export const tm_filters_due_chip = (p) => t('tm_filters_due_chip', p);
export const tm_filters_search_chip = (p) => t('tm_filters_search_chip', p);
export const tm_filters_active_lead = () => t('tm_filters_active_lead');
export const tm_filters_remove_aria = (p) => t('tm_filters_remove_aria', p);
export const tm_filters_more_n = (p) => t('tm_filters_more_n', p);
export const tm_filters_op_empty = () => t('tm_filters_op_empty');
export const tm_filters_op_not_empty = () => t('tm_filters_op_not_empty');
export const tm_filters_not_prefix = () => t('tm_filters_not_prefix');
export const tm_filters_range_value = (p) => t('tm_filters_range_value', p);
export const tm_filters_op_value = (p) => t('tm_filters_op_value', p);
export const tm_filters_filter_field_aria = (p) => t('tm_filters_filter_field_aria', p);
export const tm_filters_search_field_aria = (p) => t('tm_filters_search_field_aria', p);
export const tm_filters_period_field_aria = (p) => t('tm_filters_period_field_aria', p);
export const tm_filters_contains_ph = () => t('tm_filters_contains_ph');
export const tm_filters_filter_by = (p) => t('tm_filters_filter_by', p);
export const tm_filters_find_saved_view = () => t('tm_filters_find_saved_view');
export const tm_filters_search_tasks_ph = () => t('tm_filters_search_tasks_ph');
export const tm_filters_search_tasks_aria = () => t('tm_filters_search_tasks_aria');
export const tm_filters_unsaved_changes = () => t('tm_filters_unsaved_changes');
export const tm_filters_clear_view_aria = () => t('tm_filters_clear_view_aria');
export const tm_filters_find_view_ph = () => t('tm_filters_find_view_ph');
export const tm_filters_search_saved_view_aria = () => t('tm_filters_search_saved_view_aria');
export const tm_filters_plural = () => t('tm_filters_plural');
export const tm_filters_reset_n = (p) => t('tm_filters_reset_n', p);
export const tm_filters_subject_type = () => t('tm_filters_subject_type');
export const tm_filters_binding = () => t('tm_filters_binding');
export const tm_filters_task_count = (p) => t('tm_filters_task_count', p);
export const tm_filters_match_aria = () => t('tm_filters_match_aria');
export const tm_filters_match_label = () => t('tm_filters_match_label');
export const tm_filters_match_all = () => t('tm_filters_match_all');
export const tm_filters_empty_hint = () => t('tm_filters_empty_hint');
export const tm_filters_remove_field_aria = (p) => t('tm_filters_remove_field_aria', p);
export const tm_filters_find_field_ph = () => t('tm_filters_find_field_ph');
export const tm_filters_search_field_input_aria = () => t('tm_filters_search_field_input_aria');
export const tm_filters_all_fields_added = () => t('tm_filters_all_fields_added');
export const tm_filters_pick_values_ph = () => t('tm_filters_pick_values_ph');
export const tm_filters_select_ph_lc = () => t('tm_filters_select_ph_lc');
export const tm_filters_selected_n = (p) => t('tm_filters_selected_n', p);
export const tm_filters_value_ph_lc = () => t('tm_filters_value_ph_lc');
export const tm_filters_reset_field = () => t('tm_filters_reset_field');
export const tm_filters_search_value_aria = () => t('tm_filters_search_value_aria');
export const tm_display_by_hierarchy = () => t('tm_display_by_hierarchy');
export const tm_display_sort_created = () => t('tm_display_sort_created');
export const tm_display_sort_updated = () => t('tm_display_sort_updated');
export const tm_display_column_aria = (p) => t('tm_display_column_aria', p);
export const tm_display_col_shown = () => t('tm_display_col_shown');
export const tm_display_col_hidden = () => t('tm_display_col_hidden');
export const tm_display_show_empty = () => t('tm_display_show_empty');
export const tm_inline_due_empty_aria = (p) => t('tm_inline_due_empty_aria', p);
export const tm_inline_estimate_empty_aria = () => t('tm_inline_estimate_empty_aria');
export const tm_inline_deps_count = (p) => t('tm_inline_deps_count', p);
export const tm_inline_labels_count = (p) => t('tm_inline_labels_count', p);
export const tm_inline_cf_count = (p) => t('tm_inline_cf_count', p);
export const tm_inline_move_title = () => t('tm_inline_move_title');
export const tm_inline_move_no_states = () => t('tm_inline_move_no_states');
export const tm_inline_move_purge_prefix = () => t('tm_inline_move_purge_prefix');
export const tm_inline_move_subtasks_prefix = () => t('tm_inline_move_subtasks_prefix');
export const tm_inline_move_subtasks_suffix = () => t('tm_inline_move_subtasks_suffix');
export const tm_inline_move_newnum_prefix = () => t('tm_inline_move_newnum_prefix');
export const tm_inline_move_newnum_bold = () => t('tm_inline_move_newnum_bold');
export const tm_inline_move_newnum_suffix = () => t('tm_inline_move_newnum_suffix');
export const tm_inline_priority_aria = (p) => t('tm_inline_priority_aria', p);
export const tm_inline_task_projects_aria = () => t('tm_inline_task_projects_aria');
export const tm_inline_primary_project_title = () => t('tm_inline_primary_project_title');
export const tm_inline_status_list_aria = () => t('tm_inline_status_list_aria');
export const tm_inline_status_keys_hint = () => t('tm_inline_status_keys_hint');
export const tm_inline_status_locked = () => t('tm_inline_status_locked');
export const tm_inline_status_locked_code = (p) => t('tm_inline_status_locked_code', p);
export const tm_inline_open_in_primary = () => t('tm_inline_open_in_primary');
export const tm_peek_task = () => t('tm_peek_task');
export const tm_peek_view_aria = () => t('tm_peek_view_aria');
export const tm_peek_collapse_aria = () => t('tm_peek_collapse_aria');
export const tm_peek_expand_aria = () => t('tm_peek_expand_aria');
export const tm_notif_load_failed = () => t('tm_notif_load_failed');
export const tm_notif_migration_text = (p) => t('tm_notif_migration_text', p);
export const tm_savedviews_rbac_heading = (p) => t('tm_savedviews_rbac_heading', p);
export const tm_savedviews_rbac_hint = () => t('tm_savedviews_rbac_hint');
export const tm_savedviews_rbac_author_prefix = () => t('tm_savedviews_rbac_author_prefix');
export const tm_savedviews_rbac_author_suffix = () => t('tm_savedviews_rbac_author_suffix');
export const tm_savedviews_sys_approvals = () => t('tm_savedviews_sys_approvals');
export const tm_savedviews_sys_anomalies = () => t('tm_savedviews_sys_anomalies');
export const tm_savedviews_sys_conflicts = () => t('tm_savedviews_sys_conflicts');
export const tm_savedviews_team_view = () => t('tm_savedviews_team_view');
export const tm_savedviews_my_views = () => t('tm_savedviews_my_views');
export const tm_savedviews_no_saved = () => t('tm_savedviews_no_saved');
export const tm_savedviews_team_views = () => t('tm_savedviews_team_views');
export const tm_savedviews_make_private = () => t('tm_savedviews_make_private');
export const tm_savedviews_make_team = () => t('tm_savedviews_make_team');
export const tm_savedviews_menu_aria = () => t('tm_savedviews_menu_aria');
export const tm_savedviews_update_filter = () => t('tm_savedviews_update_filter');
export const tm_savedviews_delete_confirm = () => t('tm_savedviews_delete_confirm');
export const tm_savedviews_company = () => t('tm_savedviews_company');
export const tm_savedviews_change_company = () => t('tm_savedviews_change_company');
export const tm_savedviews_name_ph_example = () => t('tm_savedviews_name_ph_example');
export const tm_savedviews_visibility_aria = () => t('tm_savedviews_visibility_aria');
export const tm_gantt_undo_changed = (p) => t('tm_gantt_undo_changed', p);
export const tm_gantt_undo_reverted = (p) => t('tm_gantt_undo_reverted', p);
export const tm_gantt_no_project = () => t('tm_gantt_no_project');
export const tm_gantt_undo_dates = () => t('tm_gantt_undo_dates');
export const tm_gantt_undo_start = () => t('tm_gantt_undo_start');
export const tm_gantt_undo_due = () => t('tm_gantt_undo_due');
export const tm_gantt_undo_nest = () => t('tm_gantt_undo_nest');
export const tm_gantt_undo_to_root = () => t('tm_gantt_undo_to_root');
export const tm_gantt_undo_order = () => t('tm_gantt_undo_order');
export const tm_gantt_undo_assign_dates = () => t('tm_gantt_undo_assign_dates');
export const tm_gantt_undo_create = () => t('tm_gantt_undo_create');
export const tm_gantt_undo_dependency = () => t('tm_gantt_undo_dependency');
export const tm_gantt_undo_remove_link = () => t('tm_gantt_undo_remove_link');
export const tm_gantt_no_tasks_in_project = () => t('tm_gantt_no_tasks_in_project');
export const tm_gantt_critical_unavailable = () => t('tm_gantt_critical_unavailable');
export const tm_gantt_critical_path = () => t('tm_gantt_critical_path');
export const tm_gantt_links = () => t('tm_gantt_links');
export const tm_gantt_critical_needs_links = () => t('tm_gantt_critical_needs_links');
export const tm_gantt_dated_count = (p) => t('tm_gantt_dated_count', p);
export const tm_gantt_show_undated_aria = (p) => t('tm_gantt_show_undated_aria', p);
export const tm_gantt_undated_show = () => t('tm_gantt_undated_show');
export const tm_gantt_undated_only = () => t('tm_gantt_undated_only');
export const tm_gantt_undated_hide = () => t('tm_gantt_undated_hide');
export const tm_gantt_undated_mode_only = () => t('tm_gantt_undated_mode_only');
export const tm_gantt_undated_mode_hide = () => t('tm_gantt_undated_mode_hide');
export const tm_gantt_clear_dates = () => t('tm_gantt_clear_dates');
export const tm_gantt_dates_shared = (p) => t('tm_gantt_dates_shared', p);
export const tm_gantt_undated_mode_show = () => t('tm_gantt_undated_mode_show');
export const tm_gantt_links_of_row = (p) => t('tm_gantt_links_of_row', p);
export const tm_gantt_blocked_by = () => t('tm_gantt_blocked_by');
export const tm_gantt_blocks = () => t('tm_gantt_blocks');
export const tm_gantt_critical_via_undated = () => t('tm_gantt_critical_via_undated');
export const tm_gantt_schedule_undated = (p) => t('tm_gantt_schedule_undated', p);
export const tm_gantt_undated_count = (p) => t('tm_gantt_undated_count', p);
export const tm_gantt_task = () => t('tm_gantt_task');
export const tm_gantt_new_folder = () => t('tm_gantt_new_folder');
export const tm_gantt_folder = () => t('tm_gantt_folder');
export const tm_gantt_expand_project = (p) => t('tm_gantt_expand_project', p);
export const tm_gantt_collapse_project = (p) => t('tm_gantt_collapse_project', p);
export const tm_gantt_mirror_title = (p) => t('tm_gantt_mirror_title', p);
export const tm_gantt_drop_folder = () => t('tm_gantt_drop_folder');
export const tm_gantt_drop_subtask = () => t('tm_gantt_drop_subtask');
export const tm_gantt_set_dates = () => t('tm_gantt_set_dates');
export const tm_gantt_open_task = (p) => t('tm_gantt_open_task', p);
export const tm_gantt_bar_title_summary = (p) => t('tm_gantt_bar_title_summary', p);
export const tm_gantt_folder_summary_title = (p) => t('tm_gantt_folder_summary_title', p);
export const tm_gantt_date_cell_hint = () => t('tm_gantt_date_cell_hint');
export const tm_gantt_bar_title = (p) => t('tm_gantt_bar_title', p);
export const tm_gantt_overdue_suffix = () => t('tm_gantt_overdue_suffix');
export const tm_gantt_show_task = (p) => t('tm_gantt_show_task', p);
export const tm_gantt_to_task = () => t('tm_gantt_to_task');
export const tm_gantt_deps_title = () => t('tm_gantt_deps_title');
export const tm_gantt_blocks_title = () => t('tm_gantt_blocks_title');
export const tm_gantt_remove_link = () => t('tm_gantt_remove_link');
export const tm_gantt_anchored_delete_hint = () => t('tm_gantt_anchored_delete_hint');
export const tm_gantt_sort_hint = () => t('tm_gantt_sort_hint');
export const tm_gantt_reorder_sort_active = () => t('tm_gantt_reorder_sort_active');
export const tm_gantt_columns_scope = () => t('tm_gantt_columns_scope');
export const tm_gantt_col_resize = (p) => t('tm_gantt_col_resize', p);
export const tm_gantt_col_sort_asc = () => t('tm_gantt_col_sort_asc');
export const tm_gantt_col_sort_desc = () => t('tm_gantt_col_sort_desc');
export const tm_gantt_col_hide = () => t('tm_gantt_col_hide');
export const tm_gantt_col_configure = () => t('tm_gantt_col_configure');
export const tm_gantt_col_menu_label = () => t('tm_gantt_col_menu_label');
export const tm_gantt_zen_enter = () => t('tm_gantt_zen_enter');
export const tm_gantt_zen_exit = () => t('tm_gantt_zen_exit');
export const tm_gantt_link_delete_title = () => t('tm_gantt_link_delete_title');
export const tm_gantt_link_delete_body = (p) => t('tm_gantt_link_delete_body', p);
export const tm_gantt_link_delete_foreign = (p) => t('tm_gantt_link_delete_foreign', p);
export const tm_gantt_link_remained = (p) => t('tm_gantt_link_remained', p);
export const tm_gantt_relates_title = () => t('tm_gantt_relates_title');
export const tm_gantt_rail_hint = () => t('tm_gantt_rail_hint');
export const tm_gantt_drag_to_timeline_task = (p) => t('tm_gantt_drag_to_timeline_task', p);
export const tm_gantt_schedule_task = (p) => t('tm_gantt_schedule_task', p);
export const tm_gantt_release_to_assign = () => t('tm_gantt_release_to_assign');
export const tm_gantt_find_task_ph = () => t('tm_gantt_find_task_ph');
export const tm_gantt_relation_type = () => t('tm_gantt_relation_type');
export const tm_gantt_cycle = (p) => t('tm_gantt_cycle', p);
export const tm_bulk_edit = () => t('tm_bulk_edit');
export const tm_bulk_selected_count = (p) => t('tm_bulk_selected_count', p);
// work-tasks-performance F14 — на странице «выбрать всё» выделяет ЗАГРУЖЕННОЕ.
export const tm_bulk_selected_of_loaded = (p) => t('tm_bulk_selected_of_loaded', p);
export const tm_bulk_clear_due = () => t('tm_bulk_clear_due');
export const tm_bulk_unassign = () => t('tm_bulk_unassign');
export const tm_bulk_moved_ok = (p) => t('tm_bulk_moved_ok', p);
export const tm_bulk_moved_count = (p) => t('tm_bulk_moved_count', p);
export const tm_bulk_ctx_work_order = () => t('tm_bulk_ctx_work_order');
export const tm_bulk_ctx_approval = () => t('tm_bulk_ctx_approval');
export const tm_bulk_ctx_anomaly = () => t('tm_bulk_ctx_anomaly');
export const tm_bulk_ctx_conflict = () => t('tm_bulk_ctx_conflict');
export const tm_kanban_add_column = () => t('tm_kanban_add_column');
export const tm_kanban_column_name_ph = () => t('tm_kanban_column_name_ph');
export const tm_kanban_drop_here = () => t('tm_kanban_drop_here');
export const tm_kanban_column_color = () => t('tm_kanban_column_color');
export const tm_kanban_group_col_hint = () => t('tm_kanban_group_col_hint');
export const tm_kanban_delete_column_title = (p) => t('tm_kanban_delete_column_title', p);
export const tm_kanban_move_tasks_to = () => t('tm_kanban_move_tasks_to');
export const tm_kanban_target_column = () => t('tm_kanban_target_column');
export const tm_kanban_no_same_group = () => t('tm_kanban_no_same_group');
export const tm_kanban_will_move = (p) => t('tm_kanban_will_move', p);
export const tm_kanban_of_which_auto = (p) => t('tm_kanban_of_which_auto', p);
export const tm_kanban_more_count = (p) => t('tm_kanban_more_count', p);
export const tm_kanban_circular_deps = (p) => t('tm_kanban_circular_deps', p);
export const tm_kanban_delete_column = () => t('tm_kanban_delete_column');
export const tm_kanban_virtual_column = () => t('tm_kanban_virtual_column');
export const tm_kanban_column_menu = () => t('tm_kanban_column_menu');
export const tm_card_labels_aria = (p) => t('tm_card_labels_aria', p);
export const tm_card_open_parent = () => t('tm_card_open_parent');
export const tm_card_folder_title = (p) => t('tm_card_folder_title', p);
export const tm_card_overdue_days = (p) => t('tm_card_overdue_days', p);
export const tm_card_subtasks_title = (p) => t('tm_card_subtasks_title', p);
export const tm_row_select_task = () => t('tm_row_select_task');
export const tm_row_overdue_days = (p) => t('tm_row_overdue_days', p);
export const tm_inline_days = (p) => t('tm_inline_days', p);
export const tm_inline_status_ph = () => t('tm_inline_status_ph');
export const tm_inline_date_ph = () => t('tm_inline_date_ph');
export const tm_inline_reporter_title = (p) => t('tm_inline_reporter_title', p);
export const tm_inline_reporter_aria = (p) => t('tm_inline_reporter_aria', p);
export const tm_prim_select_assignee = () => t('tm_prim_select_assignee');
export const tm_prim_find_assignee = () => t('tm_prim_find_assignee');
/**
 * F26 (work-tasks-user-feedback §3.7 D15) [UF-13] — «взять на себя» требовало
 * искать себя глазами в неотсортированном списке. Себя человек ищет чаще всего.
 */
export const tm_prim_assignee_me = () => t('tm_prim_assignee_me');
export const tm_prim_assignee_me_hint = () => t('tm_prim_assignee_me_hint');
/**
 * F26, раунд 1 гейта (UX): порядок ярусов без заголовков читается случайным, а
 * клавиатурный контракт, о котором нигде не написано, работает только для того,
 * кто и так знал.
 */
export const tm_prim_assignee_group_me = () => t('tm_prim_assignee_group_me');
export const tm_prim_assignee_group_assigned = () => t('tm_prim_assignee_group_assigned');
export const tm_prim_assignee_group_rest = () => t('tm_prim_assignee_group_rest');
/**
 * F26, раунд 5: контракт клавиш стал длиннее («назначить ИЛИ СНЯТЬ») и перестал
 * помещаться в строку. Разбит на три пары: каждая неразрывна, а перенос идёт
 * между ними — рвать «Esc — закрыть» посередине нельзя.
 */
export const tm_prim_assignee_kbd_move_key = () => t('tm_prim_assignee_kbd_move_key');
export const tm_prim_assignee_kbd_move = () => t('tm_prim_assignee_kbd_move');
export const tm_prim_assignee_kbd_toggle_key = () => t('tm_prim_assignee_kbd_toggle_key');
export const tm_prim_assignee_kbd_toggle = () => t('tm_prim_assignee_kbd_toggle');
export const tm_prim_assignee_kbd_close_key = () => t('tm_prim_assignee_kbd_close_key');
export const tm_prim_assignee_kbd_close = () => t('tm_prim_assignee_kbd_close');
/**
 * F26, раунд 2 гейта (UX): модель множественного выбора не была объявлена — поле
 * называется «Ответственные», а контракт говорил «Enter — назначить», и закроется
 * ли пикер после первого выбора, приходилось узнавать опытом.
 */
export const tm_prim_assignee_multi_hint = () => t('tm_prim_assignee_multi_hint');
/**
 * F26, раунд 4 гейта (обе половины): постоянная строка о множественном выборе
 * съедала верх панели, а мышиного выхода у пикера не было вовсе — закрывали Esc
 * или кликом мимо. Подвал закрывает обе: он же объявляет модель, он же считает
 * выбранных, он же даёт кнопку выхода.
 */
export const tm_prim_assignee_selected_count = (p) => t('tm_prim_assignee_selected_count', p);
/**
 * F26, раунд 12 (UX): подвал обещает «сохраняется сразу», и у обещания обязана
 * быть видимая ВНУТРИ панели вторая половина. Общий тост её не заменяет: пока
 * пикер открыт и держит фокус, отказ записи нужно видеть здесь же.
 */
export const tm_prim_assignee_save_failed = () => t('tm_prim_assignee_save_failed');
export const tm_prim_assignee_done = () => t('tm_prim_assignee_done');
export const tm_prim_match_count = (p) => t('tm_prim_match_count', p);
export const tm_prim_member_count = (p) => t('tm_prim_member_count', p);
export const tm_prim_tab_all = () => t('tm_prim_tab_all');
export const tm_prim_prev_month = () => t('tm_prim_prev_month');
export const tm_prim_next_month = () => t('tm_prim_next_month');
export const tm_prim_clear_date_confirm_aria = () => t('tm_prim_clear_date_confirm_aria');
export const tm_prim_blocks_n_tasks = (p) => t('tm_prim_blocks_n_tasks', p);
export const tm_prim_clear_and_shift = () => t('tm_prim_clear_and_shift');
export const tm_prim_clear_only = () => t('tm_prim_clear_only');
export const tm_prim_sp_short = () => t('tm_prim_sp_short');
export const tm_prim_estimate_unit_aria = () => t('tm_prim_estimate_unit_aria');
export const tm_prim_remove_label = (p) => t('tm_prim_remove_label', p);
export const tm_prim_add_label_ph = () => t('tm_prim_add_label_ph');
export const tm_prim_create_quoted = (p) => t('tm_prim_create_quoted', p);
export const tm_prim_label_color_aria = () => t('tm_prim_label_color_aria');
export const tm_prim_user_card_aria = (p) => t('tm_prim_user_card_aria', p);
export const tm_prim_user_unavailable = () => t('tm_prim_user_unavailable');
export const tm_prim_no_access_or_deleted = () => t('tm_prim_no_access_or_deleted');
export const tm_prim_active = () => t('tm_prim_active');
export const tm_prim_inactive = () => t('tm_prim_inactive');
export const tm_prim_retry = () => t('tm_prim_retry');
export const tm_prim_priority_unset_aria = () => t('tm_prim_priority_unset_aria');
export const tm_prim_priority_named_aria = (p) => t('tm_prim_priority_named_aria', p);
export const tm_prim_end_of_week = () => t('tm_prim_end_of_week');
export const tm_prim_end_of_month = () => t('tm_prim_end_of_month');
export const tm_prim_end_of_quarter = () => t('tm_prim_end_of_quarter');
export const tm_blueprint_name_ph = () => t('tm_blueprint_name_ph');
export const tm_blueprint_kind_aria = () => t('tm_blueprint_kind_aria');
export const tm_blueprint_task_title_ph = () => t('tm_blueprint_task_title_ph');
export const tm_blueprint_offset_days_aria = () => t('tm_blueprint_offset_days_aria');
export const tm_blueprint_offset_from_start = () => t('tm_blueprint_offset_from_start');
export const tm_blueprint_duration_days_aria = () => t('tm_blueprint_duration_days_aria');
export const tm_blueprint_role_ph = () => t('tm_blueprint_role_ph');
export const tm_blueprint_assignee_role_aria = () => t('tm_blueprint_assignee_role_aria');
export const tm_blueprint_remove_node = () => t('tm_blueprint_remove_node');
export const tm_blueprint_priority_label = () => t('tm_blueprint_priority_label');
export const tm_blueprint_blocks_label = () => t('tm_blueprint_blocks_label');
export const tm_blueprint_add_node = () => t('tm_blueprint_add_node');
export const tm_blueprint_saving = () => t('tm_blueprint_saving');
export const tm_blueprint_create = () => t('tm_blueprint_create');
export const tm_cf_not_filled = () => t('tm_cf_not_filled');
export const tm_cf_currency_aria = () => t('tm_cf_currency_aria');
export const tm_cf_need_one_value = () => t('tm_cf_need_one_value');
export const tm_cf_create_failed = () => t('tm_cf_create_failed');
export const tm_cf_name_ph = () => t('tm_cf_name_ph');
export const tm_cf_values_comma = () => t('tm_cf_values_comma');
export const tm_cf_values_ph = () => t('tm_cf_values_ph');
export const tm_cf_field_hint = () => t('tm_cf_field_hint');
export const tm_cf_creating = () => t('tm_cf_creating');
export const tm_group_short_none = () => t('tm_group_short_none');
export const tm_group_short_state = () => t('tm_group_short_state');
export const tm_group_short_due = () => t('tm_group_short_due');
export const tm_group_short_priority = () => t('tm_group_short_priority');
export const tm_group_short_assignee = () => t('tm_group_short_assignee');
export const tm_group_short_author = () => t('tm_group_short_author');
export const tm_group_short_label = () => t('tm_group_short_label');
export const tm_group_short_hierarchy = () => t('tm_group_short_hierarchy');
export const tm_group_short_project = () => t('tm_group_short_project');
export const tm_group_short_entity = () => t('tm_group_short_entity');
export const tm_group_short_folder = () => t('tm_group_short_folder');
export const tm_group_color_prefix = (p) => t('tm_group_color_prefix', p);
export const tm_by_hierarchy = () => t('tm_by_hierarchy');
// FE-18/FE-19 — реестр измерений: причины недоступности осей и видимые откаты.
export const tm_by_status_group = () => t('tm_by_status_group');
export const tm_group_unavailable_board = () => t('tm_group_unavailable_board');
export const tm_group_unavailable_gantt = () => t('tm_group_unavailable_gantt');
export const tm_group_unavailable_calendar = () => t('tm_group_unavailable_calendar');
export const tm_group_fallback_unknown = () => t('tm_group_fallback_unknown');
export const tm_group_fallback_scope = (p) => t('tm_group_fallback_scope', p);
/** Короткая причина — она подставляется ВНУТРЬ tm_group_fallback_notice, поэтому не
 *  должна повторять имя оси: иначе получается «Группировка «X» недоступна: Группировка
 *  «X» недоступна…». */
export const tm_group_scope_degenerate = () => t('tm_group_scope_degenerate');
/** FE-38 — выдача обрезана: счётчики групп неполны, и экран обязан это сказать. */
export const tm_truncated_rows_only = (p) => t('tm_truncated_rows_only', p);
export const tm_truncated_badge = (p) => t('tm_truncated_badge', p);
export const tm_page_shown = (p) => t('tm_page_shown', p);
export const tm_page_shown_plain = (p) => t('tm_page_shown_plain', p);
export const tm_page_shown_action = (p) => t('tm_page_shown_action', p);
/** FE-23 — перенос карточки между группами: тост называет конкретное значение. */
export const tm_group_moved = (p) => t('tm_group_moved', p);
/** FE-25 — многозначная ось: строк больше, чем задач. Без этой подписи сумма
 *  счётчиков групп выглядит как ошибка подсчёта. */
export const tm_tasks_rows_count = (p) => t('tm_tasks_rows_count', p);
/** FE-25 — чип на строке-близнеце: задача видна ещё в N группах. */
export const tm_also_in_n = (p) => t('tm_also_in_n', p);
/** FE-37 — итог массовой операции: числа берутся ИЗ ОТВЕТА, а не считаются на клиенте.
 *  Раньше отказы и пропуски молча терялись, и пользователь не знал, что часть выборки
 *  не тронута. */
export const tm_bulk_result = (p) => t('tm_bulk_result', p);
export const tm_bulk_set_dates = () => t('tm_bulk_set_dates');
export const tm_bulk_label = () => t('tm_bulk_label');
export const tm_bulk_author = () => t('tm_bulk_author');
export const tm_start_date = () => t('tm_start_date');
/** FE-32 — связь требует общего проекта у обоих концов; отказ объясняется, а не молчит. */
export const tm_gantt_link_no_shared_project = () => t('tm_gantt_link_no_shared_project');
/** FE-31 — область действия связей: на многозначной оси межгрупповые рёбра тянутся
 *  через десятки чужих строк, и в кадре видна линия с обоими концами за экраном. */
export const tm_gantt_edges_within = () => t('tm_gantt_edges_within');
export const tm_gantt_edges_all = () => t('tm_gantt_edges_all');
/** FE-33 — порядок строк не определён в атрибутных осях; отказ объясняется, а не молчит. */
export const tm_gantt_reorder_unavailable = () => t('tm_gantt_reorder_unavailable');
/** FE-41 — приёмник дат читался как «запланировано на эту дату». Он стоит на ЯКОРЕ
 *  («раньше всего можно начать здесь»), а не на выбранной дате — и обязан это сказать. */
export const tm_gantt_not_scheduled = () => t('tm_gantt_not_scheduled');
/** FE-35 — связь озвучивается скринридеру: у якорной стрелки нет зоны попадания. */
/** FE-39 — второй конец связи без дат: на ганте у такой стрелки нет зоны попадания,
 *  и карточка становится единственным полным путём удаления. Чип это проговаривает. */
export const tm_dep_undated_chip = () => t('tm_dep_undated_chip');
export const tm_gantt_link_aria = (p) => t('tm_gantt_link_aria', p);
export const tm_gantt_anchor_hint = (p) => t('tm_gantt_anchor_hint', p);
export const tm_group_fallback_notice = (p) => t('tm_group_fallback_notice', p);
export const tm_group_open_on_dashboard = () => t('tm_group_open_on_dashboard');
export const tm_group_sort_manual_default = () => t('tm_group_sort_manual_default');
export const tm_recur_daily = () => t('tm_recur_daily');
export const tm_recur_weekly_mon = () => t('tm_recur_weekly_mon');
export const tm_recur_month_first = () => t('tm_recur_month_first');
export const tm_recur_month_last = () => t('tm_recur_month_last');
export const tm_recur_quarterly = () => t('tm_recur_quarterly');
export const tm_recur_manual_ph = () => t('tm_recur_manual_ph');
export const tm_recur_repeat_task = () => t('tm_recur_repeat_task');
export const tm_recur_delete_rule = () => t('tm_recur_delete_rule');
export const tm_route_col_executor = () => t('tm_route_col_executor');
export const tm_route_col_created = () => t('tm_route_col_created');
export const tm_route_col_updated = () => t('tm_route_col_updated');
export const tm_route_view_table = () => t('tm_route_view_table');
export const tm_route_view_board = () => t('tm_route_view_board');
export const tm_route_view_calendar = () => t('tm_route_view_calendar');
export const tm_route_view_gantt = () => t('tm_route_view_gantt');
export const tm_route_col_task = () => t('tm_route_col_task');
export const tm_route_view_switcher = () => t('tm_route_view_switcher');
export const tm_route_all_tasks = () => t('tm_route_all_tasks');
export const tm_route_tasks_heading = () => t('tm_route_tasks_heading');
export const tm_route_due_7d = () => t('tm_route_due_7d');
export const tm_route_no_assignees = () => t('tm_route_no_assignees');
export const tm_route_my_tasks = () => t('tm_route_my_tasks');
export const tm_route_no_due = () => t('tm_route_no_due');
export const tm_show_closed = () => t('tm_show_closed');
export const tm_show_closed_short = () => t('tm_show_closed_short');
export const tm_show_archived_short = () => t('tm_show_archived_short');
export const tm_show_archived = () => t('tm_show_archived');
export const tm_archived_badge = () => t('tm_archived_badge');
export const tm_page_size_label = () => t('tm_page_size_label');
export const tm_no_active_tasks = () => t('tm_no_active_tasks');
/**
 * work-tasks-performance F2/F3 — на экране прошлые строки, новые в полёте. Молчать
 * нельзя: пользователь принял бы устаревший набор за результат своего фильтра.
 */
export const tm_list_refreshing = () => t('tm_list_refreshing');
/** work-tasks-performance F1 — подпись к скелетону: молчащий скелетон не сообщает, что идёт. */
export const tm_list_loading = () => t('tm_list_loading');
/** Та же загрузка, но затянулась: честно называем причину, а не крутим спиннер молча. */
export const tm_list_loading_long = () => t('tm_list_loading_long');
/** Заголовок ошибки списка задач — родовое «Ошибка загрузки» не называет, что упало. */
export const tm_list_error_title = () => t('tm_list_error_title');
/** Подпись кнопки повтора: называет действие, а не жест. */
export const tm_list_error_retry = () => t('tm_list_error_retry');
/** Счётчик неизвестен из-за капа выдачи — «—» уже занят «значения нет». */
export const tm_count_unknown = () => t('tm_count_unknown');
export const tm_count_unknown_hint = () => t('tm_count_unknown_hint');
export const tm_count_unavailable_hint = () => t('tm_count_unavailable_hint');
/** Копирование trace_id из error state — 36 символов переписывать руками не надо. */
export const error_copy_trace = () => t('error_copy_trace');
/** Короткая подпись кнопки копирования: полная формулировка живёт в `aria-label`. */
export const error_copy_action = () => t('error_copy_action');
export const error_trace_copied = () => t('error_trace_copied');
/** work-tasks-performance F3 — почему чекбоксы недоступны на устаревшем наборе. */
export const tm_bulk_locked_stale = () => t('tm_bulk_locked_stale');
export const tm_route_project_templates = () => t('tm_route_project_templates');
// project-templates (F3.1)
export const tmpl_cta_create_project = (p) => t('tmpl_cta_create_project', p);
export const tmpl_card_source = (p) => t('tmpl_card_source', p);
export const tmpl_card_versions_prev = (p) => t('tmpl_card_versions_prev', p);
export const tmpl_card_updated = (p) => t('tmpl_card_updated', p);
export const tmpl_page_empty = (p) => t('tmpl_page_empty', p);
export const tmpl_card_menu_deactivate = (p) => t('tmpl_card_menu_deactivate', p);
export const tmpl_card_menu_delete = (p) => t('tmpl_card_menu_delete', p);
export const tmpl_card_menu_delete_note = () => t('tmpl_card_menu_delete_note');
export const tmpl_materialize_title = (p) => t('tmpl_materialize_title', p);
/**
 * W-4 (D4.7) — канон дефолтного имени материализованного проекта: имя источника
 * плюс дата запуска. Склейка — в словаре, а не в коде, и в языках она РАЗНАЯ:
 * по-русски «… от 03.08.2026», по-английски «… (08/03/2026)». Тире не годится:
 * имена проектов сами его содержат («Монтаж вентиляции — заказ №42»), и второе
 * подряд читается как часть названия.
 */
export const tmpl_materialize_default_name = (p) => t('tmpl_materialize_default_name', p);
export const tmpl_field_name = (p) => t('tmpl_field_name', p);
export const tmpl_mode_start_on = (p) => t('tmpl_mode_start_on', p);
export const tmpl_mode_finish_by = (p) => t('tmpl_mode_finish_by', p);
export const tmpl_anchor_day0 = (p) => t('tmpl_anchor_day0', p);
export const tmpl_anchor_day0_date = (p) => t('tmpl_anchor_day0_date', p);
export const tmpl_anchor_day0_date_pending = (p) => t('tmpl_anchor_day0_date_pending', p);
export const tmpl_anchor_range_pending = () => t('tmpl_anchor_range_pending');
export const tmpl_anchor_range = (p) => t('tmpl_anchor_range', p);
export const tmpl_anchor_due_only = (p) => t('tmpl_anchor_due_only', p);
export const tmpl_anchor_start_label = (p) => t('tmpl_anchor_start_label', p);
export const tmpl_anchor_finish_label = (p) => t('tmpl_anchor_finish_label', p);
export const tmpl_anchor_no_dates = (p) => t('tmpl_anchor_no_dates', p);
export const tmpl_field_customer = (p) => t('tmpl_field_customer', p);
export const tmpl_field_sales_order = (p) => t('tmpl_field_sales_order', p);
export const tmpl_order_hint = (p) => t('tmpl_order_hint', p);
export const tmpl_copy_assignees = (p) => t('tmpl_copy_assignees', p);
export const tmpl_go_to_project = (p) => t('tmpl_go_to_project', p);
export const tmpl_action_create = (p) => t('tmpl_action_create', p);
export const tmpl_save_title = (p) => t('tmpl_save_title', p);
export const tmpl_field_template_name = (p) => t('tmpl_field_template_name', p);
export const tmpl_field_description = (p) => t('tmpl_field_description', p);
export const tmpl_conflict_title = (p) => t('tmpl_conflict_title', p);
export const tmpl_conflict_update = (p) => t('tmpl_conflict_update', p);
export const tmpl_conflict_rename = (p) => t('tmpl_conflict_rename', p);
export const tmpl_open_templates = (p) => t('tmpl_open_templates', p);
export const tmpl_saved_ok = (p) => t('tmpl_saved_ok', p);
export const tmpl_duplicate_title = (p) => t('tmpl_duplicate_title', p);
export const tmpl_copy_suffix = (p) => t('tmpl_copy_suffix', p);
export const tmpl_dates_shift = (p) => t('tmpl_dates_shift', p);
export const tmpl_dates_as_is = (p) => t('tmpl_dates_as_is', p);
export const tmpl_status_reset = (p) => t('tmpl_status_reset', p);
export const tmpl_status_keep = (p) => t('tmpl_status_keep', p);
export const tmpl_status_incomplete = (p) => t('tmpl_status_incomplete', p);
export const tmpl_status_reset_hint = (p) => t('tmpl_status_reset_hint', p);
export const tmpl_status_keep_hint = (p) => t('tmpl_status_keep_hint', p);
export const tmpl_status_incomplete_hint = (p) => t('tmpl_status_incomplete_hint', p);
export const tmpl_opt_copy_customer = (p) => t('tmpl_opt_copy_customer', p);
export const tmpl_opt_copy_members = (p) => t('tmpl_opt_copy_members', p);
export const tmpl_opt_reattach_secondary = (p) => t('tmpl_opt_reattach_secondary', p);
/** B25/F64 (TPL-F9) — «Перепривяжутся задачи из N проектов» (круг 1 UX: короче, без переноса). */
export const tmpl_reattach_consequence = (p) => `${t('tmpl_reattach_consequence_prefix')} ${p.n} ${pluralForm(p.n, {
    one: t('tmpl_reattach_project_one'),
    few: t('tmpl_reattach_project_few'),
    many: t('tmpl_reattach_project_many'),
})}`;
export const tmpl_reattach_hint = (p) => t('tmpl_reattach_hint', p);
export const tmpl_action_duplicate = (p) => t('tmpl_action_duplicate', p);
export const tmpl_menu_duplicate = (p) => t('tmpl_menu_duplicate', p);
export const tmpl_menu_save_as_template = (p) => t('tmpl_menu_save_as_template', p);
export const tmpl_summary_title = (p) => t('tmpl_summary_title', p);
export const tmpl_skip_external_deps = (p) => t('tmpl_skip_external_deps', p);
export const tmpl_skip_secondary_tasks = (p) => t('tmpl_skip_secondary_tasks', p);
export const tmpl_skip_done_tasks = (p) => t('tmpl_skip_done_tasks', p);
export const tmpl_skip_recurring_instances = (p) => t('tmpl_skip_recurring_instances', p);
export const tmpl_skip_attachments = (p) => t('tmpl_skip_attachments', p);
export const tmpl_skip_comments = (p) => t('tmpl_skip_comments', p);
export const tmpl_skip_worklogs = (p) => t('tmpl_skip_worklogs', p);
export const tmpl_skip_archived_tasks = (p) => t('tmpl_skip_archived_tasks', p);
export const tmpl_skip_date_custom_fields = (p) => t('tmpl_skip_date_custom_fields', p);
export const tmpl_skip_orphan_custom_field_keys = (p) => t('tmpl_skip_orphan_custom_field_keys', p);
export const tmpl_skip_missing_entity_labels = (p) => t('tmpl_skip_missing_entity_labels', p);
export const tmpl_dropped_assignees = (p) => t('tmpl_dropped_assignees', p);
export const tmpl_prefill_title = (p) => t('tmpl_prefill_title', p);
export const tmpl_prefill_select = (p) => t('tmpl_prefill_select', p);
export const tmpl_prefill_preview = (p) => t('tmpl_prefill_preview', p);
export const tmpl_prefill_plan = (p) => t('tmpl_prefill_plan', p);
export const tmpl_company_locked_hint = (p) => t('tmpl_company_locked_hint', p);
export const tmpl_warning_default_state_assigned = (p) => t('tmpl_warning_default_state_assigned', p);
export const tmpl_prefill_clear = (p) => t('tmpl_prefill_clear', p);
export const tmpl_delete_confirm_title = (p) => t('tmpl_delete_confirm_title', p);
export const tmpl_delete_confirm_body = (p) => t('tmpl_delete_confirm_body', p);
export const tmpl_deactivate_confirm_title = (p) => t('tmpl_deactivate_confirm_title', p);
export const tmpl_deactivate_confirm_body = (p) => t('tmpl_deactivate_confirm_body', p);
// ─── Фаза B (template-editor-and-dates §3) — просмотр шаблона ────────
export const tmpl_view_offsets_only = () => t('tmpl_view_offsets_only');
export const tmpl_view_plan_days = (p) => t('tmpl_view_plan_days', p);
export const tmpl_view_no_dates = () => t('tmpl_view_no_dates');
export const tmpl_view_badge_inactive = () => t('tmpl_view_badge_inactive');
export const tmpl_view_badge_legacy = () => t('tmpl_view_badge_legacy');
export const tmpl_view_meta_kind = () => t('tmpl_view_meta_kind');
export const tmpl_view_meta_source = () => t('tmpl_view_meta_source');
export const tmpl_view_meta_author = () => t('tmpl_view_meta_author');
export const tmpl_view_meta_updated = () => t('tmpl_view_meta_updated');
export const tmpl_view_more_actions = () => t('tmpl_view_more_actions');
export const tmpl_view_section_tasks = () => t('tmpl_view_section_tasks');
export const tmpl_view_start_day = (p) => t('tmpl_view_start_day', p);
export const tmpl_view_due_day = (p) => t('tmpl_view_due_day', p);
export const tmpl_view_day = (p) => t('tmpl_view_day', p);
export const tmpl_view_duration = (p) => t('tmpl_view_duration', p);
export const tmpl_view_task_no_dates = () => t('tmpl_view_task_no_dates');
export const tmpl_view_former_member = () => t('tmpl_view_former_member');
export const tmpl_view_assignee_role = (p) => t('tmpl_view_assignee_role', p);
export const tmpl_view_assignee_group = () => t('tmpl_view_assignee_group');
export const tmpl_view_depends_on = (p) => t('tmpl_view_depends_on', p);
export const tmpl_view_no_tasks_title = () => t('tmpl_view_no_tasks_title');
export const tmpl_view_no_tasks_body = () => t('tmpl_view_no_tasks_body');
export const tmpl_view_not_found_title = () => t('tmpl_view_not_found_title');
export const tmpl_view_not_found_body = () => t('tmpl_view_not_found_body');
export const tmpl_view_section_milestones = () => t('tmpl_view_section_milestones');
export const tmpl_view_milestone_billing = (p) => t('tmpl_view_milestone_billing', p);
export const tmpl_view_milestone_linked = (p) => t('tmpl_view_milestone_linked', p);
export const tmpl_view_section_states = () => t('tmpl_view_section_states');
export const tmpl_view_state_default = () => t('tmpl_view_state_default');
export const tmpl_view_section_fields = () => t('tmpl_view_section_fields');
export const tmpl_view_field_required = () => t('tmpl_view_field_required');
export const tmpl_view_section_deps = () => t('tmpl_view_section_deps');
export const tmpl_view_section_skipped = () => t('tmpl_view_section_skipped');
export const tmpl_view_skipped_hint = () => t('tmpl_view_skipped_hint');
export const tmpl_view_section_versions = () => t('tmpl_view_section_versions');
export const tmpl_view_versions_empty = () => t('tmpl_view_versions_empty');
export const tmpl_view_version_active = () => t('tmpl_view_version_active');
export const tmpl_view_restore_action = () => t('tmpl_view_restore_action');
export const tmpl_view_restore_confirm_title = () => t('tmpl_view_restore_confirm_title');
export const tmpl_view_restore_confirm_body = (p) => t('tmpl_view_restore_confirm_body', p);
export const tmpl_view_convert_action = () => t('tmpl_view_convert_action');
export const tmpl_view_convert_confirm_title = () => t('tmpl_view_convert_confirm_title');
export const tmpl_view_convert_confirm_body = () => t('tmpl_view_convert_confirm_body');
export const tmpl_view_legacy_hint = () => t('tmpl_view_legacy_hint');
// ─── Раунд 2 дизайн-ревью фазы B (F-B1…F-B9) ────────────────────────
export const tmpl_view_col_task = () => t('tmpl_view_col_task');
export const tmpl_view_col_start = () => t('tmpl_view_col_start');
export const tmpl_view_col_due = () => t('tmpl_view_col_due');
export const tmpl_view_col_duration = () => t('tmpl_view_col_duration');
export const tmpl_view_estimate = (p) => t('tmpl_view_estimate', p);
export const tmpl_view_open_action = () => t('tmpl_view_open_action');
export const tmpl_view_cta_create_from_version = (p) => t('tmpl_view_cta_create_from_version', p);
export const tmpl_view_inactive_notice = (p) => t('tmpl_view_inactive_notice', p);
export const tmpl_view_inactive_legacy_notice = () => t('tmpl_view_inactive_legacy_notice');
export const tmpl_skip_label_external_deps = () => t('tmpl_skip_label_external_deps');
export const tmpl_skip_label_secondary_tasks = () => t('tmpl_skip_label_secondary_tasks');
export const tmpl_skip_label_done_tasks = () => t('tmpl_skip_label_done_tasks');
export const tmpl_skip_label_recurring_instances = () => t('tmpl_skip_label_recurring_instances');
export const tmpl_skip_label_attachments = () => t('tmpl_skip_label_attachments');
export const tmpl_skip_label_comments = () => t('tmpl_skip_label_comments');
export const tmpl_skip_label_worklogs = () => t('tmpl_skip_label_worklogs');
export const tmpl_skip_label_archived_tasks = () => t('tmpl_skip_label_archived_tasks');
export const tmpl_skip_label_date_custom_fields = () => t('tmpl_skip_label_date_custom_fields');
export const tmpl_skip_label_orphan_custom_field_keys = () => t('tmpl_skip_label_orphan_custom_field_keys');
export const tmpl_skip_label_missing_entity_labels = () => t('tmpl_skip_label_missing_entity_labels');
export const tm_route_blueprint_hide_editor = () => t('tm_route_blueprint_hide_editor');
export const tm_route_blueprint_new = () => t('tm_route_blueprint_new');
export const tm_route_blueprint_empty = () => t('tm_route_blueprint_empty');
// F4.8 — плюрализация вместо статичного «{count} задач».
export const tm_route_tasks_count = (p) => tmpl_tasks_count({ count: Number(p.count) });
export const tm_route_blueprint_apply_confirm = (p) => t('tm_route_blueprint_apply_confirm', p);
export const tm_route_blueprint_apply_hint = (p) => t('tm_route_blueprint_apply_hint', p);
export const tm_route_start_date = () => t('tm_route_start_date');
export const tm_route_creating = () => t('tm_route_creating');
export const tm_route_blueprint_apply = () => t('tm_route_blueprint_apply');
export const tm_route_notif_assigned = () => t('tm_route_notif_assigned');
export const tm_route_notif_mention = () => t('tm_route_notif_mention');
export const tm_route_notif_comment_watched = () => t('tm_route_notif_comment_watched');
export const tm_route_notif_status_change = () => t('tm_route_notif_status_change');
export const tm_route_notif_due_soon = () => t('tm_route_notif_due_soon');
export const tm_route_notif_unblocked = () => t('tm_route_notif_unblocked');
export const tm_route_notif_in_queue = () => t('tm_route_notif_in_queue');
export const tm_route_channel_in_app = () => t('tm_route_channel_in_app');
export const tm_route_channel_email = () => t('tm_route_channel_email');
export const tm_route_task_notifications_title = () => t('tm_route_task_notifications_title');
export const tm_route_event = () => t('tm_route_event');
export const tm_undo_label_due = () => t('tm_undo_label_due');
export const tm_undo_label_create = () => t('tm_undo_label_create');
export const tm_changed_x = (p) => t('tm_changed_x', p);
export const tm_undone_x = (p) => t('tm_undone_x', p);
export const tm_move_due_failed = () => t('tm_move_due_failed');
export const tm_create_task_failed = () => t('tm_create_task_failed');
export const tm_folder_dot_title = (p) => t('tm_folder_dot_title', p);
export const tm_prev_period = () => t('tm_prev_period');
export const tm_next_period = () => t('tm_next_period');
export const tm_calendar_mode = () => t('tm_calendar_mode');
export const tm_add_task_on = (p) => t('tm_add_task_on', p);
export const tm_plus_n_more = (p) => t('tm_plus_n_more', p);
export const tm_all_have_due = () => t('tm_all_have_due');
export const tm_empty_day = () => t('tm_empty_day');
export const tm_empty_week = () => t('tm_empty_week');
export const tm_empty_month = () => t('tm_empty_month');
export const tm_change_project = () => t('tm_change_project');
export const tm_task = () => t('tm_task');
export const tm_created = () => t('tm_created');
export const tm_updated = () => t('tm_updated');
export const tm_project_named = (p) => t('tm_project_named', p);
export const tm_overdue_n_days = (p) => t('tm_overdue_n_days', p);
export const tm_dot_n_overdue = (p) => t('tm_dot_n_overdue', p);
export const tm_all_tasks_table = () => t('tm_all_tasks_table');
export const tm_status_unchanged = () => t('tm_status_unchanged');
export const tm_status_unchanged_desc = () => t('tm_status_unchanged_desc');
export const tm_status_x_to_y = (p) => t('tm_status_x_to_y', p);
export const tm_priority_x_to_y = (p) => t('tm_priority_x_to_y', p);
export const tm_assignees_moved_label = () => t('tm_assignees_moved_label');
export const tm_assignees_changed_by_move = () => t('tm_assignees_changed_by_move');
export const tm_n_overdue = (p) => t('tm_n_overdue', p);
export const tm_view_table = () => t('tm_view_table');
export const tm_view_kanban = () => t('tm_view_kanban');
export const tm_view_gantt = () => t('tm_view_gantt');
export const tm_view_calendar = () => t('tm_view_calendar');
export const tm_saved = () => t('tm_saved');
export const tm_save_failed = () => t('tm_save_failed');
export const tm_states_warn = () => t('tm_states_warn');
export const tm_move_state_x = (p) => t('tm_move_state_x', p);
export const tm_state_color = () => t('tm_state_color');
export const tm_project_archived = () => t('tm_project_archived');
export const tm_field_added = () => t('tm_field_added');
export const tm_field_add_failed = () => t('tm_field_add_failed');
export const tm_field_deleted = () => t('tm_field_deleted');
export const tm_role_x = (p) => t('tm_role_x', p);
export const tm_states = () => t('tm_states');
export const tm_states_hint = () => t('tm_states_hint');
export const tm_project_fields = () => t('tm_project_fields');
export const tm_required_abbr = () => t('tm_required_abbr');
export const tm_delete_field_x = (p) => t('tm_delete_field_x', p);
export const tm_soft_delete_30d = () => t('tm_soft_delete_30d');
export const tm_no_fields = () => t('tm_no_fields');
export const tm_project_role = () => t('tm_project_role');
export const tm_cant_remove_last_lead = () => t('tm_cant_remove_last_lead');
export const tm_loading_summary = () => t('tm_loading_summary');
export const tm_archive_promote = (p) => t('tm_archive_promote', p);
export const tm_archive_exclusive = (p) => t('tm_archive_exclusive', p);
export const tm_archive_detach = (p) => t('tm_archive_detach', p);
export const tm_archive_section = () => t('tm_archive_section');
export const tm_archive_project = () => t('tm_archive_project');
export const tm_archive_project_ellipsis = () => t('tm_archive_project_ellipsis');
// ─── filters operators + link type (fields.ts) ───
export const tm_filters_op_in_list = () => t('tm_filters_op_in_list');
export const tm_filters_op_not_in = () => t('tm_filters_op_not_in');
export const tm_filters_op_eq = () => t('tm_filters_op_eq');
export const tm_filters_op_neq = () => t('tm_filters_op_neq');
export const tm_filters_op_contains = () => t('tm_filters_op_contains');
export const tm_filters_op_starts_with = () => t('tm_filters_op_starts_with');
export const tm_filters_op_gte = () => t('tm_filters_op_gte');
export const tm_filters_op_lte = () => t('tm_filters_op_lte');
export const tm_filters_op_between = () => t('tm_filters_op_between');
export const tm_filters_op_preset = () => t('tm_filters_op_preset');
export const tm_filters_link_type = () => t('tm_filters_link_type');
// grouping fallbacks
export const tm_group_unassigned = () => t('tm_group_unassigned');
export const tm_group_no_label = () => t('tm_group_no_label');
// shared ColumnConfigPopover
export const component_columns_title = () => t('component_columns_title');
/* Кнопка «Колонки» несёт ЕДИНСТВЕННЫЙ счётчик колонок: отрисовано / выбрано
   (гант прячет не влезшие в узкую таблицу). Круг 19 кадра 4 гейта F26. */
export const component_columns_badge_partial = (p) => t('component_columns_badge_partial', p);
export const component_columns_reset = () => t('component_columns_reset');
export const component_columns_search_ph = () => t('component_columns_search_ph');
export const component_columns_search_aria = () => t('component_columns_search_aria');
export const component_columns_pinned_title = () => t('component_columns_pinned_title');
export const component_columns_pinned_aria = () => t('component_columns_pinned_aria');
export const component_columns_empty = () => t('component_columns_empty');
export const component_columns_scope = () => t('component_columns_scope');
export const component_columns_move_aria = (p) => t('component_columns_move_aria', p);
// ─── Email-уведомления task-module (notification-center B8) ──────────
export const email_footer_settings = () => t('email_footer_settings');
export const email_cta_open_task = () => t('email_cta_open_task');
export const email_assigned_subject = (p) => t('email_assigned_subject', p);
export const email_assigned_heading = () => t('email_assigned_heading');
export const email_assigned_body = (p) => t('email_assigned_body', p);
export const email_mention_subject = (p) => t('email_mention_subject', p);
export const email_mention_heading = () => t('email_mention_heading');
export const email_mention_body = (p) => t('email_mention_body', p);
export const email_due_soon_subject = (p) => t('email_due_soon_subject', p);
export const email_due_soon_heading = () => t('email_due_soon_heading');
export const email_due_soon_body = (p) => t('email_due_soon_body', p);
export const email_overdue_subject = (p) => t('email_overdue_subject', p);
export const email_overdue_heading = () => t('email_overdue_heading');
/** WTU-BE-2 — письмо о забытом таймере (почта не зависит от активной компании). */
export const email_timer_reminder_subject = (p) => t('email_timer_reminder_subject', p);
export const email_timer_reminder_heading = () => t('email_timer_reminder_heading');
export const email_timer_reminder_body = (p) => t('email_timer_reminder_body', p);
export const email_overdue_body = (p) => t('email_overdue_body', p);
export const email_bulk_assign_subject = (p) => t('email_bulk_assign_subject', p);
export const email_bulk_assign_more = (p) => t('email_bulk_assign_more', p);
/** Плюрализованный заголовок «Вам назначено N задач/задачи/задача» (B8, plural-select). */
export const email_bulk_assign_heading = (p) => {
    const word = pluralForm(p.count, {
        one: t('email_tasks_one'),
        few: t('email_tasks_few'),
        many: t('email_tasks_many'),
    });
    return `${t('email_bulk_assign_heading_prefix')} ${p.count} ${word}`;
};
export const email_actor_fallback = () => t('email_actor_fallback');
/**
 * B17 (work-tasks-user-feedback §3.5) — проект в письме.
 *
 * Письмо «сделать нечто» без контекста заставляло открывать задачу только чтобы
 * понять, о каком проекте речь [UF-11]. Проект идёт и в теме (для сортировки в
 * почтовом клиенте), и строкой в теле — вместе с номером задачи, если он известен.
 */
/**
 * Префикс темы. Отдельным ключом, а не `{project}` внутри каждой темы: у письма
 * без проекта тема обязана остаться прежней, а не получить пустые скобки.
 */
export const email_subject_with_project = (p) => t('email_subject_with_project', p);
export const email_project_line = (p) => t('email_project_line', p);
export const email_project_line_no_ref = (p) => t('email_project_line_no_ref', p);
/** B17 — письмо по комментарию: раньше шаблона не было, и письмо молча не уходило. */
export const email_comment_subject = (p) => t('email_comment_subject', p);
export const email_comment_heading = () => t('email_comment_heading');
export const email_comment_body = (p) => t('email_comment_body', p);
// ─── Центр уведомлений FE (notification-center F2/F3) ────────────────
export const relative_time_days_ago = (p) => t('relative_time_days_ago', { count: p.count });
export const topbar_approvals = () => t('topbar_approvals');
export const tm_notif_task_unavailable = () => t('tm_notif_task_unavailable');
export const tm_notif_approval_requested = () => t('tm_notif_approval_requested');
export const tm_notif_approval_reminder = () => t('tm_notif_approval_reminder');
export const tm_notif_approval_escalation = () => t('tm_notif_approval_escalation');
export const tm_notif_approval_approved = () => t('tm_notif_approval_approved');
export const tm_notif_approval_rejected = () => t('tm_notif_approval_rejected');
export const tm_notif_approval_unconfirmed = () => t('tm_notif_approval_unconfirmed');
export const tm_notif_approval_failed = () => t('tm_notif_approval_failed');
export const tm_notif_approval_decided = () => t('tm_notif_approval_decided');
export const tm_notif_approval_expired = () => t('tm_notif_approval_expired');
export const tm_notif_approval_impact_rebuilt = () => t('tm_notif_approval_impact_rebuilt');
export const tm_notif_approval_subject_fallback = () => t('tm_notif_approval_subject_fallback');
export const tm_notif_approval_subject = (p) => t('tm_notif_approval_subject', p);
export const tm_notif_approval_subject_more = (p) => t('tm_notif_approval_subject_more', p);
export const tm_notif_all_link = () => t('tm_notif_all_link');
export const tm_notif_settings_aria = () => t('tm_notif_settings_aria');
/** «Просрочено · N задач» — плюрализованный агрегат дропдауна (F3). */
export const tm_notif_overdue_aggregate = (p) => {
    const word = pluralForm(p.count, {
        one: t('tm_notif_tasks_one'),
        few: t('tm_notif_tasks_few'),
        many: t('tm_notif_tasks_many'),
    });
    return `${t('tm_notif_overdue_prefix')} · ${p.count} ${word}`;
};
// ─── Центр уведомлений: страница + тост (notification-center F4/F6/F7) ──
export const tm_notif_center_title = () => t('tm_notif_center_title');
export const tm_notif_filter_all = () => t('tm_notif_filter_all');
export const tm_notif_filter_actionable = () => t('tm_notif_filter_actionable');
export const tm_notif_filter_info = () => t('tm_notif_filter_info');
export const tm_notif_filter_unread = () => t('tm_notif_filter_unread');
export const tm_notif_show_more = () => t('tm_notif_show_more');
export const tm_notif_mark_unread = () => t('tm_notif_mark_unread');
export const tm_notif_bulk_read = () => t('tm_notif_bulk_read');
export const tm_notif_bulk_all_read = () => t('tm_notif_bulk_all_read');
export const tm_notif_empty_title = () => t('tm_notif_empty_title');
export const tm_notif_empty_body = () => t('tm_notif_empty_body');
export const tm_notif_empty_cta = () => t('tm_notif_empty_cta');
export const tm_notif_day_today = () => t('tm_notif_day_today');
export const tm_notif_day_yesterday = () => t('tm_notif_day_yesterday');
export const tm_notif_settings_backlink = () => t('tm_notif_settings_backlink');
export const tm_notif_prefs_backlink = () => t('tm_notif_prefs_backlink');
export const tm_notif_selected = (p) => `${t('tm_notif_selected_prefix')}: ${p.count}`;
/** Тост «+N новых уведомлений» (F6, plural-select). */
export const tm_notif_toast_new = (p) => {
    const word = pluralForm(p.count, {
        one: t('tm_notif_toast_new_one'),
        few: t('tm_notif_toast_new_few'),
        many: t('tm_notif_toast_new_many'),
    });
    return `+${p.count} ${word}`;
};
export const nav_notifications = () => t('nav_notifications');
export const tm_notif_toast_single = () => t('tm_notif_toast_single');
// ─── Карточка уведомления (notification-center F11, раунд 2) ─────────
export const tm_notif_card_go_to_task = () => t('tm_notif_card_go_to_task');
/**
 * F18 (work-tasks-user-feedback §3.5) [UF-6, инвариант I5] — панель уведомления
 * перестала быть read-only: уведомление обрабатывается на месте.
 */
export const tm_notif_card_complete = () => t('tm_notif_card_complete');
export const tm_notif_card_reply = () => t('tm_notif_card_reply');
export const tm_notif_card_reply_hint = () => t('tm_notif_card_reply_hint');
/**
 * F18, раунд 1 гейта (UX-блокер): отвечать на упоминание, не видя, НА ЧТО
 * отвечаешь, — это ответ вслепую. Над полем стоят последние сообщения ветки.
 */
export const tm_notif_card_thread = () => t('tm_notif_card_thread');
export const tm_notif_card_thread_empty = () => t('tm_notif_card_thread_empty');
/**
 * F18, раунд 3 гейта (UX): срез ветки не объявлял своего объёма — человек не знал,
 * что видит хвост, а не весь разговор; и не видел, какая реплика его позвала.
 */
export const tm_notif_card_thread_more = (p) => t('tm_notif_card_thread_more', p);
export const tm_notif_card_thread_trigger = () => t('tm_notif_card_thread_trigger');
/**
 * F18, раунд 2 гейта (UX): последствия действий не были объявлены — панель
 * закроется? уведомление исчезнет навсегда? Сказано ДО клика, одной строкой.
 */
export const tm_notif_card_consequences = () => t('tm_notif_card_consequences');
/**
 * F18, раунд 7 гейта (UX): очередь рвалась — обработал уведомление и возвращайся
 * в список руками. Панель ведёт по очереди сама.
 */
export const tm_notif_card_next = () => t('tm_notif_card_next');
/**
 * F20 (§3.5) [П14] — конец очереди объявляется словами, а не молчаливым закрытием
 * панели: человек должен знать, что разобрал всё, а не что панель «сама закрылась».
 */
export const tm_notif_card_queue_end = () => t('tm_notif_card_queue_end');
export const tm_notif_card_next_unread = () => t('tm_notif_card_next_unread');
export const tm_notif_card_prev = () => t('tm_notif_card_prev');
export const tm_notif_card_queue_pos = (p) => t('tm_notif_card_queue_pos', p);
export const tm_notif_card_priority_none = () => t('tm_notif_card_priority_none');
export const tm_notif_card_completed_toast = (p) => t('tm_notif_card_completed_toast', p);
/**
 * F18, раунд 5 гейта (UX, инвариант I1): завершение в потоковом разборе очереди
 * не имело пути назад. Возврат — в том же тосте, статусом, который был до клика.
 */
export const tm_notif_card_completed_undo = () => t('tm_notif_card_completed_undo');
export const tm_notif_card_hide = () => t('tm_notif_card_hide');
/**
 * F18, раунд 7 (UI): футер занимал четверть панели, потому что подписи ряда
 * действий не влезали в его ширину и переносились. В панели УВЕДОМЛЕНИЯ слово
 * «уведомление» в каждой подписи избыточно — предмет назван самой панелью.
 */
export const tm_notif_card_mark_unread_short = () => t('tm_notif_card_mark_unread_short');
export const tm_notif_card_status = () => t('tm_notif_card_status');
export const tm_notif_card_assignees = () => t('tm_notif_card_assignees');
export const tm_notif_card_due = () => t('tm_notif_card_due');
export const tm_notif_card_priority = () => t('tm_notif_card_priority');
export const tm_notif_card_context = () => t('tm_notif_card_context');
export const tm_notif_card_none = () => t('tm_notif_card_none');
export const tm_notif_card_unavailable_reason = () => t('tm_notif_card_unavailable_reason');
/**
 * F19 (work-tasks-user-feedback §3.5) [П13] — задача под уведомлением мертва.
 *
 * Панель гейтила доступность только по пустым полям уведомления, а 404/403 от
 * загрузки задачи не ловила вовсе: в режиме очереди действия F18 стреляли в
 * мёртвую задачу и давали серию тостов ошибок. «Удалена» и «нет доступа» —
 * РАЗНЫЕ исходы: первый тупик, второй маршрут (у кого просить право).
 */
export const tm_notif_card_task_gone = () => t('tm_notif_card_task_gone');
export const tm_notif_card_task_forbidden = () => t('tm_notif_card_task_forbidden');
export const tm_notif_card_task_gone_body = () => t('tm_notif_card_task_gone_body');
export const tm_notif_card_task_forbidden_body = () => t('tm_notif_card_task_forbidden_body');
export const tm_notif_ctx_assigned = (p) => t('tm_notif_ctx_assigned', p);
export const tm_notif_ctx_comment = () => t('tm_notif_ctx_comment');
export const tm_notif_group_label = (group) => {
    switch (group) {
        case 'todo':
            return t('tm_notif_group_todo');
        case 'started':
            return t('tm_notif_group_started');
        case 'completed':
            return t('tm_notif_group_completed');
        case 'cancelled':
            return t('tm_notif_group_cancelled');
        default:
            return group;
    }
};
/** «Просрочено на N дней» / «Осталось N дней» (plural-select). */
function daysWord(n) {
    return pluralForm(n, {
        one: t('tm_notif_days_one'),
        few: t('tm_notif_days_few'),
        many: t('tm_notif_days_many'),
    });
}
export const tm_notif_ctx_overdue = (p) => `${t('tm_notif_ctx_overdue_prefix')} ${p.days} ${daysWord(p.days)}`;
export const tm_notif_ctx_due_soon = (p) => `${t('tm_notif_ctx_due_prefix')} ${p.days} ${daysWord(p.days)}`;
// SLA overdue-повтор через 3 дня (аларм) — метка + контекст.
export const tm_notif_overdue_repeat = () => t('tm_notif_overdue_repeat');
export const tm_notif_ctx_overdue_repeat = (p) => `${t('tm_notif_ctx_overdue_repeat_prefix')} ${p.days} ${daysWord(p.days)}`;
// ─── Табличный центр (notification-center F12, раунд 2) ──────────────
export const tm_notif_col_type = () => t('tm_notif_col_type');
export const tm_notif_col_task = () => t('tm_notif_col_task');
export const tm_notif_col_project = () => t('tm_notif_col_project');
export const tm_notif_col_who = () => t('tm_notif_col_who');
export const tm_notif_col_count = () => t('tm_notif_col_count');
export const tm_notif_col_date = () => t('tm_notif_col_date');
export const tm_notif_col_status = () => t('tm_notif_col_status');
export const tm_notif_columns_btn = () => t('tm_notif_columns_btn');
export const tm_notif_status_read = () => t('tm_notif_status_read');
export const tm_notif_status_unread = () => t('tm_notif_status_unread');
export const tm_notif_bulk_unread = () => t('tm_notif_bulk_unread');
export const tm_notif_mark_read_hint = () => t('tm_notif_mark_read_hint');
export const tm_notif_mark_unread_hint = () => t('tm_notif_mark_unread_hint');
export const tm_notif_bulk_clear = () => t('tm_notif_bulk_clear');
/**
 * F17 (work-tasks-user-feedback §3.5) [UF-4] — скрытие уведомлений.
 *
 * «Не убираются никак»: прочитанные копились вечно. «Скрыть» — не удаление:
 * скрытое лежит в собственном сегменте и возвращается оттуда, поэтому подписи
 * говорят «Скрыть»/«Вернуть», а не «Удалить».
 */
export const tm_notif_dismiss = () => t('tm_notif_dismiss');
/** Подсказка клавиш — инфинитив в нижнем регистре, как у соседних `x` и `e`. */
export const tm_notif_kbd_dismiss = () => t('tm_notif_kbd_dismiss');
export const tm_notif_kbd_undismiss = () => t('tm_notif_kbd_undismiss');
export const tm_notif_undismiss = () => t('tm_notif_undismiss');
export const tm_notif_dismiss_hint = () => t('tm_notif_dismiss_hint');
export const tm_notif_undismiss_hint = () => t('tm_notif_undismiss_hint');
export const tm_notif_bulk_dismiss = () => t('tm_notif_bulk_dismiss');
export const tm_notif_bulk_undismiss = () => t('tm_notif_bulk_undismiss');
export const tm_notif_dismiss_read = () => t('tm_notif_dismiss_read');
export const tm_notif_dismiss_read_hint = () => t('tm_notif_dismiss_read_hint');
export const tm_notif_segment_dismissed = () => t('tm_notif_segment_dismissed');
export const tm_notif_toast_dismissed = (p) => pluralForm(p.count, {
    one: t('tm_notif_toast_dismissed_one', p),
    few: t('tm_notif_toast_dismissed_few', p),
    many: t('tm_notif_toast_dismissed_many', p),
});
export const tm_notif_toast_undismissed = (p) => pluralForm(p.count, {
    one: t('tm_notif_toast_undismissed_one', p),
    few: t('tm_notif_toast_undismissed_few', p),
    many: t('tm_notif_toast_undismissed_many', p),
});
export const tm_notif_toast_nothing_dismissed = () => t('tm_notif_toast_nothing_dismissed');
export const tm_notif_dismissed_empty = () => t('tm_notif_dismissed_empty');
export const tm_notif_dismissed_empty_hint = () => t('tm_notif_dismissed_empty_hint');
/** Та же мысль строкой НАД таблицей — там, где скрытые есть (раунд 3 гейта). */
export const tm_notif_dismissed_hint_row = () => t('tm_notif_dismissed_hint_row');
/**
 * Раунд 4 (UX): «Скрыто 1 уведомление» не называло предмет, и отмена была
 * слепой — при одной строке в тосте стоит её заголовок.
 */
export const tm_notif_toast_dismissed_named = (p) => t('tm_notif_toast_dismissed_named', p);
export const tm_notif_bulk_region = () => t('tm_notif_bulk_region');
export const tm_notif_state_unread = () => t('tm_notif_state_unread');
export const tm_notif_state_read = () => t('tm_notif_state_read');
export const tm_notif_kbd_select = () => t('tm_notif_kbd_select');
export const tm_notif_kbd_toggle_read = () => t('tm_notif_kbd_toggle_read');
export const tm_notif_seg_count_hint = () => t('tm_notif_seg_count_hint');
export const tm_notif_sort_criticality = () => t('tm_notif_sort_criticality');
export const tm_notif_sort_criticality_hint = () => t('tm_notif_sort_criticality_hint');
export const tm_notif_shown_of = (p) => t('tm_notif_shown_of', p);
export const tm_notif_selected_of = (p) => t('tm_notif_selected_of', p);
export const tm_notif_entity_scope_hint = () => t('tm_notif_entity_scope_hint');
// ─── Табличный центр — rework раунда 2 (F15 design-review) ──────────
export const tm_notif_col_read_state = () => t('tm_notif_col_read_state');
export const tm_notif_col_events = () => t('tm_notif_col_events');
export const tm_notif_col_events_hint = () => t('tm_notif_col_events_hint');
// ─── SLA-эпизод: жизненный цикл (notification-sla-lifecycle F9) ──────
/** Бейдж «Неактуально» на резолвнутой строке/карточке (§4.1–4.2). */
export const tm_notif_resolved_badge = () => t('tm_notif_resolved_badge');
/** Причина резолва — enum-код с BE (`resolve_reason`), локализуется здесь. */
export const tm_notif_resolve_reason = (code) => {
    switch (code) {
        case 'task_completed':
            return t('tm_notif_resolve_reason_task_completed');
        case 'task_archived':
            return t('tm_notif_resolve_reason_task_archived');
        case 'task_cancelled':
            return t('tm_notif_resolve_reason_task_cancelled');
        case 'project_inactive':
            return t('tm_notif_resolve_reason_project_inactive');
        case 'due_date_changed':
            return t('tm_notif_resolve_reason_due_date_changed');
        case 'superseded':
            return t('tm_notif_resolve_reason_superseded');
        case 'prefs_changed':
            return t('tm_notif_resolve_reason_prefs_changed');
        case 'task_deleted':
            return t('tm_notif_resolve_reason_task_deleted');
        case 'approval_closed':
            return t('tm_notif_resolve_reason_approval_closed');
        default:
            return t('tm_notif_resolved_badge');
    }
};
/** «Срок изменён: 10.07.2026 → 22.07.2026» — снапшот эпизода против живой даты (И-3). */
export const tm_notif_due_changed = (p) => `${t('tm_notif_due_changed_label')}: ${p.from} → ${p.to}`;
/** Чип строки списка: «срок 22.07.2026» — к какой дате относится SLA-строка. */
export const tm_notif_episode_due = (p) => `${t('tm_notif_episode_due_prefix')} ${p.date}`;
/** «Просрочено на N дней на момент уведомления» — замороженный счётчик (§4.1). */
export const tm_notif_ctx_overdue_snapshot = (p) => `${t('tm_notif_ctx_overdue_prefix')} ${p.days} ${daysWord(p.days)} ${t('tm_notif_snapshot_suffix')}`;
export const tm_notif_show_resolved = () => t('tm_notif_show_resolved');
// ─── Дайджест SLA (F8) ───────────────────────────────────────────────
export const tm_notif_sla_digest = () => t('tm_notif_sla_digest');
/** «Просрочено: 6 · срок приближается: 2». */
export const tm_notif_digest_counts = (p) => `${t('tm_notif_digest_overdue_label')}: ${p.overdue} · ${t('tm_notif_digest_due_soon_label')}: ${p.dueSoon}`;
/** Заголовок тоста: «В проекте Логистика: просрочено 6, срок приближается 2». */
export const tm_notif_digest_toast = (p) => `${t('tm_notif_digest_toast_prefix')} ${p.project}: ${t('tm_notif_digest_overdue_label')} ${p.overdue}, ${t('tm_notif_digest_due_soon_label')} ${p.dueSoon}`;
export const tm_notif_digest_go_project = () => t('tm_notif_digest_go_project');
// ─── Вкладка настроек в центре (F11–F12) ─────────────────────────────
export const tm_notif_tab_list = () => t('tm_notif_tab_list');
export const tm_notif_tab_settings = () => t('tm_notif_tab_settings');
export const tm_notif_settings_tasks_section = () => t('tm_notif_settings_tasks_section');
export const tm_notif_settings_params_section = () => t('tm_notif_settings_params_section');
export const tm_notif_lead_stepper_label = () => t('tm_notif_lead_stepper_label');
/** Значение степпера: «за 3 дня до срока». */
export const tm_notif_lead_value = (p) => `${t('tm_notif_lead_prefix')} ${p.days} ${daysWord(p.days)} ${t('tm_notif_lead_before_due')}`.trim();
export const tm_notif_lead_decrease = () => t('tm_notif_lead_decrease');
export const tm_notif_lead_increase = () => t('tm_notif_lead_increase');
export const tm_notif_settings_apply_hint = () => t('tm_notif_settings_apply_hint');
export const tm_notif_settings_saving = () => t('tm_notif_settings_saving');
export const tm_notif_settings_saved = () => t('tm_notif_settings_saved');
/** Y-5 — парная ветка отказа сохранения настроек уведомлений. */
export const tm_notif_settings_retry = () => t('tm_notif_settings_retry');
export const tm_notif_prefs_link_center = () => t('tm_notif_prefs_link_center');
// ─── Дизайн-ревю С2/С4: честные подписи состояния и области действия ──
/** Подпись рядом с тумблером: сегмент «Все» по умолчанию скрывает резолвнутые. */
export const tm_notif_stale_hidden = () => t('tm_notif_stale_hidden');
/** Колонка «Получено» — дата уведомления, не срок задачи. */
export const tm_notif_col_received = () => t('tm_notif_col_received');
export const tm_notif_settings_scope_hint = () => t('tm_notif_settings_scope_hint');
export const tm_notif_lead_range_hint = () => t('tm_notif_lead_range_hint');
/**
 * F23 (§3.5) — тумблер письма у триггера БЕЗ шаблона включался, а письмо молча не
 * уходило: ложное обещание. Список способных на письмо триггеров — из контракта
 * B17 (`EMAIL_CAPABLE_TRIGGERS`), а не второй копией здесь.
 */
export const tm_notif_settings_no_email_template = () => t('tm_notif_settings_no_email_template');
export const tm_notif_sla_tick_hint = () => t('tm_notif_sla_tick_hint');
/** Суффикс терминального состояния в колонке «Тип»: «Просрочено · снято». */
export const tm_notif_label_cleared_suffix = () => t('tm_notif_label_cleared_suffix');
/** Слово «день/дня/дней» с корректной плюрализацией — для составных подписей,
 *  где число рендерится отдельным элементом (моноширинный слот степпера). */
export const tm_notif_days_word = (n) => daysWord(n);
/** Префикс подписи lead («За» / пусто в en) — рендерится вокруг числа. */
export const tm_notif_lead_prefix = () => t('tm_notif_lead_prefix');
export const tm_notif_lead_before_due = () => t('tm_notif_lead_before_due');
// ─── project-task-progress (прогресс задач и проектов) ────────────────
/** Подпись поля/колонки прогресса задачи. */
export const tm_progress = () => t('tm_progress');
/** Источник роллапа в карточке: иерархия per-project, дерево надо назвать явно. */
export const tm_progress_rollup_by_main_project = (p) => t('tm_progress_rollup_by_main_project', p);
/** Завершённая задача показывается как 100 по display-правилу — объясняем почему. */
export const tm_progress_completed_note = () => t('tm_progress_completed_note');
/** Сырое значение поля завершённой задачи: оно вернётся при переоткрытии. */
export const tm_progress_stored_value = (p) => t('tm_progress_stored_value', p);
/** Папка своего прогресса не хранит — только сводку по детям. */
export const tm_progress_folder_note = () => t('tm_progress_folder_note');
/** Пустое значение прогресса (отменённая задача исключена из агрегатов). */
export const tm_progress_none = () => t('tm_progress_none');
export const tm_progress_project = () => t('tm_progress_project');
export const tm_progress_method = () => t('tm_progress_method');
export const tm_progress_manual_value = () => t('tm_progress_manual_value');
export const tm_progress_manual_only_hint = () => t('tm_progress_manual_only_hint');
/** Формулы методов — тултипы селектора (И-3: метод обязан быть понятен). */
export const pc_method_manual_hint = () => t('pc_method_manual_hint');
export const pc_method_task_completion_hint = () => t('pc_method_task_completion_hint');
export const pc_method_task_progress_hint = () => t('pc_method_task_progress_hint');
export const pc_method_task_weight_hint = () => t('pc_method_task_weight_hint');
/** Предупреждение о смешанных единицах веса (SOLUTION §6 п.1). */
export const pc_method_task_weight_warning = () => t('pc_method_task_weight_warning');
/** Z-B5-FE — разрез знаменателя взвешенного метода по единицам оценки.
    Механику предупреждение называло и раньше; последствие — сколько задач в каких
    единицах попало в знаменатель — назвать было нечем, пока BE не отдал разрез. */
export const pc_method_task_weight_breakdown_intro = () => t('pc_method_task_weight_breakdown_intro');
export const pc_method_task_weight_breakdown_col_uom = () => t('pc_method_task_weight_breakdown_col_uom');
export const pc_method_task_weight_breakdown_col_tasks = () => t('pc_method_task_weight_breakdown_col_tasks');
export const pc_method_task_weight_breakdown_col_sum = () => t('pc_method_task_weight_breakdown_col_sum');
export const pc_method_task_weight_breakdown_no_estimate = () => t('pc_method_task_weight_breakdown_no_estimate');
export const pc_method_task_weight_breakdown_empty = () => t('pc_method_task_weight_breakdown_empty');
/** Названия методов расчёта прогресса проекта (селектор + подпись в шапке). */
export const pc_method_manual = () => t('pc_method_manual');
export const pc_method_task_completion = () => t('pc_method_task_completion');
export const pc_method_task_progress = () => t('pc_method_task_progress');
export const pc_method_task_weight = () => t('pc_method_task_weight');
/**
 * Подпись под Hero-метрикой прогресса проекта. Описывает СВОЙ метод, а не чужую
 * дробь: «0 / 3» под средним прогрессом читалось как противоречие (0 завершено,
 * а показано 25%) и дублировало строку «Задачи» ниже.
 */
export const tm_progress_hero_subtitle_completion = (p) => t('tm_progress_hero_subtitle_completion', p);
export const tm_progress_hero_subtitle_avg = (p) => t('tm_progress_hero_subtitle_avg', p);
export const tm_progress_hero_subtitle_weighted = (p) => t('tm_progress_hero_subtitle_weighted', p);
export const tm_progress_hero_subtitle_manual = () => t('tm_progress_hero_subtitle_manual');
/** Пояснения к ячейкам прогресса: почему 100 и почему «—» (раунд 2 ревью П2). */
export const tm_progress_cell_completed_title = (p) => t('tm_progress_cell_completed_title', p);
export const tm_progress_cell_cancelled_title = () => t('tm_progress_cell_cancelled_title');
/** F15 — прочерк роллапа: у папки без учитываемых листьев прогресса НЕ СУЩЕСТВУЕТ. */
export const tm_progress_cell_no_leaves_title = () => t('tm_progress_cell_no_leaves_title');
/**
 * F14 — крошка родителя, вырезанного фильтром. Без неё живые дети скрытого
 * этапа рисуются корнями, и план выглядит рассыпавшимся.
 */
export const tm_detached_parent = (p) => t('tm_detached_parent', p);
export const tm_detached_parent_unknown = () => t('tm_detached_parent_unknown');
/**
 * F14, раунд 5 гейта (UX) — соседние заголовки групп несут число строк, крошка не
 * несла. Считается по ВИДИМЫМ детям группы: обещать полный состав скрытого этапа
 * крошка не может — его знает только выдача под другим фильтром.
 */
export const tm_detached_parent_count = (p) => pluralForm(p.count, {
    one: t('tm_detached_parent_count_one', p),
    few: t('tm_detached_parent_count_few', p),
    many: t('tm_detached_parent_count_many', p),
});
/**
 * F14, раунд 4 гейта (UX) — крошка была главным входом в карточку этапа, но
 * выглядела подписью. Подсказка называет действие, а не повторяет имя.
 */
export const tm_detached_parent_open = (p) => t('tm_detached_parent_open', p);
/**
 * F14, раунд 2 гейта (UX) — крошка объясняла пропажу, но не давала выхода.
 * Действие снимает срез «только активные», и вырезанный этап возвращается в список.
 */
/**
 * F14, раунд 3 гейта (UX-блокер) — действие ушло из крошки к фильтру.
 *
 * Ссылка под конкретной крошкой обещала «раскрыть ЭТОТ этап», а снимала срез по
 * всему списку. Область действия обязана совпадать с местом, где оно стоит: счётчик
 * у фильтра называет масштаб (сколько этапов вырезано) и снимает срез целиком.
 */
export const tm_detached_parents_hidden = (p) => t('tm_detached_parents_hidden', p);
export const tm_detached_parents_hidden_title = () => t('tm_detached_parents_hidden_title');
/**
 * F14, раунд 9 гейта (UX) — из ВИДИМОЙ подписи существительное убрано ради ширины
 * ряда, но из доступного имени убирать его нельзя: «Показать скрытые (1)» без
 * контекста экрана не отвечает, что именно скрыто.
 */
export const tm_detached_parents_hidden_aria = (p) => t('tm_detached_parents_hidden_aria', p);
export const tm_progress_cell_rollup_title = (p) => t('tm_progress_cell_rollup_title', p);
export const tm_progress_reason_placeholder = () => t('tm_progress_reason_placeholder');
/** Раунд 3 ревью П2 — путь от числа к правилу и явное имя агрегации. */
export const tm_progress_hero_method_link = () => t('tm_progress_hero_method_link');
export const tm_progress_cell_rollup_title_v2 = (p) => t('tm_progress_cell_rollup_title_v2', p);
export const tm_progress_cell_full_not_done_title = () => t('tm_progress_cell_full_not_done_title');
/** Имя контрола по природе значения — словарь обязан доходить и до скринридера. */
/** Z-F9 — легенда маркеров колонки «Прогресс» (ключ для зрячих, не только aria). */
export const tm_progress_legend_title = () => t('tm_progress_legend_title');
export const tm_progress_legend_heading = () => t('tm_progress_legend_heading');
export const tm_progress_legend_rollup = () => t('tm_progress_legend_rollup');
export const tm_progress_legend_derived = () => t('tm_progress_legend_derived');
export const tm_progress_legend_excluded = () => t('tm_progress_legend_excluded');
export const tm_progress_legend_own = () => t('tm_progress_legend_own');
export const tm_progress_aria_rollup = () => t('tm_progress_aria_rollup');
export const tm_progress_aria_derived = () => t('tm_progress_aria_derived');
export const tm_progress_aria_excluded = () => t('tm_progress_aria_excluded');
/** Подпись дельты в предпросмотре baseline — «Δ» голой буквой не читается. */
export const project_baseline_delta = () => t('project_baseline_delta');
export const project_baseline_no_change = () => t('project_baseline_no_change');
/**
 * Раунд 4 ревью П2 — знаменатель объясняет сам себя, ссылка честна про namesto.
 *
 * D4.10 (Z-F6) — формулировка исправлена по замеру агрегата. Число считается как
 * `total − leaf_count`, а оба слагаемых уже отбрасывают отменённые и вовсе не видят
 * контейнеры (`kind='folder'`): в разность попадают ТОЛЬКО задачи, у которых внутри
 * проекта есть подзадачи. Прежнее «(контейнеры и отменённые)» называло две категории,
 * которых в этом числе нет ни одной штуки.
 */
export const tm_progress_hero_excluded_note = (p) => t('tm_progress_hero_excluded_note', p);
/** D4.10 — доступная подпись ссылки «не в счёте» (сама строка идёт в тексте метрики). */
export const tm_progress_hero_excluded_link_aria = (p) => t('tm_progress_hero_excluded_link_aria', p);
/** D4.10 — чип активного среза «вне расчёта прогресса» над видами задач проекта. */
export const tm_filters_progress_excluded_chip = () => t('tm_filters_progress_excluded_chip');
export const tm_progress_hero_change_method = () => t('tm_progress_hero_change_method');
/** Раунд 5 ревью П2 — смена метода не должна подтверждаться вслепую. */
export const tm_progress_baseline_current = () => t('tm_progress_baseline_current');
export const tm_progress_baseline_after_save = () => t('tm_progress_baseline_after_save');
export const tm_progress_baseline_after = () => t('tm_progress_baseline_after');
/** Подписи редактора baseline — были русскими литералами мимо i18n. */
export const project_baseline_margin_before = () => t('project_baseline_margin_before');
export const project_baseline_margin_after = () => t('project_baseline_margin_after');
export const project_baseline_amount = () => t('project_baseline_amount');
export const project_baseline_currency = () => t('project_baseline_currency');
export const project_baseline_reason = () => t('project_baseline_reason');
export const project_financial_lifecycle_note = () => t('project_financial_lifecycle_note');
export const project_financial_schedule_variance_hint = () => t('project_financial_schedule_variance_hint');
export const project_baseline_tier2_warning = () => t('project_baseline_tier2_warning');
export const project_baseline_variance_undefined = () => t('project_baseline_variance_undefined');
export const project_baseline_variance_undefined_hint = () => t('project_baseline_variance_undefined_hint');
export const project_baseline_variance_undefined_sentence = () => t('project_baseline_variance_undefined_sentence');
export const project_baseline_reason_required = () => t('project_baseline_reason_required');
/** Z-F8 — guard ухода с заполненной формы baseline (потеря обоснования — не молча). */
export const project_baseline_discard_title = () => t('project_baseline_discard_title');
export const project_baseline_discard_body = () => t('project_baseline_discard_body');
export const project_baseline_discard_confirm = () => t('project_baseline_discard_confirm');
export const project_baseline_discard_keep = () => t('project_baseline_discard_keep');
export const project_baseline_dirty_form_title = () => t('project_baseline_dirty_form_title');
export const project_baseline_tier2_absolute = () => t('project_baseline_tier2_absolute');
export const project_baseline_save_title = () => t('project_baseline_save_title');
export const project_financial_cost_variance_hint = () => t('project_financial_cost_variance_hint');
/** PLM-8 (B15): смета в другой валюте — отклонение от плана не считается. */
export const project_financial_currency_mismatch = () => t('project_financial_currency_mismatch');
/** PLM-7 (B18): часть агрегатов метрик проекта не посчиталась. */
export const project_financial_degraded = () => t('project_financial_degraded');
/** F12 / PLM-6 (B20): что мешает закрыть проект. */
export const project_closeout_blocking_title = () => t('project_closeout_blocking_title');
export const project_closeout_warning_title = () => t('project_closeout_warning_title');
export const project_closeout_open = () => t('project_closeout_open');
export const project_closeout_tasks_open = (p) => t('project_closeout_tasks_open', p);
export const project_closeout_ar_unpaid = (p) => t('project_closeout_ar_unpaid', p);
export const project_closeout_ap_unpaid = (p) => t('project_closeout_ap_unpaid', p);
export const project_closeout_landed_cost_open = (p) => t('project_closeout_landed_cost_open', p);
export const project_closeout_work_orders_open = (p) => t('project_closeout_work_orders_open', p);
/** PLM-4 (B17): выбор подписанта Tier 2 и правка бюджета поверх чужой. */
export const tier_approval_signer = () => t('tier_approval_signer');
export const tier_approval_signer_placeholder = () => t('tier_approval_signer_placeholder');
export const tier_approval_no_candidates = () => t('tier_approval_no_candidates');
export const budget_stale_title = () => t('budget_stale_title');
export const budget_stale_body = () => t('budget_stale_body');
/** PLM-1 (B13/F89; В-26): часы без ставки в марже проекта. */
export const project_financial_labor_rate_missing = (p) => t('project_financial_labor_rate_missing', p);
export const project_financial_costs_empty_title = () => t('project_financial_costs_empty_title');
export const project_financial_costs_empty_hint = () => t('project_financial_costs_empty_hint');
export const project_financial_costs_empty_cta = () => t('project_financial_costs_empty_cta');
export const project_financial_costs_empty_import = () => t('project_financial_costs_empty_import');
export const project_baseline_tier2_method = () => t('project_baseline_tier2_method');
export const project_baseline_margin_no_revenue = () => t('project_baseline_margin_no_revenue');
// NC-39 (F14 tails-closeout) — выручка — операнд маржи в предпросчёте.
export const project_baseline_revenue_label = () => t('project_baseline_revenue_label');
// F14 круг 2 — основание Tier 2 и причина отдельными строками; метод в строке пояснений.
export const project_baseline_tier2_queue_lead = () => t('project_baseline_tier2_queue_lead');
export const project_baseline_tier2_reason_one = () => t('project_baseline_tier2_reason_one');
export const project_baseline_tier2_reason_many = () => t('project_baseline_tier2_reason_many');
export const pc_method_not_computing_short = () => t('pc_method_not_computing_short');
export const project_baseline_revenue_no_ar = () => t('project_baseline_revenue_no_ar');
export const project_baseline_revenue_from_form = () => t('project_baseline_revenue_from_form');
export const project_baseline_cost_before = () => t('project_baseline_cost_before');
export const project_baseline_cost_after = () => t('project_baseline_cost_after');
export const project_baseline_progress_delta = () => t('project_baseline_progress_delta');
export const project_baseline_pp = () => t('project_baseline_pp');
export const project_baseline_save_action = () => t('project_baseline_save_action');
export const project_baseline_save_with_review = () => t('project_baseline_save_with_review');
export const tm_progress_baseline_subject = () => t('tm_progress_baseline_subject');
export const project_baseline_cost_subject = () => t('project_baseline_cost_subject');
export const project_baseline_margin_subject = () => t('project_baseline_margin_subject');
export const project_financial_gross_margin_base_plan = () => t('project_financial_gross_margin_base_plan');
export const project_financial_gross_margin_base_fact = () => t('project_financial_gross_margin_base_fact');
export const project_financial_fact_not_started = () => t('project_financial_fact_not_started');
export const project_baseline_margin_subject_preview = () => t('project_baseline_margin_subject_preview');
export const project_baseline_before = () => t('project_baseline_before');
export const project_baseline_after = () => t('project_baseline_after');
export const project_baseline_tier2_first = () => t('project_baseline_tier2_first');
export const project_baseline_tier2_material = () => t('project_baseline_tier2_material');
export const project_baseline_saved_flagged = () => t('project_baseline_saved_flagged');
export const project_baseline_saved_plain = () => t('project_baseline_saved_plain');
export const project_baseline_saved_link = () => t('project_baseline_saved_link');
export const project_baseline_save_failed = () => t('project_baseline_save_failed');
export const ui_podtverzhdaem_novyy_email = () => t('ui_podtverzhdaem_novyy_email');
export const ui_vvedite_6_znachnyy_kod_iz_prilozheniya_autentifikatora = () => t('ui_vvedite_6_znachnyy_kod_iz_prilozheniya_autentifikatora');
export const ui_zagruzka = () => t('ui_zagruzka');
export const ui_sozdat_kompaniyu = () => t('ui_sozdat_kompaniyu');
export const ui_upravlenie_holdingami_dostupno_tolko_super_admin_svyazhit = () => t('ui_upravlenie_holdingami_dostupno_tolko_super_admin_svyazhit');
export const ui_dostupno_tolko_super_admin = () => t('ui_dostupno_tolko_super_admin');
export const ui_administrirovanie = () => t('ui_administrirovanie');
export const ui_izmenit = () => t('ui_izmenit');
export const ui_avtomatizaciya_zadach = () => t('ui_avtomatizaciya_zadach');
export const ui_dostup_k_adminke_trebuet_roli_admin_svyazhites_s_administ = () => t('ui_dostup_k_adminke_trebuet_roli_admin_svyazhites_s_administ');
export const ui_super = () => t('ui_super');
export const ui_aktivnye_filtry = () => t('ui_aktivnye_filtry');
export const ui_otkryt = () => t('ui_otkryt');
export const ui_kopiya_chernovik = () => t('ui_kopiya_chernovik');
export const ui_otkryt_posledniy_period = () => t('ui_otkryt_posledniy_period');
export const ui_vybrano = () => t('ui_vybrano');
export const ui_redaktirovat = () => t('ui_redaktirovat');
export const ui_otkryt_2 = () => t('ui_otkryt_2');
export const ui_dokumentaciya_staiter = () => t('ui_dokumentaciya_staiter');
export const ui_poisk_po_dokumentacii = () => t('ui_poisk_po_dokumentacii');
export const ui_vsego_poziciy = () => t('ui_vsego_poziciy');
export const ui_privyazat_postuplenie = () => t('ui_privyazat_postuplenie');
export const ui_avatar = () => t('ui_avatar');
export const ui_perehod_v_rabochee_prostranstvo_zadach = () => t('ui_perehod_v_rabochee_prostranstvo_zadach');
export const ui_prichina_perehoda_dlya_audita = () => t('ui_prichina_perehoda_dlya_audita');
export const ui_sohranit = () => t('ui_sohranit');
export const ui_dokument_nedostupen = () => t('ui_dokument_nedostupen');
export const ui_poziciya = () => t('ui_poziciya');
export const ui_tip = () => t('ui_tip');
export const ui_sbrosit_vse = () => t('ui_sbrosit_vse');
export const ui_vyberite_uzel_dereva_sleva_dlya_prosmotra_detaley = () => t('ui_vyberite_uzel_dereva_sleva_dlya_prosmotra_detaley');
export const ui_vybrat_zakaz = () => t('ui_vybrat_zakaz');
export const ui_sozdat_proekt = () => t('ui_sozdat_proekt');
export const ui_ne_naznachen = () => t('ui_ne_naznachen');
export const ui_vybrat_kompaniyu = () => t('ui_vybrat_kompaniyu');
export const ui_vam_poka_ne_vydan_dostup_ni_k_odnoy_kompanii = () => t('ui_vam_poka_ne_vydan_dostup_ni_k_odnoy_kompanii');
export const ui_net_dostupa = () => t('ui_net_dostupa');
export const ui_novyy_razdel = () => t('ui_novyy_razdel');
export const ui_v_tekuschey_kompanii_est_nesohranennye_dannye = () => t('ui_v_tekuschey_kompanii_est_nesohranennye_dannye');
export const ui_format_csv = () => t('ui_format_csv');
export const ui_rekvizity_i_kontrakt_mozhno_dozapolnit_pozzhe_v_kartochke = () => t('ui_rekvizity_i_kontrakt_mozhno_dozapolnit_pozzhe_v_kartochke');
export const ui_obosnovanie_minimum_10_simvolov_obyazatelno_dlya_audit = () => t('ui_obosnovanie_minimum_10_simvolov_obyazatelno_dlya_audit');
export const ui_super_admin = () => t('ui_super_admin');
export const ui_gant_poyavitsya_kogda_vy_dobavite_zadachi_s_planovymi_dat = () => t('ui_gant_poyavitsya_kogda_vy_dobavite_zadachi_s_planovymi_dat');
export const ui_plan = () => t('ui_plan');
export const ui_zagruzhaem_aktivnost = () => t('ui_zagruzhaem_aktivnost');
export const ui_ai_gipoteza = () => t('ui_ai_gipoteza');
export const ui_do = () => t('ui_do');
export const ui_sohranit_i_dobavit_esche = () => t('ui_sohranit_i_dobavit_esche');
export const ui_naydeno = () => t('ui_naydeno');
export const ui_budut_udaleny = () => t('ui_budut_udaleny');
export const ui_ne_udalos_zagruzit_dokument = () => t('ui_ne_udalos_zagruzit_dokument');
export const ui_otpustite_fayl_zagruzka_v_minio = () => t('ui_otpustite_fayl_zagruzka_v_minio');
export const ui_v_etom_razdele_poka_pusto = () => t('ui_v_etom_razdele_poka_pusto');
export const ui_zagruzka_istorii = () => t('ui_zagruzka_istorii');
export const ui_na_stranice = () => t('ui_na_stranice');
export const ui_posmotret_chto_izmenilos = () => t('ui_posmotret_chto_izmenilos');
export const ui_nazad = () => t('ui_nazad');
export const ui_sekundu_primenyaem_smenu_adresa = () => t('ui_sekundu_primenyaem_smenu_adresa');
export const ui_voyti = () => t('ui_voyti');
export const ui_ne_udalos_zagruzit_shablony = () => t('ui_ne_udalos_zagruzit_shablony');
export const ui_kompaniya_ne_naydena = () => t('ui_kompaniya_ne_naydena');
export const ui_sozdat_holding = () => t('ui_sozdat_holding');
export const ui_sistemnye_nastroyki_staiter_kazhdyy_razdel_dostupen_chere = () => t('ui_sistemnye_nastroyki_staiter_kazhdyy_razdel_dostupen_chere');
export const ui_sobytiynye_pravila = () => t('ui_sobytiynye_pravila');
export const ui_dobavit_dostup_k_kompanii = () => t('ui_dobavit_dostup_k_kompanii');
export const ui_dubl = () => t('ui_dubl');
export const ui_aktivirovat = () => t('ui_aktivirovat');
export const ui_istoriya = () => t('ui_istoriya');
export const ui_vasha_rol = () => t('ui_vasha_rol');
export const ui_vvedite_minimum_2_simvola_dlya_poiska = () => t('ui_vvedite_minimum_2_simvola_dlya_poiska');
export const ui_provesti = () => t('ui_provesti');
export const ui_iz_1s = () => t('ui_iz_1s');
export const ui_udalit = () => t('ui_udalit');
export const ui_vliyanie_pauzy_na_proekt = () => t('ui_vliyanie_pauzy_na_proekt');
export const ui_podtverdit = () => t('ui_podtverdit');
export const ui_server_vernul_dannye_kotorye_my_ne_mozhem_otobrazit_obrat = () => t('ui_server_vernul_dannye_kotorye_my_ne_mozhem_otobrazit_obrat');
export const ui_sklad_istochnik = () => t('ui_sklad_istochnik');
export const ui_status = () => t('ui_status');
export const ui_svyazhites_s_administratorom_sistemy_on_dobavit_vas_v_nuz = () => t('ui_svyazhites_s_administratorom_sistemy_on_dobavit_vas_v_nuz');
export const ui_vidy = () => t('ui_vidy');
export const ui_sbrosit_poryadok = () => t('ui_sbrosit_poryadok');
export const ui_otmena_pereklyucheniya = () => t('ui_otmena_pereklyucheniya');
export const ui_legacy_v_actor_s_entity = () => t('ui_legacy_v_actor_s_entity');
export const ui_otmena = () => t('ui_otmena');
export const ui_profil = () => t('ui_profil');
export const ui_pereyti_k_zadacham = () => t('ui_pereyti_k_zadacham');
export const ui_aktivnost = () => t('ui_aktivnost');
export const ui_ai_gipoteza_agenta_ne_istochnik_pravdy_proverte_pered_pri = () => t('ui_ai_gipoteza_agenta_ne_istochnik_pravdy_proverte_pered_pri');
export const ui_sbrosit_filtry = () => t('ui_sbrosit_filtry');
export const ui_deystvie_neobratimo = () => t('ui_deystvie_neobratimo');
export const ui_nesohranennye_izmeneniya = () => t('ui_nesohranennye_izmeneniya');
export const ui_dokumenty_poyavyatsya_posle_publikacii = () => t('ui_dokumenty_poyavyatsya_posle_publikacii');
export const ui_net_dannyh_istorii = () => t('ui_net_dannyh_istorii');
export const ui_zagruzka_soderzhaniya = () => t('ui_zagruzka_soderzhaniya');
export const ui_dalee = () => t('ui_dalee');
export const ui_email_izmenen = () => t('ui_email_izmenen');
export const ui_tema_pisma = () => t('ui_tema_pisma');
export const ui_otmena_2 = () => t('ui_otmena_2');
export const ui_informaciya = () => t('ui_informaciya');
export const ui_holding_ne_nayden = () => t('ui_holding_ne_nayden');
export const ui_proverit = () => t('ui_proverit');
export const ui_sozdat_pravilo = () => t('ui_sozdat_pravilo');
export const ui_dobavit_dostup_k_holdingu = () => t('ui_dobavit_dostup_k_holdingu');
export const ui_ne_udalos_zagruzit_dokument_poprobuyte_obnovit_stranicu = () => t('ui_ne_udalos_zagruzit_dokument_poprobuyte_obnovit_stranicu');
export const ui_poisk = () => t('ui_poisk');
export const ui_otmenit = () => t('ui_otmenit');
export const ui_sbrosit_vse_esc = () => t('ui_sbrosit_vse_esc');
export const ui_png_jpeg_ili_webp_do_5_mb = () => t('ui_png_jpeg_ili_webp_do_5_mb');
export const ui_v_rabote = () => t('ui_v_rabote');
export const ui_perehody_sostoyaniya = () => t('ui_perehody_sostoyaniya');
export const ui_schet = () => t('ui_schet');
export const ui_dokument_ne_nayden = () => t('ui_dokument_ne_nayden');
export const ui_sklad_poluchatel = () => t('ui_sklad_poluchatel');
export const ui_data_provodki = () => t('ui_data_provodki');
export const ui_istochnik = () => t('ui_istochnik');
export const ui_sohranit_kak_chernovik_i_pereklyuchit = () => t('ui_sohranit_kak_chernovik_i_pereklyuchit');
export const ui_rezultat = () => t('ui_rezultat');
export const ui_otobrazhaemoe_imya = () => t('ui_otobrazhaemoe_imya');
export const ui_billinga = () => t('ui_billinga');
export const ui_net_aktivnosti_za_period = () => t('ui_net_aktivnosti_za_period');
export const ui_po_filtru_zapisi_ne_naydeny = () => t('ui_po_filtru_zapisi_ne_naydeny');
export const ui_zagruzka_predvaritelnogo_analiza = () => t('ui_zagruzka_predvaritelnogo_analiza');
export const ui_tekuschaya = () => t('ui_tekuschaya');
export const ui_novoe = () => t('ui_novoe');
export const ui_novyy_adres_podtverzhden_voydite_zanovo_ispolzuya_novyy_e = () => t('ui_novyy_adres_podtverzhden_voydite_zanovo_ispolzuya_novyy_e');
export const ui_lokal_ru_multiyazychnyy_predprosmotr_poyavitsya_posle_lok = () => t('ui_lokal_ru_multiyazychnyy_predprosmotr_poyavitsya_posle_lok');
export const ui_sozdat = () => t('ui_sozdat');
export const ui_polzovateli = () => t('ui_polzovateli');
export const ui_shablony_poley_workspace = () => t('ui_shablony_poley_workspace');
export const ui_izmenit_rol = () => t('ui_izmenit_rol');
export const ui_perevod_nedostupen_pokazan_russkiy_original = () => t('ui_perevod_nedostupen_pokazan_russkiy_original');
export const ui_drugie_roli = () => t('ui_drugie_roli');
export const ui_poprobuyte_sokratit_zapros_ili_proverit_napisanie = () => t('ui_poprobuyte_sokratit_zapros_ili_proverit_napisanie');
export const ui_snyat_vydelenie = () => t('ui_snyat_vydelenie');
export const ui_email_dlya_vhoda = () => t('ui_email_dlya_vhoda');
export const ui_upravlenie = () => t('ui_upravlenie');
export const ui_kol_vo = () => t('ui_kol_vo');
export const ui_strok = () => t('ui_strok');
export const ui_adres = () => t('ui_adres');
export const ui_zakryt = () => t('ui_zakryt');
export const ui_telefon = () => t('ui_telefon');
export const ui_import_iz_bom = () => t('ui_import_iz_bom');
export const ui_summa = () => t('ui_summa');
export const ui_zadacha_bez_podzadach_zavisimostey_i_spisannyh_chasov_pro = () => t('ui_zadacha_bez_podzadach_zavisimostey_i_spisannyh_chasov_pro');
export const ui_vozmozhno_ssylka_ustarela_ili_razdel_byl_udalen = () => t('ui_vozmozhno_ssylka_ustarela_ili_razdel_byl_udalen');
export const ui_iz_ishodnikov = () => t('ui_iz_ishodnikov');
export const ui_pereyti_ko_vhodu = () => t('ui_pereyti_ko_vhodu');
export const ui_nastroyki = () => t('ui_nastroyki');
export const ui_skvoznye_razdely = () => t('ui_skvoznye_razdely');
export const ui_izmenit_gruppu = () => t('ui_izmenit_gruppu');
export const ui_tekuschiy_adres = () => t('ui_tekuschiy_adres');
export const ui_shablon_uvedomleniy = () => t('ui_shablon_uvedomleniy');
export const ui_schetov_poka_net_ar_schet_mozhno_sozdat_posle_podtverzhde = () => t('ui_schetov_poka_net_ar_schet_mozhno_sozdat_posle_podtverzhde');
export const ui_ed_izm = () => t('ui_ed_izm');
export const ui_docherniy = () => t('ui_docherniy');
export const ui_pereklyuchit_bez_sohraneniya = () => t('ui_pereklyuchit_bez_sohraneniya');
export const ui_otpravit = () => t('ui_otpravit');
export const ui_globalnaya_rol = () => t('ui_globalnaya_rol');
export const ui_ispolzuyte_knopku_zapisat_vremya_sverhu_ili_klavishu_n = () => t('ui_ispolzuyte_knopku_zapisat_vremya_sverhu_ili_klavishu_n');
export const ui_k_soderzhaniyu_dokumentacii = () => t('ui_k_soderzhaniyu_dokumentacii');
export const ui_posmotret = () => t('ui_posmotret');
export const ui_ssylka_nedeystvitelna = () => t('ui_ssylka_nedeystvitelna');
export const ui_shablony_pisem_predprosmotr = () => t('ui_shablony_pisem_predprosmotr');
export const ui_deaktivirovat = () => t('ui_deaktivirovat');
export const ui_vhod_i_2fa = () => t('ui_vhod_i_2fa');
export const ui_pismo_otpravleno_otkroyte_ssylku_v_pisme_na_novom_adrese = () => t('ui_pismo_otpravleno_otkroyte_ssylku_v_pisme_na_novom_adrese');
export const ui_opasnaya_zona = () => t('ui_opasnaya_zona');
export const ui_dokument_ne_soderzhit_poziciy = () => t('ui_dokument_ne_soderzhit_poziciy');
export const ui_pereimenovat = () => t('ui_pereimenovat');
export const ui_kompanii_i_roli = () => t('ui_kompanii_i_roli');
export const ui_segodnya = () => t('ui_segodnya');
export const ui_fakt = () => t('ui_fakt');
export const ui_prodolzhit_predyduschuyu_zapis = () => t('ui_prodolzhit_predyduschuyu_zapis');
export const ui_prichina_izmeneniya_opcionalno = () => t('ui_prichina_izmeneniya_opcionalno');
export const ui_dostup_ogranichen = () => t('ui_dostup_ogranichen');
export const ui_sravnit_s_tekuschey = () => t('ui_sravnit_s_tekuschey');
export const ui_smena_holdinga_vliyaet_na_dostup_polzovateley_unasledovan = () => t('ui_smena_holdinga_vliyaet_na_dostup_polzovateley_unasledovan');
export const ui_net_dannyh_predprosmotra = () => t('ui_net_dannyh_predprosmotra');
export const ui_navigaciya = () => t('ui_navigaciya');
export const ui_arhivirovat = () => t('ui_arhivirovat');
export const ui_mozhno_izmenit_seychas = () => t('ui_mozhno_izmenit_seychas');
export const ui_polzovatel_ne_privyazan_ni_k_odnoy_kompanii = () => t('ui_polzovatel_ne_privyazan_ni_k_odnoy_kompanii');
export const ui_etot_razdel_dostupen_tolko_opredelennym_rolyam_obratites = () => t('ui_etot_razdel_dostupen_tolko_opredelennym_rolyam_obratites');
export const ui_ne_udalos_podtverdit = () => t('ui_ne_udalos_podtverdit');
export const ui_temy_oformleniya = () => t('ui_temy_oformleniya');
export const ui_bezopasnost = () => t('ui_bezopasnost');
export const ui_vnutrennie_polya_ne_vliyayut_na_obyazatelstva_pered_klien = () => t('ui_vnutrennie_polya_ne_vliyayut_na_obyazatelstva_pered_klien');
export const ui_health_obnovleniya_pm = () => t('ui_health_obnovleniya_pm');
export const ui_dobavit = () => t('ui_dobavit');
export const ui_oshibka_razmetki_dokumenta = () => t('ui_oshibka_razmetki_dokumenta');
export const ui_otkatit = () => t('ui_otkatit');
export const ui_oshibka_seti_otkroyte_ssylku_iz_pisma_esche_raz = () => t('ui_oshibka_seti_otkroyte_ssylku_iz_pisma_esche_raz');
export const ui_izmenenie_rezhima_sinhronizacii_tier_2_operaciya = () => t('ui_izmenenie_rezhima_sinhronizacii_tier_2_operaciya');
export const ui_goryachie_klavishi = () => t('ui_goryachie_klavishi');
export const ui_dvuhfaktornaya_autentifikaciya_totp = () => t('ui_dvuhfaktornaya_autentifikaciya_totp');
export const ui_sobytiya_lifecycle_tier_action_actor = () => t('ui_sobytiya_lifecycle_tier_action_actor');
export const ui_udalit_holding = () => t('ui_udalit_holding');
export const ui_nbsp_vklyuchena = () => t('ui_nbsp_vklyuchena');
export const ui_prichina_izmeneniya_dlya_zhurnala_audita = () => t('ui_prichina_izmeneniya_dlya_zhurnala_audita');
export const ui_shag_1 = () => t('ui_shag_1');
export const ui_holdingi = () => t('ui_holdingi');
export const ui_konflikt_versiy = () => t('ui_konflikt_versiy');
export const ui_podschet_diff = () => t('ui_podschet_diff');
export const ui_otklyuchennye_integracii_ne_sinhroniziruyutsya_no_ne_tery = () => t('ui_otklyuchennye_integracii_ne_sinhroniziruyutsya_no_ne_tery');
export const ui_minimum_10_simvolov = () => t('ui_minimum_10_simvolov');
export const ui_akkaunt = () => t('ui_akkaunt');
export const ui_po_pozicii = () => t('ui_po_pozicii');
export const ui_otkryt_aktualnuyu_poteryat_pravki = () => t('ui_otkryt_aktualnuyu_poteryat_pravki');
export const ui_prichina_obyazatelno = () => t('ui_prichina_obyazatelno');
export const ui_kakie_spravochniki_sinhronizirovat = () => t('ui_kakie_spravochniki_sinhronizirovat');
export const ui_shag_2 = () => t('ui_shag_2');
export const ui_po_datam = () => t('ui_po_datam');
export const ui_skachat_moi_pravki_v_md = () => t('ui_skachat_moi_pravki_v_md');
export const ui_snyatyy_chekboks_isklyuchaet_kategoriyu_iz_master_data_sy = () => t('ui_snyatyy_chekboks_isklyuchaet_kategoriyu_iz_master_data_sy');
export const ui_vvedite_6_znachnyy_kod_iz_prilozheniya = () => t('ui_vvedite_6_znachnyy_kod_iz_prilozheniya');
export const ui_bezopasnost_i_sessii = () => t('ui_bezopasnost_i_sessii');
export const ui_perezapisat_poverh = () => t('ui_perezapisat_poverh');
export const ui_vse_kategorii_otklyucheny_integraciya_ne_budet_sinhronizi = () => t('ui_vse_kategorii_otklyucheny_integraciya_ne_budet_sinhronizi');
export const ui_otozvat_sessii = () => t('ui_otozvat_sessii');
export const ui_uchetnye_dannye = () => t('ui_uchetnye_dannye');
export const ui_rezervnye_kody_pokazyvayutsya_odin_raz = () => t('ui_rezervnye_kody_pokazyvayutsya_odin_raz');
export const ui_prava_super_administratora = () => t('ui_prava_super_administratora');
export const ui_skopirovat_vse = () => t('ui_skopirovat_vse');
export const ui_sbrosit_parol = () => t('ui_sbrosit_parol');
export const ui_ya_sohranil_kody = () => t('ui_ya_sohranil_kody');
export const ui_voyti_kak = () => t('ui_voyti_kak');
export const ui_otklyuchit_2fa = () => t('ui_otklyuchit_2fa');
export const ui_gotovo = () => t('ui_gotovo');
export const ui_sgenerirovat_novye_rezervnye_kody = () => t('ui_sgenerirovat_novye_rezervnye_kody');
export const ui_podtverdit_otklyuchenie = () => t('ui_podtverdit_otklyuchenie');
export const ui_naznachenie = () => t('ui_naznachenie');
export const ui_upominanie = () => t('ui_upominanie');
export const ui_priblizhaetsya_srok = () => t('ui_priblizhaetsya_srok');
export const ui_prosrocheno = () => t('ui_prosrocheno');
export const ui_aktiven = () => t('ui_aktiven');
export const ui_priglashen = () => t('ui_priglashen');
export const ui_otklyuchen = () => t('ui_otklyuchen');
export const ui_stadiya_izmenena = () => t('ui_stadiya_izmenena');
export const ui_zdorove_izmenilos = () => t('ui_zdorove_izmenilos');
export const ui_etap_dostignut = () => t('ui_etap_dostignut');
export const ui_schet_vystavlen = () => t('ui_schet_vystavlen');
export const ui_zadacha_zavershena = () => t('ui_zadacha_zavershena');
export const ui_zadacha_prosrochena = () => t('ui_zadacha_prosrochena');
// N1 (заход С6) — ключи на ПОЛНЫЕ фразы вместо обрубков.
// Проход линтеров BE-4 подставлял короткие ключи внутрь строк (`ui_summa` в «Сумма к
// биллингу», `ui_plan` в «Плановый») — на экране оставался текст вызова, а в одном
// месте обрубок слова. Фразу переводит один ключ, склейка из фрагментов запрещена.
export const ui_izmenit_nastroyki_integracii = () => t('ui_izmenit_nastroyki_integracii');
export const ui_proverit_podklyuchenie_k_peer = () => t('ui_proverit_podklyuchenie_k_peer');
export const ui_udalit_dostup_confirm = () => t('ui_udalit_dostup_confirm');
export const ui_udalit_dostup_k_holdingu_confirm = () => t('ui_udalit_dostup_k_holdingu_confirm');
export const ui_minimum_10_simvolov_zachem_rol = () => t('ui_minimum_10_simvolov_zachem_rol');
export const ui_otkryt_v_novoy_vkladke = () => t('ui_otkryt_v_novoy_vkladke');
export const ui_ispolnitel_ne_naznachen = () => t('ui_ispolnitel_ne_naznachen');
export const ui_sbrosit_poryadok_i_razdely = () => t('ui_sbrosit_poryadok_i_razdely');
export const ui_dobavit_v_holding = () => t('ui_dobavit_v_holding');
export const ui_summa_k_billingu = () => t('ui_summa_k_billingu');
export const ui_planovyy_srok_etapa = () => t('ui_planovyy_srok_etapa');
// ─── документация: фразы с номером версии ────────────────────────────────
// N5 — собирались из ключа-глагола и русского литерала: `{m.ui_sohranit()} как
// версию {n}`. Перевод ключа давал в en-локали «Save как версию 3»: фраза целиком
// обязана быть одним ключом с параметром.
export const docs_editor_save_as_version = (p) => t('docs_editor_save_as_version', p);
export const docs_history_rollback_to_version = (p) => t('docs_history_rollback_to_version', p);
// ─── названия ролей (CC-6) ───────────────────────────────────────────
// SSOT названий ролей — `getRoleLabel` в apps/frontend/src/lib/rbac.ts. До CC-6 он
// возвращал русские строки константами, мимо i18n: en-локаль показывала «Контролёр»
// и «Наблюдатель» на каждом экране, где роль вообще упоминается.
export const role_label_accountant = () => t('role_label_accountant');
export const role_label_reviewer = () => t('role_label_reviewer');
export const role_label_sme = () => t('role_label_sme');
export const role_label_warehouse_operator = () => t('role_label_warehouse_operator');
export const role_label_production_planner = () => t('role_label_production_planner');
export const role_label_admin = () => t('role_label_admin');
export const role_label_manager = () => t('role_label_manager');
export const role_label_viewer = () => t('role_label_viewer');
export const role_label_company_admin = () => t('role_label_company_admin');
export const role_label_holding_admin = () => t('role_label_holding_admin');
export const role_label_holding_viewer = () => t('role_label_holding_viewer');
export const role_label_member = () => t('role_label_member');
// ─── раздел администрирования (CC-6) ─────────────────────────────────
// Ключи общие для вкладок (`_auth.admin.tsx`) и карточек обзора
// (`_auth.admin.index.tsx`): две копии одних названий разъехались бы при
// первом переименовании раздела.
export const admin_nav_integrations = () => t('admin_nav_integrations');
export const admin_card_desc_integrations = () => t('admin_card_desc_integrations');
export const admin_nav_sync_staging = () => t('admin_nav_sync_staging');
export const admin_card_desc_sync_staging = () => t('admin_card_desc_sync_staging');
export const admin_nav_approval_matrix = () => t('admin_nav_approval_matrix');
export const admin_card_desc_approval_matrix = () => t('admin_card_desc_approval_matrix');
export const admin_nav_holdings = () => t('admin_nav_holdings');
export const admin_nav_entities = () => t('admin_nav_entities');
export const admin_nav_users = () => t('admin_nav_users');
export const admin_card_desc_users = () => t('admin_card_desc_users');
export const admin_nav_warehouse_access = () => t('admin_nav_warehouse_access');
export const admin_card_desc_warehouse_access = () => t('admin_card_desc_warehouse_access');
export const admin_nav_bank_import = () => t('admin_nav_bank_import');
export const admin_card_desc_bank_import = () => t('admin_card_desc_bank_import');
export const admin_nav_fx_rates = () => t('admin_nav_fx_rates');
export const admin_card_desc_fx_rates = () => t('admin_card_desc_fx_rates');
export const admin_nav_jobs = () => t('admin_nav_jobs');
export const admin_card_desc_jobs = () => t('admin_card_desc_jobs');
export const admin_nav_email_templates = () => t('admin_nav_email_templates');
export const admin_nav_audit = () => t('admin_nav_audit');
export const admin_card_desc_audit = () => t('admin_card_desc_audit');
export const admin_nav_memory = () => t('admin_nav_memory');
export const admin_card_desc_memory = () => t('admin_card_desc_memory');
// ─── профиль и настройки (CC-6) ──────────────────────────────────────
// Экран был наполовину на каждом языке: «Interface language» рядом с «Имя».
export const prefs_avatar_updated = () => t('prefs_avatar_updated');
export const prefs_avatar_upload_failed = () => t('prefs_avatar_upload_failed');
export const prefs_avatar_removed = () => t('prefs_avatar_removed');
export const prefs_avatar_upload = () => t('prefs_avatar_upload');
export const prefs_email_sent = () => t('prefs_email_sent');
export const prefs_email_check_new = () => t('prefs_email_check_new');
export const prefs_failed = () => t('prefs_failed');
export const prefs_new_email = () => t('prefs_new_email');
export const prefs_send_confirmation = () => t('prefs_send_confirmation');
export const prefs_profile_saved = () => t('prefs_profile_saved');
export const prefs_full_name = () => t('prefs_full_name');
export const prefs_password_changed = () => t('prefs_password_changed');
export const prefs_sign_in_again = () => t('prefs_sign_in_again');
export const prefs_passwords_mismatch = () => t('prefs_passwords_mismatch');
export const prefs_change_password = () => t('prefs_change_password');
export const prefs_2fa_setup_failed = () => t('prefs_2fa_setup_failed');
export const prefs_2fa_enabled = () => t('prefs_2fa_enabled');
export const prefs_2fa_save_codes = () => t('prefs_2fa_save_codes');
export const prefs_2fa_code_verified = () => t('prefs_2fa_code_verified');
export const prefs_2fa_code_invalid = () => t('prefs_2fa_code_invalid');
export const prefs_2fa_disabled = () => t('prefs_2fa_disabled');
export const prefs_2fa_disable_failed = () => t('prefs_2fa_disable_failed');
export const prefs_2fa_codes_regenerated = () => t('prefs_2fa_codes_regenerated');
export const prefs_2fa_old_codes_dead = () => t('prefs_2fa_old_codes_dead');
export const prefs_2fa_enable = () => t('prefs_2fa_enable');
export const prefs_2fa_qr_alt = () => t('prefs_2fa_qr_alt');
export const prefs_2fa_code_label = () => t('prefs_2fa_code_label');
export const prefs_2fa_codes_copied = () => t('prefs_2fa_codes_copied');
export const prefs_2fa_prompt_current_code = () => t('prefs_2fa_prompt_current_code');
export const prefs_2fa_code_6digit = () => t('prefs_2fa_code_6digit');
// ─── Фаза C (template-editor-and-dates §5) — редактор шаблона ────────
export const tmpl_view_edit_action = () => t('tmpl_view_edit_action');
export const tmpl_view_assignee_chip = (p) => t('tmpl_view_assignee_chip', p);
export const tmpl_view_cta_tooltip_active = (p) => t('tmpl_view_cta_tooltip_active', p);
export const tmpl_view_cta_tooltip_inactive = (p) => t('tmpl_view_cta_tooltip_inactive', p);
export const tmpl_create_title = () => t('tmpl_create_title');
export const tmpl_reason_name_required = () => t('tmpl_reason_name_required');
export const tmpl_date_mode_label = () => t('tmpl_date_mode_label');
export const tmpl_reason_anchor_required = () => t('tmpl_reason_anchor_required');
export const tmpl_reason_finish_required = () => t('tmpl_reason_finish_required');
export const tmpl_reason_due_required = () => t('tmpl_reason_due_required');
export const tmpl_create_submit = () => t('tmpl_create_submit');
export const tmpl_create_advanced = () => t('tmpl_create_advanced');
export const tmpl_create_kind_hint = () => t('tmpl_create_kind_hint');
export const tmpl_edit_title = () => t('tmpl_edit_title');
export const tmpl_edit_save = () => t('tmpl_edit_save');
export const tmpl_edit_unsaved = () => t('tmpl_edit_unsaved');
export const tmpl_edit_add_task = () => t('tmpl_edit_add_task');
export const tmpl_edit_add_folder = () => t('tmpl_edit_add_folder');
export const tmpl_edit_new_task = () => t('tmpl_edit_new_task');
export const tmpl_edit_new_folder = () => t('tmpl_edit_new_folder');
export const tmpl_edit_task_title = () => t('tmpl_edit_task_title');
export const tmpl_edit_empty_hint = () => t('tmpl_edit_empty_hint');
export const tmpl_edit_move_to = () => t('tmpl_edit_move_to');
export const tmpl_edit_move_root = () => t('tmpl_edit_move_root');
export const tmpl_edit_move_no_folders = () => t('tmpl_edit_move_no_folders');
export const tmpl_edit_move_up = () => t('tmpl_edit_move_up');
export const tmpl_edit_move_down = () => t('tmpl_edit_move_down');
export const tmpl_edit_props = () => t('tmpl_edit_props');
export const tmpl_edit_delete_task = () => t('tmpl_edit_delete_task');
export const tmpl_edit_kind_to_folder = () => t('tmpl_edit_kind_to_folder');
export const tmpl_edit_kind_to_task = () => t('tmpl_edit_kind_to_task');
export const tmpl_edit_folder_cleared = () => t('tmpl_edit_folder_cleared');
export const tmpl_edit_normalized = (p) => t('tmpl_edit_normalized', p);
export const tmpl_edit_set_day = () => t('tmpl_edit_set_day');
export const tmpl_edit_clear_day = () => t('tmpl_edit_clear_day');
export const tmpl_edit_day_plus = () => t('tmpl_edit_day_plus');
export const tmpl_edit_day_minus = () => t('tmpl_edit_day_minus');
export const tmpl_edit_estimate_value = () => t('tmpl_edit_estimate_value');
export const tmpl_edit_estimate_uom = () => t('tmpl_edit_estimate_uom');
export const tmpl_edit_assignees = () => t('tmpl_edit_assignees');
export const tmpl_edit_remove_assignee = () => t('tmpl_edit_remove_assignee');
export const tmpl_edit_description_placeholder = () => t('tmpl_edit_description_placeholder');
export const tmpl_edit_add_milestone = () => t('tmpl_edit_add_milestone');
export const tmpl_edit_new_milestone = () => t('tmpl_edit_new_milestone');
export const tmpl_edit_no_milestones = () => t('tmpl_edit_no_milestones');
export const tmpl_edit_milestone_name = () => t('tmpl_edit_milestone_name');
export const tmpl_edit_billing_pct = () => t('tmpl_edit_billing_pct');
export const tmpl_edit_milestone_link = () => t('tmpl_edit_milestone_link');
export const tmpl_edit_milestone_no_link = () => t('tmpl_edit_milestone_no_link');
export const tmpl_edit_delete_milestone = () => t('tmpl_edit_delete_milestone');
export const tmpl_edit_add_dep = () => t('tmpl_edit_add_dep');
export const tmpl_edit_dep_from = () => t('tmpl_edit_dep_from');
export const tmpl_edit_dep_to = () => t('tmpl_edit_dep_to');
export const tmpl_edit_dep_type = () => t('tmpl_edit_dep_type');
export const tmpl_edit_dep_duplicate = () => t('tmpl_edit_dep_duplicate');
export const tmpl_edit_dep_self = () => t('tmpl_edit_dep_self');
export const tmpl_edit_no_deps = () => t('tmpl_edit_no_deps');
export const tmpl_edit_delete_dep = () => t('tmpl_edit_delete_dep');
export const tmpl_edit_validation_title = () => t('tmpl_edit_validation_title');
export const tmpl_edit_restore_draft_title = () => t('tmpl_edit_restore_draft_title');
export const tmpl_edit_restore_draft_body = () => t('tmpl_edit_restore_draft_body');
export const tmpl_edit_restore_draft_confirm = () => t('tmpl_edit_restore_draft_confirm');
export const tmpl_edit_restore_draft_discard = () => t('tmpl_edit_restore_draft_discard');
export const tmpl_edit_conflict_title = () => t('tmpl_edit_conflict_title');
export const tmpl_edit_conflict_body = (p) => t('tmpl_edit_conflict_body', p);
export const tmpl_edit_conflict_confirm = () => t('tmpl_edit_conflict_confirm');
export const tmpl_edit_leave_title = () => t('tmpl_edit_leave_title');
export const tmpl_edit_leave_confirm = () => t('tmpl_edit_leave_confirm');
export const tmpl_edit_leave_discard = () => t('tmpl_edit_leave_discard');
// Раунд 2 дизайн-гейта фазы C (F-C5…F-C9): версия на экране, авторство чужой
// правки, судьба черновика и подсветки нормализации.
export const tmpl_edit_editing_version = (p) => t('tmpl_edit_editing_version', p);
export const tmpl_edit_save_hint = (p) => t('tmpl_edit_save_hint', p);
export const tmpl_edit_save_first = () => t('tmpl_edit_save_first');
export const tmpl_edit_save_hint_first = (p) => t('tmpl_edit_save_hint_first', p);
export const tmpl_edit_notice_dismiss = () => t('tmpl_edit_notice_dismiss');
export const tmpl_edit_shifted_hint = () => t('tmpl_edit_shifted_hint');
export const tmpl_edit_restore_draft_saved_at = (p) => t('tmpl_edit_restore_draft_saved_at', p);
export const tmpl_edit_restore_draft_discard_warning = () => t('tmpl_edit_restore_draft_discard_warning');
export const tmpl_edit_conflict_author = (p) => t('tmpl_edit_conflict_author', p);
export const tmpl_edit_conflict_author_unknown = (p) => t('tmpl_edit_conflict_author_unknown', p);
export const tmpl_edit_conflict_cancel_hint = () => t('tmpl_edit_conflict_cancel_hint');
// Фаза D — честность дат: предупреждение «план в прошлом» и превью «как есть».
// error_* коды пака идут через `error_by_code`, отдельных экспортов не требуют.
export const tmpl_anchor_past_warn = (p) => t('tmpl_anchor_past_warn', p);
export const tmpl_dup_as_is_range = (p) => t('tmpl_dup_as_is_range', p);
export const tmpl_dup_as_is_past = () => t('tmpl_dup_as_is_past');
export const tmpl_dup_as_is_no_dates = () => t('tmpl_dup_as_is_no_dates');
// Раунд 2 дизайн-гейта фазы D: placeholder'ы пикеров, путь исправления в
// предупреждении, сущность в пустом превью дубликата, success-состояния.
export const picker_sales_order_placeholder = () => t('picker_sales_order_placeholder');
export const picker_counterparty_placeholder = () => t('picker_counterparty_placeholder');
export const tmpl_anchor_past_hint_finish = () => t('tmpl_anchor_past_hint_finish');
export const tmpl_anchor_past_hint_start = () => t('tmpl_anchor_past_hint_start');
export const tmpl_anchor_no_dates_project = () => t('tmpl_anchor_no_dates_project');
export const tmpl_materialize_done_title = () => t('tmpl_materialize_done_title');
export const tmpl_materialize_done = (p) => t('tmpl_materialize_done', p);
export const tmpl_materialize_done_plan = (p) => t('tmpl_materialize_done_plan', p);
export const tmpl_duplicate_done_title = () => t('tmpl_duplicate_done_title');
export const tmpl_duplicate_done = (p) => t('tmpl_duplicate_done', p);
/**
 * F-D13 — имя копии БЕЗ повтора глагола.
 *
 * Заголовок диалога уже говорит «Копия создана»; карточка под ним повторяла тот
 * же глагол («Копия „X“ создана»), и подтверждение читалось дважды. Карточке
 * остаётся назвать предмет — что именно создано.
 */
export const tmpl_duplicate_done_name = (p) => t('tmpl_duplicate_done_name', p);
/** Почему «Сдвинуть даты» выключено: у проекта нет плановых дат. */
export const tmpl_dup_shift_no_dates_hint = () => t('tmpl_dup_shift_no_dates_hint');
/** Подпись группы режимов дат в диалоге дубликата. */
export const tmpl_dup_dates_group_label = () => t('tmpl_dup_dates_group_label');
/** Подписи остальных групп диалога дубликата — подписана обязана быть каждая. */
export const tmpl_dup_statuses_group_label = () => t('tmpl_dup_statuses_group_label');
export const tmpl_dup_copy_group_label = () => t('tmpl_dup_copy_group_label');
export const tmpl_saved_title = () => t('tmpl_saved_title');
// Пак context-menu (F1–F15) — подписи пунктов ПКМ-меню, причины disabled
// и aria-label'ы панелей. Состав и порядок — Figma «Контекстное меню (ПКМ)».
export const ctx_menu_task_label = () => t('ctx_menu_task_label');
export const ctx_menu_tasks_label = () => t('ctx_menu_tasks_label');
export const ctx_menu_project_label = () => t('ctx_menu_project_label');
export const ctx_menu_blank_label = () => t('ctx_menu_blank_label');
export const ctx_open = () => t('ctx_open');
export const ctx_open_full = () => t('ctx_open_full');
export const ctx_open_task_project = () => t('ctx_open_task_project');
export const ctx_open_source = () => t('ctx_open_source');
export const ctx_copy = () => t('ctx_copy');
export const ctx_copy_link = () => t('ctx_copy_link');
export const ctx_copy_code = () => t('ctx_copy_code');
export const ctx_copy_title = () => t('ctx_copy_title');
export const ctx_copy_links_n = (p) => t('ctx_copy_links_n', p);
export const ctx_copy_codes_n = (p) => t('ctx_copy_codes_n', p);
export const ctx_copied = () => t('ctx_copied');
export const ctx_labels = () => t('ctx_labels');
export const ctx_due_today = () => t('ctx_due_today');
export const ctx_due_tomorrow = () => t('ctx_due_tomorrow');
export const ctx_due_next_week = () => t('ctx_due_next_week');
export const ctx_due_pick = () => t('ctx_due_pick');
export const ctx_due_clear = () => t('ctx_due_clear');
export const ctx_assignee_me = () => t('ctx_assignee_me');
export const ctx_assignee_clear = () => t('ctx_assignee_clear');
export const ctx_assignee_none = () => t('ctx_assignee_none');
export const ctx_assignee_search = () => t('ctx_assignee_search');
export const ctx_set_dates = () => t('ctx_set_dates');
export const ctx_add_subtask = () => t('ctx_add_subtask');
export const ctx_duplicate_task = () => t('ctx_duplicate_task');
export const ctx_move_to_project = () => t('ctx_move_to_project');
export const ctx_add_to_project = () => t('ctx_add_to_project');
export const ctx_dependencies = () => t('ctx_dependencies');
export const ctx_dep_blocked_by = () => t('ctx_dep_blocked_by');
export const ctx_dep_blocks = () => t('ctx_dep_blocks');
export const ctx_log_time = () => t('ctx_log_time');
export const ctx_subscribe = () => t('ctx_subscribe');
export const ctx_unsubscribe = () => t('ctx_unsubscribe');
export const ctx_archive_task = () => t('ctx_archive_task');
export const ctx_archive_n = (p) => t('ctx_archive_n', p);
export const ctx_restore_task = () => t('ctx_restore_task');
export const ctx_archived_undo = () => t('ctx_archived_undo');
export const ctx_undo = () => t('ctx_undo');
export const ctx_create_task = () => t('ctx_create_task');
export const ctx_paste = () => t('ctx_paste');
export const ctx_grouping = () => t('ctx_grouping');
export const ctx_columns = () => t('ctx_columns');
export const ctx_collapse_all = () => t('ctx_collapse_all');
export const ctx_clear_dates = () => t('ctx_clear_dates');
export const ctx_show_in_table = () => t('ctx_show_in_table');
export const ctx_remove_from_calendar = () => t('ctx_remove_from_calendar');
export const ctx_move_to_date = () => t('ctx_move_to_date');
export const ctx_open_project_tasks = () => t('ctx_open_project_tasks');
export const ctx_open_project_card = () => t('ctx_open_project_card');
export const ctx_new_task_in_project = () => t('ctx_new_task_in_project');
export const ctx_rename = () => t('ctx_rename');
export const ctx_stage = () => t('ctx_stage');
export const ctx_status_update = () => t('ctx_status_update');
export const ctx_project_settings = () => t('ctx_project_settings');
export const ctx_members = () => t('ctx_members');
export const ctx_visibility = () => t('ctx_visibility');
export const ctx_visibility_value_open = () => t('ctx_visibility_value_open');
export const ctx_visibility_value_private = () => t('ctx_visibility_value_private');
export const ctx_duplicate_project = () => t('ctx_duplicate_project');
export const ctx_save_as_template = () => t('ctx_save_as_template');
export const ctx_move_to_company = () => t('ctx_move_to_company');
export const ctx_archive_project = () => t('ctx_archive_project');
export const ctx_cancel_project = () => t('ctx_cancel_project');
export const ctx_stage_needs_signature = () => t('ctx_stage_needs_signature');
export const ctx_why_archived = () => t('ctx_why_archived');
export const ctx_note_archived = () => t('ctx_note_archived');
export const ctx_why_no_states = () => t('ctx_why_no_states');
export const ctx_why_no_labels = () => t('ctx_why_no_labels');
export const ctx_why_no_members = () => t('ctx_why_no_members');
export const ctx_why_folder_needs_subtree = () => t('ctx_why_folder_needs_subtree');
export const ctx_why_needs_admin = () => t('ctx_why_needs_admin');
export const ctx_why_no_target_projects = () => t('ctx_why_no_target_projects');
export const ctx_why_paste_unavailable = () => t('ctx_why_paste_unavailable');
export const ctx_why_grouping_unavailable = () => t('ctx_why_grouping_unavailable');
export const ctx_why_no_grouping = () => t('ctx_why_no_grouping');
export const ctx_note_clipboard_empty = () => t('ctx_note_clipboard_empty');
export const ctx_note_no_grouping = () => t('ctx_note_no_grouping');
export const ctx_why_project_archived = () => t('ctx_why_project_archived');
export const ctx_why_project_cancelled = () => t('ctx_why_project_cancelled');
export const ctx_why_already_on_card = () => t('ctx_why_already_on_card');
export const ctx_why_cross_project_selection = () => t('ctx_why_cross_project_selection');
export const ctx_note_admin = () => t('ctx_note_admin');
export const ctx_note_no_second_company = () => t('ctx_note_no_second_company');
export const ctx_why_no_second_company = () => t('ctx_why_no_second_company');
export const ctx_copy_failed = () => t('ctx_copy_failed');
/**
 * D4.1 (CC-6) — en-локаль разделов администрирования, переключателя компаний и
 * личных настроек. Строки жили литералами: на английском эти поверхности читались
 * наполовину по-русски, а половина из них — атрибуты (aria-label/placeholder/title),
 * которые барьер видит только правилом BB-2.
 */
export const toast_close_notification = () => t('toast_close_notification');
export const toast_region_label = () => t('toast_region_label');
export const entity_switcher_no_holding = () => t('entity_switcher_no_holding');
export const entity_switcher_all_companies = () => t('entity_switcher_all_companies');
export const entity_switcher_pick_company = () => t('entity_switcher_pick_company');
export const entity_switcher_available = () => t('entity_switcher_available');
export const entity_switcher_none_available = () => t('entity_switcher_none_available');
export const entity_switcher_toggle = () => t('entity_switcher_toggle');
export const entity_switcher_search = () => t('entity_switcher_search');
export const entity_switcher_search_placeholder = () => t('entity_switcher_search_placeholder');
export const entity_switcher_holding = () => t('entity_switcher_holding');
export const sidebar_main_nav = () => t('sidebar_main_nav');
export const sidebar_section_hint = () => t('sidebar_section_hint');
export const sidebar_section_name = () => t('sidebar_section_name');
export const sidebar_cancel = () => t('sidebar_cancel');
export const sidebar_rename = () => t('sidebar_rename');
export const sidebar_drag_reorder = () => t('sidebar_drag_reorder');
export const sidebar_drag_item = () => t('sidebar_drag_item');
export const sidebar_create = () => t('sidebar_create');
export const sidebar_create_section = () => t('sidebar_create_section');
export const sidebar_delete = () => t('sidebar_delete');
export const sidebar_delete_section = () => t('sidebar_delete_section');
export const switch_entity_dialog_title = () => t('switch_entity_dialog_title');
export const admin_subnav_label = () => t('admin_subnav_label');
export const admin_holdings_subtitle = () => t('admin_holdings_subtitle');
export const admin_holdings_col_companies = () => t('admin_holdings_col_companies');
export const admin_col_name = () => t('admin_col_name');
export const admin_holdings_create_failed = () => t('admin_holdings_create_failed');
export const admin_holdings_empty = () => t('admin_holdings_empty');
export const admin_holdings_empty_hint = () => t('admin_holdings_empty_hint');
export const admin_col_created = () => t('admin_col_created');
export const admin_col_status = () => t('admin_col_status');
export const admin_holdings_created = () => t('admin_holdings_created');
export const admin_holdings_title = () => t('admin_holdings_title');
export const admin_entities_bin_hint = () => t('admin_entities_bin_hint');
export const admin_entities_bin = () => t('admin_entities_bin');
export const admin_entities_bin_optional = () => t('admin_entities_bin_optional');
export const admin_entities_currency = () => t('admin_entities_currency');
export const admin_entities_currency_default = () => t('admin_entities_currency_default');
export const admin_entities_title = () => t('admin_entities_title');
export const admin_entities_created = () => t('admin_entities_created');
export const admin_entities_create_failed = () => t('admin_entities_create_failed');
export const admin_entities_empty = () => t('admin_entities_empty');
export const admin_entities_holding = () => t('admin_entities_holding');
export const admin_entities_holding_optional = () => t('admin_entities_holding_optional');
export const admin_entities_subtitle = () => t('admin_entities_subtitle');
export const admin_users_super = () => t('admin_users_super');
export const admin_users_set_display_name = () => t('admin_users_set_display_name');
/** Короткая пометка в ячейке имени: показан логин, потому что имя не заполнено. */
export const admin_users_name_missing = () => t('admin_users_name_missing');
export const admin_users_display_name = () => t('admin_users_display_name');
export const admin_users_manage_access = () => t('admin_users_manage_access');
export const admin_email_templates_subtitle = () => t('admin_email_templates_subtitle');
export const admin_email_templates_list = () => t('admin_email_templates_list');
export const admin_email_templates_title = () => t('admin_email_templates_title');
export const admin_automation_project_uuid = () => t('admin_automation_project_uuid');
export const admin_automation_event_approval = () => t('admin_automation_event_approval');
export const admin_automation_event_conflict = () => t('admin_automation_event_conflict');
export const admin_automation_rule_name = () => t('admin_automation_rule_name');
export const admin_automation_event_anomaly = () => t('admin_automation_event_anomaly');
export const admin_automation_signature = () => t('admin_automation_signature');
export const admin_automation_email_preview = () => t('admin_automation_email_preview');
export const admin_automation_task_title_default = (p) => t('admin_automation_task_title_default', p);
export const admin_automation_event = () => t('admin_automation_event');
export const admin_automation_type = () => t('admin_automation_type');
export const admin_automation_delete_rule = () => t('admin_automation_delete_rule');
export const admin_automation_delete_template = () => t('admin_automation_delete_template');
export const admin_automation_task_title_placeholder = () => t('admin_automation_task_title_placeholder');
export const admin_automation_email_template = () => t('admin_automation_email_template');
export const admin_integrations_pick_approver = () => t('admin_integrations_pick_approver');
export const admin_integrations_mode_both = () => t('admin_integrations_mode_both');
export const admin_integrations_disabled_toast = () => t('admin_integrations_disabled_toast');
export const admin_integrations_scope_counterparties = () => t('admin_integrations_scope_counterparties');
export const admin_integrations_reason_example = () => t('admin_integrations_reason_example');
export const admin_integrations_saved = () => t('admin_integrations_saved');
export const admin_integrations_disable_failed = () => t('admin_integrations_disable_failed');
export const admin_integrations_save_failed = () => t('admin_integrations_save_failed');
export const admin_integrations_scope_items = () => t('admin_integrations_scope_items');
export const admin_integrations_approver = () => t('admin_integrations_approver');
export const admin_integrations_disable = () => t('admin_integrations_disable');
export const admin_integrations_disable_and_wipe = () => t('admin_integrations_disable_and_wipe');
export const admin_integrations_disable_title = () => t('admin_integrations_disable_title');
export const admin_integrations_state_disabled = () => t('admin_integrations_state_disabled');
export const admin_integrations_conn_error = () => t('admin_integrations_conn_error');
export const admin_integrations_scope_batches = () => t('admin_integrations_scope_batches');
export const admin_integrations_conn_ok = () => t('admin_integrations_conn_ok');
export const admin_integrations_reason = () => t('admin_integrations_reason');
export const admin_integrations_sync_mode = () => t('admin_integrations_sync_mode');
export const admin_integrations_scope_warehouses = () => t('admin_integrations_scope_warehouses');
export const admin_integrations_scope_boms = () => t('admin_integrations_scope_boms');
export const admin_integrations_mode_push = () => t('admin_integrations_mode_push');
export const admin_integrations_mode_pull = () => t('admin_integrations_mode_pull');
export const admin_integrations_creds_valid = () => t('admin_integrations_creds_valid');
export const admin_integrations_creds_invalid = () => t('admin_integrations_creds_invalid');
export const admin_integrations_not_set = () => t('admin_integrations_not_set');
export const admin_integrations_masked_note = () => t('admin_integrations_masked_note');
export const admin_integrations_approver_note = () => t('admin_integrations_approver_note');
export const me_2fa_enable = () => t('me_2fa_enable');
export const me_2fa_disable = () => t('me_2fa_disable');
export const me_email_change_note = () => t('me_email_change_note');
export const me_2fa_note = () => t('me_2fa_note');
export const me_2fa_scan_note = () => t('me_2fa_scan_note');
export const me_2fa_backup_note = () => t('me_2fa_backup_note');
/**
 * D4.1 (CC-6) — ДЕФОЛТЫ примитивов. Находка крупнее самой задачи: «Нет данных»,
 * «Отменить», «Подтвердить», «Причина» и формат-подсказки дат были русскими
 * литералами В ЗНАЧЕНИЯХ ПО УМОЛЧАНИЮ `Table`, `ConfirmDialog`, `DatePicker`,
 * `DateRangePicker`. То есть каждый экран, не передавший проп явно, показывал
 * русское слово в любой локали — и раздел администрирования тут ни при чём.
 * Дефолт обязан быть переводом, иначе перевод остаётся делом каждого места вызова.
 */
export const tm_group_count_of_total = (p) => t('tm_group_count_of_total', p);
export const tm_truncated_page_order = (p) => t('tm_truncated_page_order', p);
export const tm_show_max_page = () => t('tm_show_max_page');
/** FND-17 — постраничная навигация по выдаче задач. */
export const tm_page_prev = () => t('tm_page_prev');
export const tm_page_next = () => t('tm_page_next');
export const tm_page_position = (p) => t('tm_page_position', p);
export const tm_page_rows_range = (p) => t('tm_page_rows_range', p);
export const tm_page_nav_label = () => t('tm_page_nav_label');
export const tm_page_drop_grouping = () => t('tm_page_drop_grouping');
export const tm_page_clears_selection = (p) => t('tm_page_clears_selection', p);
export const tm_page_grouped_unavailable = () => t('tm_page_grouped_unavailable');
export const tm_counts_scope_all = () => t('tm_counts_scope_all');
export const tm_group_off_page = () => t('tm_group_off_page');
export const tm_rows_position = (p) => t('tm_rows_position', p);
export const tm_group_only_this = () => t('tm_group_only_this');
export const component_table_select_all = () => t('component_table_select_all');
export const component_table_select_row = (p) => t('component_table_select_row', p);
export const component_table_resize_aria = (p) => t('component_table_resize_aria', p);
export const primitive_table_empty = () => t('primitive_table_empty');
export const primitive_confirm_ok = () => t('primitive_confirm_ok');
export const primitive_confirm_cancel = () => t('primitive_confirm_cancel');
export const primitive_confirm_reason = () => t('primitive_confirm_reason');
export const primitive_date_placeholder = () => t('primitive_date_placeholder');
export const primitive_required_sr = () => t('primitive_required_sr');
export const primitive_date_range_placeholder = () => t('primitive_date_range_placeholder');
/**
 * D4.1 (CC-6) — названия почтовых шаблонов приходили ГОТОВОЙ русской строкой с
 * сервера (`label` в `/admin/email-templates/preview`), то есть прямое нарушение
 * правила 19: UI-подпись сочинял бэкенд. Сервер отдаёт `kind`, подпись — здесь.
 * Само письмо остаётся русским: это письмо, а не интерфейс.
 */
export const admin_email_kind_invite = () => t('admin_email_kind_invite');
export const admin_email_kind_password_reset = () => t('admin_email_kind_password_reset');
export const admin_email_kind_email_change = () => t('admin_email_kind_email_change');
export const admin_email_kind_task_notification = () => t('admin_email_kind_task_notification');
export const admin_email_preview_of = (p) => t('admin_email_preview_of', p);
/** D4.1 — подпись копирования trace у тоста (была шаблонным литералом, класс BB-2). */
export const toast_copy_trace = (p) => t('toast_copy_trace', p);
/**
 * D4.3 (TE-F6) — сводка задач в карточке проекта. Найдено живым обходом в en:
 * подпись оставалась «3 задач · ✓ 0 завершено · ⚠ 1 просрочено» на полностью
 * английском экране — JSX-текст с числом между тегами, который правило №1
 * барьера не ловит (оно смотрит только цельный `>текст<`).
 */
export const project_tasks_total_n = (p) => t('project_tasks_total_n', p);
export const project_tasks_completed_n = (p) => t('project_tasks_completed_n', p);
export const project_tasks_overdue_n = (p) => t('project_tasks_overdue_n', p);
/**
 * Rework дизайн-гейта D4 (UX-ревьюер, раунд 2).
 * `..._shows_closed` — побочное следствие среза названо ПРИ нём: срез снимает
 * умолчание «скрывать завершённые», и молчаливое переопределение пользователь
 * унёс бы в следующий фильтр, не поняв, откуда лишние строки.
 * `..._note_one` — форма единственного числа: «1 … у них есть подзадачи» не сходится.
 */
export const tm_filters_scope_shows_closed = () => t('tm_filters_scope_shows_closed');
export const tm_progress_hero_excluded_note_one = () => t('tm_progress_hero_excluded_note_one');
/**
 * Rework дизайн-гейта D4 (UX-ревьюер, раунд 2) — обратный путь из рабочего
 * пространства задач в карточку проекта. Переход по срезу «вне расчёта» менял
 * навигационную модель («Обзор · Этапы · Финансы» → «Таблица · Канбан · Гант»),
 * и дороги назад на экране не было: drill-down без drill-up.
 */
export const tm_workspace_back_to_card = () => t('tm_workspace_back_to_card');
/**
 * F12 — индикация паузы проекта. Само действие «Приостановить» в реестре было и
 * до пака; чего не было — видимого признака, что проект СЕЙЧАС на паузе, и
 * объяснения, почему по нему перестали приходить алармы.
 */
export const tm_project_on_hold_badge = () => t('tm_project_on_hold_badge');
export const tm_project_on_hold_title = () => t('tm_project_on_hold_title');
/** F12, раунд 2: отказ перехода обязан быть виден — иначе «кнопка не нажалась». */
export const tm_project_resume_failed = () => t('tm_project_resume_failed');
/** Раунд 3: ожидание видно не только глазом — подпись меняется, диктор её произносит. */
export const tm_project_resuming = () => t('tm_project_resuming');
/** Раунд 5: у успеха и отказа равные права на голос — иначе успех «сообщается пропажей». */
export const tm_project_resumed = () => t('tm_project_resumed');
/**
 * Rework дизайн-гейта D4, раунд 3 (совпавшая находка ОБОИХ ревьюеров): срез
 * переопределял умолчание «скрывать завершённые», а сам переключатель об этом
 * молчал — иконка перечёркнутого глаза спорила с припиской чипа. Контрол обязан
 * показывать своё фактическое положение и называть, кто его задал.
 */
export const tm_show_closed_forced_by_scope = () => t('tm_show_closed_forced_by_scope');
/**
 * Rework дизайн-гейта D4, раунд 3 (UX-ревьюер): «3 задач» на том же экране, где
 * склонение уже починено точечно. Правило заводится для СЧЁТЧИКА, а не для одной
 * подписи: формы выбираются `Intl.PluralRules` по текущему языку.
 */
export const project_tasks_total_one = (p) => t('project_tasks_total_one', p);
export const project_tasks_total_few = (p) => t('project_tasks_total_few', p);
/**
 * Rework дизайн-гейта D4, раунд 4 (UX-ревьюер): срез показывает исключённые
 * задачи ВМЕСТЕ с подзадачами, и число строк перестало совпадать с числом из
 * карточки. Арифметику 1↔3 пользователь выводил глазами по отступам — счёт
 * называется словами: сколько корневых и почему их больше на экране.
 * Склоняется ВСЯ фраза, а не только число: «1 задача … показаны» не сходится.
 */
export const tm_scope_roots_one = (p) => t('tm_scope_roots_one', p);
export const tm_scope_roots_few = (p) => t('tm_scope_roots_few', p);
export const tm_scope_roots_many = (p) => t('tm_scope_roots_many', p);
export const tm_scope_shown_one = (p) => t('tm_scope_shown_one', p);
export const tm_scope_shown_few = (p) => t('tm_scope_shown_few', p);
export const tm_scope_shown_many = (p) => t('tm_scope_shown_many', p);
/** Rework дизайн-гейта D4 (UI-ревьюер, р.5): «· 2» у названия не называло, чего счёт. */
export const tm_tree_child_count_aria = (p) => t('tm_tree_child_count_aria', p);
/**
 * Rework дизайн-гейта D4, раунд 6 (UX-ревьюер): единственный НЕ описанный случай
 * оказался ровно тем, что видно на экране — «поле 100%, метод не засчитал» жил
 * только в native-подсказке, недостижимой с клавиатуры. Словарь колонки обязан
 * покрывать все состояния, а не все ЗНАЧКИ.
 *
 * Раунд 7 — ревьюер поймал ФАКТИЧЕСКУЮ ошибку первой редакции этой строки: она
 * говорила «метод не засчитывает», описывая метод, который на экране НЕ включён.
 * У проекта средний прогресс, и 100% как раз засчитано (50 и 100 → 75). Текст
 * обязан называть общее правило и оговаривать исключение, а не наоборот.
 */
export const tm_progress_legend_full_not_done = () => t('tm_progress_legend_full_not_done');
/**
 * F1 (project-plan-change §4.1) — баннер зафиксированного говорит правду.
 *
 * Прежние `ui_kontrakt_i_plan_zafiksirovany` и `ui_napisat_pm` удалены вместе с
 * кнопкой: первая обещала запрет, которого на сервере не было, вторая вела в
 * `mailto:pm@staiter.local` — домена `.local` в интернете не существует.
 */
export const project_edit_page_title = () => t('project_edit_page_title');
export const project_fixed_lead = () => t('project_fixed_lead');
export const project_locked_group_label = () => t('project_locked_group_label');
export const project_fixed_plan_title = () => t('project_fixed_plan_title');
/** F5 — ссылка из баннера на само действие: до неё правило висело без выхода. */
export const project_fixed_plan_link = () => t('project_fixed_plan_link');
export const project_fixed_plan_body = () => t('project_fixed_plan_body');
export const project_fixed_contract_title = () => t('project_fixed_contract_title');
export const project_fixed_contract_body_with_order = () => t('project_fixed_contract_body_with_order');
export const project_fixed_contract_body_no_order = () => t('project_fixed_contract_body_no_order');
export const project_fixed_contract_tail = () => t('project_fixed_contract_tail');
/**
 * F4/F5/F6 (project-plan-change §2, §4.3) — перенос срока с обоснованием.
 * Основания эскалации локализуются по ключу на основание: список, а не одна
 * строка, потому что перенос может задеть сразу несколько порогов.
 */
export const project_plan_shift_title = () => t('project_plan_shift_title');
export const project_plan_shift_action = () => t('project_plan_shift_action');
export const project_plan_shift_start_label = () => t('project_plan_shift_start_label');
export const project_plan_shift_end_label = () => t('project_plan_shift_end_label');
export const project_plan_shift_reason_label = () => t('project_plan_shift_reason_label');
export const project_plan_shift_reason_hint = (p) => t('project_plan_shift_reason_hint', p);
export const project_plan_shift_preview_title = () => t('project_plan_shift_preview_title');
/** Подписи пар дат — полей два, и превью обязано показывать оба (гейт фазы B, UX-3). */
export const project_plan_shift_pair_start = () => t('project_plan_shift_pair_start');
export const project_plan_shift_pair_end = () => t('project_plan_shift_pair_end');
export const project_plan_shift_preview_pending = () => t('project_plan_shift_preview_pending');
export const project_plan_shift_preview_none = () => t('project_plan_shift_preview_none');
export const project_plan_shift_delta_start = (p) => t('project_plan_shift_delta_start', p);
export const project_plan_shift_delta_end = (p) => t('project_plan_shift_delta_end', p);
export const project_plan_shift_delta_none = () => t('project_plan_shift_delta_none');
export const project_plan_shift_days_signed_fwd = (p) => t('project_plan_shift_days_signed_fwd', p);
export const project_plan_shift_days_signed_back = (p) => t('project_plan_shift_days_signed_back', p);
export const project_plan_shift_days_forward = (p) => t('project_plan_shift_days_forward', p);
export const project_plan_shift_days_backward = (p) => t('project_plan_shift_days_backward', p);
export const project_plan_shift_no_baseline = () => t('project_plan_shift_no_baseline');
export const project_plan_shift_baseline_kept = () => t('project_plan_shift_baseline_kept');
export const project_plan_shift_basis_first_baseline = () => t('project_plan_shift_basis_first_baseline');
export const project_plan_shift_basis_absolute = () => t('project_plan_shift_basis_absolute');
export const project_plan_shift_basis_relative = () => t('project_plan_shift_basis_relative');
export const project_plan_shift_basis_backward = () => t('project_plan_shift_basis_backward');
export const project_plan_shift_signature_needed = () => t('project_plan_shift_signature_needed');
export const project_plan_shift_signature_free = () => t('project_plan_shift_signature_free');
export const project_plan_shift_approver_label = () => t('project_plan_shift_approver_label');
export const project_plan_shift_approver_placeholder = () => t('project_plan_shift_approver_placeholder');
export const project_plan_shift_block_dates = () => t('project_plan_shift_block_dates');
export const project_plan_shift_block_reason = (p) => t('project_plan_shift_block_reason', p);
export const project_plan_shift_block_approver = () => t('project_plan_shift_block_approver');
/** Гейт фазы B (BLOCKER) — перевёрнутый диапазон принимался молча. */
export const project_plan_shift_block_range = () => t('project_plan_shift_block_range');
/**
 * Восьмой проход гейта — под полем стоит только ДИАГНОЗ, инструкция «что сделать»
 * живёт в статус-строке. Прежде оба говорили «исправьте даты» подряд.
 */
export const project_plan_shift_range_diagnosis = () => t('project_plan_shift_range_diagnosis');
/** Заголовок блока в состоянии ошибки: он больше не «что изменится». */
export const project_plan_shift_preview_invalid_title = () => t('project_plan_shift_preview_invalid_title');
/** Строка причины говорит, ЧТО СДЕЛАТЬ, а не повторяет диагноз из превью. */
export const project_plan_shift_block_range_hint = () => t('project_plan_shift_block_range_hint');
export const project_plan_shift_confirm = () => t('project_plan_shift_confirm');
export const project_plan_shift_keep = () => t('project_plan_shift_keep');
export const project_plan_shift_toast_ok = () => t('project_plan_shift_toast_ok');
/**
 * Гейт фазы B (UI-3 / UX-5) — плитка «СРОКИ 29 дн» и строка «сдвинут на 30 дн.»
 * стояли под одним словом и считали РАЗНОЕ: первая — сколько осталось, вторая —
 * насколько уехал план. Плитка получает собственную подпись.
 */
export const project_hero_until_deadline = () => t('project_hero_until_deadline');
export const project_hero_deadline_shifted = () => t('project_hero_deadline_shifted');
export const project_hero_deadline_absent = () => t('project_hero_deadline_absent');
export const project_card_plan_shift_link_aria = () => t('project_card_plan_shift_link_aria');
export const project_card_plan_shift = (p) => t('project_card_plan_shift', p);
export const project_card_plan_shift_back = (p) => t('project_card_plan_shift_back', p);
/** F8 (B10) — подписать некому: перечень недостающих ролей словами. */
export const approver_none_available = (p) => t('approver_none_available', p);
/**
 * F15 (project-plan-change §C) — мастер спецификации доводит до ПРИГОДНОГО.
 *
 * Мастер жёстко слал `is_active: false`, а производственный заказ отвергает
 * неактивную спецификацию кодом `INACTIVE_BOM`: человек проходил три шага,
 * получал «создано» и упирался на следующем экране. Тупик после успеха.
 */
export const route_bom_new_activate_label = () => t('route_bom_new_activate_label');
export const route_bom_new_activate_hint = () => t('route_bom_new_activate_hint');
export const route_bom_new_activate_conflict = () => t('route_bom_new_activate_conflict');
/**
 * Гейт фазы C (UX-1, UX-4) — исход и пустой список согласующих получают ПУТЬ.
 *
 * Бейдж «Отменён» не отвечал ни на «когда», ни на «кем», ни на «почему»; текст
 * «подписать некому» называл роли, но не говорил, куда идти их выдавать.
 */
export const project_outcome_evidence_link = () => t('project_outcome_evidence_link');
export const approver_none_available_cta = () => t('approver_none_available_cta');
/** B12 (project-plan-change) — проект отменён, задачи исполнителя закрыты. */
export const tm_notif_project_cancelled = () => t('tm_notif_project_cancelled');
export const tm_notif_project_cancelled_body = (p) => t('tm_notif_project_cancelled_body', p);
export const tm_notif_go_project_card = () => t('tm_notif_go_project_card');
// ─── tier2-approval-requests (F1-F7) — Tier 2 как адресная заявка ───
//
// Ключи интерфейса заявки: отправка вместо подделанной подписи, метка «ждёт
// согласования» на объекте, экран очереди с адресностью, решение адресата.
export const approval_request_cta = () => t('approval_request_cta');
export const approval_request_addressee_label = () => t('approval_request_addressee_label');
export const approval_request_addressee_placeholder = () => t('approval_request_addressee_placeholder');
export const approval_request_addressee_hint = () => t('approval_request_addressee_hint');
export const approval_request_addressee_why = () => t('approval_request_addressee_why');
export const approval_request_impact_pending = () => t('approval_request_impact_pending');
export const approval_request_sent_title = () => t('approval_request_sent_title');
export const approval_request_sent_body = () => t('approval_request_sent_body');
export const approval_request_open_queue = () => t('approval_request_open_queue');
export const approval_request_block_addressee = () => t('approval_request_block_addressee');
export const approval_pending_badge_short = () => t('approval_pending_badge_short');
export const approval_pending_menu_disabled = () => t('approval_pending_menu_disabled');
export const approval_action_project_cancel = () => t('approval_action_project_cancel');
export const approval_action_project_uncancel = () => t('approval_action_project_uncancel');
export const approval_action_project_archive = () => t('approval_action_project_archive');
export const approval_action_project_restore = () => t('approval_action_project_restore');
export const approval_action_project_complete = () => t('approval_action_project_complete');
export const approval_action_project_close = () => t('approval_action_project_close');
export const approval_action_project_plan_shift = () => t('approval_action_project_plan_shift');
export const approval_action_unknown = () => t('approval_action_unknown');
export const approval_queue_tab_inbox = () => t('approval_queue_tab_inbox');
export const approval_queue_tab_outbox = () => t('approval_queue_tab_outbox');
export const approval_queue_tab_history = () => t('approval_queue_tab_history');
export const approval_queue_col_addressee = () => t('approval_queue_col_addressee');
export const approval_queue_col_requester = () => t('approval_queue_col_requester');
export const approval_queue_col_decided = () => t('approval_queue_col_decided');
/**
 * Возраст заявки словами — решение владельца В-8: у заявки Tier 2 нет срока, есть
 * время ожидания. «556ч» это не время ожидания, а показание счётчика: человеку
 * нужно «ждёт 23 дня», чтобы понять, что заявка забыта, без деления в уме.
 *
 * Часы держатся до двух суток — там они ещё информативны («ждёт 6 часов» точнее,
 * чем «ждёт 0 дней»); дальше единица укрупняется.
 */
export const approval_age_waiting = (p) => {
    const h = Math.max(0, Math.round(p.hours));
    if (h < 1)
        return t('approval_age_lt_hour');
    if (h < 48) {
        const word = pluralForm(h, {
            one: t('approval_age_hours_one'),
            few: t('approval_age_hours_few'),
            many: t('approval_age_hours_many'),
        });
        return `${t('approval_age_prefix')} ${h} ${word}`;
    }
    const d = Math.floor(h / 24);
    const word = pluralForm(d, {
        one: t('approval_age_days_one'),
        few: t('approval_age_days_few'),
        many: t('approval_age_days_many'),
    });
    return `${t('approval_age_prefix')} ${d} ${word}`;
};
export const approval_queue_col_waiting = () => t('approval_queue_col_waiting');
export const approval_impact_title = () => t('approval_impact_title');
export const approval_impact_snapshot_note = () => t('approval_impact_snapshot_note');
/** Тот же снимок, но с названным моментом расчёта и его возрастом (гейт T2A-F4, раунд 2). */
export const approval_impact_snapshot_note_at = (p) => t('approval_impact_snapshot_note_at', p);
/** Предмет заявки недостижим по правам — место видно, переход нет (STAITER_UX.md:59). */
export const approval_subject_link_no_module = () => t('approval_subject_link_no_module');
/** Метка поля «за кем решение» в карточке — не путать с колонкой очереди «Кому». */
export const approval_meta_decider = () => t('approval_meta_decider');
export const approval_queue_col_expires = () => t('approval_queue_col_expires');
export const approval_queue_empty_inbox = () => t('approval_queue_empty_inbox');
export const approval_queue_empty_outbox = () => t('approval_queue_empty_outbox');
export const approval_queue_empty_history = () => t('approval_queue_empty_history');
export const approval_queue_open_subject = () => t('approval_queue_open_subject');
export const approval_status_waiting = () => t('approval_status_waiting');
export const approval_status_executing = () => t('approval_status_executing');
export const approval_status_approved = () => t('approval_status_approved');
export const approval_status_rejected = () => t('approval_status_rejected');
export const approval_status_expired = () => t('approval_status_expired');
export const approval_status_withdrawn = () => t('approval_status_withdrawn');
export const approval_status_cancelled = () => t('approval_status_cancelled');
export const approval_decide_intent_title = () => t('approval_decide_intent_title');
export const approval_decide_reason_label = () => t('approval_decide_reason_label');
export const approval_decide_reason_required = () => t('approval_decide_reason_required');
export const approval_decide_approve = () => t('approval_decide_approve');
export const approval_decide_reject = () => t('approval_decide_reject');
export const approval_decide_failed_title = () => t('approval_decide_failed_title');
export const approval_decide_failed_hint = () => t('approval_decide_failed_hint');
export const approval_reassign_cta = () => t('approval_reassign_cta');
export const approval_reassign_title = () => t('approval_reassign_title');
export const approval_reassign_hint = () => t('approval_reassign_hint');
export const approval_withdraw_cta = () => t('approval_withdraw_cta');
export const approval_withdraw_confirm = () => t('approval_withdraw_confirm');
export const approval_pending_badge = (p) => t('approval_pending_badge', p);
export const approval_pending_addressee = (p) => t('approval_pending_addressee', p);
export const approval_pending_until = (p) => t('approval_pending_until', p);
/** Величина сдвига плана словами: «Сдвиг плана: −29 дней» (T2A-F12(а)). */
export const approval_plan_shift_summary = (p) => {
    const word = pluralForm(p.days, {
        one: t('approval_age_days_one'),
        few: t('approval_age_days_few'),
        many: t('approval_age_days_many'),
    });
    return `${t('approval_plan_shift_prefix')}: ${p.sign}${p.days} ${word}`;
};
export const role_label_service = () => t('role_label_service');
export const module_grants_empty_default_note = () => t('module_grants_empty_default_note');
export const service_account_badge = () => t('service_account_badge');
export const service_account_badge_title = () => t('service_account_badge_title');
export const module_grants_service_readonly = () => t('module_grants_service_readonly');
export const service_account_no_ui = () => t('service_account_no_ui');
export const service_account_rate_na = () => t('service_account_rate_na');
export const service_account_rotate_cta = () => t('service_account_rotate_cta');
export const service_account_rotate_title = () => t('service_account_rotate_title');
export const service_account_rotate_body = () => t('service_account_rotate_body');
export const service_account_rotate_copy = () => t('service_account_rotate_copy');
export const service_account_rotate_copied = () => t('service_account_rotate_copied');
export const service_account_rotate_confirm_title = () => t('service_account_rotate_confirm_title');
export const service_account_rotate_confirm_body = () => t('service_account_rotate_confirm_body');
export const service_account_rotate_confirm_cta = () => t('service_account_rotate_confirm_cta');
export const service_account_rotate_revoked = (p) => t('service_account_rotate_revoked', p);
export const approval_decide_requester_hint = () => t('approval_decide_requester_hint');
export const approval_diff_field_outcome = () => t('approval_diff_field_outcome');
export const approval_diff_field_archive = () => t('approval_diff_field_archive');
export const approval_diff_field_stage = () => t('approval_diff_field_stage');
export const approval_diff_field_plan_start = () => t('approval_diff_field_plan_start');
export const approval_diff_field_plan_end = () => t('approval_diff_field_plan_end');
export const approval_diff_archived = () => t('approval_diff_archived');
export const approval_diff_not_archived = () => t('approval_diff_not_archived');
// F24 (SR-F14) — подпись Tier 3 как вход в журнал сущности.
export const tier3_signature_open_source = () => t('tier3_signature_open_source');
// F27 — плашка отключённой учётной записи на экране доступов; подсказка выбора компании.
export const admin_memberships_user_disabled = () => t('admin_memberships_user_disabled');
export const user_drawer_company_placeholder = () => t('user_drawer_company_placeholder');
// F27 круг 2 — отключённая учётная запись и легенда признака роли.
export const admin_memberships_enable_who = () => t('admin_memberships_enable_who');
export const admin_memberships_not_in_effect = () => t('admin_memberships_not_in_effect');
export const module_grant_effective_suspended = () => t('module_grant_effective_suspended');
// F28 (T2A-F14) — расхождение снимка последствий и пересборка заявки.
export const approval_impact_drift_title = () => t('approval_impact_drift_title');
export const approval_impact_drift_hint = () => t('approval_impact_drift_hint');
export const approval_impact_drift_aria = (p) => t('approval_impact_drift_aria', p);
export const approval_impact_key_cancel_count = () => t('approval_impact_key_cancel_count');
export const approval_impact_key_keep_shared_count = () => t('approval_impact_key_keep_shared_count');
export const approval_impact_key_keep_no_state_count = () => t('approval_impact_key_keep_no_state_count');
export const approval_impact_key_keep_done_count = () => t('approval_impact_key_keep_done_count');
export const approval_impact_key_other = () => t('approval_impact_key_other');
export const approval_impact_rebuild_cta = () => t('approval_impact_rebuild_cta');
export const approval_impact_rebuild_who = () => t('approval_impact_rebuild_who');
export const approval_impact_rebuilt_toast = () => t('approval_impact_rebuilt_toast');
export const approval_impact_rebuild_failed = () => t('approval_impact_rebuild_failed');
export const approval_impact_changed_blocked = () => t('approval_impact_changed_blocked');
export const approval_impact_changed_toast_title = () => t('approval_impact_changed_toast_title');
export const approval_impact_rebuilt_note_at = (p) => t('approval_impact_rebuilt_note_at', p);
// F29 (T2A-F15) — ApprovalDiff: подпись таблицы и колонки направления.
export const component_approval_diff_aria = () => t('component_approval_diff_aria');
export const component_approval_diff_direction = () => t('component_approval_diff_direction');
// F33 (T2A-F19) — метка секции меты в дровере согласования.
export const route_approvals_section_meta = () => t('route_approvals_section_meta');
// F29 круг 1 — расчёт на сейчас, подсказка инициатору, обратимость отмены.
export const approval_impact_drift_hint_requester = () => t('approval_impact_drift_hint_requester');
export const approval_impact_now_note = () => t('approval_impact_now_note');
export const project_cancel_impact_reversible = () => t('project_cancel_impact_reversible');
// F30 (T2A-F16) — «дольше нормы» и напоминание решающему.
export const approval_overdue_norm = (p) => t('approval_overdue_norm', p);
export const approval_remind_cta = () => t('approval_remind_cta');
export const approval_remind_done = () => t('approval_remind_done');
export const approval_remind_failed = () => t('approval_remind_failed');
export const approval_remind_too_soon = (p) => t('approval_remind_too_soon', p);
export const approval_remind_last = (p) => t('approval_remind_last', p);
export const approval_remind_last_next = (p) => t('approval_remind_last_next', p);
// F29 круг 2 — состояние заявки, ход инициатора при расхождении.
export const approval_state_waits_you_rebuild = () => t('approval_state_waits_you_rebuild');
export const approval_state_waits_rebuild = () => t('approval_state_waits_rebuild');
export const approval_state_waits_decider = () => t('approval_state_waits_decider');
export const approval_decide_requester_rebuild_hint = () => t('approval_decide_requester_rebuild_hint');
export const approval_remind_blocked_drift = () => t('approval_remind_blocked_drift');
// F29 круг 3 — дата снимка у расхождения.
export const approval_impact_drift_title_at = (p) => t('approval_impact_drift_title_at', p);
// F31 (T2A-F17) — маршрут заявки.
export const approval_meta_route = () => t('approval_meta_route');
export const approval_route_position = (p) => t('approval_route_position', p);
export const approval_route_status_done = () => t('approval_route_status_done');
export const approval_route_status_current = () => t('approval_route_status_current');
export const approval_route_status_pending = () => t('approval_route_status_pending');
export const approval_route_status_rejected = () => t('approval_route_status_rejected');
export const approval_route_status_skipped = () => t('approval_route_status_skipped');
export const approval_route_who_unknown = () => t('approval_route_who_unknown');
export const approval_route_who_any = (p) => t('approval_route_who_any', p);
export const approval_route_status_after_rebuild = () => t('approval_route_status_after_rebuild');
// F32 (T2A-F18, CIA-F7) — адресат отказа по имени.
export const approval_subject_link_no_module_named = () => t('approval_subject_link_no_module_named');
// F34 круг 1 — список выдающих доступ строками.
export const access_grantors_title_one = () => t('access_grantors_title_one');
export const access_grantors_title_many = () => t('access_grantors_title_many');
export const access_grantors_more = (p) => t('access_grantors_more', p);
// F60 (TPL-F1/F5) — сводка «вошло» и откат копии.
export const tmpl_summary_included_title = () => t('tmpl_summary_included_title');
export const tmpl_summary_included_tasks = () => t('tmpl_summary_included_tasks');
export const tmpl_summary_included_deps = () => t('tmpl_summary_included_deps');
export const tmpl_duplicate_archive_copy = () => t('tmpl_duplicate_archive_copy');
// F61 (TPL-F3/F4/F6/F10) — тексты диалогов шаблонов.
export const tmpl_dup_links_group_label = () => t('tmpl_dup_links_group_label');
export const tmpl_visibility_open_consequence = () => t('tmpl_visibility_open_consequence');
export const tmpl_default_template_name = (p) => t('tmpl_default_template_name', p);
export const tmpl_save_copy_group_label = () => t('tmpl_save_copy_group_label');
export const tmpl_opt_include_done = () => t('tmpl_opt_include_done');
export const tmpl_action_save_short = () => t('tmpl_action_save_short');
// F61 круг 1 — существительные в группах, последствия у кнопки.
export const tmpl_opt_assignees = () => t('tmpl_opt_assignees');
export const tmpl_opt_done_tasks = () => t('tmpl_opt_done_tasks');
export const tmpl_template_private_consequence = () => t('tmpl_template_private_consequence');
export const tmpl_template_private_consequence_people = () => t('tmpl_template_private_consequence_people');
export const tmpl_dup_dates_none = () => t('tmpl_dup_dates_none');
// F66 круг 1 — плашка результата копии.
export const tmpl_duplicate_done_created = () => t('tmpl_duplicate_done_created');
export const tmpl_summary_nothing_skipped = () => t('tmpl_summary_nothing_skipped');
export const tmpl_duplicate_archive_copy_hint = () => t('tmpl_duplicate_archive_copy_hint');
// F68 (WL-B1) — чип заказа с номером.
export const tm_worklog_wo_chip_numbered = (p) => t('tm_worklog_wo_chip_numbered', p);
// F52 (WHS-F27…F31) — реестр складских документов.
export const route_se_list_col_warehouse = () => t('route_se_list_col_warehouse');
export const route_se_list_filter_all_types = () => t('route_se_list_filter_all_types');
export const route_se_list_filter_all_warehouses = () => t('route_se_list_filter_all_warehouses');
export const route_se_list_filter_from = () => t('route_se_list_filter_from');
export const route_se_list_filter_to = () => t('route_se_list_filter_to');
export const route_se_list_shown_of = (p) => t('route_se_list_shown_of', p);
// F53 (WHS-F19/F21/F22/F23/F26, B22) — предпросмотр инвентаризации.
export const route_sr_preview_title_named = (p) => t('route_sr_preview_title_named', p);
export const route_sr_preview_count = (p) => t('route_sr_preview_count', p);
export const route_sr_preview_provenance = (p) => t('route_sr_preview_provenance', p);
export const route_sr_reasons_where = () => t('route_sr_reasons_where');
export const route_sr_btn_continue = () => t('route_sr_btn_continue');
export const valuation_method_moving_avg = () => t('valuation_method_moving_avg');
export const valuation_method_fifo = () => t('valuation_method_fifo');
export const valuation_method_lifo = () => t('valuation_method_lifo');
// F53 (WHS-F21) — причины отклонений в предпросмотре.
export const route_sr_reason_for = (p) => t('route_sr_reason_for', p);
export const route_sr_reason_none = () => t('route_sr_reason_none');
export const route_sr_reason_save_failed = () => t('route_sr_reason_save_failed');
// F55 (WHS-F14/F16) — мастер складского документа.
export const route_se_new_policy_title = () => t('route_se_new_policy_title');
export const route_se_new_uom_pending = () => t('route_se_new_uom_pending');
// F56 (WHS-F17) — метрика остатков называет, что считает.
// WHS-B3 — проведено по нулевой оценке.
export const se_valued_at_zero_title = () => t('se_valued_at_zero_title');
export const se_valued_at_zero_body = () => t('se_valued_at_zero_body');
export const se_valued_at_zero_col = () => t('se_valued_at_zero_col');
export const se_valued_at_zero_chip = () => t('se_valued_at_zero_chip');
// Гейт F59 (D-UX-1…3) — метрики остатков по всей выборке.
export const stock_balance_hero_rows = () => t('stock_balance_hero_rows');
export const stock_balance_hero_value = () => t('stock_balance_hero_value');
export const stock_balance_hero_value_sub = (p) => t('stock_balance_hero_value_sub', p);
export const stock_balance_hero_value_mixed = () => t('stock_balance_hero_value_mixed');
export const stock_balance_hero_unvalued_rows = () => t('stock_balance_hero_unvalued_rows');
export const stock_balance_hero_unvalued_sub = (p) => t('stock_balance_hero_unvalued_sub', p);
// Гейт F59 (B) — подвал предпросмотра инвентаризации.
export const route_sr_irreversible_effect = (p) => t('route_sr_irreversible_effect', p);
export const route_sr_submit_blocked_reasons = (p) => t('route_sr_submit_blocked_reasons', p);
// Гейт F59 (E-UX-2) — провенанс учётного остатка в мастере инвентаризации.
export const route_sr_new_lines_provenance = (p) => t('route_sr_new_lines_provenance', p);
export const route_sr_col_reason = () => t('route_sr_col_reason');
// Гейт F59 (C2-UI-1) — короткие подсказки пикеров.
export const picker_item_placeholder = () => t('picker_item_placeholder');
export const picker_warehouse_placeholder = () => t('picker_warehouse_placeholder');
// Гейт F59 (D2) — остатки: подпись плитки, отбор строк без оценки.
export const stock_balance_hero_rows_sub_items = (p) => t('stock_balance_hero_rows_sub_items', p);
export const stock_balance_hero_rows_sub_shown = (p) => t('stock_balance_hero_rows_sub_shown', p);
export const stock_balance_unvalued_show = () => t('stock_balance_unvalued_show');
export const stock_balance_unvalued_show_all = () => t('stock_balance_unvalued_show_all');
export const stock_balance_unvalued_mark = () => t('stock_balance_unvalued_mark');
// Гейт F59 (E2) — шаг «Готово» мастера инвентаризации.
export const route_sr_new_draft_created_named = (p) => t('route_sr_new_draft_created_named', p);
export const route_sr_new_btn_open_draft = () => t('route_sr_new_btn_open_draft');
export const route_sr_new_done_reasons_next = (p) => t('route_sr_new_done_reasons_next', p);
export const route_sr_new_reason_rule = () => t('route_sr_new_reason_rule');
// Гейт F59 (D3) — остатки: частичная сумма, метка отбора.
export const stock_balance_hero_value_sub_partial = (p) => t('stock_balance_hero_value_sub_partial', p);
export const stock_balance_unvalued_filter_chip = () => t('stock_balance_unvalued_filter_chip');
export const stock_balance_shown_of = (p) => t('stock_balance_shown_of', p);
// F40 (WTU-F18) — пустые «Ответственные» у просроченной задачи.
export const tm_card_overdue_no_assignee = (p) => t('tm_card_overdue_no_assignee', { days: String(p.days) });
// F43 (WTU-FND-B2, WTU-FND-D3) — панель массовых действий.
export const tm_bulk_dates = () => t('tm_bulk_dates');
export const tm_bulk_unarchive = (p) => t('tm_bulk_unarchive', { count: String(p.count) });
// F44/F45 (WTU-FND-D2, WTU-FND-D4) — выделение между страницами, все найденные.
export const tm_bulk_show_all_rows = () => t('tm_bulk_show_all_rows');
// F95 (§9.6) — «Не на экране» и исход «Выбрать все найденные».
export const tm_bulk_off_screen_show = (p) => t('tm_bulk_off_screen_show', p);
export const tm_bulk_off_screen = (p) => t('tm_bulk_off_screen', p);
export const tm_bulk_select_all_found_add = (p) => t('tm_bulk_select_all_found_add', p);
export const tm_bulk_select_all_found_replace = (p) => t('tm_bulk_select_all_found_replace', p);
export const tm_bulk_confirm_show_them = () => t('tm_bulk_confirm_show_them');
export const tm_bulk_confirm_off_screen = (p) => t('tm_bulk_confirm_off_screen', p);
export const tm_bulk_large_confirm_title = (p) => t('tm_bulk_large_confirm_title', p);
export const tm_bulk_large_confirm_body = (p) => t('tm_bulk_large_confirm_body', p);
export const tm_bulk_large_confirm_go = (p) => t('tm_bulk_large_confirm_go', p);
export const tm_bulk_batches_stopped = (p) => t('tm_bulk_batches_stopped', p);
export const tm_bulk_unarchive_plain = () => t('tm_bulk_unarchive_plain');
export const tm_group_select_all = (p) => t('tm_group_select_all', p);
export const tm_group_legend_aria = () => t('tm_group_legend_aria');
export const tm_filter_link_expired = () => t('tm_filter_link_expired');
export const tm_cancel_subtree_folder_fate = () => t('tm_cancel_subtree_folder_fate');
export const lbl_runtime_status_queued = () => t('lbl_runtime_status_queued');
export const lbl_runtime_status_running = () => t('lbl_runtime_status_running');
export const lbl_runtime_status_in_progress = () => t('lbl_runtime_status_in_progress');
export const lbl_runtime_status_waiting_approval = () => t('lbl_runtime_status_waiting_approval');
export const lbl_runtime_status_waiting_external = () => t('lbl_runtime_status_waiting_external');
export const lbl_runtime_status_failed = () => t('lbl_runtime_status_failed');
export const lbl_runtime_status_completed = () => t('lbl_runtime_status_completed');
export const lbl_runtime_status_cancelled = () => t('lbl_runtime_status_cancelled');
export const lbl_task_type_inventory_reconcile = () => t('lbl_task_type_inventory_reconcile');
export const lbl_task_type_writeoff_review = () => t('lbl_task_type_writeoff_review');
export const lbl_task_type_cost_draft = () => t('lbl_task_type_cost_draft');
export const lbl_task_type_anomaly_investigation = () => t('lbl_task_type_anomaly_investigation');
export const lbl_task_type_period_close_draft = () => t('lbl_task_type_period_close_draft');
export const lbl_approval_status_pending = () => t('lbl_approval_status_pending');
export const lbl_approval_status_approved = () => t('lbl_approval_status_approved');
export const lbl_approval_status_rejected = () => t('lbl_approval_status_rejected');
export const lbl_approval_status_expired = () => t('lbl_approval_status_expired');
export const lbl_approval_task_type_stock_entry_submit = () => t('lbl_approval_task_type_stock_entry_submit');
export const lbl_approval_task_type_stock_entry_cancel = () => t('lbl_approval_task_type_stock_entry_cancel');
export const lbl_approval_task_type_stock_reconciliation_submit = () => t('lbl_approval_task_type_stock_reconciliation_submit');
export const lbl_approval_task_type_opening_stock_submit = () => t('lbl_approval_task_type_opening_stock_submit');
export const lbl_approval_task_type_landed_cost_submit = () => t('lbl_approval_task_type_landed_cost_submit');
export const lbl_approval_task_type_valuation_method_change = () => t('lbl_approval_task_type_valuation_method_change');
export const lbl_approval_task_type_bom_activate = () => t('lbl_approval_task_type_bom_activate');
export const lbl_approval_task_type_warehouse_move = () => t('lbl_approval_task_type_warehouse_move');
export const lbl_approval_task_type_batch_transition = () => t('lbl_approval_task_type_batch_transition');
export const lbl_approval_task_type_items_variants_bulk = () => t('lbl_approval_task_type_items_variants_bulk');
export const lbl_approval_task_type_bulk_import_commit = () => t('lbl_approval_task_type_bulk_import_commit');
export const lbl_approval_task_type_bulk_invite = () => t('lbl_approval_task_type_bulk_invite');
export const lbl_approval_task_type_wo_cancel = () => t('lbl_approval_task_type_wo_cancel');
export const lbl_approval_task_type_wo_complete = () => t('lbl_approval_task_type_wo_complete');
export const lbl_approval_task_type_wo_reassign = () => t('lbl_approval_task_type_wo_reassign');
export const lbl_approval_task_type_period_close_lock = () => t('lbl_approval_task_type_period_close_lock');
export const lbl_approval_task_type_master_data_push_1c = () => t('lbl_approval_task_type_master_data_push_1c');
export const lbl_approval_task_type_aggregates_push_1c = () => t('lbl_approval_task_type_aggregates_push_1c');
export const lbl_approval_task_type_regulatory_push_esf = () => t('lbl_approval_task_type_regulatory_push_esf');
export const lbl_approval_task_type_regulatory_push_kpn = () => t('lbl_approval_task_type_regulatory_push_kpn');
export const lbl_approval_task_type_cogs_publish = () => t('lbl_approval_task_type_cogs_publish');
export const lbl_approval_task_type_counterparty_push_1c = () => t('lbl_approval_task_type_counterparty_push_1c');
export const lbl_approval_task_type_ar_invoice_post = () => t('lbl_approval_task_type_ar_invoice_post');
export const lbl_approval_task_type_ap_invoice_post = () => t('lbl_approval_task_type_ap_invoice_post');
export const lbl_approval_task_type_payment_post = () => t('lbl_approval_task_type_payment_post');
export const lbl_approval_task_type_settlement_post = () => t('lbl_approval_task_type_settlement_post');
export const lbl_approval_task_type_cash_flow_publish = () => t('lbl_approval_task_type_cash_flow_publish');
export const lbl_approval_task_type_pnl_publish = () => t('lbl_approval_task_type_pnl_publish');
export const lbl_approval_task_type_budget_activate = () => t('lbl_approval_task_type_budget_activate');
export const lbl_approval_task_type_budget_lock = () => t('lbl_approval_task_type_budget_lock');
export const lbl_approval_task_type_sales_order_cancel = () => t('lbl_approval_task_type_sales_order_cancel');
export const lbl_approval_task_type_project_complete = () => t('lbl_approval_task_type_project_complete');
export const lbl_approval_task_type_project_close = () => t('lbl_approval_task_type_project_close');
export const lbl_approval_task_type_project_cancel = () => t('lbl_approval_task_type_project_cancel');
export const lbl_approval_task_type_milestone_draft_invoice = () => t('lbl_approval_task_type_milestone_draft_invoice');
export const lbl_approval_task_type_project_archive = () => t('lbl_approval_task_type_project_archive');
export const lbl_approval_task_type_project_restore = () => t('lbl_approval_task_type_project_restore');
export const lbl_approval_task_type_project_plan_shift = () => t('lbl_approval_task_type_project_plan_shift');
export const lbl_stock_entry_type_material_receipt = () => t('lbl_stock_entry_type_material_receipt');
export const lbl_stock_entry_type_material_issue = () => t('lbl_stock_entry_type_material_issue');
export const lbl_stock_entry_type_material_transfer = () => t('lbl_stock_entry_type_material_transfer');
export const lbl_stock_entry_type_material_transfer_for_manufacture = () => t('lbl_stock_entry_type_material_transfer_for_manufacture');
export const lbl_stock_entry_type_manufacture = () => t('lbl_stock_entry_type_manufacture');
export const lbl_stock_entry_type_manufacture_reversal = () => t('lbl_stock_entry_type_manufacture_reversal');
export const lbl_stock_entry_type_repack = () => t('lbl_stock_entry_type_repack');
export const lbl_landed_cost_status_draft = () => t('lbl_landed_cost_status_draft');
export const lbl_landed_cost_status_submitted = () => t('lbl_landed_cost_status_submitted');
export const lbl_landed_cost_status_applied = () => t('lbl_landed_cost_status_applied');
export const lbl_landed_cost_status_cancelled = () => t('lbl_landed_cost_status_cancelled');
export const lbl_landed_cost_state_draft = () => t('lbl_landed_cost_state_draft');
export const lbl_landed_cost_state_pending_allocation = () => t('lbl_landed_cost_state_pending_allocation');
export const lbl_landed_cost_state_allocated = () => t('lbl_landed_cost_state_allocated');
export const lbl_landed_cost_state_cancelled = () => t('lbl_landed_cost_state_cancelled');
export const lbl_work_order_status_open = () => t('lbl_work_order_status_open');
export const lbl_work_order_status_in_progress = () => t('lbl_work_order_status_in_progress');
export const lbl_work_order_status_submitted = () => t('lbl_work_order_status_submitted');
export const lbl_work_order_status_completed = () => t('lbl_work_order_status_completed');
export const lbl_work_order_status_cancelled = () => t('lbl_work_order_status_cancelled');
export const lbl_wo_signal_release = () => t('lbl_wo_signal_release');
export const lbl_wo_signal_start = () => t('lbl_wo_signal_start');
export const lbl_wo_signal_pause_external = () => t('lbl_wo_signal_pause_external');
export const lbl_wo_signal_resume_external = () => t('lbl_wo_signal_resume_external');
export const lbl_wo_signal_complete = () => t('lbl_wo_signal_complete');
export const lbl_wo_signal_cancel = () => t('lbl_wo_signal_cancel');
export const lbl_wo_warning_insufficient_stock = () => t('lbl_wo_warning_insufficient_stock');
export const lbl_wo_warning_batch_expiry_imminent = () => t('lbl_wo_warning_batch_expiry_imminent');
export const lbl_wo_warning_batch_expired = () => t('lbl_wo_warning_batch_expired');
export const lbl_wo_warning_bom_alternate_used = () => t('lbl_wo_warning_bom_alternate_used');
export const lbl_wo_warning_bom_scrap_exceeded = () => t('lbl_wo_warning_bom_scrap_exceeded');
export const lbl_wo_warning_job_card_missing = () => t('lbl_wo_warning_job_card_missing');
export const lbl_wo_warning_cost_variance_high = () => t('lbl_wo_warning_cost_variance_high');
export const lbl_wo_warning_no_active_bom_version = () => t('lbl_wo_warning_no_active_bom_version');
export const lbl_conflict_severity_low = () => t('lbl_conflict_severity_low');
export const lbl_conflict_severity_medium = () => t('lbl_conflict_severity_medium');
export const lbl_conflict_severity_high = () => t('lbl_conflict_severity_high');
export const lbl_conflict_severity_critical = () => t('lbl_conflict_severity_critical');
export const lbl_conflict_type_intra_level = () => t('lbl_conflict_type_intra_level');
export const lbl_conflict_type_master_data_drift = () => t('lbl_conflict_type_master_data_drift');
export const lbl_conflict_type_bom_explosion_loop = () => t('lbl_conflict_type_bom_explosion_loop');
export const lbl_conflict_type_valuation_mismatch = () => t('lbl_conflict_type_valuation_mismatch');
export const lbl_conflict_type_temporal_precedence_unresolved = () => t('lbl_conflict_type_temporal_precedence_unresolved');
export const lbl_conflict_type_reconciliation_variance = () => t('lbl_conflict_type_reconciliation_variance');
export const lbl_conflict_type_tolerance_exceeded = () => t('lbl_conflict_type_tolerance_exceeded');
export const lbl_conflict_type_manual_override_deferred = () => t('lbl_conflict_type_manual_override_deferred');
export const lbl_conflict_type_master_data_field_mismatch = () => t('lbl_conflict_type_master_data_field_mismatch');
export const lbl_conflict_type_transaction_amount_mismatch = () => t('lbl_conflict_type_transaction_amount_mismatch');
export const lbl_anomaly_severity_info = () => t('lbl_anomaly_severity_info');
export const lbl_anomaly_severity_warn = () => t('lbl_anomaly_severity_warn');
export const lbl_anomaly_severity_major = () => t('lbl_anomaly_severity_major');
export const lbl_anomaly_severity_critical = () => t('lbl_anomaly_severity_critical');
export const lbl_anomaly_severity_warning = () => t('lbl_anomaly_severity_warning');
export const lbl_anomaly_type_qty_zscore_outlier = () => t('lbl_anomaly_type_qty_zscore_outlier');
export const lbl_anomaly_type_valuation_rate_jump = () => t('lbl_anomaly_type_valuation_rate_jump');
export const lbl_anomaly_type_bom_yield_variance = () => t('lbl_anomaly_type_bom_yield_variance');
export const lbl_anomaly_type_cost_layer_imbalance = () => t('lbl_anomaly_type_cost_layer_imbalance');
export const lbl_anomaly_type_velocity_drop = () => t('lbl_anomaly_type_velocity_drop');
export const lbl_anomaly_type_duplicate_voucher = () => t('lbl_anomaly_type_duplicate_voucher');
export const lbl_anomaly_type_orphan_movement = () => t('lbl_anomaly_type_orphan_movement');
export const lbl_anomaly_type_overdue_ap = () => t('lbl_anomaly_type_overdue_ap');
export const lbl_anomaly_type_overdue_ar = () => t('lbl_anomaly_type_overdue_ar');
export const lbl_anomaly_type_reconciliation_variance = () => t('lbl_anomaly_type_reconciliation_variance');
export const lbl_anomaly_type_fx_rate_jump = () => t('lbl_anomaly_type_fx_rate_jump');
export const lbl_anomaly_type_cash_flow_negative = () => t('lbl_anomaly_type_cash_flow_negative');
export const lbl_anomaly_type_scrap_above_threshold = () => t('lbl_anomaly_type_scrap_above_threshold');
export const lbl_anomaly_type_negative_stock = () => t('lbl_anomaly_type_negative_stock');
export const lbl_anomaly_type_duplicate_payment_candidate = () => t('lbl_anomaly_type_duplicate_payment_candidate');
export const lbl_anomaly_type_counterparty_missing_bin = () => t('lbl_anomaly_type_counterparty_missing_bin');
export const lbl_anomaly_type_valuation_rate_missing = () => t('lbl_anomaly_type_valuation_rate_missing');
export const lbl_qc_status_pending = () => t('lbl_qc_status_pending');
export const lbl_qc_status_passed = () => t('lbl_qc_status_passed');
export const lbl_qc_status_failed = () => t('lbl_qc_status_failed');
export const lbl_qc_status_quarantine = () => t('lbl_qc_status_quarantine');
export const lbl_valuation_method_moving_avg = () => t('lbl_valuation_method_moving_avg');
export const lbl_valuation_method_fifo = () => t('lbl_valuation_method_fifo');
export const lbl_valuation_method_lifo = () => t('lbl_valuation_method_lifo');
export const lbl_integration_health_ok = () => t('lbl_integration_health_ok');
export const lbl_integration_health_degraded = () => t('lbl_integration_health_degraded');
export const lbl_integration_health_disconnected = () => t('lbl_integration_health_disconnected');
export const lbl_integration_sync_mode_read = () => t('lbl_integration_sync_mode_read');
export const lbl_integration_sync_mode_read_write = () => t('lbl_integration_sync_mode_read_write');
export const lbl_source_system_staiter = () => t('lbl_source_system_staiter');
export const lbl_source_system_1c = () => t('lbl_source_system_1c');
export const lbl_source_system_bitrix = () => t('lbl_source_system_bitrix');
export const lbl_source_system_adesk = () => t('lbl_source_system_adesk');
export const lbl_source_system_excel = () => t('lbl_source_system_excel');
export const lbl_source_system_manual = () => t('lbl_source_system_manual');
export const lbl_source_system_bank_import = () => t('lbl_source_system_bank_import');
export const lbl_source_system_adesk_seed = () => t('lbl_source_system_adesk_seed');
export const lbl_source_system_prod_baseline_seed = () => t('lbl_source_system_prod_baseline_seed');
// TCA-F2 (пак tails-closeout, С-D) — пустая выдача под активным отбором называет отбор.
export const list_filtered_empty_title = () => t('list_filtered_empty_title');
export const list_filtered_empty_hint = () => t('list_filtered_empty_hint');
export const list_filtered_empty_total = (p) => t('list_filtered_empty_total', p);
export const list_filter_part = (p) => t('list_filter_part', p);
export const list_filter_reset = () => t('list_filter_reset');
export const tier3_requester_not_signer_hint = () => t('tier3_requester_not_signer_hint');
export const tier3_approver_eligible_hint = () => t('tier3_approver_eligible_hint');
export const tier3_signer_n = (params) => t('tier3_signer_n', params);
export const tier3_approver_field_label = () => t('tier3_approver_field_label');
export const tier3_not_enough_approvers = () => t('tier3_not_enough_approvers');
export const tier3_confirm_blocked_reason = () => t('tier3_confirm_blocked_reason');
export const stock_cancel_no_approvers = () => t('stock_cancel_no_approvers');
export const stock_cancel_approvers_load_failed = () => t('stock_cancel_approvers_load_failed');
export const stock_cancel_toast_approved_by = (params) => t('stock_cancel_toast_approved_by', params);
// ─── B75 (Ф6): письма и подписи заявок уровня 3 ───────────────────────
export const email_cta_open_request = () => t('email_cta_open_request');
export const email_tier3_requested_subject = (p) => t('email_tier3_requested_subject', p);
export const email_tier3_requested_heading = () => t('email_tier3_requested_heading');
export const email_tier3_requested_body = (p) => t('email_tier3_requested_body', p);
export const email_tier3_decided_subject = (p) => t('email_tier3_decided_subject', p);
export const email_tier3_decided_heading = () => t('email_tier3_decided_heading');
export const email_tier3_decided_approved = (p) => t('email_tier3_decided_approved', p);
export const email_tier3_decided_rejected = (p) => t('email_tier3_decided_rejected', p);
export const email_tier3_decided_unconfirmed = (p) => t('email_tier3_decided_unconfirmed', p);
/** Название действия уровня 3 по строке матрицы; неизвестная строка — общее название. */
// F96 (гейт Ф6, круг 1) — что произойдёт после второй подписи, по типу действия.
export const tier3_consequence = (p) => {
    const r = t(`tier3_consequence_${p.task_type}`);
    return typeof r === 'string' && r.startsWith('[missing:') ? null : r;
};
export const tier3_dialog_consequence_heading = () => t('tier3_dialog_consequence_heading');
export const tier3_dialog_after_send = () => t('tier3_dialog_after_send');
export const tier3_dialog_missing_cosigner = () => t('tier3_dialog_missing_cosigner');
export const tier3_dialog_missing_reason = (p) => t('tier3_dialog_missing_reason', p);
export const tier3_dialog_deadline_passed = () => t('tier3_dialog_deadline_passed');
export const tier3_dialog_deadline_passed_esf = () => t('tier3_dialog_deadline_passed_esf');
export const tier3_dialog_deadline_passed_kpn = () => t('tier3_dialog_deadline_passed_kpn');
export const tier3_dialog_deadline_passed_other = () => t('tier3_dialog_deadline_passed_other');
export const tier3_dialog_cosigner_label_roles = (p) => t('tier3_dialog_cosigner_label_roles', p);
export const tier3_dialog_roles_or = () => t('tier3_dialog_roles_or');
export const approvals_tier3_stale_badge = () => t('approvals_tier3_stale_badge');
export const approvals_tier3_waits_rebuild = () => t('approvals_tier3_waits_rebuild');
export const approvals_tier3_sign_second = () => t('approvals_tier3_sign_second');
export const approvals_tier3_requester_rebuild_hint = () => t('approvals_tier3_requester_rebuild_hint');
export const onec_push_ids_missing = () => t('onec_push_ids_missing');
// F96 (гейт Ф6, круг 2).
export const onec_push_pending_link = () => t('onec_push_pending_link');
export const tier3_dialog_documents_list_label = () => t('tier3_dialog_documents_list_label');
export const tier3_card_signature_stale = () => t('tier3_card_signature_stale');
export const tm_bulk_confirm_examples = () => t('tm_bulk_confirm_examples');
export const tm_bulk_off_screen_fact = (p) => t('tm_bulk_off_screen_fact', p);
export const tm_bulk_off_screen_action = () => t('tm_bulk_off_screen_action');
export const tier3_action_name = (p) => {
    const r = t(`tier3_action_${p.task_type}`);
    return typeof r === 'string' && r.startsWith('[missing:') ? t('tier3_action_other') : r;
};
// ─── F90 (Ф6): диалог и кнопка заявки уровня 3 ───────────────────────
export const tier3_dialog_title = (p) => t('tier3_dialog_title', p);
export const tier3_dialog_intro = () => t('tier3_dialog_intro');
export const tier3_dialog_offline_note = () => t('tier3_dialog_offline_note');
export const tier3_dialog_summary_heading = () => t('tier3_dialog_summary_heading');
export const tier3_dialog_documents_label = () => t('tier3_dialog_documents_label');
export const tier3_dialog_records_label_work_order = () => t('tier3_dialog_records_label_work_order');
export const tier3_dialog_records_label = () => t('tier3_dialog_records_label');
export const tier3_dialog_amount_label = () => t('tier3_dialog_amount_label');
export const tier3_dialog_period_label = () => t('tier3_dialog_period_label');
export const tier3_dialog_deadline_label = () => t('tier3_dialog_deadline_label');
export const tier3_dialog_more = (p) => t('tier3_dialog_more', p);
export const tier3_dialog_cosigner_label = () => t('tier3_dialog_cosigner_label');
export const tier3_dialog_cosigner_placeholder = () => t('tier3_dialog_cosigner_placeholder');
export const tier3_dialog_cosigner_hint = () => t('tier3_dialog_cosigner_hint');
export const tier3_candidate_label = (p) => t('tier3_candidate_label', p);
export const tier3_dialog_reason_label = () => t('tier3_dialog_reason_label');
export const tier3_dialog_submit = () => t('tier3_dialog_submit');
export const tier3_dialog_created = () => t('tier3_dialog_created');
export const tier3_send_reason_integration_not_connected = () => t('tier3_send_reason_integration_not_connected');
export const tier3_send_reason_no_eligible_cosigner = () => t('tier3_send_reason_no_eligible_cosigner');
export const tier3_send_reason_already_pushed = () => t('tier3_send_reason_already_pushed');
export const tier3_send_reason_initiator_not_in_zone = () => t('tier3_send_reason_initiator_not_in_zone');
export const tier3_send_reason_two_signatures = () => t('tier3_send_reason_two_signatures');
export const tier3_send_reason_zone_roles = (p) => t('tier3_send_reason_zone_roles', p);
export const tier3_send_reason_zone_nobody = () => t('tier3_send_reason_zone_nobody');
export const tier3_send_reason_zone_more = (p) => t('tier3_send_reason_zone_more', p);
export const tier3_send_reason_zone_any_role = (p) => t('tier3_send_reason_zone_any_role', p);
export const tier3_send_reason_role_head = (p) => t('tier3_send_reason_role_head', p);
export const tier3_send_reason_role_nobody = () => t('tier3_send_reason_role_nobody');
export const tier3_person_unnamed = () => t('tier3_person_unnamed');
export const tier3_unnamed_hidden = (p) => t('tier3_unnamed_hidden', p);
export const tier3_send_reason_role_unnamed_only = (p) => t('tier3_send_reason_role_unnamed_only', p);
export const tier3_send_reason_role_and_unnamed = (p) => t('tier3_send_reason_role_and_unnamed', p);
export const tier3_send_reason_unnamed_note = () => t('tier3_send_reason_unnamed_note');
export const tier3_send_reason_cosigner_unnamed = (p) => t('tier3_send_reason_cosigner_unnamed', p);
export const tier3_send_reason_zone_less = () => t('tier3_send_reason_zone_less');
export const tier3_send_reason_more_aria = (p) => t('tier3_send_reason_more_aria', p);
export const tier3_send_reason_less_aria = (p) => t('tier3_send_reason_less_aria', p);
export const tier3_send_prepare = () => t('tier3_send_prepare');
export const tier3_send_active = () => t('tier3_send_active');
export const lbl_approval_task_type_documents_push_1c = () => t('lbl_approval_task_type_documents_push_1c');
export const lbl_approval_task_type_onec_push_rollback = () => t('lbl_approval_task_type_onec_push_rollback');
export const lbl_approval_task_type_work_order_cancel_completed = () => t('lbl_approval_task_type_work_order_cancel_completed');
/** Зона ответственности подписи уровня 3 (finance / management / production). */
export const tier3_zone_name = (p) => {
    const r = t(`tier3_zone_${p.zone}`);
    return typeof r === 'string' && r.startsWith('[missing:') ? p.zone : r;
};
export const tier3_dialog_reason_min = (p) => t('tier3_dialog_reason_min', p);
// ─── F91 (Ф6): экран «Отправки в 1С» ────────────────────────────────
export const onec_push_title = () => t('onec_push_title');
export const onec_push_subtitle = () => t('onec_push_subtitle');
export const onec_push_master_title = () => t('onec_push_master_title');
export const onec_push_master_text = () => t('onec_push_master_text');
export const onec_push_btn_master = () => t('onec_push_btn_master');
export const onec_push_aggr_title = () => t('onec_push_aggr_title');
export const onec_push_aggr_type = () => t('onec_push_aggr_type');
export const onec_push_type_sle_aggregates = () => t('onec_push_type_sle_aggregates');
export const onec_push_type_stock_entries = () => t('onec_push_type_stock_entries');
export const onec_push_type_landed_cost = () => t('onec_push_type_landed_cost');
export const onec_push_period = () => t('onec_push_period');
export const onec_push_ids_label = () => t('onec_push_ids_label');
export const onec_push_ids_hint = (p) => t('onec_push_ids_hint', p);
export const onec_push_btn_aggr = () => t('onec_push_btn_aggr');
export const onec_push_docs_title = () => t('onec_push_docs_title');
export const onec_push_btn_docs = () => t('onec_push_btn_docs');
export const onec_push_history_title = () => t('onec_push_history_title');
export const onec_push_history_empty = () => t('onec_push_history_empty');
export const onec_push_col_created = () => t('onec_push_col_created');
export const onec_push_col_kind = () => t('onec_push_col_kind');
export const onec_push_col_status = () => t('onec_push_col_status');
export const onec_push_col_count = () => t('onec_push_col_count');
export const onec_push_col_actions = () => t('onec_push_col_actions');
export const onec_push_btn_rollback = () => t('onec_push_btn_rollback');
export const onec_push_rollback_reason = () => t('onec_push_rollback_reason');
export const admin_onec_push_moved = () => t('admin_onec_push_moved');
export const admin_onec_push_moved_link = () => t('admin_onec_push_moved_link');
/** Состояние строки отправки в 1С; неизвестное — кодом. */
export const onec_push_status_name = (p) => {
    const r = t(`onec_push_status_${p.status}`);
    return typeof r === 'string' && r.startsWith('[missing:') ? p.status : r;
};
// ─── F92 (Ф6): карточка заявки уровня 3 в очереди ───────────────────
export const tier3_card_heading = () => t('tier3_card_heading');
export const tier3_card_stale_requester = () => t('tier3_card_stale_requester');
export const tier3_card_stale_addressee = () => t('tier3_card_stale_addressee');
export const tier3_card_offline_addressee = () => t('tier3_card_offline_addressee');
export const tier3_card_offline_other = () => t('tier3_card_offline_other');
export const tier3_card_snapshot = () => t('tier3_card_snapshot');
export const tier3_card_deadline = (p) => t('tier3_card_deadline', p);
export const tier3_card_state_now = () => t('tier3_card_state_now');
export const tier3_card_subject_missing = () => t('tier3_card_subject_missing');
export const tier3_card_changed = () => t('tier3_card_changed');
export const tier3_card_document = () => t('tier3_card_document');
export const tier3_card_signatures = () => t('tier3_card_signatures');
export const tier3_card_signature_n = (p) => t('tier3_card_signature_n', p);
export const tier3_card_signature_pending = () => t('tier3_card_signature_pending');
export const tier3_card_rebuild = () => t('tier3_card_rebuild');
export const tier3_card_block_stale = () => t('tier3_card_block_stale');
export const tier3_card_block_offline = () => t('tier3_card_block_offline');
/** Состояние документа заявки уровня 3; неизвестное — кодом. */
export const tier3_doc_state = (p) => {
    const r = t(`tier3_doc_state_${p.state}`);
    return typeof r === 'string' && r.startsWith('[missing:') ? p.state : r;
};
export const tier3_card_open_document = () => t('tier3_card_open_document');
export const tier3_card_documents_n = (p) => t('tier3_card_documents_n', p);
export const tier3_send_reason_unavailable = () => t('tier3_send_reason_unavailable');
export const nav_finance_onec_push = () => t('nav_finance_onec_push');
export const tm_notif_go_request = () => t('tm_notif_go_request');
//# sourceMappingURL=messages.js.map